/**!
Copyright © 2016-2020 - Convergence Labs, Inc.
@version 1.0.0-rc.12
@license Licensed under the terms of the GNU Lesser General Public License version 3 (GPLv3). See https://www.gnu.org/licenses/lgpl-3.0.html
*/
import { Subject, BehaviorSubject } from 'rxjs';
import { share, filter, map, tap } from 'rxjs/operators';
import * as protobuf from 'protobufjs/light';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

var LogLevel;
(function (LogLevel) {
    LogLevel["TRACE"] = "trace";
    LogLevel["DEBUG"] = "debug";
    LogLevel["INFO"] = "info";
    LogLevel["WARN"] = "warn";
    LogLevel["ERROR"] = "error";
    LogLevel["SILENT"] = "silent";
})(LogLevel || (LogLevel = {}));

class Immutable {
    static make(source) {
        if (typeof source === "object") {
            Object.freeze(source);
            Object.keys(source).forEach((val, idx, array) => {
                Immutable.make(val);
            });
        }
    }
    static copy(source, updates) {
        const result = {};
        Object.keys(source).forEach((prop, idx, array) => {
            result[prop] = updates[prop] !== undefined ? updates[prop] : source[prop];
        });
        Object.freeze(result);
        return result;
    }
    static getOrDefault(value, defaultValue) {
        return value === undefined ? defaultValue : value;
    }
    static update(current, update) {
        return update !== undefined ? update : current;
    }
}

class LogEvent {
    constructor(timestamp, logger, level, message, error) {
        this.timestamp = timestamp;
        this.logger = logger;
        this.level = level;
        this.message = message;
        this.error = error;
        Immutable.make(this);
    }
}

class TypeChecker {
    static isArray(value) {
        return Array.isArray(value);
    }
    static isMap(value) {
        return value instanceof Map;
    }
    static isBoolean(value) {
        return typeof value === "boolean";
    }
    static isDate(value) {
        return value instanceof Date || value.constructor.name === "Date";
    }
    static isError(value) {
        return value instanceof Error && typeof value.message !== "undefined";
    }
    static isFunction(value) {
        return typeof value === "function";
    }
    static isNull(value) {
        return value === null;
    }
    static isNumber(value) {
        return typeof value === "number" && isFinite(value);
    }
    static isObject(value) {
        return value && typeof value === "object" && value.constructor === Object;
    }
    static isString(value) {
        return typeof value === "string" || value instanceof String;
    }
    static isSymbol(value) {
        return typeof value === "symbol";
    }
    static isRegExp(value) {
        return value && typeof value === "object" && value.constructor === RegExp;
    }
    static isUndefined(value) {
        return typeof value === "undefined";
    }
    static isSet(value) {
        return !TypeChecker.isNull(value) && !TypeChecker.isUndefined(value);
    }
    static isNotSet(value) {
        return !TypeChecker.isSet(value);
    }
    static switch(value, matcher) {
        if (TypeChecker.isUndefined(matcher) || TypeChecker.isNull(matcher)) {
            throw new Error("matcher must be defined.");
        }
        if (TypeChecker.isArray(value) && !TypeChecker.isUndefined(matcher.array)) {
            matcher.array(value);
        }
        else if (TypeChecker.isBoolean(value) && !TypeChecker.isUndefined(matcher.boolean)) {
            matcher.boolean(value);
        }
        else if (TypeChecker.isError(value) && !TypeChecker.isUndefined(matcher.error)) {
            matcher.error(value);
        }
        else if (TypeChecker.isDate(value) && !TypeChecker.isUndefined(matcher.date)) {
            matcher.date(value);
        }
        else if (TypeChecker.isFunction(value) && !TypeChecker.isUndefined(matcher.function)) {
            matcher.function(value);
        }
        else if (TypeChecker.isNull(value) && !TypeChecker.isUndefined(matcher.null)) {
            matcher.null();
        }
        else if (TypeChecker.isNumber(value) && !TypeChecker.isUndefined(matcher.number)) {
            matcher.number(value);
        }
        else if (TypeChecker.isObject(value) && !TypeChecker.isUndefined(matcher.object)) {
            matcher.object(value);
        }
        else if (TypeChecker.isRegExp(value) && !TypeChecker.isUndefined(matcher.regexp)) {
            matcher.regexp(value);
        }
        else if (TypeChecker.isString(value) && !TypeChecker.isUndefined(matcher.string)) {
            matcher.string(value);
        }
        else if (TypeChecker.isSymbol(value) && !TypeChecker.isUndefined(matcher.symbol)) {
            matcher.symbol(value);
        }
        else if (TypeChecker.isUndefined(value) && !TypeChecker.isUndefined(matcher.undefined)) {
            matcher.undefined();
        }
        else {
            const customRule = TypeChecker.isArray(matcher.custom) ?
                matcher.custom.find(rule => {
                    return TypeChecker.isFunction(rule.test) &&
                        TypeChecker.isFunction(rule.callback) &&
                        rule.test(value);
                }) :
                undefined;
            if (!TypeChecker.isUndefined(customRule)) {
                customRule.callback(value);
            }
            else if (!TypeChecker.isUndefined(matcher.default)) {
                matcher.default(value);
            }
        }
    }
}

const LogLevelPriority = {
    SILENT: -1,
    TRACE: 0,
    DEBUG: 1,
    INFO: 2,
    WARN: 3,
    ERROR: 4
};
class Logger {
    constructor(id, level, logWriters) {
        this._id = id;
        this._logWriters = logWriters;
        this.setLevel(level);
    }
    getId() {
        return this._id;
    }
    getLevel() {
        return this._level;
    }
    setLevel(logLevel) {
        this._level = logLevel;
        this._levelPriority = LogLevelPriority[this._level.toUpperCase()];
    }
    trace(message) {
        if (LogLevelPriority.TRACE >= this._levelPriority) {
            const event = new LogEvent(new Date(), this._id, LogLevel.TRACE, this._resolveLogMessage(message));
            this._log(event);
        }
    }
    debug(message) {
        if (LogLevelPriority.DEBUG >= this._levelPriority) {
            const event = new LogEvent(new Date(), this._id, LogLevel.DEBUG, this._resolveLogMessage(message));
            this._log(event);
        }
    }
    info(message) {
        if (LogLevelPriority.INFO >= this._levelPriority) {
            const event = new LogEvent(new Date(), this._id, LogLevel.INFO, this._resolveLogMessage(message));
            this._log(event);
        }
    }
    warn(message) {
        if (LogLevelPriority.WARN >= this._levelPriority) {
            const event = new LogEvent(new Date(), this._id, LogLevel.WARN, this._resolveLogMessage(message));
            this._log(event);
        }
    }
    error(message, e) {
        if (LogLevelPriority.ERROR >= this._levelPriority) {
            const event = new LogEvent(new Date(), this._id, LogLevel.ERROR, this._resolveLogMessage(message), e);
            this._log(event);
        }
    }
    _log(event) {
        if (this._levelPriority !== LogLevelPriority.SILENT) {
            this._logWriters.forEach(writer => writer.writeLog(event));
        }
    }
    _resolveLogMessage(message) {
        if (TypeChecker.isFunction(message)) {
            return message();
        }
        else {
            return message;
        }
    }
}

function mapObjectValues(obj, mapFunc) {
    return Object.keys(obj).reduce((newObj, value) => {
        newObj[value] = mapFunc(obj[value]);
        return newObj;
    }, {});
}
function objectForEach(obj, callback) {
    return Object.keys(obj).forEach(key => {
        callback(key, obj[key]);
    });
}
function deepClone(from) {
    const type = typeof from;
    if (from === null || from === undefined || type === "string" || type === "number" || type === "boolean") {
        return from;
    }
    if (from instanceof Date) {
        return new Date(from.getTime());
    }
    if (Array.isArray(from)) {
        return from.map(e => deepClone(e));
    }
    if (from instanceof Map) {
        const result = new Map();
        from.forEach((v, k) => {
            result.set(k, deepClone(v));
        });
        return result;
    }
    if (from instanceof Set) {
        const result = new Set();
        from.forEach(v => result.add(deepClone(v)));
        return result;
    }
    if (from.constructor === Object) {
        const result = {};
        Object.keys(from).forEach(key => {
            result[key] = deepClone(from[key]);
        });
        return result;
    }
    const name = from.constructor.name || from.constructor.toString();
    throw new Error("Can not clone unknown type: " + name);
}

class Validation {
    static isSet(value) {
        return !Validation.isNotSet(value);
    }
    static isNotSet(value) {
        return value === undefined || value === null;
    }
    static nonEmptyString(value) {
        return typeof value === "string" && value.length > 0;
    }
    static assertNonEmptyString(value, name) {
        if (name === undefined) {
            name = "value";
        }
        if (!Validation.nonEmptyString(value)) {
            throw new Error(name + " must be a non-empty string: " + typeof value);
        }
    }
    static assertString(value, name) {
        if (typeof value !== "string") {
            throw new Error(`${Validation.getValueName(name)} must be a string: ${typeof value}`);
        }
    }
    static assertValidStringIndex(index, str, inclusiveEnd, name) {
        Validation.assertValidIndex(index, 0, inclusiveEnd ? str.length + 1 : str.length, name);
    }
    static assertNumber(value, name) {
        if (typeof value !== "number") {
            throw new Error(`${Validation.getValueName(name)} must be a number: ${typeof value}`);
        }
    }
    static assertArray(value, name) {
        if (!Array.isArray(value)) {
            throw new Error(`${Validation.getValueName(name)} must be an array: ${typeof value}`);
        }
    }
    static assertNonEmptyArray(value, name) {
        Validation.assertArray(value, name);
        if (value.length === 0) {
            throw new Error(`${Validation.getValueName(name)} must be a non-empty array: ${typeof value}`);
        }
    }
    static assertValidArrayIndex(index, array, name) {
        Validation.assertValidIndex(index, 0, array.length, name);
    }
    static assertValidIndex(index, lower, upper, name) {
        Validation.assertNumber(index, name);
        if (index < lower || index >= upper) {
            name = Validation.getValueName(name);
            throw new Error(`Index out of bounds. ${name} must be > 0 and <= ${upper}: ${index}`);
        }
    }
    static assertBoolean(value, name) {
        if (typeof value !== "boolean") {
            throw new Error(`${Validation.getValueName(name)} must be a boolean but was: ${typeof value}`);
        }
    }
    static assertDate(value, name) {
        if (!TypeChecker.isDate(value)) {
            throw new Error(`${Validation.getValueName(name)} must be a Date but was: ${value.constructor.name}`);
        }
    }
    static getValueName(name, defaultValue) {
        return name || defaultValue || "value";
    }
}

class LoggingConfig {
    constructor(config) {
        this._loggers = new Map();
        if (TypeChecker.isSet(config.loggers)) {
            objectForEach(config.loggers, (id, loggerConfig) => {
                if (!Validation.nonEmptyString(id)) {
                    throw new Error("A logger's id must be a non-empty string");
                }
                this._loggers.set(id, this._processLoggerConfig(loggerConfig));
            });
        }
        this._loggers.set(LoggingConfig.ROOT_LOGGER_ID, this._processLoggerConfig(config.root));
    }
    resolveLoggerConfig(loggerId) {
        let id = loggerId;
        let logger = this._loggers.get(id);
        while (logger === undefined && id !== "") {
            const dot = id.lastIndexOf(".");
            id = id.substring(0, dot);
            logger = this._loggers.get(id);
        }
        return logger !== undefined ?
            logger :
            this._loggers.get(LoggingConfig.ROOT_LOGGER_ID);
    }
    _processLoggerConfig(config) {
        if (TypeChecker.isString(config)) {
            return { level: config };
        }
        else {
            return config;
        }
    }
}
LoggingConfig.ROOT_LOGGER_ID = "";

class PatternLogWriter {
    constructor(pattern) {
        this._pattern = pattern || PatternLogWriter.DEFAULT_PATTERN;
    }
    _formatMessage(event) {
        const level = this._pad(event.level.toUpperCase(), 5, " ", false);
        const time = this._formatTime(event);
        return `${level} ${time} ${event.message}`;
    }
    _formatTime(event) {
        const t = event.timestamp;
        return this._pad(t.getHours(), 2, "0") +
            ":" +
            this._pad(t.getMinutes(), 2, "0") +
            ":" +
            this._pad(t.getSeconds(), 2, "0") +
            "." +
            this._pad(t.getMilliseconds(), 3, "0");
    }
    _pad(value, minLen, char, left = true) {
        let str = String(value);
        while (str.length < minLen) {
            str = left ? char + str : str + char;
        }
        return str;
    }
}
PatternLogWriter.FIELDS = {
    LOG_NAME: "%l",
    MESSAGE: "%m",
    DATE_TIME: "%t",
    LOG_LEVEL: "%p"
};
PatternLogWriter.DEFAULT_PATTERN = "%t %p %m";

class ConsoleLogWriter extends PatternLogWriter {
    constructor(pattern) {
        super(pattern);
    }
    writeLog(event) {
        switch (event.level) {
            case LogLevel.TRACE:
                console.debug(this._formatMessage(event));
                break;
            case LogLevel.DEBUG:
                console.debug(this._formatMessage(event));
                break;
            case LogLevel.INFO:
                console.info(this._formatMessage(event));
                break;
            case LogLevel.WARN:
                console.warn(this._formatMessage(event));
                break;
            case LogLevel.ERROR:
                if (event.error) {
                    console.error(this._formatMessage(event) + "\n", event.error);
                }
                else {
                    console.error(this._formatMessage(event));
                }
                break;
        }
    }
}

const DEFAULT_CONFIG = {
    root: {
        level: LogLevel.WARN
    }
};
class ConvergenceLogging {
    constructor(config) {
        this.configure(config || {});
        this._writer = new ConsoleLogWriter("");
    }
    configure(config) {
        const defaulted = Object.assign(Object.assign({}, DEFAULT_CONFIG), config);
        this._config = new LoggingConfig(defaulted);
        this._loggers = new Map();
    }
    root() {
        return this.logger();
    }
    logger(id) {
        if (id === null || id === undefined) {
            id = LoggingConfig.ROOT_LOGGER_ID;
        }
        if (!this._loggers.has(id)) {
            const config = this._config.resolveLoggerConfig(id);
            this._loggers.set(id, new Logger(id, config.level, [this._writer]));
        }
        return this._loggers.get(id);
    }
}
const Logging = new ConvergenceLogging();

class HeartbeatHelper {
    constructor(handler, pingInterval, pongTimeout) {
        this._sendPing = () => {
            this._handler.sendPing();
            this._schedulePongTimeout();
        };
        this._onTimeout = () => {
            this._logger.trace(() => "A pong timeout occurred");
            this._handler.onTimeout();
        };
        this._handler = handler;
        this._pingInterval = pingInterval;
        this._pongTimeout = pongTimeout;
        this._started = false;
        this._logger = Logging.logger("heartbeat");
    }
    messageReceived() {
        if (this._started) {
            this._cancelPongTimeout();
            this._restartPingTimeout();
        }
    }
    start() {
        if (this._handler == null) {
            throw new Error("Can't start the HeartbeatManager unless the callback is set.");
        }
        this._logger.trace(() => "HeartbeatHelper started with Ping Interval " + this._pingInterval +
            " and Pong Timeout " + this._pongTimeout);
        this._started = true;
        this.messageReceived();
    }
    stop() {
        this._started = false;
        this._stopPingTimer();
        this._cancelPongTimeout();
        this._logger.trace(() => "HeartbeatHelper stopped.");
    }
    get started() {
        return this._started;
    }
    dispose() {
        this.stop();
    }
    _schedulePongTimeout() {
        this._cancelPongTimeout();
        const timeoutMillis = this._pongTimeout * 1000;
        this._timeoutFuture = setTimeout(this._onTimeout, timeoutMillis);
    }
    _cancelPongTimeout() {
        if (this._timeoutFuture !== null) {
            clearTimeout(this._timeoutFuture);
            this._timeoutFuture = null;
        }
    }
    _stopPingTimer() {
        if (this._pingFuture !== null) {
            clearTimeout(this._pingFuture);
            this._pingFuture = null;
        }
    }
    _restartPingTimeout() {
        this._stopPingTimer();
        const pingIntervalMillis = this._pingInterval * 1000;
        this._pingFuture = setTimeout(this._sendPing, pingIntervalMillis);
    }
}

class ConvergenceError extends Error {
    constructor(message, code, details) {
        super(message);
        this._code = code;
        this._details = details || {};
        this.name = "ConvergenceError";
        Object.setPrototypeOf(this, ConvergenceError.prototype);
    }
    get code() {
        return this._code;
    }
    get details() {
        return this._details;
    }
}

class ConvergenceEventEmitter {
    constructor() {
        this._defaultSubject = new Subject();
        this._observable = this._defaultSubject.asObservable().pipe(share());
        this._listeners = new Map();
    }
    static _resolveEventKey(event) {
        if (typeof event === "string") {
            return event.toLowerCase();
        }
        else {
            throw new Error("Event names must be strings: " + typeof event);
        }
    }
    addListener(event, listener) {
        if (typeof listener !== "function") {
            throw new TypeError("Listeners must be functions");
        }
        event = ConvergenceEventEmitter._resolveEventKey(event);
        let listeners = this._listeners.get(event);
        if (listeners === undefined) {
            listeners = [];
            this._listeners.set(event, listeners);
        }
        else if (listeners.find(r => r.listener === listener) !== undefined) {
            return this;
        }
        const subscription = this._observable
            .pipe(filter((e) => e.name.toLowerCase() === event))
            .subscribe((e) => {
            listener(e);
        });
        listeners.push({ listener, subscription });
        return this;
    }
    on(event, listener) {
        return this.addListener(event, listener);
    }
    once(event, listener) {
        const wrapper = (e) => {
            this.removeListener(event, wrapper);
            listener(e);
        };
        return this.addListener(event, wrapper);
    }
    removeAllListeners() {
        Array
            .from(this._listeners.keys())
            .forEach(event => this.removeListeners(event));
        return this;
    }
    removeListeners(event) {
        event = ConvergenceEventEmitter._resolveEventKey(event);
        if (this._listeners.has(event)) {
            const registrations = this._listeners.get(event);
            registrations.forEach(r => r.subscription.unsubscribe());
            this._listeners.delete(event);
        }
        return this;
    }
    removeListener(event, listener) {
        event = ConvergenceEventEmitter._resolveEventKey(event);
        if (this._listeners.has(event)) {
            const listeners = this._listeners.get(event);
            const index = listeners.findIndex(r => r.listener === listener);
            if (index !== -1) {
                const r = listeners[index];
                listeners.splice(index, 1);
                r.subscription.unsubscribe();
            }
            if (listeners.length === 0) {
                this._listeners.delete(event);
            }
        }
        return this;
    }
    off(event, listener) {
        return this.removeListener(event, listener);
    }
    events() {
        return this._observable;
    }
    _emitFrom(observable) {
        return observable.subscribe((value) => {
            this._defaultSubject.next(value);
        }, (error) => {
            this._defaultSubject.error(error);
        });
    }
    _emitEvent(value) {
        if (Validation.isNotSet(value.name)) {
            throw new ConvergenceError("An event must have a name.");
        }
        this._defaultSubject.next(value);
    }
    _completeEventStream() {
        this._defaultSubject.complete();
    }
}

class ConvergenceServerError extends Error {
    constructor(m, code, details) {
        super(m);
        this._code = code;
        this._details = details;
        Object.setPrototypeOf(this, ConvergenceServerError.prototype);
    }
    get code() {
        return this._code;
    }
    get details() {
        return this._details;
    }
}

class CancellationToken {
    constructor() {
        this._callback = null;
        this._canceled = false;
    }
    static create() {
        return new CancellationToken();
    }
    cancel() {
        if (this._callback === null) {
            throw new Error("The cancellation token must be bound before calling cancel.");
        }
        if (this._canceled) {
            throw new Error("The cancellation token has already been cancelled.");
        }
        this._callback();
        this._canceled = true;
    }
    isBound() {
        return this._callback !== null;
    }
    _bind(callback) {
        if (typeof callback !== "function") {
            throw new Error("callback must be a function");
        }
        if (this._callback !== null) {
            throw new Error("The cancellation token was already bound");
        }
        this._callback = callback;
    }
}

class PagedData {
    constructor(data, offset, totalResults) {
        this.data = data;
        this.offset = offset;
        this.totalResults = totalResults;
        Object.freeze(this);
        Object.freeze(data);
    }
}

class AbstractDeferred {
    constructor() {
        this._rejected = false;
        this._resolved = false;
    }
    isPending() {
        return !this._resolved && !this._rejected;
    }
    isRejected() {
        return this._rejected;
    }
    isResolved() {
        return this._resolved;
    }
    resolve(value) {
        this._rejected = false;
        this._resolved = true;
    }
    reject(error) {
        this._rejected = true;
        this._resolved = false;
    }
    resolveFromPromise(p) {
        p.then((r) => this.resolve(r)).catch((e) => this.reject(e));
    }
}

class Deferred extends AbstractDeferred {
    constructor() {
        super();
        this._promise = new Promise((resolve, reject) => {
            this._resolve = resolve;
            this._reject = reject;
        });
    }
    resolve(value) {
        super.resolve(value);
        this._resolve(value);
    }
    reject(error) {
        super.reject(error);
        this._reject(error);
    }
    promise() {
        return this._promise;
    }
}

class ConvergenceSocket extends ConvergenceEventEmitter {
    constructor(url, webSocketClass, webSocketFactory) {
        super();
        this._connectionError = null;
        this._logger = Logging.logger("socket");
        let tmp = url;
        tmp = tmp.replace(/https:/i, "wss:");
        tmp = tmp.replace(/http:/i, "ws:");
        this._url = tmp;
        this._socket = null;
        this._webSocketClass = webSocketClass !== null ? webSocketClass : WebSocket;
        this._webSocketFactory = webSocketFactory !== null ? webSocketFactory : (u) => new this._webSocketClass(u);
    }
    static _detachFromSocket(socket) {
        socket.onmessage = undefined;
        socket.onopen = undefined;
        socket.onerror = undefined;
        socket.onclose = undefined;
    }
    get url() {
        return this._url;
    }
    open() {
        this._openDeferred = new Deferred();
        if (this._socket && this._socket.readyState === this._webSocketClass.CONNECTING) {
            throw new Error("Socket already in the process of opening.");
        }
        else if (this._socket && this._socket.readyState === this._webSocketClass.OPEN) {
            throw new Error("Can not call connect on a socket that is already connected.");
        }
        else if (this._socket && this._socket.readyState === this._webSocketClass.CLOSING) {
            throw new Error("Can not call connect on a socket that is in the process of closing.");
        }
        else {
            this._socket = this._webSocketFactory(this._url);
            this._socket.binaryType = "arraybuffer";
            this._attachToSocket(this._socket);
        }
        return this._openDeferred.promise();
    }
    close() {
        return this._doClose(true);
    }
    terminate(reason) {
        return this._doClose(false, reason);
    }
    isOpen() {
        return this._socket !== null && this._socket.readyState === this._webSocketClass.OPEN;
    }
    isConnecting() {
        return this._socket !== null && this._socket.readyState === this._webSocketClass.CONNECTING;
    }
    isClosed() {
        return this._socket === null || this._socket.readyState === this._webSocketClass.CLOSED;
    }
    send(message) {
        if (!this.isOpen()) {
            throw new Error("Can't send messages because the WebSocket is not open.");
        }
        this._socket.send(message);
    }
    _doClose(clean, reason) {
        if (!this._socket || this._socket.readyState === this._webSocketClass.CLOSED) {
            this._logger.trace("Attempted to close a WebSocket that is in the CLOSED state.");
            return Promise.reject(new Error("Can not call close on a WebSocket in the CLOSED state."));
        }
        else if (this._socket.readyState === this._webSocketClass.CLOSING) {
            this._logger.trace("Attempted to close a WebSocket that is in the CLOSING state.");
            return Promise.reject(new Error("Can not call close on a WebSocket in the CLOSING state."));
        }
        else {
            if (this._socket.readyState === this._webSocketClass.CONNECTING) {
                this._logger.trace("Closing a connecting WebSocket.");
            }
            else {
                this._logger.trace("Closing an open WebSocket.");
            }
            this._closeDeferred = new Deferred();
            if (clean) {
                this._logger.debug("Closing WebSocket normally.");
                this._socket.close(1000, reason || "The client closed the connection");
            }
            else {
                this._logger.debug("Closing WebSocket abnormally: " + reason);
                this._socket.close(4006, reason);
            }
            if (this._openDeferred !== null) {
                const tmp = this._openDeferred;
                this._openDeferred = null;
                tmp.reject(new Error("Web Socket connection closed while opening."));
            }
            return this._closeDeferred.promise();
        }
    }
    _attachToSocket(socket) {
        socket.onmessage = (evt) => {
            try {
                if (evt.data instanceof ArrayBuffer) {
                    const buffer = new Uint8Array(evt.data);
                    this._emitEvent({
                        name: ConvergenceSocket.Events.MESSAGE,
                        message: buffer
                    });
                }
                else {
                    throw new ConvergenceError("Convergence protocol does not accept text frames: " + evt.data);
                }
            }
            catch (e) {
                this._emitEvent({
                    name: ConvergenceSocket.Events.ERROR,
                    error: e
                });
                this._logger.error("Error handling  web socket frame", e);
            }
        };
        socket.onopen = (_) => {
            if (this._openDeferred) {
                this._logger.debug("WebSocket connection opened");
                try {
                    this._openDeferred.resolve();
                }
                catch (e) {
                    this._logger.error("Error resolving WebSocket Open Promise.", e);
                }
                this._openDeferred = null;
            }
            else {
                const event = {
                    name: ConvergenceSocket.Events.ERROR,
                    error: new ConvergenceError("Received onOpen event while in state: " + this._socket.readyState)
                };
                this._emitEvent(event);
            }
        };
        socket.onerror = (evt) => {
            this._logger.debug("WebSocket Error: " + evt.message);
            if (this._socket === undefined || this._socket.readyState === this._webSocketClass.CONNECTING) {
                try {
                    const event = {
                        name: ConvergenceSocket.Events.ERROR,
                        error: new ConvergenceError("WebSocket error: " + evt.message)
                    };
                    this._emitEvent(event);
                }
                catch (e) {
                    this._logger.error("Error handling WebSocket error.", e);
                }
            }
            else {
                this._connectionError = evt.error;
            }
        };
        socket.onclose = (evt) => {
            this._logger.trace(() => `WebSocket close event: {code: ${evt.code}, reason: "${evt.reason}"}`);
            ConvergenceSocket._detachFromSocket(socket);
            this._socket = null;
            try {
                if (this._openDeferred) {
                    this._logger.debug(() => `Web Socket connection failed: {code: ${evt.code}, reason: "${evt.reason}"}`);
                    this._openDeferred.reject(new Error(this._connectionError));
                    this._openDeferred = null;
                }
                if (this._closeDeferred) {
                    const event = {
                        name: ConvergenceSocket.Events.CLOSE,
                        reason: "close requested by client"
                    };
                    this._emitEvent(event);
                    this._closeDeferred.resolve();
                }
                else {
                    this._logger.debug(() => `Web Socket connection unexpectedly closed: {code: ${evt.code}, reason: "${evt.reason}"}`);
                    const event = {
                        name: ConvergenceSocket.Events.CLOSE,
                        reason: "unexpected Web Socket closure."
                    };
                    this._emitEvent(event);
                }
            }
            catch (e) {
                this._logger.error("Error handling web socket close event.", e);
            }
        };
    }
}
ConvergenceSocket.Events = {
    MESSAGE: "message",
    ERROR: "error",
    CLOSE: "close"
};

var nested = {
	com: {
		nested: {
			convergencelabs: {
				nested: {
					convergence: {
						nested: {
							proto: {
								nested: {
									ConvergenceMessage: {
										oneofs: {
											body: {
												oneof: [
													"ok",
													"error",
													"ping",
													"pong",
													"connectionRequest",
													"connectionResponse",
													"serverTimeRequest",
													"serverTimeResponse",
													"openRealTimeModelRequest",
													"openRealTimeModelResponse",
													"closeRealTimeModelRequest",
													"closeRealTimeModelResponse",
													"createRealTimeModelRequest",
													"createRealTimeModelResponse",
													"deleteRealtimeModelRequest",
													"deleteRealtimeModelResponse",
													"forceCloseRealTimeModel",
													"remoteClientOpenedModel",
													"remoteClientClosedModel",
													"modelAutoCreateConfigRequest",
													"modelAutoCreateConfigResponse",
													"remoteOperation",
													"operationSubmission",
													"operationAck",
													"shareReference",
													"setReference",
													"clearReference",
													"unshareReference",
													"referenceShared",
													"referenceSet",
													"referenceCleared",
													"referenceUnshared",
													"modelsQueryRequest",
													"modelsQueryResponse",
													"getModelPermissionsRequest",
													"getModelPermissionsResponse",
													"setModelPermissionsRequest",
													"setModelPermissionsResponse",
													"modelPermissionsChanged",
													"modelResyncRequest",
													"modelResyncResponse",
													"modelResyncClientComplete",
													"modelResyncServerComplete",
													"remoteClientResyncStarted",
													"remoteClientResyncCompleted",
													"modelOfflineSubscriptionChange",
													"modelOfflineUpdated",
													"historicalDataRequest",
													"historicalDataResponse",
													"historicalOperationsRequest",
													"historicalOperationsResponse",
													"modelGetVersionAtTimeRequest",
													"modelGetVersionAtTimeResponse",
													"identityCacheUpdate",
													"usersGetRequest",
													"userSearchRequest",
													"userListResponse",
													"userGroupsRequest",
													"userGroupsResponse",
													"userGroupsForUsersRequest",
													"userGroupsForUsersResponse",
													"activityParticipantsRequest",
													"activityParticipantsResponse",
													"activityCreateRequest",
													"activityDeleteRequest",
													"activityJoinRequest",
													"activityJoinResponse",
													"activityLeaveRequest",
													"activitySessionJoined",
													"activitySessionLeft",
													"activityUpdateState",
													"activityStateUpdated",
													"activityDeleted",
													"activityForceLeave",
													"presenceSetState",
													"presenceRemoveState",
													"presenceClearState",
													"presenceStateSet",
													"presenceStateRemoved",
													"presenceStateCleared",
													"presenceAvailabilityChanged",
													"presenceRequest",
													"presenceResponse",
													"presenceSubscribeRequest",
													"presenceSubscribeResponse",
													"presenceUnsubscribe",
													"createChatRequest",
													"createChatResponse",
													"removeChatRequest",
													"removeChatResponse",
													"chatRemoved",
													"getChatsRequest",
													"getChatsResponse",
													"chatsExistRequest",
													"chatsExistResponse",
													"getDirectChatsRequest",
													"getDirectChatsResponse",
													"getJoinedChatsRequest",
													"getJoinedChatsResponse",
													"chatsSearchRequest",
													"chatsSearchResponse",
													"joinChatRequest",
													"joinChatResponse",
													"userJoinedChat",
													"leaveChatRequest",
													"leaveChatResponse",
													"userLeftChat",
													"addUserToChatRequest",
													"addUserToChatResponse",
													"userAddedToChat",
													"removeUserFromChatRequest",
													"removeUserFromChatResponse",
													"userRemovedFromChat",
													"setChatNameRequest",
													"setChatNameResponse",
													"chatNameChanged",
													"setChatTopicRequest",
													"setChatTopicResponse",
													"chatTopicChanged",
													"markChatEventsSeenRequest",
													"markChatEventsSeenResponse",
													"chatEventsMarkedSeen",
													"publishChatMessageRequest",
													"publishChatMessageResponse",
													"remoteChatMessage",
													"getChatHistoryRequest",
													"getChatHistoryResponse",
													"resolvePermissionsForConnectedSessionRequest",
													"resolvePermissionsForConnectedSessionResponse",
													"addPermissionsRequest",
													"removePermissionsRequest",
													"setPermissionsRequest",
													"getPermissionsRequest",
													"getPermissionsResponse"
												]
											}
										},
										fields: {
											requestId: {
												type: "google.protobuf.Int32Value",
												id: 1
											},
											responseId: {
												type: "google.protobuf.Int32Value",
												id: 2
											},
											ok: {
												type: "core.OkResponse",
												id: 3
											},
											error: {
												type: "core.ErrorMessage",
												id: 4
											},
											ping: {
												type: "core.PingMessage",
												id: 5
											},
											pong: {
												type: "core.PongMessage",
												id: 6
											},
											connectionRequest: {
												type: "core.ConnectionRequestMessage",
												id: 7
											},
											connectionResponse: {
												type: "core.ConnectionResponseMessage",
												id: 8
											},
											serverTimeRequest: {
												type: "core.GetServerTimeRequestMessage",
												id: 20
											},
											serverTimeResponse: {
												type: "core.GetServerTimeResponseMessage",
												id: 21
											},
											openRealTimeModelRequest: {
												type: "model.OpenRealtimeModelRequestMessage",
												id: 100
											},
											openRealTimeModelResponse: {
												type: "model.OpenRealtimeModelResponseMessage",
												id: 101
											},
											closeRealTimeModelRequest: {
												type: "model.CloseRealtimeModelRequestMessage",
												id: 102
											},
											closeRealTimeModelResponse: {
												type: "model.CloseRealTimeModelResponseMessage",
												id: 103
											},
											createRealTimeModelRequest: {
												type: "model.CreateRealtimeModelRequestMessage",
												id: 104
											},
											createRealTimeModelResponse: {
												type: "model.CreateRealtimeModelResponseMessage",
												id: 105
											},
											deleteRealtimeModelRequest: {
												type: "model.DeleteRealtimeModelRequestMessage",
												id: 106
											},
											deleteRealtimeModelResponse: {
												type: "model.DeleteRealtimeModelResponseMessage",
												id: 107
											},
											forceCloseRealTimeModel: {
												type: "model.ModelForceCloseMessage",
												id: 108
											},
											remoteClientOpenedModel: {
												type: "model.RemoteClientOpenedMessage",
												id: 109
											},
											remoteClientClosedModel: {
												type: "model.RemoteClientClosedMessage",
												id: 110
											},
											modelAutoCreateConfigRequest: {
												type: "model.AutoCreateModelConfigRequestMessage",
												id: 111
											},
											modelAutoCreateConfigResponse: {
												type: "model.AutoCreateModelConfigResponseMessage",
												id: 112
											},
											remoteOperation: {
												type: "model.RemoteOperationMessage",
												id: 113
											},
											operationSubmission: {
												type: "model.OperationSubmissionMessage",
												id: 114
											},
											operationAck: {
												type: "model.OperationAcknowledgementMessage",
												id: 115
											},
											shareReference: {
												type: "model.ShareReferenceMessage",
												id: 116
											},
											setReference: {
												type: "model.SetReferenceMessage",
												id: 117
											},
											clearReference: {
												type: "model.ClearReferenceMessage",
												id: 118
											},
											unshareReference: {
												type: "model.UnshareReferenceMessage",
												id: 119
											},
											referenceShared: {
												type: "model.RemoteReferenceSharedMessage",
												id: 120
											},
											referenceSet: {
												type: "model.RemoteReferenceSetMessage",
												id: 121
											},
											referenceCleared: {
												type: "model.RemoteReferenceClearedMessage",
												id: 122
											},
											referenceUnshared: {
												type: "model.RemoteReferenceUnsharedMessage",
												id: 123
											},
											modelsQueryRequest: {
												type: "model.ModelsQueryRequestMessage",
												id: 124
											},
											modelsQueryResponse: {
												type: "model.ModelsQueryResponseMessage",
												id: 125
											},
											getModelPermissionsRequest: {
												type: "model.GetModelPermissionsRequestMessage",
												id: 126
											},
											getModelPermissionsResponse: {
												type: "model.GetModelPermissionsResponseMessage",
												id: 127
											},
											setModelPermissionsRequest: {
												type: "model.SetModelPermissionsRequestMessage",
												id: 128
											},
											setModelPermissionsResponse: {
												type: "model.SetModelPermissionsResponseMessage",
												id: 129
											},
											modelPermissionsChanged: {
												type: "model.ModelPermissionsChangedMessage",
												id: 130
											},
											modelResyncRequest: {
												type: "model.ModelResyncRequestMessage",
												id: 131
											},
											modelResyncResponse: {
												type: "model.ModelResyncResponseMessage",
												id: 132
											},
											modelResyncClientComplete: {
												type: "model.ModelResyncClientCompleteMessage",
												id: 133
											},
											modelResyncServerComplete: {
												type: "model.ModelResyncServerCompleteMessage",
												id: 134
											},
											remoteClientResyncStarted: {
												type: "model.RemoteClientResyncStartedMessage",
												id: 135
											},
											remoteClientResyncCompleted: {
												type: "model.RemoteClientResyncCompletedMessage",
												id: 136
											},
											modelOfflineSubscriptionChange: {
												type: "model.ModelOfflineSubscriptionChangeRequestMessage",
												id: 137
											},
											modelOfflineUpdated: {
												type: "model.OfflineModelUpdatedMessage",
												id: 138
											},
											historicalDataRequest: {
												type: "model.HistoricalDataRequestMessage",
												id: 139
											},
											historicalDataResponse: {
												type: "model.HistoricalDataResponseMessage",
												id: 140
											},
											historicalOperationsRequest: {
												type: "model.HistoricalOperationRequestMessage",
												id: 141
											},
											historicalOperationsResponse: {
												type: "model.HistoricalOperationsResponseMessage",
												id: 142
											},
											modelGetVersionAtTimeRequest: {
												type: "model.ModelGetVersionAtTimeRequestMessage",
												id: 143
											},
											modelGetVersionAtTimeResponse: {
												type: "model.ModelGetVersionAtTimeResponseMessage",
												id: 144
											},
											identityCacheUpdate: {
												type: "identity.IdentityCacheUpdateMessage",
												id: 200
											},
											usersGetRequest: {
												type: "identity.GetUsersRequestMessage",
												id: 201
											},
											userSearchRequest: {
												type: "identity.SearchUsersRequestMessage",
												id: 202
											},
											userListResponse: {
												type: "identity.UserListMessage",
												id: 203
											},
											userGroupsRequest: {
												type: "identity.UserGroupsRequestMessage",
												id: 204
											},
											userGroupsResponse: {
												type: "identity.UserGroupsResponseMessage",
												id: 205
											},
											userGroupsForUsersRequest: {
												type: "identity.UserGroupsForUsersRequestMessage",
												id: 206
											},
											userGroupsForUsersResponse: {
												type: "identity.UserGroupsForUsersResponseMessage",
												id: 207
											},
											activityParticipantsRequest: {
												type: "activity.ActivityParticipantsRequestMessage",
												id: 300
											},
											activityParticipantsResponse: {
												type: "activity.ActivityParticipantsResponseMessage",
												id: 301
											},
											activityCreateRequest: {
												type: "activity.ActivityCreateRequestMessage",
												id: 302
											},
											activityDeleteRequest: {
												type: "activity.ActivityDeleteRequestMessage",
												id: 304
											},
											activityJoinRequest: {
												type: "activity.ActivityJoinRequestMessage",
												id: 306
											},
											activityJoinResponse: {
												type: "activity.ActivityJoinResponseMessage",
												id: 307
											},
											activityLeaveRequest: {
												type: "activity.ActivityLeaveRequestMessage",
												id: 308
											},
											activitySessionJoined: {
												type: "activity.ActivitySessionJoinedMessage",
												id: 310
											},
											activitySessionLeft: {
												type: "activity.ActivitySessionLeftMessage",
												id: 311
											},
											activityUpdateState: {
												type: "activity.ActivityUpdateStateMessage",
												id: 312
											},
											activityStateUpdated: {
												type: "activity.ActivityStateUpdatedMessage",
												id: 313
											},
											activityDeleted: {
												type: "activity.ActivityDeletedMessage",
												id: 314
											},
											activityForceLeave: {
												type: "activity.ActivityForceLeaveMessage",
												id: 315
											},
											presenceSetState: {
												type: "presence.PresenceSetStateMessage",
												id: 400
											},
											presenceRemoveState: {
												type: "presence.PresenceRemoveStateMessage",
												id: 401
											},
											presenceClearState: {
												type: "presence.PresenceClearStateMessage",
												id: 402
											},
											presenceStateSet: {
												type: "presence.PresenceStateSetMessage",
												id: 403
											},
											presenceStateRemoved: {
												type: "presence.PresenceStateRemovedMessage",
												id: 404
											},
											presenceStateCleared: {
												type: "presence.PresenceStateClearedMessage",
												id: 405
											},
											presenceAvailabilityChanged: {
												type: "presence.PresenceAvailabilityChangedMessage",
												id: 406
											},
											presenceRequest: {
												type: "presence.PresenceRequestMessage",
												id: 407
											},
											presenceResponse: {
												type: "presence.PresenceResponseMessage",
												id: 408
											},
											presenceSubscribeRequest: {
												type: "presence.SubscribePresenceRequestMessage",
												id: 409
											},
											presenceSubscribeResponse: {
												type: "presence.SubscribePresenceResponseMessage",
												id: 410
											},
											presenceUnsubscribe: {
												type: "presence.UnsubscribePresenceMessage",
												id: 411
											},
											createChatRequest: {
												type: "chat.CreateChatRequestMessage",
												id: 500
											},
											createChatResponse: {
												type: "chat.CreateChatResponseMessage",
												id: 501
											},
											removeChatRequest: {
												type: "chat.RemoveChatRequestMessage",
												id: 502
											},
											removeChatResponse: {
												type: "chat.RemoveChatResponseMessage",
												id: 503
											},
											chatRemoved: {
												type: "chat.ChatRemovedMessage",
												id: 504
											},
											getChatsRequest: {
												type: "chat.GetChatsRequestMessage",
												id: 505
											},
											getChatsResponse: {
												type: "chat.GetChatsResponseMessage",
												id: 506
											},
											chatsExistRequest: {
												type: "chat.ChatsExistRequestMessage",
												id: 507
											},
											chatsExistResponse: {
												type: "chat.ChatsExistResponseMessage",
												id: 508
											},
											getDirectChatsRequest: {
												type: "chat.GetDirectChatsRequestMessage",
												id: 509
											},
											getDirectChatsResponse: {
												type: "chat.GetDirectChatsResponseMessage",
												id: 510
											},
											getJoinedChatsRequest: {
												type: "chat.GetJoinedChatsRequestMessage",
												id: 511
											},
											getJoinedChatsResponse: {
												type: "chat.GetJoinedChatsResponseMessage",
												id: 512
											},
											chatsSearchRequest: {
												type: "chat.ChatsSearchRequestMessage",
												id: 513
											},
											chatsSearchResponse: {
												type: "chat.ChatsSearchResponseMessage",
												id: 514
											},
											joinChatRequest: {
												type: "chat.JoinChatRequestMessage",
												id: 515
											},
											joinChatResponse: {
												type: "chat.JoinChatResponseMessage",
												id: 516
											},
											userJoinedChat: {
												type: "chat.UserJoinedChatMessage",
												id: 517
											},
											leaveChatRequest: {
												type: "chat.LeaveChatRequestMessage",
												id: 518
											},
											leaveChatResponse: {
												type: "chat.LeaveChatResponseMessage",
												id: 519
											},
											userLeftChat: {
												type: "chat.UserLeftChatMessage",
												id: 520
											},
											addUserToChatRequest: {
												type: "chat.AddUserToChatRequestMessage",
												id: 521
											},
											addUserToChatResponse: {
												type: "chat.AddUserToChatResponseMessage",
												id: 522
											},
											userAddedToChat: {
												type: "chat.UserAddedToChatMessage",
												id: 523
											},
											removeUserFromChatRequest: {
												type: "chat.RemoveUserFromChatRequestMessage",
												id: 524
											},
											removeUserFromChatResponse: {
												type: "chat.RemoveUserFromChatResponseMessage",
												id: 525
											},
											userRemovedFromChat: {
												type: "chat.UserRemovedFromChatMessage",
												id: 526
											},
											setChatNameRequest: {
												type: "chat.SetChatNameRequestMessage",
												id: 527
											},
											setChatNameResponse: {
												type: "chat.SetChatNameResponseMessage",
												id: 528
											},
											chatNameChanged: {
												type: "chat.ChatNameSetMessage",
												id: 529
											},
											setChatTopicRequest: {
												type: "chat.SetChatTopicRequestMessage",
												id: 530
											},
											setChatTopicResponse: {
												type: "chat.SetChatTopicResponseMessage",
												id: 531
											},
											chatTopicChanged: {
												type: "chat.ChatTopicSetMessage",
												id: 532
											},
											markChatEventsSeenRequest: {
												type: "chat.MarkChatEventsSeenRequestMessage",
												id: 533
											},
											markChatEventsSeenResponse: {
												type: "chat.MarkChatEventsSeenResponseMessage",
												id: 534
											},
											chatEventsMarkedSeen: {
												type: "chat.ChatEventsMarkedSeenMessage",
												id: 535
											},
											publishChatMessageRequest: {
												type: "chat.PublishChatRequestMessage",
												id: 536
											},
											publishChatMessageResponse: {
												type: "chat.PublishChatResponseMessage",
												id: 537
											},
											remoteChatMessage: {
												type: "chat.RemoteChatMessageMessage",
												id: 538
											},
											getChatHistoryRequest: {
												type: "chat.ChatHistoryRequestMessage",
												id: 539
											},
											getChatHistoryResponse: {
												type: "chat.ChatHistoryResponseMessage",
												id: 540
											},
											resolvePermissionsForConnectedSessionRequest: {
												type: "core.ResolvePermissionsForConnectedSessionRequestMessage",
												id: 601
											},
											resolvePermissionsForConnectedSessionResponse: {
												type: "core.ResolvePermissionsForConnectedSessionResponseMessage",
												id: 602
											},
											addPermissionsRequest: {
												type: "core.AddPermissionsRequestMessage",
												id: 603
											},
											removePermissionsRequest: {
												type: "core.RemovePermissionsRequestMessage",
												id: 605
											},
											setPermissionsRequest: {
												type: "core.SetPermissionsRequestMessage",
												id: 607
											},
											getPermissionsRequest: {
												type: "core.GetPermissionsRequestMessage",
												id: 609
											},
											getPermissionsResponse: {
												type: "core.GetPermissionsResponseMessage",
												id: 610
											}
										}
									},
									core: {
										nested: {
											PingMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
												}
											},
											PongMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
												}
											},
											ConnectionRequestMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ClientMessage"
												},
												oneofs: {
													auth: {
														oneof: [
															"password",
															"jwt",
															"anonymous",
															"reconnect"
														]
													}
												},
												fields: {
													client: {
														type: "string",
														id: 1
													},
													clientVersion: {
														type: "string",
														id: 2
													},
													password: {
														type: "PasswordAuthRequestData",
														id: 3
													},
													jwt: {
														type: "JwtAuthRequestData",
														id: 4
													},
													anonymous: {
														type: "AnonymousAuthRequestData",
														id: 5
													},
													reconnect: {
														type: "ReconnectTokenAuthRequestData",
														id: 6
													}
												},
												nested: {
													PasswordAuthRequestData: {
														fields: {
															username: {
																type: "string",
																id: 1
															},
															password: {
																type: "string",
																id: 2
															}
														}
													},
													JwtAuthRequestData: {
														fields: {
															jwt: {
																type: "string",
																id: 1
															}
														}
													},
													ReconnectTokenAuthRequestData: {
														fields: {
															token: {
																type: "string",
																id: 1
															}
														}
													},
													AnonymousAuthRequestData: {
														fields: {
															displayName: {
																type: "google.protobuf.StringValue",
																id: 1
															}
														}
													}
												}
											},
											ConnectionResponseMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												oneofs: {
													response: {
														oneof: [
															"success",
															"failure"
														]
													}
												},
												fields: {
													server: {
														type: "ServerData",
														id: 1
													},
													success: {
														type: "ConnectionSuccessData",
														id: 3
													},
													failure: {
														type: "ConnectionFailureData",
														id: 4
													}
												},
												nested: {
													ServerData: {
														fields: {
															version: {
																type: "string",
																id: 1
															}
														}
													},
													ConnectionSuccessData: {
														fields: {
															namespace: {
																type: "string",
																id: 1
															},
															domainId: {
																type: "string",
																id: 2
															},
															user: {
																type: "DomainUserData",
																id: 3
															},
															sessionId: {
																type: "string",
																id: 4
															},
															reconnectToken: {
																type: "string",
																id: 5
															},
															presenceState: {
																keyType: "string",
																type: "google.protobuf.Value",
																id: 6
															}
														}
													},
													ConnectionFailureData: {
														fields: {
															code: {
																type: "string",
																id: 1
															},
															details: {
																type: "string",
																id: 2
															},
															retryOk: {
																type: "bool",
																id: 3
															}
														}
													}
												}
											},
											DomainUserTypeData: {
												values: {
													Normal: 0,
													Convergence: 1,
													Anonymous: 2
												}
											},
											DomainUserIdData: {
												fields: {
													userType: {
														type: "DomainUserTypeData",
														id: 1
													},
													username: {
														type: "string",
														id: 2
													}
												}
											},
											DomainUserIdList: {
												fields: {
													values: {
														rule: "repeated",
														type: "DomainUserIdData",
														id: 1
													}
												}
											},
											DomainUserData: {
												fields: {
													userId: {
														type: "DomainUserIdData",
														id: 1
													},
													firstName: {
														type: "google.protobuf.StringValue",
														id: 2
													},
													lastName: {
														type: "google.protobuf.StringValue",
														id: 3
													},
													displayName: {
														type: "google.protobuf.StringValue",
														id: 4
													},
													email: {
														type: "google.protobuf.StringValue",
														id: 5
													},
													disabled: {
														type: "bool",
														id: 6
													},
													deleted: {
														type: "bool",
														id: 7
													},
													deletedUsername: {
														type: "google.protobuf.StringValue",
														id: 8
													}
												}
											},
											UserPermissionsEntry: {
												fields: {
													user: {
														type: "DomainUserIdData",
														id: 1
													},
													permissions: {
														rule: "repeated",
														type: "string",
														id: 2
													}
												}
											},
											PermissionsList: {
												fields: {
													values: {
														rule: "repeated",
														type: "string",
														id: 1
													}
												}
											},
											PermissionTarget: {
												oneofs: {
													targetType: {
														oneof: [
															"chat",
															"activity"
														]
													}
												},
												fields: {
													chat: {
														type: "Chat",
														id: 1
													},
													activity: {
														type: "Activity",
														id: 2
													}
												},
												nested: {
													Chat: {
														fields: {
															id: {
																type: "string",
																id: 1
															}
														}
													},
													Activity: {
														fields: {
															type: {
																type: "string",
																id: 1
															},
															id: {
																type: "string",
																id: 2
															}
														}
													}
												}
											},
											AddPermissionsRequestMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ClientMessage"
												},
												fields: {
													target: {
														type: "PermissionTarget",
														id: 1
													},
													world: {
														rule: "repeated",
														type: "string",
														id: 2
													},
													user: {
														rule: "repeated",
														type: "UserPermissionsEntry",
														id: 3
													},
													group: {
														keyType: "string",
														type: "PermissionsList",
														id: 4
													}
												}
											},
											RemovePermissionsRequestMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ClientMessage"
												},
												fields: {
													target: {
														type: "PermissionTarget",
														id: 1
													},
													world: {
														rule: "repeated",
														type: "string",
														id: 2
													},
													user: {
														rule: "repeated",
														type: "UserPermissionsEntry",
														id: 3
													},
													group: {
														keyType: "string",
														type: "PermissionsList",
														id: 4
													}
												}
											},
											SetPermissionsRequestMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ClientMessage"
												},
												fields: {
													target: {
														type: "PermissionTarget",
														id: 1
													},
													world: {
														type: "SetWorld",
														id: 2
													},
													user: {
														type: "SetUser",
														id: 3
													},
													group: {
														type: "SetGroup",
														id: 4
													}
												},
												nested: {
													SetWorld: {
														fields: {
															permissions: {
																rule: "repeated",
																type: "string",
																id: 1
															}
														}
													},
													SetUser: {
														fields: {
															permissions: {
																rule: "repeated",
																type: "UserPermissionsEntry",
																id: 1
															},
															replaceAll: {
																type: "bool",
																id: 2
															}
														}
													},
													SetGroup: {
														fields: {
															permissions: {
																keyType: "string",
																type: "PermissionsList",
																id: 1
															},
															replaceAll: {
																type: "bool",
																id: 2
															}
														}
													}
												}
											},
											GetPermissionsRequestMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ClientMessage"
												},
												fields: {
													target: {
														type: "PermissionTarget",
														id: 1
													}
												}
											},
											GetPermissionsResponseMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
													user: {
														rule: "repeated",
														type: "UserPermissionsEntry",
														id: 1
													},
													group: {
														keyType: "string",
														type: "PermissionsList",
														id: 2
													},
													world: {
														rule: "repeated",
														type: "string",
														id: 3
													}
												}
											},
											ResolvePermissionsForConnectedSessionRequestMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ClientMessage"
												},
												fields: {
													target: {
														type: "PermissionTarget",
														id: 1
													}
												}
											},
											ResolvePermissionsForConnectedSessionResponseMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
													permissions: {
														rule: "repeated",
														type: "string",
														id: 1
													}
												}
											},
											ErrorMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
													code: {
														type: "string",
														id: 1
													},
													message: {
														type: "string",
														id: 2
													},
													details: {
														keyType: "string",
														type: "google.protobuf.Value",
														id: 3
													}
												}
											},
											OkResponse: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
												}
											},
											GetServerTimeRequestMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ClientMessage"
												},
												fields: {
												}
											},
											GetServerTimeResponseMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
													serverTime: {
														type: "google.protobuf.Timestamp",
														id: 1
													}
												}
											},
											StringList: {
												fields: {
													values: {
														rule: "repeated",
														type: "string",
														id: 1
													}
												}
											},
											Int32List: {
												fields: {
													values: {
														rule: "repeated",
														type: "int32",
														id: 1
													}
												}
											}
										}
									},
									activity: {
										nested: {
											ActivityCreateRequestMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ClientMessage"
												},
												fields: {
													activityType: {
														type: "string",
														id: 1
													},
													activityId: {
														type: "string",
														id: 2
													},
													worldPermissions: {
														rule: "repeated",
														type: "string",
														id: 3
													},
													userPermissions: {
														rule: "repeated",
														type: "core.UserPermissionsEntry",
														id: 4
													},
													groupPermissions: {
														keyType: "string",
														type: "core.PermissionsList",
														id: 5
													}
												}
											},
											ActivityDeleteRequestMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ClientMessage"
												},
												fields: {
													activityType: {
														type: "string",
														id: 1
													},
													activityId: {
														type: "string",
														id: 2
													}
												}
											},
											ActivityParticipantsRequestMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ClientMessage"
												},
												fields: {
													activityType: {
														type: "string",
														id: 1
													},
													activityId: {
														type: "string",
														id: 2
													}
												}
											},
											ActivityParticipantsResponseMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
													state: {
														keyType: "string",
														type: "ActivityStateData",
														id: 1
													}
												}
											},
											ActivityJoinRequestMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ClientMessage"
												},
												fields: {
													activityType: {
														type: "string",
														id: 1
													},
													activityId: {
														type: "string",
														id: 2
													},
													lurk: {
														type: "bool",
														id: 3
													},
													state: {
														keyType: "string",
														type: "google.protobuf.Value",
														id: 4
													},
													autoCreateData: {
														type: "AutoCreateData",
														id: 5
													}
												},
												nested: {
													AutoCreateData: {
														fields: {
															ephemeral: {
																type: "bool",
																id: 1
															},
															worldPermissions: {
																rule: "repeated",
																type: "string",
																id: 3
															},
															userPermissions: {
																rule: "repeated",
																type: "core.UserPermissionsEntry",
																id: 4
															},
															groupPermissions: {
																keyType: "string",
																type: "core.PermissionsList",
																id: 5
															}
														}
													}
												}
											},
											ActivityJoinResponseMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
													resourceId: {
														type: "int32",
														id: 1
													},
													state: {
														keyType: "string",
														type: "ActivityStateData",
														id: 2
													},
													ephemeral: {
														type: "bool",
														id: 3
													},
													created: {
														type: "google.protobuf.Timestamp",
														id: 5
													}
												}
											},
											ActivityLeaveRequestMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.RequestMessage"
												},
												fields: {
													resourceId: {
														type: "int32",
														id: 1
													}
												}
											},
											ActivityUpdateStateMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ClientMessage"
												},
												fields: {
													resourceId: {
														type: "int32",
														id: 1
													},
													set: {
														keyType: "string",
														type: "google.protobuf.Value",
														id: 2
													},
													complete: {
														type: "bool",
														id: 3
													},
													removed: {
														rule: "repeated",
														type: "string",
														id: 4
													}
												}
											},
											ActivitySessionJoinedMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
													resourceId: {
														type: "int32",
														id: 1
													},
													sessionId: {
														type: "string",
														id: 2
													},
													state: {
														keyType: "string",
														type: "google.protobuf.Value",
														id: 3
													}
												}
											},
											ActivitySessionLeftMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
													resourceId: {
														type: "int32",
														id: 1
													},
													sessionId: {
														type: "string",
														id: 2
													}
												}
											},
											ActivityStateUpdatedMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
													resourceId: {
														type: "int32",
														id: 1
													},
													sessionId: {
														type: "string",
														id: 2
													},
													set: {
														keyType: "string",
														type: "google.protobuf.Value",
														id: 3
													},
													complete: {
														type: "bool",
														id: 4
													},
													removed: {
														rule: "repeated",
														type: "string",
														id: 5
													}
												}
											},
											ActivityDeletedMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
													resourceId: {
														type: "int32",
														id: 1
													}
												}
											},
											ActivityForceLeaveMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
													resourceId: {
														type: "int32",
														id: 1
													},
													reason: {
														type: "string",
														id: 2
													}
												}
											},
											ActivityStateData: {
												fields: {
													state: {
														keyType: "string",
														type: "google.protobuf.Value",
														id: 1
													}
												}
											}
										}
									},
									chat: {
										nested: {
											PublishChatRequestMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ClientMessage"
												},
												fields: {
													chatId: {
														type: "string",
														id: 1
													},
													message: {
														type: "string",
														id: 2
													}
												}
											},
											PublishChatResponseMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
													eventNumber: {
														type: "int64",
														id: 1
													},
													timestamp: {
														type: "google.protobuf.Timestamp",
														id: 2
													}
												}
											},
											RemoteChatMessageMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
													chatId: {
														type: "string",
														id: 1
													},
													eventNumber: {
														type: "int64",
														id: 2
													},
													timestamp: {
														type: "google.protobuf.Timestamp",
														id: 3
													},
													user: {
														type: "com.convergencelabs.convergence.proto.core.DomainUserIdData",
														id: 4
													},
													message: {
														type: "string",
														id: 5
													}
												}
											},
											CreateChatRequestMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ClientMessage"
												},
												fields: {
													chatId: {
														type: "google.protobuf.StringValue",
														id: 1
													},
													chatType: {
														type: "string",
														id: 2
													},
													membership: {
														type: "string",
														id: 3
													},
													name: {
														type: "string",
														id: 4
													},
													topic: {
														type: "string",
														id: 5
													},
													members: {
														rule: "repeated",
														type: "com.convergencelabs.convergence.proto.core.DomainUserIdData",
														id: 6
													}
												}
											},
											CreateChatResponseMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
													chatId: {
														type: "string",
														id: 1
													}
												}
											},
											RemoveChatRequestMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ClientMessage"
												},
												fields: {
													chatId: {
														type: "string",
														id: 1
													}
												}
											},
											RemoveChatResponseMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
												}
											},
											ChatRemovedMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
													chatId: {
														type: "string",
														id: 1
													}
												}
											},
											JoinChatRequestMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ClientMessage"
												},
												fields: {
													chatId: {
														type: "string",
														id: 1
													}
												}
											},
											JoinChatResponseMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
													chatInfo: {
														type: "ChatInfoData",
														id: 1
													}
												}
											},
											UserJoinedChatMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
													chatId: {
														type: "string",
														id: 1
													},
													eventNumber: {
														type: "int64",
														id: 2
													},
													timestamp: {
														type: "google.protobuf.Timestamp",
														id: 3
													},
													user: {
														type: "com.convergencelabs.convergence.proto.core.DomainUserIdData",
														id: 4
													}
												}
											},
											AddUserToChatRequestMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ClientMessage"
												},
												fields: {
													chatId: {
														type: "string",
														id: 1
													},
													userToAdd: {
														type: "com.convergencelabs.convergence.proto.core.DomainUserIdData",
														id: 2
													}
												}
											},
											AddUserToChatResponseMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
												}
											},
											UserAddedToChatMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
													chatId: {
														type: "string",
														id: 1
													},
													eventNumber: {
														type: "int64",
														id: 2
													},
													timestamp: {
														type: "google.protobuf.Timestamp",
														id: 3
													},
													user: {
														type: "com.convergencelabs.convergence.proto.core.DomainUserIdData",
														id: 4
													},
													addedUser: {
														type: "com.convergencelabs.convergence.proto.core.DomainUserIdData",
														id: 5
													}
												}
											},
											ChatJoinedMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
													chatId: {
														type: "string",
														id: 1
													}
												}
											},
											LeaveChatRequestMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ClientMessage"
												},
												fields: {
													chatId: {
														type: "string",
														id: 1
													}
												}
											},
											LeaveChatResponseMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
												}
											},
											UserLeftChatMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
													chatId: {
														type: "string",
														id: 1
													},
													eventNumber: {
														type: "int64",
														id: 2
													},
													timestamp: {
														type: "google.protobuf.Timestamp",
														id: 3
													},
													user: {
														type: "com.convergencelabs.convergence.proto.core.DomainUserIdData",
														id: 4
													}
												}
											},
											RemoveUserFromChatRequestMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ClientMessage"
												},
												fields: {
													chatId: {
														type: "string",
														id: 1
													},
													userToRemove: {
														type: "com.convergencelabs.convergence.proto.core.DomainUserIdData",
														id: 2
													}
												}
											},
											RemoveUserFromChatResponseMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
												}
											},
											UserRemovedFromChatMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
													chatId: {
														type: "string",
														id: 1
													},
													eventNumber: {
														type: "int64",
														id: 2
													},
													timestamp: {
														type: "google.protobuf.Timestamp",
														id: 3
													},
													user: {
														type: "com.convergencelabs.convergence.proto.core.DomainUserIdData",
														id: 4
													},
													removedUser: {
														type: "com.convergencelabs.convergence.proto.core.DomainUserIdData",
														id: 5
													}
												}
											},
											ChatLeftMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
													chatId: {
														type: "string",
														id: 1
													}
												}
											},
											SetChatNameRequestMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ClientMessage"
												},
												fields: {
													chatId: {
														type: "string",
														id: 1
													},
													name: {
														type: "string",
														id: 2
													}
												}
											},
											SetChatNameResponseMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
												}
											},
											ChatNameSetMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
													chatId: {
														type: "string",
														id: 1
													},
													eventNumber: {
														type: "int64",
														id: 2
													},
													timestamp: {
														type: "google.protobuf.Timestamp",
														id: 3
													},
													user: {
														type: "com.convergencelabs.convergence.proto.core.DomainUserIdData",
														id: 4
													},
													name: {
														type: "string",
														id: 5
													}
												}
											},
											SetChatTopicRequestMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ClientMessage"
												},
												fields: {
													chatId: {
														type: "string",
														id: 1
													},
													topic: {
														type: "string",
														id: 2
													}
												}
											},
											SetChatTopicResponseMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
												}
											},
											ChatTopicSetMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
													chatId: {
														type: "string",
														id: 1
													},
													eventNumber: {
														type: "int64",
														id: 2
													},
													timestamp: {
														type: "google.protobuf.Timestamp",
														id: 3
													},
													user: {
														type: "com.convergencelabs.convergence.proto.core.DomainUserIdData",
														id: 4
													},
													topic: {
														type: "string",
														id: 5
													}
												}
											},
											MarkChatEventsSeenRequestMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ClientMessage"
												},
												fields: {
													chatId: {
														type: "string",
														id: 1
													},
													eventNumber: {
														type: "int64",
														id: 2
													}
												}
											},
											MarkChatEventsSeenResponseMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
												}
											},
											ChatEventsMarkedSeenMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
													chatId: {
														type: "string",
														id: 1
													},
													user: {
														type: "com.convergencelabs.convergence.proto.core.DomainUserIdData",
														id: 2
													},
													eventNumber: {
														type: "int64",
														id: 3
													}
												}
											},
											GetJoinedChatsRequestMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ClientMessage"
												},
												fields: {
												}
											},
											GetJoinedChatsResponseMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
													chatInfo: {
														rule: "repeated",
														type: "ChatInfoData",
														id: 1
													}
												}
											},
											GetChatsRequestMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ClientMessage"
												},
												fields: {
													chatIds: {
														rule: "repeated",
														type: "string",
														id: 1
													}
												}
											},
											GetChatsResponseMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
													chatInfo: {
														rule: "repeated",
														type: "ChatInfoData",
														id: 1
													}
												}
											},
											GetDirectChatsRequestMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ClientMessage"
												},
												fields: {
													userLists: {
														rule: "repeated",
														type: "com.convergencelabs.convergence.proto.core.DomainUserIdList",
														id: 1
													}
												}
											},
											GetDirectChatsResponseMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
													chatInfo: {
														rule: "repeated",
														type: "ChatInfoData",
														id: 1
													}
												}
											},
											ChatHistoryRequestMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ClientMessage"
												},
												fields: {
													chatId: {
														type: "string",
														id: 1
													},
													offset: {
														type: "google.protobuf.Int64Value",
														id: 2
													},
													limit: {
														type: "google.protobuf.Int64Value",
														id: 3
													},
													startEvent: {
														type: "google.protobuf.Int64Value",
														id: 4
													},
													forward: {
														type: "google.protobuf.BoolValue",
														id: 5
													},
													eventFilter: {
														rule: "repeated",
														type: "string",
														id: 6
													}
												}
											},
											ChatHistoryResponseMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
													data: {
														rule: "repeated",
														type: "ChatEventData",
														id: 1
													},
													startIndex: {
														type: "int64",
														id: 2
													},
													totalResults: {
														type: "int64",
														id: 3
													}
												}
											},
											ChatsExistRequestMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ClientMessage"
												},
												fields: {
													chatIds: {
														rule: "repeated",
														type: "string",
														id: 1
													}
												}
											},
											ChatsExistResponseMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
													exists: {
														rule: "repeated",
														type: "bool",
														id: 1
													}
												}
											},
											ChatsSearchRequestMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ClientMessage"
												},
												fields: {
													searchTerm: {
														type: "string",
														id: 1
													},
													searchFields: {
														rule: "repeated",
														type: "string",
														id: 2
													},
													types: {
														rule: "repeated",
														type: "string",
														id: 3
													},
													membership: {
														type: "string",
														id: 4
													},
													offset: {
														type: "google.protobuf.Int64Value",
														id: 5
													},
													limit: {
														type: "google.protobuf.Int64Value",
														id: 6
													}
												}
											},
											ChatsSearchResponseMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
													data: {
														rule: "repeated",
														type: "ChatInfoData",
														id: 1
													},
													startIndex: {
														type: "int64",
														id: 2
													},
													totalResults: {
														type: "int64",
														id: 3
													}
												}
											},
											ChatMemberData: {
												fields: {
													user: {
														type: "com.convergencelabs.convergence.proto.core.DomainUserIdData",
														id: 1
													},
													maxSeenEventNumber: {
														type: "int64",
														id: 2
													}
												}
											},
											ChatInfoData: {
												fields: {
													id: {
														type: "string",
														id: 1
													},
													chatType: {
														type: "string",
														id: 2
													},
													membership: {
														type: "string",
														id: 3
													},
													name: {
														type: "string",
														id: 4
													},
													topic: {
														type: "string",
														id: 5
													},
													createdTime: {
														type: "google.protobuf.Timestamp",
														id: 6
													},
													lastEventTime: {
														type: "google.protobuf.Timestamp",
														id: 7
													},
													lastEventNumber: {
														type: "int64",
														id: 8
													},
													members: {
														rule: "repeated",
														type: "ChatMemberData",
														id: 9
													}
												}
											},
											ChatCreatedEventData: {
												fields: {
													chatId: {
														type: "string",
														id: 1
													},
													eventNumber: {
														type: "int64",
														id: 2
													},
													timestamp: {
														type: "google.protobuf.Timestamp",
														id: 3
													},
													user: {
														type: "com.convergencelabs.convergence.proto.core.DomainUserIdData",
														id: 4
													},
													name: {
														type: "string",
														id: 5
													},
													topic: {
														type: "string",
														id: 6
													},
													members: {
														rule: "repeated",
														type: "com.convergencelabs.convergence.proto.core.DomainUserIdData",
														id: 7
													}
												}
											},
											ChatMessageEventData: {
												fields: {
													chatId: {
														type: "string",
														id: 1
													},
													eventNumber: {
														type: "int64",
														id: 2
													},
													timestamp: {
														type: "google.protobuf.Timestamp",
														id: 3
													},
													user: {
														type: "com.convergencelabs.convergence.proto.core.DomainUserIdData",
														id: 4
													},
													message: {
														type: "string",
														id: 5
													}
												}
											},
											ChatUserJoinedEventData: {
												fields: {
													chatId: {
														type: "string",
														id: 1
													},
													eventNumber: {
														type: "int64",
														id: 2
													},
													timestamp: {
														type: "google.protobuf.Timestamp",
														id: 3
													},
													user: {
														type: "com.convergencelabs.convergence.proto.core.DomainUserIdData",
														id: 4
													}
												}
											},
											ChatUserLeftEventData: {
												fields: {
													chatId: {
														type: "string",
														id: 1
													},
													eventNumber: {
														type: "int64",
														id: 2
													},
													timestamp: {
														type: "google.protobuf.Timestamp",
														id: 3
													},
													user: {
														type: "com.convergencelabs.convergence.proto.core.DomainUserIdData",
														id: 4
													}
												}
											},
											ChatUserAddedEventData: {
												fields: {
													chatId: {
														type: "string",
														id: 1
													},
													eventNumber: {
														type: "int64",
														id: 2
													},
													timestamp: {
														type: "google.protobuf.Timestamp",
														id: 3
													},
													user: {
														type: "com.convergencelabs.convergence.proto.core.DomainUserIdData",
														id: 4
													},
													addedUser: {
														type: "com.convergencelabs.convergence.proto.core.DomainUserIdData",
														id: 5
													}
												}
											},
											ChatUserRemovedEventData: {
												fields: {
													chatId: {
														type: "string",
														id: 1
													},
													eventNumber: {
														type: "int64",
														id: 2
													},
													timestamp: {
														type: "google.protobuf.Timestamp",
														id: 3
													},
													user: {
														type: "com.convergencelabs.convergence.proto.core.DomainUserIdData",
														id: 4
													},
													removedUser: {
														type: "com.convergencelabs.convergence.proto.core.DomainUserIdData",
														id: 5
													}
												}
											},
											ChatNameChangedEventData: {
												fields: {
													chatId: {
														type: "string",
														id: 1
													},
													eventNumber: {
														type: "int64",
														id: 2
													},
													timestamp: {
														type: "google.protobuf.Timestamp",
														id: 3
													},
													user: {
														type: "com.convergencelabs.convergence.proto.core.DomainUserIdData",
														id: 4
													},
													name: {
														type: "string",
														id: 5
													}
												}
											},
											ChatTopicChangedEventData: {
												fields: {
													chatId: {
														type: "string",
														id: 1
													},
													eventNumber: {
														type: "int64",
														id: 2
													},
													timestamp: {
														type: "google.protobuf.Timestamp",
														id: 3
													},
													user: {
														type: "com.convergencelabs.convergence.proto.core.DomainUserIdData",
														id: 4
													},
													topic: {
														type: "string",
														id: 5
													}
												}
											},
											ChatEventData: {
												oneofs: {
													event: {
														oneof: [
															"created",
															"message",
															"userJoined",
															"userLeft",
															"userAdded",
															"userRemoved",
															"nameChanged",
															"topicChanged"
														]
													}
												},
												fields: {
													created: {
														type: "ChatCreatedEventData",
														id: 1
													},
													message: {
														type: "ChatMessageEventData",
														id: 2
													},
													userJoined: {
														type: "ChatUserJoinedEventData",
														id: 3
													},
													userLeft: {
														type: "ChatUserLeftEventData",
														id: 4
													},
													userAdded: {
														type: "ChatUserAddedEventData",
														id: 5
													},
													userRemoved: {
														type: "ChatUserRemovedEventData",
														id: 6
													},
													nameChanged: {
														type: "ChatNameChangedEventData",
														id: 7
													},
													topicChanged: {
														type: "ChatTopicChangedEventData",
														id: 8
													}
												}
											}
										}
									},
									identity: {
										nested: {
											GetUsersRequestMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.IdentityMessage"
												},
												fields: {
													userIds: {
														rule: "repeated",
														type: "com.convergencelabs.convergence.proto.core.DomainUserIdData",
														id: 1
													}
												}
											},
											SearchUsersRequestMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.IdentityMessage"
												},
												fields: {
													fields: {
														rule: "repeated",
														type: "UserField",
														id: 1
													},
													value: {
														type: "string",
														id: 2
													},
													offset: {
														type: "google.protobuf.Int32Value",
														id: 3
													},
													limit: {
														type: "google.protobuf.Int32Value",
														id: 4
													},
													orderField: {
														type: "UserField",
														id: 5
													},
													ascending: {
														type: "bool",
														id: 6
													}
												}
											},
											UserListMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.IdentityMessage"
												},
												fields: {
													userData: {
														rule: "repeated",
														type: "com.convergencelabs.convergence.proto.core.DomainUserData",
														id: 1
													}
												}
											},
											UserGroupsRequestMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.IdentityMessage"
												},
												fields: {
													ids: {
														rule: "repeated",
														type: "string",
														id: 1
													}
												}
											},
											UserGroupsResponseMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.IdentityMessage"
												},
												fields: {
													groupData: {
														rule: "repeated",
														type: "UserGroupData",
														id: 1
													}
												}
											},
											UserGroupsForUsersRequestMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.IdentityMessage"
												},
												fields: {
													users: {
														rule: "repeated",
														type: "com.convergencelabs.convergence.proto.core.DomainUserIdData",
														id: 1
													}
												}
											},
											UserGroupsForUsersResponseMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.IdentityMessage"
												},
												fields: {
													userGroups: {
														rule: "repeated",
														type: "UserGroupsEntry",
														id: 1
													}
												}
											},
											IdentityCacheUpdateMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.IdentityMessage"
												},
												fields: {
													sessions: {
														keyType: "string",
														type: "com.convergencelabs.convergence.proto.core.DomainUserIdData",
														id: 1
													},
													users: {
														rule: "repeated",
														type: "com.convergencelabs.convergence.proto.core.DomainUserData",
														id: 2
													}
												}
											},
											UserField: {
												values: {
													FIELD_NOT_SET: 0,
													USERNAME: 1,
													EMAIL: 2,
													FIRST_NAME: 3,
													LAST_NAME: 4,
													DISPLAY_NAME: 5
												}
											},
											UserGroupData: {
												fields: {
													id: {
														type: "string",
														id: 1
													},
													description: {
														type: "string",
														id: 2
													},
													members: {
														rule: "repeated",
														type: "com.convergencelabs.convergence.proto.core.DomainUserIdData",
														id: 3
													}
												}
											},
											UserGroupsEntry: {
												fields: {
													user: {
														type: "com.convergencelabs.convergence.proto.core.DomainUserIdData",
														id: 1
													},
													groups: {
														rule: "repeated",
														type: "string",
														id: 2
													}
												}
											}
										}
									},
									model: {
										nested: {
											OpenRealtimeModelRequestMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ClientMessage"
												},
												fields: {
													modelId: {
														type: "google.protobuf.StringValue",
														id: 1
													},
													autoCreateId: {
														type: "google.protobuf.Int32Value",
														id: 2
													}
												}
											},
											OpenRealtimeModelResponseMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
													resourceId: {
														type: "int32",
														id: 1
													},
													modelId: {
														type: "string",
														id: 2
													},
													collection: {
														type: "string",
														id: 3
													},
													valueIdPrefix: {
														type: "string",
														id: 4
													},
													version: {
														type: "int64",
														id: 5
													},
													createdTime: {
														type: "google.protobuf.Timestamp",
														id: 6
													},
													modifiedTime: {
														type: "google.protobuf.Timestamp",
														id: 7
													},
													data: {
														type: "ObjectValue",
														id: 8
													},
													connectedClients: {
														rule: "repeated",
														type: "string",
														id: 9
													},
													resyncingClients: {
														rule: "repeated",
														type: "string",
														id: 10
													},
													references: {
														rule: "repeated",
														type: "ReferenceData",
														id: 11
													},
													permissions: {
														type: "ModelPermissionsData",
														id: 12
													}
												}
											},
											CloseRealtimeModelRequestMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ClientMessage"
												},
												fields: {
													resourceId: {
														type: "int32",
														id: 1
													}
												}
											},
											CloseRealTimeModelResponseMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
												}
											},
											AutoCreateModelConfigRequestMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
													autoCreateId: {
														type: "int32",
														id: 1
													}
												}
											},
											AutoCreateModelConfigResponseMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ClientMessage"
												},
												fields: {
													collection: {
														type: "string",
														id: 1
													},
													data: {
														type: "ObjectValue",
														id: 2
													},
													overridePermissions: {
														type: "bool",
														id: 3
													},
													worldPermissions: {
														type: "ModelPermissionsData",
														id: 4
													},
													userPermissions: {
														rule: "repeated",
														type: "UserModelPermissionsData",
														id: 5
													},
													ephemeral: {
														type: "bool",
														id: 6
													}
												}
											},
											RemoteClientClosedMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
													resourceId: {
														type: "int32",
														id: 1
													},
													sessionId: {
														type: "string",
														id: 2
													}
												}
											},
											RemoteClientOpenedMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
													resourceId: {
														type: "int32",
														id: 1
													},
													sessionId: {
														type: "string",
														id: 2
													}
												}
											},
											ModelForceCloseMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
													resourceId: {
														type: "int32",
														id: 1
													},
													reason: {
														type: "string",
														id: 2
													},
													reasonCode: {
														type: "int32",
														id: 3
													}
												}
											},
											CreateRealtimeModelRequestMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ClientMessage"
												},
												fields: {
													collectionId: {
														type: "string",
														id: 1
													},
													modelId: {
														type: "google.protobuf.StringValue",
														id: 2
													},
													data: {
														type: "ObjectValue",
														id: 3
													},
													createdTime: {
														type: "google.protobuf.Timestamp",
														id: 4
													},
													overrideWorldPermissions: {
														type: "bool",
														id: 5
													},
													worldPermissions: {
														type: "ModelPermissionsData",
														id: 6
													},
													userPermissions: {
														rule: "repeated",
														type: "UserModelPermissionsData",
														id: 7
													}
												}
											},
											CreateRealtimeModelResponseMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
													modelId: {
														type: "string",
														id: 1
													}
												}
											},
											DeleteRealtimeModelRequestMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ClientMessage"
												},
												fields: {
													modelId: {
														type: "string",
														id: 1
													}
												}
											},
											DeleteRealtimeModelResponseMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
												}
											},
											ModelResyncRequestMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ClientMessage"
												},
												fields: {
													modelId: {
														type: "string",
														id: 1
													},
													contextVersion: {
														type: "int64",
														id: 2
													}
												}
											},
											ModelResyncResponseMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
													resourceId: {
														type: "int32",
														id: 1
													},
													currentVersion: {
														type: "int64",
														id: 2
													},
													permissions: {
														type: "ModelPermissionsData",
														id: 3
													}
												}
											},
											ModelResyncClientCompleteMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ClientMessage"
												},
												fields: {
													resourceId: {
														type: "int32",
														id: 1
													},
													open: {
														type: "bool",
														id: 2
													}
												}
											},
											ModelResyncServerCompleteMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
													resourceId: {
														type: "int32",
														id: 1
													},
													connectedClients: {
														rule: "repeated",
														type: "string",
														id: 2
													},
													resyncingClients: {
														rule: "repeated",
														type: "string",
														id: 3
													},
													references: {
														rule: "repeated",
														type: "ReferenceData",
														id: 4
													}
												}
											},
											RemoteClientResyncStartedMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
													resourceId: {
														type: "int32",
														id: 1
													},
													sessionId: {
														type: "string",
														id: 2
													}
												}
											},
											RemoteClientResyncCompletedMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
													resourceId: {
														type: "int32",
														id: 1
													},
													sessionId: {
														type: "string",
														id: 2
													}
												}
											},
											GetModelPermissionsRequestMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ClientMessage"
												},
												fields: {
													modelId: {
														type: "string",
														id: 1
													}
												}
											},
											GetModelPermissionsResponseMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
													overridesCollection: {
														type: "bool",
														id: 1
													},
													worldPermissions: {
														type: "ModelPermissionsData",
														id: 2
													},
													userPermissions: {
														rule: "repeated",
														type: "UserModelPermissionsData",
														id: 3
													}
												}
											},
											SetModelPermissionsRequestMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ClientMessage"
												},
												fields: {
													modelId: {
														type: "string",
														id: 1
													},
													overrideCollection: {
														type: "google.protobuf.BoolValue",
														id: 2
													},
													worldPermissions: {
														type: "ModelPermissionsData",
														id: 3
													},
													removeAllUserPermissionsBeforeSet: {
														type: "bool",
														id: 4
													},
													setUserPermissions: {
														rule: "repeated",
														type: "UserModelPermissionsData",
														id: 5
													},
													removedUserPermissions: {
														rule: "repeated",
														type: "com.convergencelabs.convergence.proto.core.DomainUserIdData",
														id: 6
													}
												}
											},
											SetModelPermissionsResponseMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
												}
											},
											ModelPermissionsChangedMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
													resourceId: {
														type: "int32",
														id: 1
													},
													permissions: {
														type: "ModelPermissionsData",
														id: 2
													}
												}
											},
											ModelsQueryRequestMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ClientMessage"
												},
												fields: {
													query: {
														type: "string",
														id: 1
													}
												}
											},
											ModelsQueryResponseMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
													models: {
														rule: "repeated",
														type: "ModelResult",
														id: 1
													},
													offset: {
														type: "int64",
														id: 2
													},
													totalResults: {
														type: "int64",
														id: 3
													}
												},
												nested: {
													ModelResult: {
														fields: {
															collectionId: {
																type: "string",
																id: 1
															},
															modelId: {
																type: "string",
																id: 2
															},
															createdTime: {
																type: "google.protobuf.Timestamp",
																id: 3
															},
															modifiedTime: {
																type: "google.protobuf.Timestamp",
																id: 4
															},
															version: {
																type: "int64",
																id: 5
															},
															data: {
																type: "google.protobuf.Struct",
																id: 6
															}
														}
													}
												}
											},
											OperationSubmissionMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ClientMessage"
												},
												fields: {
													resourceId: {
														type: "int32",
														id: 1
													},
													sequenceNumber: {
														type: "int32",
														id: 2
													},
													contextVersion: {
														type: "int64",
														id: 3
													},
													operation: {
														type: "OperationData",
														id: 4
													}
												}
											},
											OperationAcknowledgementMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
													resourceId: {
														type: "int32",
														id: 1
													},
													sequenceNumber: {
														type: "int32",
														id: 2
													},
													version: {
														type: "int64",
														id: 3
													},
													timestamp: {
														type: "google.protobuf.Timestamp",
														id: 4
													}
												}
											},
											RemoteOperationMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
													resourceId: {
														type: "int32",
														id: 1
													},
													sessionId: {
														type: "string",
														id: 2
													},
													contextVersion: {
														type: "int64",
														id: 3
													},
													timestamp: {
														type: "google.protobuf.Timestamp",
														id: 4
													},
													operation: {
														type: "OperationData",
														id: 5
													}
												}
											},
											ShareReferenceMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ClientMessage"
												},
												fields: {
													resourceId: {
														type: "int32",
														id: 1
													},
													valueId: {
														type: "google.protobuf.StringValue",
														id: 2
													},
													key: {
														type: "string",
														id: 3
													},
													references: {
														type: "ReferenceValues",
														id: 4
													},
													version: {
														type: "int64",
														id: 5
													}
												}
											},
											RemoteReferenceSharedMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
													resourceId: {
														type: "int32",
														id: 1
													},
													valueId: {
														type: "google.protobuf.StringValue",
														id: 2
													},
													key: {
														type: "string",
														id: 3
													},
													references: {
														type: "ReferenceValues",
														id: 4
													},
													sessionId: {
														type: "string",
														id: 5
													}
												}
											},
											UnshareReferenceMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ClientMessage"
												},
												fields: {
													resourceId: {
														type: "int32",
														id: 1
													},
													valueId: {
														type: "google.protobuf.StringValue",
														id: 2
													},
													key: {
														type: "string",
														id: 3
													}
												}
											},
											RemoteReferenceUnsharedMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
													resourceId: {
														type: "int32",
														id: 1
													},
													valueId: {
														type: "google.protobuf.StringValue",
														id: 2
													},
													key: {
														type: "string",
														id: 3
													},
													sessionId: {
														type: "string",
														id: 4
													}
												}
											},
											SetReferenceMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ClientMessage"
												},
												fields: {
													resourceId: {
														type: "int32",
														id: 1
													},
													valueId: {
														type: "google.protobuf.StringValue",
														id: 2
													},
													key: {
														type: "string",
														id: 3
													},
													references: {
														type: "ReferenceValues",
														id: 4
													},
													version: {
														type: "int64",
														id: 5
													}
												}
											},
											RemoteReferenceSetMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
													resourceId: {
														type: "int32",
														id: 1
													},
													valueId: {
														type: "google.protobuf.StringValue",
														id: 2
													},
													key: {
														type: "string",
														id: 3
													},
													references: {
														type: "ReferenceValues",
														id: 4
													},
													sessionId: {
														type: "string",
														id: 5
													}
												}
											},
											ClearReferenceMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ClientMessage"
												},
												fields: {
													resourceId: {
														type: "int32",
														id: 1
													},
													valueId: {
														type: "google.protobuf.StringValue",
														id: 2
													},
													key: {
														type: "string",
														id: 3
													}
												}
											},
											RemoteReferenceClearedMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
													resourceId: {
														type: "int32",
														id: 1
													},
													valueId: {
														type: "google.protobuf.StringValue",
														id: 2
													},
													key: {
														type: "string",
														id: 3
													},
													sessionId: {
														type: "string",
														id: 4
													}
												}
											},
											ModelOfflineSubscriptionChangeRequestMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ClientMessage"
												},
												fields: {
													subscribe: {
														rule: "repeated",
														type: "ModelOfflineSubscriptionData",
														id: 1
													},
													unsubscribe: {
														rule: "repeated",
														type: "string",
														id: 2
													},
													all: {
														type: "bool",
														id: 3
													}
												},
												nested: {
													ModelOfflineSubscriptionData: {
														fields: {
															modelId: {
																type: "string",
																id: 1
															},
															currentVersion: {
																type: "int64",
																id: 2
															},
															currentPermissions: {
																type: "ModelPermissionsData",
																id: 3
															}
														}
													}
												}
											},
											OfflineModelUpdatedMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												oneofs: {
													action: {
														oneof: [
															"deleted",
															"permissionRevoked",
															"updated",
															"initial"
														]
													}
												},
												fields: {
													modelId: {
														type: "string",
														id: 1
													},
													deleted: {
														type: "bool",
														id: 2
													},
													permissionRevoked: {
														type: "bool",
														id: 3
													},
													updated: {
														type: "OfflineModelUpdateData",
														id: 4
													},
													initial: {
														type: "OfflineModelInitialData",
														id: 5
													}
												},
												nested: {
													OfflineModelInitialData: {
														fields: {
															collection: {
																type: "string",
																id: 1
															},
															valueIdPrefix: {
																type: "string",
																id: 2
															},
															model: {
																type: "ModelUpdateData",
																id: 3
															},
															permissions: {
																type: "ModelPermissionsData",
																id: 4
															}
														}
													},
													OfflineModelUpdateData: {
														fields: {
															model: {
																type: "ModelUpdateData",
																id: 1
															},
															permissions: {
																type: "ModelPermissionsData",
																id: 2
															}
														}
													},
													ModelUpdateData: {
														fields: {
															version: {
																type: "int64",
																id: 1
															},
															createdTime: {
																type: "google.protobuf.Timestamp",
																id: 2
															},
															modifiedTime: {
																type: "google.protobuf.Timestamp",
																id: 3
															},
															data: {
																type: "ObjectValue",
																id: 4
															}
														}
													}
												}
											},
											HistoricalDataRequestMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ClientMessage"
												},
												fields: {
													modelId: {
														type: "string",
														id: 1
													}
												}
											},
											HistoricalDataResponseMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
													collectionId: {
														type: "string",
														id: 1
													},
													data: {
														type: "ObjectValue",
														id: 2
													},
													version: {
														type: "int64",
														id: 3
													},
													createdTime: {
														type: "google.protobuf.Timestamp",
														id: 4
													},
													modifiedTime: {
														type: "google.protobuf.Timestamp",
														id: 5
													}
												}
											},
											HistoricalOperationRequestMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ClientMessage"
												},
												fields: {
													modelId: {
														type: "string",
														id: 1
													},
													first: {
														type: "int64",
														id: 2
													},
													last: {
														type: "int64",
														id: 3
													}
												}
											},
											HistoricalOperationsResponseMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
													operations: {
														rule: "repeated",
														type: "ModelOperationData",
														id: 1
													}
												},
												nested: {
													ModelOperationData: {
														fields: {
															modelId: {
																type: "string",
																id: 1
															},
															version: {
																type: "int64",
																id: 2
															},
															timestamp: {
																type: "google.protobuf.Timestamp",
																id: 3
															},
															sessionId: {
																type: "string",
																id: 4
															},
															operation: {
																type: "AppliedOperationData",
																id: 5
															}
														}
													}
												}
											},
											ModelGetVersionAtTimeRequestMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ClientMessage"
												},
												fields: {
													modelId: {
														type: "string",
														id: 1
													},
													targetTime: {
														type: "google.protobuf.Timestamp",
														id: 2
													}
												}
											},
											ModelGetVersionAtTimeResponseMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.ServerMessage"
												},
												fields: {
													versionAtTime: {
														type: "int64",
														id: 1
													}
												}
											},
											ObjectValue: {
												fields: {
													id: {
														type: "string",
														id: 1
													},
													children: {
														keyType: "string",
														type: "DataValue",
														id: 2
													}
												}
											},
											ArrayValue: {
												fields: {
													id: {
														type: "string",
														id: 1
													},
													children: {
														rule: "repeated",
														type: "DataValue",
														id: 2
													}
												}
											},
											BooleanValue: {
												fields: {
													id: {
														type: "string",
														id: 1
													},
													value: {
														type: "bool",
														id: 2
													}
												}
											},
											DoubleValue: {
												fields: {
													id: {
														type: "string",
														id: 1
													},
													value: {
														type: "double",
														id: 2
													}
												}
											},
											NullValue: {
												fields: {
													id: {
														type: "string",
														id: 1
													}
												}
											},
											StringValue: {
												fields: {
													id: {
														type: "string",
														id: 1
													},
													value: {
														type: "string",
														id: 2
													}
												}
											},
											DateValue: {
												fields: {
													id: {
														type: "string",
														id: 1
													},
													value: {
														type: "google.protobuf.Timestamp",
														id: 2
													}
												}
											},
											DataValue: {
												oneofs: {
													value: {
														oneof: [
															"objectValue",
															"arrayValue",
															"booleanValue",
															"doubleValue",
															"nullValue",
															"stringValue",
															"dateValue"
														]
													}
												},
												fields: {
													objectValue: {
														type: "ObjectValue",
														id: 1
													},
													arrayValue: {
														type: "ArrayValue",
														id: 2
													},
													booleanValue: {
														type: "BooleanValue",
														id: 3
													},
													doubleValue: {
														type: "DoubleValue",
														id: 4
													},
													nullValue: {
														type: "NullValue",
														id: 5
													},
													stringValue: {
														type: "StringValue",
														id: 6
													},
													dateValue: {
														type: "DateValue",
														id: 7
													}
												}
											},
											StringSpliceOperationData: {
												fields: {
													id: {
														type: "string",
														id: 1
													},
													noOp: {
														type: "bool",
														id: 2
													},
													index: {
														type: "int32",
														id: 3
													},
													deleteCount: {
														type: "int32",
														id: 4
													},
													insertedValue: {
														type: "string",
														id: 5
													}
												}
											},
											StringSetOperationData: {
												fields: {
													id: {
														type: "string",
														id: 1
													},
													noOp: {
														type: "bool",
														id: 2
													},
													value: {
														type: "string",
														id: 3
													}
												}
											},
											ArrayInsertOperationData: {
												fields: {
													id: {
														type: "string",
														id: 1
													},
													noOp: {
														type: "bool",
														id: 2
													},
													index: {
														type: "int32",
														id: 3
													},
													value: {
														type: "DataValue",
														id: 4
													}
												}
											},
											ArrayRemoveOperationData: {
												fields: {
													id: {
														type: "string",
														id: 1
													},
													noOp: {
														type: "bool",
														id: 2
													},
													index: {
														type: "int32",
														id: 3
													}
												}
											},
											ArrayReplaceOperationData: {
												fields: {
													id: {
														type: "string",
														id: 1
													},
													noOp: {
														type: "bool",
														id: 2
													},
													index: {
														type: "int32",
														id: 3
													},
													value: {
														type: "DataValue",
														id: 4
													}
												}
											},
											ArrayMoveOperationData: {
												fields: {
													id: {
														type: "string",
														id: 1
													},
													noOp: {
														type: "bool",
														id: 2
													},
													fromIndex: {
														type: "int32",
														id: 3
													},
													toIndex: {
														type: "int32",
														id: 4
													}
												}
											},
											ArraySetOperationData: {
												fields: {
													id: {
														type: "string",
														id: 1
													},
													noOp: {
														type: "bool",
														id: 2
													},
													values: {
														rule: "repeated",
														type: "DataValue",
														id: 3
													}
												}
											},
											ObjectAddPropertyOperationData: {
												fields: {
													id: {
														type: "string",
														id: 1
													},
													noOp: {
														type: "bool",
														id: 2
													},
													key: {
														type: "string",
														id: 3
													},
													value: {
														type: "DataValue",
														id: 4
													}
												}
											},
											ObjectSetPropertyOperationData: {
												fields: {
													id: {
														type: "string",
														id: 1
													},
													noOp: {
														type: "bool",
														id: 2
													},
													key: {
														type: "string",
														id: 3
													},
													value: {
														type: "DataValue",
														id: 4
													}
												}
											},
											ObjectRemovePropertyOperationData: {
												fields: {
													id: {
														type: "string",
														id: 1
													},
													noOp: {
														type: "bool",
														id: 2
													},
													key: {
														type: "string",
														id: 3
													}
												}
											},
											ObjectSetOperationData: {
												fields: {
													id: {
														type: "string",
														id: 1
													},
													noOp: {
														type: "bool",
														id: 2
													},
													values: {
														keyType: "string",
														type: "DataValue",
														id: 3
													}
												}
											},
											NumberDeltaOperationData: {
												fields: {
													id: {
														type: "string",
														id: 1
													},
													noOp: {
														type: "bool",
														id: 2
													},
													delta: {
														type: "double",
														id: 3
													}
												}
											},
											NumberSetOperationData: {
												fields: {
													id: {
														type: "string",
														id: 1
													},
													noOp: {
														type: "bool",
														id: 2
													},
													value: {
														type: "double",
														id: 3
													}
												}
											},
											BooleanSetOperationData: {
												fields: {
													id: {
														type: "string",
														id: 1
													},
													noOp: {
														type: "bool",
														id: 2
													},
													value: {
														type: "bool",
														id: 3
													}
												}
											},
											DateSetOperationData: {
												fields: {
													id: {
														type: "string",
														id: 1
													},
													noOp: {
														type: "bool",
														id: 2
													},
													value: {
														type: "google.protobuf.Timestamp",
														id: 3
													}
												}
											},
											DiscreteOperationData: {
												oneofs: {
													operation: {
														oneof: [
															"stringSpliceOperation",
															"stringSetOperation",
															"arrayInsertOperation",
															"arrayRemoveOperation",
															"arrayReplaceOperation",
															"arrayMoveOperation",
															"arraySetOperation",
															"objectAddPropertyOperation",
															"objectSetPropertyOperation",
															"objectRemovePropertyOperation",
															"objectSetOperation",
															"numberDeltaOperation",
															"numberSetOperation",
															"booleanSetOperation",
															"dateSetOperation"
														]
													}
												},
												fields: {
													stringSpliceOperation: {
														type: "StringSpliceOperationData",
														id: 1
													},
													stringSetOperation: {
														type: "StringSetOperationData",
														id: 2
													},
													arrayInsertOperation: {
														type: "ArrayInsertOperationData",
														id: 3
													},
													arrayRemoveOperation: {
														type: "ArrayRemoveOperationData",
														id: 4
													},
													arrayReplaceOperation: {
														type: "ArrayReplaceOperationData",
														id: 5
													},
													arrayMoveOperation: {
														type: "ArrayMoveOperationData",
														id: 6
													},
													arraySetOperation: {
														type: "ArraySetOperationData",
														id: 7
													},
													objectAddPropertyOperation: {
														type: "ObjectAddPropertyOperationData",
														id: 8
													},
													objectSetPropertyOperation: {
														type: "ObjectSetPropertyOperationData",
														id: 9
													},
													objectRemovePropertyOperation: {
														type: "ObjectRemovePropertyOperationData",
														id: 10
													},
													objectSetOperation: {
														type: "ObjectSetOperationData",
														id: 11
													},
													numberDeltaOperation: {
														type: "NumberDeltaOperationData",
														id: 12
													},
													numberSetOperation: {
														type: "NumberSetOperationData",
														id: 13
													},
													booleanSetOperation: {
														type: "BooleanSetOperationData",
														id: 14
													},
													dateSetOperation: {
														type: "DateSetOperationData",
														id: 15
													}
												}
											},
											CompoundOperationData: {
												fields: {
													operations: {
														rule: "repeated",
														type: "DiscreteOperationData",
														id: 1
													}
												}
											},
											OperationData: {
												oneofs: {
													operation: {
														oneof: [
															"discreteOperation",
															"compoundOperation"
														]
													}
												},
												fields: {
													discreteOperation: {
														type: "DiscreteOperationData",
														id: 1
													},
													compoundOperation: {
														type: "CompoundOperationData",
														id: 2
													}
												}
											},
											ModelPermissionsData: {
												fields: {
													read: {
														type: "bool",
														id: 1
													},
													write: {
														type: "bool",
														id: 2
													},
													remove: {
														type: "bool",
														id: 3
													},
													manage: {
														type: "bool",
														id: 4
													}
												}
											},
											UserModelPermissionsData: {
												fields: {
													user: {
														type: "com.convergencelabs.convergence.proto.core.DomainUserIdData",
														id: 1
													},
													permissions: {
														type: "ModelPermissionsData",
														id: 2
													}
												}
											},
											AppliedStringSpliceOperationData: {
												fields: {
													id: {
														type: "string",
														id: 1
													},
													noOp: {
														type: "bool",
														id: 2
													},
													index: {
														type: "int32",
														id: 3
													},
													deletedValue: {
														type: "string",
														id: 4
													},
													insertedValue: {
														type: "string",
														id: 5
													}
												}
											},
											AppliedStringSetOperationData: {
												fields: {
													id: {
														type: "string",
														id: 1
													},
													noOp: {
														type: "bool",
														id: 2
													},
													value: {
														type: "string",
														id: 3
													},
													oldValue: {
														type: "string",
														id: 4
													}
												}
											},
											AppliedArrayInsertOperationData: {
												fields: {
													id: {
														type: "string",
														id: 1
													},
													noOp: {
														type: "bool",
														id: 2
													},
													index: {
														type: "int32",
														id: 3
													},
													value: {
														type: "DataValue",
														id: 4
													}
												}
											},
											AppliedArrayRemoveOperationData: {
												fields: {
													id: {
														type: "string",
														id: 1
													},
													noOp: {
														type: "bool",
														id: 2
													},
													index: {
														type: "int32",
														id: 3
													},
													oldValue: {
														type: "DataValue",
														id: 4
													}
												}
											},
											AppliedArrayReplaceOperationData: {
												fields: {
													id: {
														type: "string",
														id: 1
													},
													noOp: {
														type: "bool",
														id: 2
													},
													index: {
														type: "int32",
														id: 3
													},
													value: {
														type: "DataValue",
														id: 4
													},
													oldValue: {
														type: "DataValue",
														id: 5
													}
												}
											},
											AppliedArrayMoveOperationData: {
												fields: {
													id: {
														type: "string",
														id: 1
													},
													noOp: {
														type: "bool",
														id: 2
													},
													fromIndex: {
														type: "int32",
														id: 3
													},
													toIndex: {
														type: "int32",
														id: 4
													}
												}
											},
											AppliedArraySetOperationData: {
												fields: {
													id: {
														type: "string",
														id: 1
													},
													noOp: {
														type: "bool",
														id: 2
													},
													values: {
														rule: "repeated",
														type: "DataValue",
														id: 3
													},
													oldValues: {
														rule: "repeated",
														type: "DataValue",
														id: 4
													}
												}
											},
											AppliedObjectAddPropertyOperationData: {
												fields: {
													id: {
														type: "string",
														id: 1
													},
													noOp: {
														type: "bool",
														id: 2
													},
													key: {
														type: "string",
														id: 3
													},
													value: {
														type: "DataValue",
														id: 4
													}
												}
											},
											AppliedObjectSetPropertyOperationData: {
												fields: {
													id: {
														type: "string",
														id: 1
													},
													noOp: {
														type: "bool",
														id: 2
													},
													key: {
														type: "string",
														id: 3
													},
													value: {
														type: "DataValue",
														id: 4
													},
													oldValue: {
														type: "DataValue",
														id: 5
													}
												}
											},
											AppliedObjectRemovePropertyOperationData: {
												fields: {
													id: {
														type: "string",
														id: 1
													},
													noOp: {
														type: "bool",
														id: 2
													},
													key: {
														type: "string",
														id: 3
													},
													oldValue: {
														type: "DataValue",
														id: 4
													}
												}
											},
											AppliedObjectSetOperationData: {
												fields: {
													id: {
														type: "string",
														id: 1
													},
													noOp: {
														type: "bool",
														id: 2
													},
													values: {
														keyType: "string",
														type: "DataValue",
														id: 3
													},
													oldValues: {
														keyType: "string",
														type: "DataValue",
														id: 4
													}
												}
											},
											AppliedNumberDeltaOperationData: {
												fields: {
													id: {
														type: "string",
														id: 1
													},
													noOp: {
														type: "bool",
														id: 2
													},
													delta: {
														type: "double",
														id: 3
													}
												}
											},
											AppliedNumberSetOperationData: {
												fields: {
													id: {
														type: "string",
														id: 1
													},
													noOp: {
														type: "bool",
														id: 2
													},
													value: {
														type: "double",
														id: 3
													},
													oldValue: {
														type: "double",
														id: 4
													}
												}
											},
											AppliedBooleanSetOperationData: {
												fields: {
													id: {
														type: "string",
														id: 1
													},
													noOp: {
														type: "bool",
														id: 2
													},
													value: {
														type: "bool",
														id: 3
													},
													oldValue: {
														type: "bool",
														id: 4
													}
												}
											},
											AppliedDateSetOperationData: {
												fields: {
													id: {
														type: "string",
														id: 1
													},
													noOp: {
														type: "bool",
														id: 2
													},
													value: {
														type: "google.protobuf.Timestamp",
														id: 3
													},
													oldValue: {
														type: "google.protobuf.Timestamp",
														id: 4
													}
												}
											},
											AppliedDiscreteOperationData: {
												oneofs: {
													operation: {
														oneof: [
															"stringSpliceOperation",
															"stringSetOperation",
															"arrayInsertOperation",
															"arrayRemoveOperation",
															"arrayReplaceOperation",
															"arrayMoveOperation",
															"arraySetOperation",
															"objectAddPropertyOperation",
															"objectSetPropertyOperation",
															"objectRemovePropertyOperation",
															"objectSetOperation",
															"numberDeltaOperation",
															"numberSetOperation",
															"booleanSetOperation",
															"dateSetOperation"
														]
													}
												},
												fields: {
													stringSpliceOperation: {
														type: "AppliedStringSpliceOperationData",
														id: 1
													},
													stringSetOperation: {
														type: "AppliedStringSetOperationData",
														id: 2
													},
													arrayInsertOperation: {
														type: "AppliedArrayInsertOperationData",
														id: 3
													},
													arrayRemoveOperation: {
														type: "AppliedArrayRemoveOperationData",
														id: 4
													},
													arrayReplaceOperation: {
														type: "AppliedArrayReplaceOperationData",
														id: 5
													},
													arrayMoveOperation: {
														type: "AppliedArrayMoveOperationData",
														id: 6
													},
													arraySetOperation: {
														type: "AppliedArraySetOperationData",
														id: 7
													},
													objectAddPropertyOperation: {
														type: "AppliedObjectAddPropertyOperationData",
														id: 8
													},
													objectSetPropertyOperation: {
														type: "AppliedObjectSetPropertyOperationData",
														id: 9
													},
													objectRemovePropertyOperation: {
														type: "AppliedObjectRemovePropertyOperationData",
														id: 10
													},
													objectSetOperation: {
														type: "AppliedObjectSetOperationData",
														id: 11
													},
													numberDeltaOperation: {
														type: "AppliedNumberDeltaOperationData",
														id: 12
													},
													numberSetOperation: {
														type: "AppliedNumberSetOperationData",
														id: 13
													},
													booleanSetOperation: {
														type: "AppliedBooleanSetOperationData",
														id: 14
													},
													dateSetOperation: {
														type: "AppliedDateSetOperationData",
														id: 15
													}
												}
											},
											AppliedCompoundOperationData: {
												fields: {
													operations: {
														rule: "repeated",
														type: "AppliedDiscreteOperationData",
														id: 1
													}
												}
											},
											AppliedOperationData: {
												oneofs: {
													operation: {
														oneof: [
															"discreteOperation",
															"compoundOperation"
														]
													}
												},
												fields: {
													discreteOperation: {
														type: "AppliedDiscreteOperationData",
														id: 1
													},
													compoundOperation: {
														type: "AppliedCompoundOperationData",
														id: 2
													}
												}
											},
											IndexRange: {
												fields: {
													startIndex: {
														type: "int32",
														id: 1
													},
													endIndex: {
														type: "int32",
														id: 2
													}
												}
											},
											IndexRangeList: {
												fields: {
													values: {
														rule: "repeated",
														type: "IndexRange",
														id: 1
													}
												}
											},
											ReferenceValues: {
												oneofs: {
													values: {
														oneof: [
															"indices",
															"ranges",
															"properties",
															"elements"
														]
													}
												},
												fields: {
													indices: {
														type: "com.convergencelabs.convergence.proto.core.Int32List",
														id: 1
													},
													ranges: {
														type: "IndexRangeList",
														id: 2
													},
													properties: {
														type: "com.convergencelabs.convergence.proto.core.StringList",
														id: 3
													},
													elements: {
														type: "com.convergencelabs.convergence.proto.core.StringList",
														id: 4
													}
												}
											},
											ReferenceData: {
												fields: {
													sessionId: {
														type: "string",
														id: 1
													},
													valueId: {
														type: "google.protobuf.StringValue",
														id: 2
													},
													key: {
														type: "string",
														id: 3
													},
													reference: {
														type: "ReferenceValues",
														id: 4
													}
												}
											}
										}
									},
									presence: {
										nested: {
											PresenceRequestMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.PresenceMessage"
												},
												fields: {
													users: {
														rule: "repeated",
														type: "com.convergencelabs.convergence.proto.core.DomainUserIdData",
														id: 1
													}
												}
											},
											PresenceResponseMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.PresenceMessage"
												},
												fields: {
													userPresences: {
														rule: "repeated",
														type: "UserPresenceData",
														id: 1
													}
												}
											},
											SubscribePresenceRequestMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.PresenceMessage"
												},
												fields: {
													users: {
														rule: "repeated",
														type: "com.convergencelabs.convergence.proto.core.DomainUserIdData",
														id: 1
													}
												}
											},
											SubscribePresenceResponseMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.PresenceMessage"
												},
												fields: {
													userPresences: {
														rule: "repeated",
														type: "UserPresenceData",
														id: 1
													}
												}
											},
											UnsubscribePresenceMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.PresenceMessage"
												},
												fields: {
													users: {
														rule: "repeated",
														type: "com.convergencelabs.convergence.proto.core.DomainUserIdData",
														id: 1
													}
												}
											},
											PresenceSetStateMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.PresenceMessage"
												},
												fields: {
													state: {
														keyType: "string",
														type: "google.protobuf.Value",
														id: 1
													}
												}
											},
											PresenceRemoveStateMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.PresenceMessage"
												},
												fields: {
													keys: {
														rule: "repeated",
														type: "string",
														id: 1
													}
												}
											},
											PresenceClearStateMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.PresenceMessage"
												},
												fields: {
												}
											},
											PresenceStateSetMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.PresenceMessage"
												},
												fields: {
													user: {
														type: "com.convergencelabs.convergence.proto.core.DomainUserIdData",
														id: 1
													},
													state: {
														keyType: "string",
														type: "google.protobuf.Value",
														id: 2
													}
												}
											},
											PresenceStateRemovedMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.PresenceMessage"
												},
												fields: {
													user: {
														type: "com.convergencelabs.convergence.proto.core.DomainUserIdData",
														id: 1
													},
													keys: {
														rule: "repeated",
														type: "string",
														id: 2
													}
												}
											},
											PresenceStateClearedMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.PresenceMessage"
												},
												fields: {
													user: {
														type: "com.convergencelabs.convergence.proto.core.DomainUserIdData",
														id: 1
													}
												}
											},
											PresenceAvailabilityChangedMessage: {
												options: {
													"(scalapb.message).extends": "com.convergencelabs.convergence.proto.PresenceMessage"
												},
												fields: {
													user: {
														type: "com.convergencelabs.convergence.proto.core.DomainUserIdData",
														id: 1
													},
													available: {
														type: "bool",
														id: 2
													}
												}
											},
											UserPresenceData: {
												fields: {
													user: {
														type: "com.convergencelabs.convergence.proto.core.DomainUserIdData",
														id: 1
													},
													available: {
														type: "bool",
														id: 2
													},
													state: {
														keyType: "string",
														type: "google.protobuf.Value",
														id: 3
													}
												}
											}
										}
									}
								}
							}
						}
					}
				}
			}
		}
	},
	google: {
		nested: {
			protobuf: {
				nested: {
					DoubleValue: {
						fields: {
							value: {
								type: "double",
								id: 1
							}
						}
					},
					FloatValue: {
						fields: {
							value: {
								type: "float",
								id: 1
							}
						}
					},
					Int64Value: {
						fields: {
							value: {
								type: "int64",
								id: 1
							}
						}
					},
					UInt64Value: {
						fields: {
							value: {
								type: "uint64",
								id: 1
							}
						}
					},
					Int32Value: {
						fields: {
							value: {
								type: "int32",
								id: 1
							}
						}
					},
					UInt32Value: {
						fields: {
							value: {
								type: "uint32",
								id: 1
							}
						}
					},
					BoolValue: {
						fields: {
							value: {
								type: "bool",
								id: 1
							}
						}
					},
					StringValue: {
						fields: {
							value: {
								type: "string",
								id: 1
							}
						}
					},
					BytesValue: {
						fields: {
							value: {
								type: "bytes",
								id: 1
							}
						}
					},
					Struct: {
						fields: {
							fields: {
								keyType: "string",
								type: "Value",
								id: 1
							}
						}
					},
					Value: {
						oneofs: {
							kind: {
								oneof: [
									"nullValue",
									"numberValue",
									"stringValue",
									"boolValue",
									"structValue",
									"listValue"
								]
							}
						},
						fields: {
							nullValue: {
								type: "NullValue",
								id: 1
							},
							numberValue: {
								type: "double",
								id: 2
							},
							stringValue: {
								type: "string",
								id: 3
							},
							boolValue: {
								type: "bool",
								id: 4
							},
							structValue: {
								type: "Struct",
								id: 5
							},
							listValue: {
								type: "ListValue",
								id: 6
							}
						}
					},
					NullValue: {
						values: {
							NULL_VALUE: 0
						}
					},
					ListValue: {
						fields: {
							values: {
								rule: "repeated",
								type: "Value",
								id: 1
							}
						}
					},
					Timestamp: {
						fields: {
							seconds: {
								type: "int64",
								id: 1
							},
							nanos: {
								type: "int32",
								id: 2
							}
						}
					}
				}
			}
		}
	}
};
var convergenceProto = {
	nested: nested
};

var convergenceProtoJson = /*#__PURE__*/Object.freeze({
    __proto__: null,
    nested: nested,
    'default': convergenceProto
});

const ROOT = protobuf.Root.fromJSON(convergenceProtoJson);
const CONVERGENCE_MESSAGE_PATH = "com.convergencelabs.convergence.proto.ConvergenceMessage";
class ConvergenceMessageIO {
    static decode(bytes) {
        const protocolMessage = ConvergenceMessageIO._convergenceMessageType.decode(bytes);
        return ConvergenceMessageIO._convergenceMessageType.toObject(protocolMessage);
    }
    static encode(message) {
        const protocolMessage = ConvergenceMessageIO._convergenceMessageType.fromObject(message);
        return ConvergenceMessageIO._convergenceMessageType.encode(protocolMessage).finish();
    }
}
ConvergenceMessageIO._convergenceMessageType = ROOT.lookupType(CONVERGENCE_MESSAGE_PATH);

const ConvergenceErrorCodes = {
    MODEL_ALREADY_EXISTS: "model_already_exists",
    REQUEST_TIMEOUT: "request_timeout",
    CONNECTION_FAILED: "connection_failed",
    AUTHENTICATION_FAILED: "authentication_failed",
    OFFLINE: "offline",
    CHAT_NOT_JOINED: "chat_not_joined"
};
Immutable.make(ConvergenceErrorCodes);

var DomainUserType;
(function (DomainUserType) {
    DomainUserType["NORMAL"] = "normal";
    DomainUserType["CONVERGENCE"] = "convergence";
    DomainUserType["ANONYMOUS"] = "anonymous";
})(DomainUserType || (DomainUserType = {}));
class DomainUserId {
    constructor(userType, username) {
        this.userType = userType;
        this.username = username;
        Validation.assertString(username, "username");
        this._guid = DomainUserId.guid(this.userType, this.username);
        Object.freeze(this);
    }
    static normal(username) {
        return new DomainUserId(DomainUserType.NORMAL, username);
    }
    static anonymous(username) {
        return new DomainUserId(DomainUserType.ANONYMOUS, username);
    }
    static convergence(username) {
        return new DomainUserId(DomainUserType.CONVERGENCE, username);
    }
    static guid(userType, username) {
        return `${userType}:${username}`;
    }
    static fromGuid(guid) {
        Validation.assertNonEmptyString(guid, "guid");
        const index = guid.indexOf(":");
        if (index < 0) {
            throw new Error("Invalid guid value. Not separator found: " + guid);
        }
        const userType = DomainUserId.toDomainUserType(guid.substring(0, index));
        const username = guid.substring(index + 1, guid.length);
        return new DomainUserId(userType, username);
    }
    static toDomainUserType(typeStr) {
        switch (typeStr) {
            case DomainUserType.NORMAL:
                return DomainUserType.NORMAL;
            case DomainUserType.CONVERGENCE:
                return DomainUserType.CONVERGENCE;
            case DomainUserType.ANONYMOUS:
                return DomainUserType.ANONYMOUS;
            default:
                throw new ConvergenceError("Invalid guid value. Unrecognized user type: " + typeStr);
        }
    }
    static toDomainUserId(userId) {
        return DomainUserId.of(userId);
    }
    static of(userId) {
        return (userId instanceof DomainUserId) ? userId : DomainUserId.normal(userId);
    }
    toGuid() {
        return this._guid;
    }
    equals(other) {
        return this.username === other.username && this.userType === other.userType;
    }
}

class DomainUser {
    constructor(userType, username, firstName, lastName, displayName, email) {
        this.userType = userType;
        this.username = username;
        this.firstName = firstName;
        this.lastName = lastName;
        this.displayName = displayName;
        this.email = email;
        this.anonymous = userType === DomainUserType.ANONYMOUS;
        this.convergence = userType === DomainUserType.CONVERGENCE;
        this.normal = userType === DomainUserType.NORMAL;
        this.userId = new DomainUserId(this.userType, this.username);
        Object.freeze(this);
    }
}

class UserGroup {
    constructor(id, description, members) {
        this.id = id;
        this.description = description;
        this.members = members;
        Object.freeze(this);
    }
}

function toUserFieldCode(field) {
    switch (field) {
        case "username":
            return 1;
        case "email":
            return 2;
        case "firstName":
            return 3;
        case "lastName":
            return 4;
        case "displayName":
            return 5;
        default:
            throw new ConvergenceError("Invalid user field: " + field);
    }
}
function toDomainUser(userData) {
    return new DomainUser(protoToDomainUserType(userData.userId.userType), userData.userId.username, fromOptional(userData.firstName), fromOptional(userData.lastName), fromOptional(userData.displayName), fromOptional(userData.email));
}
function toUserGroup(userData) {
    return new UserGroup(userData.id, userData.description, userData.members ? userData.members.map(m => protoToDomainUserId(m).username) : []);
}

const validSearchFields = ["username", "email", "firstName", "lastName", "displayName"];
class IdentityService {
    constructor(connection) {
        this._connection = connection;
    }
    session() {
        return this._connection.session();
    }
    profile() {
        return this.user(this._connection.session().user().userId);
    }
    userExists(userId) {
        return this.user(userId).then((user) => user !== undefined);
    }
    user(userId) {
        if (Validation.isNotSet(userId)) {
            return Promise.reject("Must specify a user id.");
        }
        return this.users([userId]).then((users) => {
            if (users.length === 0) {
                return Promise.resolve(undefined);
            }
            else if (users.length === 1) {
                return Promise.resolve(users[0]);
            }
            else {
                return Promise.reject(new Error("Error getting user."));
            }
        });
    }
    users(users) {
        Validation.assertArray(users, "users");
        if (users.length === 0) {
            return Promise.resolve([]);
        }
        const guids = users.map((id) => {
            if (!(id instanceof DomainUserId)) {
                return DomainUserId.normal(id);
            }
            return id;
        });
        const unique = Array.from(new Set(guids));
        const message = {
            usersGetRequest: {
                userIds: unique.map(domainUserIdToProto)
            }
        };
        return this._connection
            .request(message)
            .then(mapUserResultList);
    }
    search(query) {
        if (query.fields === undefined || query.fields === null ||
            (Array.isArray(query.fields) && query.fields.length === 0)) {
            return Promise.reject(new Error("Must specify at least one field to search"));
        }
        else if (query.term === undefined || query.term === null) {
            return Promise.reject(new Error("Must specify a search term"));
        }
        else {
            const fields = Array.isArray(query.fields) ? query.fields : [query.fields];
            const orderBy = query.orderBy || {
                field: "username",
                ascending: true
            };
            const fieldCodes = this._processSearchFields(fields);
            const message = {
                userSearchRequest: {
                    fields: fieldCodes,
                    value: query.term,
                    offset: toOptional(query.offset),
                    limit: toOptional(query.limit),
                    orderField: toUserFieldCode(orderBy.field || "username"),
                    ascending: orderBy.ascending
                }
            };
            return this._connection.request(message).then(mapUserResultList);
        }
    }
    groups(ids) {
        Validation.assertNonEmptyArray(ids, "ids");
        const message = {
            userGroupsRequest: {
                ids
            }
        };
        return this._connection.request(message).then((response) => {
            const { userGroupsResponse } = response;
            return userGroupsResponse.groupData.map(d => toUserGroup(d));
        });
    }
    group(id) {
        return this.groups([id]).then(groups => groups[0]);
    }
    groupsForUser(username) {
        return this.groupsForUsers([username])
            .then(users => users[username]);
    }
    groupsForUsers(usernames) {
        const userIds = usernames.map(u => domainUserIdToProto(DomainUserId.normal(u)));
        const message = {
            userGroupsForUsersRequest: {
                users: userIds
            }
        };
        return this._connection.request(message).then((response) => {
            const { userGroupsForUsersResponse } = response;
            const groupsForUsers = {};
            userGroupsForUsersResponse.userGroups.forEach(userGroupsEntry => {
                if (userGroupsEntry.hasOwnProperty("user")) {
                    let domainUserId = protoToDomainUserId(userGroupsEntry.user);
                    groupsForUsers[domainUserId.username] = userGroupsEntry.groups;
                }
            });
            return groupsForUsers;
        });
    }
    _processSearchFields(fields) {
        const result = [];
        fields.forEach((field) => {
            if (validSearchFields.indexOf(field) < 0) {
                throw new Error("Invalid user search field: " + field);
            }
            const fieldCode = toUserFieldCode(field);
            if (result.indexOf(fieldCode) < 0) {
                result.push(fieldCode);
            }
        });
        return result;
    }
}
function mapUserResultList(response) {
    const { userListResponse } = response;
    const userData = getOrDefaultArray(userListResponse.userData);
    return userData.map(toDomainUser);
}

class StringMap {
    static objectToMap(obj) {
        const map = new Map();
        Object.keys(obj).forEach(k => map.set(k, obj[k]));
        return map;
    }
    static mapToObject(map) {
        const obj = {};
        map.forEach((v, k) => obj[k] = v);
        return obj;
    }
    static coerceToObject(map) {
        if (map === undefined) {
            return {};
        }
        else if (map instanceof Map) {
            return StringMap.mapToObject(map);
        }
        else {
            return map;
        }
    }
    static coerceToMap(map) {
        if (map === undefined) {
            return new Map();
        }
        else if (map instanceof Map) {
            return map;
        }
        else {
            return StringMap.objectToMap(map);
        }
    }
    static toStringMap(map) {
        if (!map) {
            return map;
        }
        if (map instanceof Map) {
            return map;
        }
        return StringMap.objectToMap(map);
    }
}

class DomainUserIdMap {
    constructor() {
        this._map = new Map();
    }
    static of(source) {
        if (TypeChecker.isObject(source)) {
            source = StringMap.coerceToMap(source);
        }
        if (source instanceof DomainUserIdMap || TypeChecker.isMap(source)) {
            const result = new DomainUserIdMap();
            source.forEach((val, user) => {
                result.set(user, val);
            });
            return result;
        }
        else {
            throw new ConvergenceError("Can not convert the supplied value to a DomainUserIdMap");
        }
    }
    static fromGuidObjectMap(map) {
        const result = new DomainUserIdMap();
        objectForEach(map, (key, val) => {
            result.set(DomainUserId.fromGuid(key), val);
        });
        return result;
    }
    get(user) {
        return this._map.get(DomainUserId.of(user).toGuid());
    }
    set(user, value) {
        this._map.set(DomainUserId.of(user).toGuid(), value);
    }
    has(user) {
        return this._map.has(DomainUserId.of(user).toGuid());
    }
    delete(user) {
        this._map.delete(DomainUserId.of(user).toGuid());
    }
    keys() {
        return Array.from(this._map.keys()).map(DomainUserId.fromGuid);
    }
    entries() {
        return Array.from(this._map.entries()).map(v => [DomainUserId.fromGuid(v[0]), v[1]]);
    }
    size() {
        return this._map.size;
    }
    forEach(callback) {
        this._map.forEach((v, guid) => {
            callback(v, DomainUserId.fromGuid(guid));
        });
    }
    toGuidObjectMap() {
        const result = {};
        this._map.forEach((v, guid) => {
            result[guid] = v;
        });
        return result;
    }
}

function getOrDefaultNumber(val) {
    if (val === null || val === undefined) {
        return 0;
    }
    else if (typeof val === "number") {
        return val;
    }
    else {
        return val.toNumber();
    }
}
function getOrDefaultBoolean(val) {
    return val || false;
}
function getOrDefaultString(val) {
    return val || "";
}
function getOrDefaultArray(val) {
    return val || [];
}
function getOrDefaultObject(val) {
    return val || {};
}
function toOptional(value) {
    if (value === undefined) {
        return null;
    }
    else {
        return { value };
    }
}
function fromOptional(optional) {
    if (optional) {
        return optional.value;
    }
    else {
        return null;
    }
}
function timestampToDate(timestamp) {
    const seconds = getOrDefaultNumber(timestamp.seconds);
    const nanos = getOrDefaultNumber(timestamp.nanos);
    const nanosAsMillis = Math.round(nanos / 1000000);
    const millis = (seconds * 1000) + nanosAsMillis;
    return new Date(millis);
}
function dateToTimestamp(date) {
    const millis = date.getTime();
    const seconds = Math.floor(millis / 1000);
    const nanos = (millis - (seconds * 1000)) * 1000000;
    return { seconds, nanos };
}
function protoValueToJson(value) {
    if (typeof value.nullValue !== "undefined") {
        return null;
    }
    else if (typeof value.boolValue !== "undefined") {
        return getOrDefaultBoolean(value.boolValue);
    }
    else if (typeof value.stringValue !== "undefined") {
        return getOrDefaultString(value.stringValue);
    }
    else if (typeof value.numberValue !== "undefined") {
        return getOrDefaultNumber(value.numberValue);
    }
    else if (typeof value.structValue !== "undefined") {
        return mapObjectValues(getOrDefaultObject(value.structValue.fields), protoValueToJson);
    }
    else if (typeof value.listValue !== "undefined") {
        return getOrDefaultArray(value.listValue.values).map(protoValueToJson);
    }
    else {
        throw new ConvergenceError("Could not deserialize json value: " + JSON.stringify(value));
    }
}
function jsonToProtoValue(value) {
    if (value === null) {
        return { nullValue: 0 };
    }
    else if (typeof value === "boolean") {
        return { boolValue: value };
    }
    else if (typeof value === "string") {
        return { stringValue: value };
    }
    else if (typeof value === "number") {
        return { numberValue: value };
    }
    else if (Array.isArray(value)) {
        return { listValue: { values: value.map(jsonToProtoValue) } };
    }
    else if (value !== undefined && value.constructor === Object) {
        return { structValue: { fields: mapObjectValues(value, jsonToProtoValue) } };
    }
    else if (value === undefined) {
        return undefined;
    }
    else {
        throw new ConvergenceError("Can not serialize unknown data type: " + value);
    }
}
function protoToDomainUserId(data) {
    return new DomainUserId(protoToDomainUserType(data.userType), getOrDefaultString(data.username));
}
function domainUserIdToProto(domainUserId) {
    return { userType: domainUserTypeToProto(domainUserId.userType), username: domainUserId.username };
}
function protoToDomainUserType(data) {
    switch (data) {
        case 0:
        case undefined:
            return DomainUserType.NORMAL;
        case 1:
            return DomainUserType.CONVERGENCE;
        case 2:
            return DomainUserType.ANONYMOUS;
    }
}
function domainUserTypeToProto(type) {
    switch (type) {
        case DomainUserType.NORMAL:
            return 0;
        case DomainUserType.CONVERGENCE:
            return 1;
        case DomainUserType.ANONYMOUS:
            return 2;
    }
}

class ProtocolConnection extends ConvergenceEventEmitter {
    constructor(socket, protocolConfig) {
        super();
        this._nextRequestId = 0;
        this._closeRequested = false;
        this._connected = false;
        this._logger = Logging.logger("protocol");
        this._protocolConfig = protocolConfig;
        this._socket = socket;
        this._socket.on(ConvergenceSocket.Events.MESSAGE, (event) => {
            this._onSocketMessage(event.message);
        });
        this._socket.on(ConvergenceSocket.Events.ERROR, (event) => {
            this._onSocketError(event.error);
        });
        this._socket.on(ConvergenceSocket.Events.CLOSE, (event) => {
            if (this._connected) {
                if (this._closeRequested) {
                    this._onSocketClosed(event.reason);
                }
                else {
                    this._onSocketDropped();
                }
            }
        });
        this._requests = new Map();
    }
    connect() {
        return this._socket.open().then(() => {
            this._connected = true;
            const heartbeatHandler = {
                sendPing: () => {
                    this.sendMessage({ ping: {} });
                },
                onTimeout: () => {
                    this.abort("heartbeat pong timeout")
                        .then(() => {
                        this._logger.debug("Connection aborted after pong timeout");
                    })
                        .catch(err => {
                        this._logger.error("Error aborting connection after a pong timeout", err);
                    });
                }
            };
            this._heartbeatHelper = new HeartbeatHelper(heartbeatHandler, this._protocolConfig.heartbeatConfig.pingInterval, this._protocolConfig.heartbeatConfig.pongTimeout);
            if (this._protocolConfig.heartbeatConfig.enabled) {
                this._heartbeatHelper.start();
            }
        });
    }
    send(message) {
        this.sendMessage(message);
    }
    request(message, timeout) {
        const reqId = this._nextRequestId;
        this._nextRequestId++;
        const replyDeferred = new Deferred();
        const requestTimeout = (timeout !== undefined ? timeout : this._protocolConfig.defaultRequestTimeout) * 1000;
        const timeoutTask = setTimeout(() => {
            const req = this._requests.get(reqId);
            if (req) {
                req.replyDeferred.reject(new ConvergenceError("A request timeout occurred.", ConvergenceErrorCodes.REQUEST_TIMEOUT, { message }));
            }
        }, requestTimeout);
        this._requests.set(reqId, { reqId, replyDeferred, timeoutTask });
        this.sendMessage(Object.assign(Object.assign({}, message), { requestId: { value: reqId } }));
        return replyDeferred.promise();
    }
    abort(reason) {
        if (this._heartbeatHelper && this._heartbeatHelper.started) {
            this._heartbeatHelper.stop();
        }
        const result = this._socket.isOpen() || this._socket.isConnecting() ?
            this._socket.terminate(reason) : Promise.resolve();
        this._onSocketDropped();
        return result;
    }
    close() {
        this._closeRequested = true;
        this.removeAllListeners();
        if (this._heartbeatHelper !== undefined && this._heartbeatHelper.started) {
            this._heartbeatHelper.stop();
        }
        this._requests.forEach(task => {
            clearTimeout(task.timeoutTask);
            task.replyDeferred.reject(new Error("The connection was closed while waiting for a reply to a request message."));
        });
        return this._socket.close();
    }
    sendMessage(message) {
        if (message.ping || message.pong) {
            this._logger.trace(() => "SND: " + JSON.stringify(message));
        }
        else {
            this._logger.debug(() => "SND: " + JSON.stringify(message));
        }
        try {
            const bytes = ConvergenceMessageIO.encode(message);
            this._socket.send(bytes);
        }
        catch (e) {
            this._onSocketError(e);
            throw e;
        }
    }
    _onSocketMessage(data) {
        const convergenceMessage = ConvergenceMessageIO.decode(data);
        if (this._protocolConfig.heartbeatConfig.enabled && this._heartbeatHelper) {
            this._heartbeatHelper.messageReceived();
        }
        if (convergenceMessage.ping || convergenceMessage.pong) {
            this._logger.trace(() => "RCV: " + JSON.stringify(convergenceMessage));
        }
        else {
            this._logger.debug(() => "RCV: " + JSON.stringify(convergenceMessage));
        }
        if (convergenceMessage.ping) {
            this._onPing();
        }
        else if (convergenceMessage.pong) ;
        else if (convergenceMessage.requestId !== undefined) {
            this._onRequest(convergenceMessage);
        }
        else if (convergenceMessage.responseId !== undefined) {
            this._onReply(convergenceMessage);
        }
        else {
            this._onNormalMessage(convergenceMessage);
        }
    }
    _onSocketClosed(reason) {
        this._connected = false;
        if (this._heartbeatHelper && this._heartbeatHelper.started) {
            this._heartbeatHelper.stop();
        }
        const event = { name: ProtocolConnection.Events.CLOSED, reason };
        this._emitEvent(event);
    }
    _onSocketDropped() {
        this._connected = false;
        if (this._heartbeatHelper && this._heartbeatHelper.started) {
            this._heartbeatHelper.stop();
        }
        this._emitEvent({ name: ProtocolConnection.Events.DROPPED });
    }
    _onSocketError(error) {
        const event = { name: ProtocolConnection.Events.ERROR, error };
        this._emitEvent(event);
    }
    _onNormalMessage(message) {
        Promise.resolve().then(() => {
            const event = {
                name: "message",
                request: false,
                message
            };
            this._emitEvent(event);
        });
    }
    _onRequest(message) {
        const requestId = message.requestId.value || 0;
        const event = {
            name: "message",
            request: true,
            callback: new ReplyCallbackImpl(requestId, this),
            message
        };
        this._emitEvent(event);
    }
    _onReply(message) {
        const requestId = message.responseId.value || 0;
        const record = this._requests.get(requestId);
        this._requests.delete(requestId);
        if (record) {
            clearTimeout(record.timeoutTask);
            if (message.error) {
                const errorMessage = message.error;
                const details = mapObjectValues(getOrDefaultObject(errorMessage.details), protoValueToJson);
                record.replyDeferred.reject(new ConvergenceServerError(errorMessage.message, errorMessage.code, details));
            }
            else {
                record.replyDeferred.resolve(message);
            }
        }
    }
    _onPing() {
        this.sendMessage({ pong: {} });
    }
}
ProtocolConnection.Events = {
    MESSAGE: "message",
    ERROR: "error",
    CLOSED: "close",
    DROPPED: "dropped"
};
class ReplyCallbackImpl {
    constructor(reqId, protocolConnection) {
        this._reqId = reqId;
        this._protocolConnection = protocolConnection;
    }
    reply(message) {
        this._protocolConnection.sendMessage(Object.assign(Object.assign({}, message), { responseId: { value: this._reqId } }));
    }
    unknownError() {
        this.unexpectedError("An unknown error has occurred");
    }
    unexpectedError(message) {
        this.expectedError("unknown", message);
    }
    expectedError(code, message, details) {
        details = details || {};
        const error = {
            code,
            message,
            details
        };
        this._protocolConnection.sendMessage({ error, responseId: { value: this._reqId } });
    }
}

class ConvergenceSession {
    constructor(domain, connection, user, sessionId, reconnectToken) {
        this._domain = domain;
        this._sessionId = sessionId;
        this._user = user;
        this._reconnectToken = reconnectToken;
        this._connection = connection;
    }
    domain() {
        return this._domain;
    }
    sessionId() {
        return this._sessionId;
    }
    user() {
        return this._user;
    }
    reconnectToken() {
        return this._reconnectToken;
    }
    isConnected() {
        return this._connection.isConnected();
    }
    assertOnline() {
        if (!this.isConnected()) {
            const message = `Cannot perform this action while offline`;
            throw new ConvergenceError(message, ConvergenceErrorCodes.OFFLINE);
        }
    }
    _setSessionId(sessionId) {
        this._sessionId = sessionId;
    }
    _setUser(user) {
        this._user = user;
    }
    _setReconnectToken(reconnectToken) {
        this._reconnectToken = reconnectToken;
    }
}

class FallbackAuthCoordinator {
    constructor() {
        this._deferred = new Deferred();
        this._canceled = false;
        this._password = null;
        this._jwt = null;
        this._displayName = null;
    }
    challenge() {
        return {
            password: (password) => {
                this._handlePassword(password);
            },
            jwt: (jwt) => {
                this._handleJwt(jwt);
            },
            anonymous: (displayName) => {
                this._handleAnonymous(displayName);
            },
            cancel: () => {
                this._cancel();
            }
        };
    }
    fulfilled() {
        return this._deferred.promise();
    }
    isCompleted() {
        return this._completed;
    }
    isPassword() {
        return this._password !== null;
    }
    isJwt() {
        return this._jwt !== null;
    }
    isAnonymous() {
        return this._displayName !== null;
    }
    isCanceled() {
        return this._canceled;
    }
    getPassword() {
        return this._password;
    }
    getJwt() {
        return this._jwt;
    }
    getDisplayName() {
        return this._displayName;
    }
    _handlePassword(password) {
        this._completed = true;
        this._resolveValue(password).then(p => {
            this._password = p;
            this._deferred.resolve();
        });
    }
    _handleJwt(jwt) {
        this._completed = true;
        this._resolveValue(jwt).then(j => {
            this._jwt = j;
            this._deferred.resolve();
        });
    }
    _handleAnonymous(displayName) {
        this._completed = true;
        this._resolveValue(displayName).then(d => {
            this._displayName = d;
            this._deferred.resolve();
        });
    }
    _cancel() {
        this._completed = true;
        this._canceled = true;
        this._deferred.resolve();
    }
    _resolveValue(val) {
        try {
            if (TypeChecker.isFunction(val)) {
                return Promise.resolve(val());
            }
            else if (TypeChecker.isFunction(val["then"])) {
                return val;
            }
            else {
                return Promise.resolve(val);
            }
        }
        catch (e) {
            return Promise.reject(e);
        }
    }
}

var AuthenticationMethods;
(function (AuthenticationMethods) {
    AuthenticationMethods["ANONYMOUS"] = "anonymous";
    AuthenticationMethods["PASSWORD"] = "password";
    AuthenticationMethods["JWT"] = "jwt";
    AuthenticationMethods["RECONNECT"] = "reconnect";
})(AuthenticationMethods || (AuthenticationMethods = {}));

class RandomStringGenerator {
    constructor(length, symbols) {
        if (length < 1) {
            throw new Error();
        }
        if (symbols.length < 2) {
            throw new Error();
        }
        this._symbols = symbols;
        this._length = length;
    }
    nextString() {
        let result = "";
        for (let i = 0; i < this._length; i++) {
            result += this._symbols.charAt(Math.floor(Math.random() * this._length));
        }
        return result;
    }
}
RandomStringGenerator.UpperCaseLetters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
RandomStringGenerator.LowerCaseLetters = RandomStringGenerator.UpperCaseLetters.toLowerCase();
RandomStringGenerator.Digits = "0123456789";
RandomStringGenerator.AlphaNumeric = RandomStringGenerator.UpperCaseLetters + RandomStringGenerator.LowerCaseLetters + RandomStringGenerator.Digits;
RandomStringGenerator.Base64 = RandomStringGenerator.AlphaNumeric + "/" + "+";

class ConvergenceConnection extends ConvergenceEventEmitter {
    constructor(url, domain, options) {
        super();
        this._logger = Logging.logger("connection");
        this._onWindowOnline = (_) => {
            this._logger.debug(() => `Browser connectivity changed, restarting connection schedule.`);
            if (this._connectionState === ConnectionState.CONNECTING) {
                this._connectionAttempts = 0;
                this._attemptConnection();
            }
        };
        this._scheduleConnectionTimeout = () => {
            this._clearConnectionTimeout();
            const timeout = this._options.connectionTimeout * 1000;
            this._connectionTimeoutTask = setTimeout(this._onConnectionTimeout, timeout);
        };
        this._clearConnectionTimeout = () => {
            if (this._connectionTimeoutTask !== null) {
                clearTimeout(this._connectionTimeoutTask);
                this._connectionTimeoutTask = null;
            }
        };
        this._onConnectionTimeout = () => {
            this._protocolConnection
                .abort("connection timeout exceeded")
                .catch(e => this._logger.error("error aborting connection after a connection timeout", e));
        };
        this._url = url.trim().toLowerCase();
        const urlExpression = /^(https?|wss?):\/{2}.+\/.+\/.+/g;
        if (!urlExpression.test(this._url)) {
            throw new Error(`Invalid domain connection url: ${this._url}`);
        }
        this._options = options;
        this._url = url;
        this._connectionAttempts = 0;
        this._connectionState = ConnectionState.DISCONNECTED;
        this._connectionTimeoutTask = null;
        this._connectionAttemptTask = null;
        this._initialConnection = true;
        this._protocolConnection = null;
        this._protocolConnectionSubscription = null;
        this._connectionRequestGenerator = null;
        this._authMethod = null;
        const initialSessionId = "offline:" + ConvergenceConnection._SessionIdGenerator.nextString();
        this._session = new ConvergenceSession(domain, this, null, initialSessionId, null);
        if (typeof window !== "undefined") {
            window.addEventListener("online", this._onWindowOnline);
        }
    }
    url() {
        return this._url;
    }
    session() {
        return this._session;
    }
    disconnect() {
        this._connectionRequestGenerator = null;
        this._authMethod = null;
        if (this._connectionTimeoutTask !== null) {
            clearTimeout(this._connectionTimeoutTask);
            this._connectionTimeoutTask = null;
        }
        if (this._connectionAttemptTask !== null) {
            clearTimeout(this._connectionAttemptTask);
            this._connectionAttemptTask = null;
        }
        if (this._connectionDeferred !== null) {
            this._connectionDeferred.reject(new Error("Connection canceled by user"));
            this._connectionDeferred = null;
        }
        if (this._connectionState === ConnectionState.DISCONNECTED) {
            return Promise.reject(new Error("Connection is already disconnected."));
        }
        else {
            this._connectionState = ConnectionState.DISCONNECTING;
            this._initialConnection = true;
            if (typeof window !== "undefined") {
                window.removeEventListener("online", this._onWindowOnline);
            }
            return this._protocolConnection.close().then(() => {
                this._protocolConnectionSubscription.unsubscribe();
                this._protocolConnection = null;
                this._protocolConnectionSubscription = null;
                this._handleDisconnected();
            });
        }
    }
    reconnect() {
        if (this._connectionState !== ConnectionState.INTERRUPTED &&
            this._connectionState !== ConnectionState.DISCONNECTED) {
            throw new Error("Can only call reconnect on an disconnected or interrupted connection.");
        }
        return this.connectWithReconnectToken(() => Promise.resolve(this._session.reconnectToken()))
            .then(() => undefined);
    }
    isConnected() {
        return this._connectionState === ConnectionState.CONNECTED;
    }
    isDisconnected() {
        return this._connectionState === ConnectionState.DISCONNECTED;
    }
    send(message) {
        this._protocolConnection.send(message);
    }
    request(message, timeout) {
        return this._protocolConnection.request(message, timeout);
    }
    connectWithPassword(credentialsCallback) {
        this._authMethod = AuthenticationMethods.PASSWORD;
        const requestGenerator = () => {
            return credentialsCallback().then(credentials => {
                Validation.assertNonEmptyString(credentials.username, "username");
                Validation.assertNonEmptyString(credentials.password, "password");
                const password = {
                    username: credentials.username,
                    password: credentials.password
                };
                return { password };
            });
        };
        return this._connect(requestGenerator);
    }
    connectWithJwt(jwtCallback) {
        this._authMethod = AuthenticationMethods.JWT;
        const requestGenerator = () => {
            return jwtCallback().then(token => {
                Validation.assertNonEmptyString(token, "jwt");
                const jwt = { jwt: token };
                return { jwt };
            });
        };
        return this._connect(requestGenerator);
    }
    connectWithReconnectToken(tokenCallback) {
        this._authMethod = AuthenticationMethods.RECONNECT;
        const requestGenerator = () => {
            return tokenCallback().then(token => {
                Validation.assertNonEmptyString(token, "token");
                const message = { token };
                return { reconnect: message };
            });
        };
        return this._connect(requestGenerator)
            .catch((e) => this._handleReconnectFailure(e));
    }
    _handleReconnectFailure(e) {
        if (e instanceof ConvergenceError && e.code === ConvergenceErrorCodes.AUTHENTICATION_FAILED) {
            if (TypeChecker.isFunction(this._options.fallbackAuth)) {
                const authCoordinator = new FallbackAuthCoordinator();
                this._options.fallbackAuth(authCoordinator.challenge());
                if (!authCoordinator.isCompleted()) {
                    return Promise.reject(new Error("You must call one of the auth challenge methods."));
                }
                authCoordinator.fulfilled().then(() => {
                    if (authCoordinator.isPassword()) {
                        const username = this.session().user().username;
                        const password = authCoordinator.getPassword();
                        return this.connectWithPassword(() => Promise.resolve({ username, password }));
                    }
                    else if (authCoordinator.isJwt()) {
                        return this.connectWithJwt(() => Promise.resolve(authCoordinator.getJwt()));
                    }
                    else if (authCoordinator.isAnonymous()) {
                        return this.connectAnonymously(() => Promise.resolve(authCoordinator.getDisplayName()));
                    }
                    else if (authCoordinator.isCanceled()) {
                        return Promise.reject(e);
                    }
                    else {
                        return Promise.reject(e);
                    }
                });
            }
            else {
                return Promise.resolve(e);
            }
        }
        else {
            return Promise.resolve(e);
        }
    }
    connectAnonymously(displayNameCallback) {
        this._authMethod = AuthenticationMethods.ANONYMOUS;
        const requestGenerator = () => {
            return displayNameCallback().then(displayName => {
                const message = {
                    displayName: toOptional(displayName)
                };
                return { anonymous: message };
            });
        };
        return this._connect(requestGenerator);
    }
    messages() {
        return this
            .events()
            .pipe(filter(e => e.name === "message"));
    }
    _connect(gen) {
        this._connectionRequestGenerator = gen;
        if (this._connectionState === ConnectionState.CONNECTED) {
            return Promise.reject(new ConvergenceError("already connected"));
        }
        else if (this._connectionState === ConnectionState.CONNECTING) {
            return Promise.reject(new ConvergenceError("already connecting"));
        }
        else {
            return this._connectToServer();
        }
    }
    _connectToServer() {
        if (this._connectionState !== ConnectionState.DISCONNECTED &&
            this._connectionState !== ConnectionState.INTERRUPTED) {
            throw new Error("Can only call connect on a disconnected or interrupted connection.");
        }
        this._connectionAttempts = 0;
        this._connectionDeferred = new Deferred();
        this._connectionState = ConnectionState.CONNECTING;
        this._attemptConnection();
        return this._connectionDeferred.promise();
    }
    _attemptConnection() {
        if (this._connectionAttemptTask !== null) {
            clearTimeout(this._connectionAttemptTask);
            this._connectionAttemptTask = null;
        }
        this._connectionAttempts++;
        this._logger.debug(() => `Attempting to open web socket connection to: ${this._url}`);
        this._scheduleConnectionTimeout();
        this._emitEvent({ name: ConvergenceConnection.Events.CONNECTING });
        this._protocolConnection = this._createProtocolConnection();
        this._openConnection();
    }
    _openConnection() {
        this._protocolConnection
            .connect()
            .then(() => {
            this._clearConnectionTimeout();
            this._logger.debug("Server connection established.");
            this._initialConnection = false;
            return this._sendConnectionRequest();
        })
            .catch((e) => {
            this._logger.debug(e.message);
            this._clearConnectionTimeout();
            const failedEvent = {
                name: ConvergenceConnection.Events.CONNECTION_FAILED,
                authMethod: this._authMethod,
                code: "server_connection_failed",
                details: e.message
            };
            this._emitEvent(failedEvent);
            if (!this._initialConnection || this._options.autoReconnectOnInitial) {
                this._scheduleConnection();
            }
            else {
                const message = `The initial connection to '${this._url}' failed, and 'reconnect.autoReconnectOnInitial' was set to false`;
                this._connectionDeferred.reject(new ConvergenceError(message, "initial_connection_failed"));
                this._handleDisconnected();
            }
        });
    }
    _createProtocolConnection() {
        const socket = new ConvergenceSocket(this._url, this._options.webSocketClass, this._options.webSocketFactory);
        const protocolConnection = new ProtocolConnection(socket, {
            defaultRequestTimeout: this._options.defaultRequestTimeout,
            heartbeatConfig: {
                enabled: this._options.heartbeatEnabled,
                pingInterval: this._options.pingInterval,
                pongTimeout: this._options.pongTimeout
            }
        });
        this._protocolConnectionSubscription = protocolConnection
            .events()
            .subscribe(e => {
            switch (e.name) {
                case ProtocolConnection.Events.ERROR: {
                    const errorEvent = e;
                    const event = { name: ConvergenceConnection.Events.ERROR, error: errorEvent.error };
                    this._emitEvent(event);
                    break;
                }
                case ProtocolConnection.Events.DROPPED: {
                    this._handleInterrupted();
                    break;
                }
                case ProtocolConnection.Events.CLOSED: {
                    this._handleDisconnected();
                    break;
                }
                case ProtocolConnection.Events.MESSAGE: {
                    const messageEvent = e;
                    const event = {
                        name: ConvergenceConnection.Events.MESSAGE,
                        request: messageEvent.request,
                        callback: messageEvent.callback,
                        message: messageEvent.message
                    };
                    this._emitEvent(event);
                    break;
                }
            }
        });
        return protocolConnection;
    }
    _sendConnectionRequest() {
        return __awaiter(this, void 0, void 0, function* () {
            const partialRequest = yield this._connectionRequestGenerator();
            const connectionRequest = ConvergenceConnection._createConnectionRequest(partialRequest);
            const authenticatingEvent = {
                name: ConvergenceConnection.Events.AUTHENTICATING,
                method: this._authMethod
            };
            this._emitEvent(authenticatingEvent);
            const requestTimeout = this._options.connectionRequestTimeout;
            const response = yield this.request({ connectionRequest }, requestTimeout);
            const connectionResponse = response.connectionResponse;
            if (connectionResponse.success) {
                return this._handleConnectionSuccess(connectionResponse.success);
            }
            else {
                return this._handleConnectionFailure(connectionResponse.failure, this._authMethod);
            }
        });
    }
    _handleConnectionSuccess(connectionSuccessData) {
        this._domainId = connectionSuccessData.domainId;
        this._namespace = connectionSuccessData.namespace;
        this._session._setUser(toDomainUser(connectionSuccessData.user));
        this._session._setSessionId(connectionSuccessData.sessionId);
        this._session._setReconnectToken(connectionSuccessData.reconnectToken);
        this._connectionState = ConnectionState.CONNECTED;
        const connectedEvent = {
            name: ConvergenceConnection.Events.CONNECTED,
            state: getOrDefaultObject(connectionSuccessData.presenceState)
        };
        this._emitEvent(connectedEvent);
        this._connectionDeferred.resolve();
        this._connectionDeferred = null;
        this._connectionAttempts = 0;
    }
    _handleConnectionFailure(failureData, method) {
        this._clearConnectionTimeout();
        const { code, details, retryOk } = failureData;
        const connectionFailedEvent = {
            name: ConvergenceConnection.Events.CONNECTION_FAILED,
            authMethod: method,
            code: getOrDefaultString(code),
            details: getOrDefaultString(details)
        };
        this._emitEvent(connectionFailedEvent);
        this._protocolConnectionSubscription.unsubscribe();
        this._protocolConnection.close()
            .catch(e => this._logger.error("Error closing protocol connection after connection failure", e));
        this._protocolConnection = null;
        this._protocolConnectionSubscription = null;
        if (!getOrDefaultBoolean(retryOk)) {
            this._connectionDeferred.reject(new ConvergenceError(getOrDefaultString(details), getOrDefaultString(code)));
            this._handleDisconnected();
        }
        else if (!this._initialConnection || this._options.autoReconnectOnInitial) {
            this._scheduleConnection();
        }
        else {
            this._connectionRequestGenerator = null;
            this._authMethod = null;
            const message = `The initial connection to '${this._url}' failed, and 'reconnect.autoReconnectOnInitial' was set to false.`;
            this._connectionDeferred.reject(new ConvergenceError(message, getOrDefaultString(code)));
            this._handleDisconnected();
        }
    }
    static _createConnectionRequest(partial) {
        return Object.assign(Object.assign({}, partial), { client: "JavaScript", clientVersion: "1.0.0-rc.12" });
    }
    _scheduleConnection() {
        if (this._connectionState === ConnectionState.CONNECTING) {
            const idx = Math.min(this._connectionAttempts, this._options.reconnectIntervals.length - 1);
            const delay = this._options.reconnectIntervals[idx];
            this._logger.debug(() => `Scheduling web socket connection in ${delay} seconds.`);
            const event = { name: ConvergenceConnection.Events.CONNECTION_SCHEDULED, delay };
            this._emitEvent(event);
            this._connectionAttemptTask = setTimeout(() => this._attemptConnection(), delay * 1000);
        }
    }
    _handleDisconnected() {
        if (this._connectionState !== ConnectionState.DISCONNECTED) {
            this._connectionState = ConnectionState.DISCONNECTED;
            this._emitEvent({ name: ConvergenceConnection.Events.DISCONNECTED });
        }
    }
    _handleInterrupted() {
        this._connectionState = ConnectionState.INTERRUPTED;
        this._emitEvent({ name: ConvergenceConnection.Events.INTERRUPTED });
        if (this._options.autoReconnect && this._session.reconnectToken()) {
            this.reconnect()
                .catch(e => this._logger.error("Unexpected error reconnecting", e));
        }
        else {
            this._handleDisconnected();
        }
    }
}
ConvergenceConnection.Events = {
    MESSAGE: "message",
    CONNECTION_SCHEDULED: "connection_scheduled",
    CONNECTING: "connecting",
    AUTHENTICATING: "authenticating",
    CONNECTED: "connected",
    CONNECTION_FAILED: "connection_failed",
    INTERRUPTED: "interrupted",
    DISCONNECTED: "disconnected",
    ERROR: "error"
};
ConvergenceConnection._SessionIdGenerator = new RandomStringGenerator(32, RandomStringGenerator.AlphaNumeric);
var ConnectionState;
(function (ConnectionState) {
    ConnectionState[ConnectionState["DISCONNECTED"] = 0] = "DISCONNECTED";
    ConnectionState[ConnectionState["CONNECTING"] = 1] = "CONNECTING";
    ConnectionState[ConnectionState["CONNECTED"] = 2] = "CONNECTED";
    ConnectionState[ConnectionState["INTERRUPTED"] = 3] = "INTERRUPTED";
    ConnectionState[ConnectionState["DISCONNECTING"] = 4] = "DISCONNECTING";
})(ConnectionState || (ConnectionState = {}));

class Operation {
    constructor(type) {
        this.type = type;
    }
}

const types = {
    COMPOUND: "Compound",
    ARRAY_INSERT: "ArrayInsert",
    ARRAY_REORDER: "ArrayReorder",
    ARRAY_REMOVE: "ArrayRemove",
    ARRAY_SET: "ArraySet",
    ARRAY_VALUE: "ArrayValue",
    BOOLEAN_VALUE: "BooleanValue",
    NUMBER_DELTA: "NumberDelta",
    NUMBER_VALUE: "NumberValue",
    OBJECT_ADD: "ObjectAdd",
    OBJECT_REMOVE: "ObjectRemove",
    OBJECT_SET: "ObjectSet",
    OBJECT_VALUE: "ObjectValue",
    STRING_SPLICE: "StringSplice",
    STRING_VALUE: "StringValue",
    DATE_VALUE: "DateValue"
};
Object.freeze(types);
const OperationType = types;

class CompoundOperation extends Operation {
    constructor(ops) {
        super(OperationType.COMPOUND);
        this.ops = ops;
        Object.freeze(this);
    }
    copy(updates) {
        return new CompoundOperation(Immutable.update(this.ops, updates.ops));
    }
}

class OperationPair {
    constructor(serverOp, clientOp) {
        this.serverOp = serverOp;
        this.clientOp = clientOp;
        Object.freeze(this);
    }
}

class OperationTransformer {
    constructor(tfr) {
        this._tfr = tfr;
    }
    transform(s, c) {
        if (s instanceof CompoundOperation) {
            return this.transformServerCompoundOperation(s, c);
        }
        else if (c instanceof CompoundOperation) {
            return this.transformClientCompoundOperation(s, c);
        }
        else {
            return this.transformTwoDiscreteOps(s, c);
        }
    }
    transformClientCompoundOperation(s, c) {
        let xFormedS = s;
        const newOps = c.ops.map((o) => {
            const opPair = this.transform(xFormedS, o);
            xFormedS = opPair.serverOp;
            return opPair.clientOp;
        });
        return new OperationPair(xFormedS, new CompoundOperation(newOps));
    }
    transformServerCompoundOperation(s, c) {
        let xFormedC = c;
        const newOps = s.ops.map((o) => {
            const opPair = this.transform(o, xFormedC);
            xFormedC = opPair.clientOp;
            return opPair.serverOp;
        });
        return new OperationPair(new CompoundOperation(newOps), xFormedC);
    }
    transformTwoDiscreteOps(s, c) {
        if (s.noOp || c.noOp) {
            return new OperationPair(s, c);
        }
        else if (s.id === c.id) {
            return this.transformIdenticalPathOperations(s, c);
        }
        else {
            return new OperationPair(s, c);
        }
    }
    transformIdenticalPathOperations(s, c) {
        const tf = this._tfr.getOperationTransformationFunction(s, c);
        if (tf) {
            return tf(s, c);
        }
        else {
            throw new Error(`No function found for: (${s.type},${s.type})`);
        }
    }
}

const ArrayInsertInsertOTF = (s, c) => {
    if (s.index <= c.index) {
        return new OperationPair(s, c.copy({ index: c.index + 1 }));
    }
    else {
        return new OperationPair(s.copy({ index: s.index + 1 }), c);
    }
};

const ArrayInsertRemoveOTF = (s, c) => {
    if (s.index <= c.index) {
        return new OperationPair(s, c.copy({ index: c.index + 1 }));
    }
    else {
        return new OperationPair(s.copy({ index: s.index - 1 }), c);
    }
};

const ArrayInsertReplaceOTF = (s, c) => {
    if (s.index <= c.index) {
        return new OperationPair(s, c.copy({ index: c.index + 1 }));
    }
    else {
        return new OperationPair(s, c);
    }
};

class RangeRelationshipUtil {
    static getRangeIndexRelationship(rStart, rEnd, index) {
        if (index < rStart) {
            return RangeIndexRelationship.Before;
        }
        else if (index > rEnd) {
            return RangeIndexRelationship.After;
        }
        else if (index === rStart) {
            return RangeIndexRelationship.Start;
        }
        else if (index === rEnd) {
            return RangeIndexRelationship.End;
        }
        else {
            return RangeIndexRelationship.Within;
        }
    }
    static getRangeRangeRelationship(sStart, sEnd, cStart, cEnd) {
        if (sStart === cStart) {
            if (sEnd === cEnd) {
                return RangeRangeRelationship.EqualTo;
            }
            else if (cEnd > sEnd) {
                return RangeRangeRelationship.Starts;
            }
            else {
                return RangeRangeRelationship.StartedBy;
            }
        }
        else if (sStart > cStart) {
            if (sStart > cEnd) {
                return RangeRangeRelationship.PrecededBy;
            }
            else if (cEnd === sEnd) {
                return RangeRangeRelationship.Finishes;
            }
            else if (sEnd < cEnd) {
                return RangeRangeRelationship.ContainedBy;
            }
            else if (sStart === cEnd) {
                return RangeRangeRelationship.MetBy;
            }
            else {
                return RangeRangeRelationship.OverlappedBy;
            }
        }
        else {
            if (sEnd < cStart) {
                return RangeRangeRelationship.Precedes;
            }
            else if (cEnd === sEnd) {
                return RangeRangeRelationship.FinishedBy;
            }
            else if (sEnd > cEnd) {
                return RangeRangeRelationship.Contains;
            }
            else if (sEnd === cStart) {
                return RangeRangeRelationship.Meets;
            }
            else {
                return RangeRangeRelationship.Overlaps;
            }
        }
    }
}
var RangeIndexRelationship;
(function (RangeIndexRelationship) {
    RangeIndexRelationship[RangeIndexRelationship["Before"] = 0] = "Before";
    RangeIndexRelationship[RangeIndexRelationship["Start"] = 1] = "Start";
    RangeIndexRelationship[RangeIndexRelationship["Within"] = 2] = "Within";
    RangeIndexRelationship[RangeIndexRelationship["End"] = 3] = "End";
    RangeIndexRelationship[RangeIndexRelationship["After"] = 4] = "After";
})(RangeIndexRelationship || (RangeIndexRelationship = {}));
var RangeRangeRelationship;
(function (RangeRangeRelationship) {
    RangeRangeRelationship[RangeRangeRelationship["Precedes"] = 0] = "Precedes";
    RangeRangeRelationship[RangeRangeRelationship["PrecededBy"] = 1] = "PrecededBy";
    RangeRangeRelationship[RangeRangeRelationship["Meets"] = 2] = "Meets";
    RangeRangeRelationship[RangeRangeRelationship["MetBy"] = 3] = "MetBy";
    RangeRangeRelationship[RangeRangeRelationship["Overlaps"] = 4] = "Overlaps";
    RangeRangeRelationship[RangeRangeRelationship["OverlappedBy"] = 5] = "OverlappedBy";
    RangeRangeRelationship[RangeRangeRelationship["Starts"] = 6] = "Starts";
    RangeRangeRelationship[RangeRangeRelationship["StartedBy"] = 7] = "StartedBy";
    RangeRangeRelationship[RangeRangeRelationship["Contains"] = 8] = "Contains";
    RangeRangeRelationship[RangeRangeRelationship["ContainedBy"] = 9] = "ContainedBy";
    RangeRangeRelationship[RangeRangeRelationship["Finishes"] = 10] = "Finishes";
    RangeRangeRelationship[RangeRangeRelationship["FinishedBy"] = 11] = "FinishedBy";
    RangeRangeRelationship[RangeRangeRelationship["EqualTo"] = 12] = "EqualTo";
})(RangeRangeRelationship || (RangeRangeRelationship = {}));

class ArrayMoveHelper {
    static isForwardMove(op) {
        return op.fromIndex < op.toIndex;
    }
    static isBackwardMoveMove(op) {
        return op.fromIndex > op.toIndex;
    }
    static isIdentityMove(op) {
        return op.fromIndex === op.toIndex;
    }
    static getMoveDirection(op) {
        if (this.isForwardMove(op)) {
            return MoveDirection.Forward;
        }
        else if (this.isBackwardMoveMove(op)) {
            return MoveDirection.Backward;
        }
        else {
            return MoveDirection.Identity;
        }
    }
    static indexBeforeRange(op, index) {
        return index < this.getRangeMin(op);
    }
    static indexAfterRange(op, index) {
        return index > this.getRangeMax(op);
    }
    static indexWithinRange(op, index) {
        return index > this.getRangeMin(op) && index < this.getRangeMax(op);
    }
    static getRangeIndexRelationship(op, index) {
        return RangeRelationshipUtil.getRangeIndexRelationship(this.getRangeMin(op), this.getRangeMax(op), index);
    }
    static getRangeRelationship(op1, op2) {
        return RangeRelationshipUtil.getRangeRangeRelationship(this.getRangeMin(op1), this.getRangeMax(op1), this.getRangeMin(op2), this.getRangeMax(op2));
    }
    static getRangeMin(op) {
        return Math.min(op.fromIndex, op.toIndex);
    }
    static getRangeMax(op) {
        return Math.max(op.fromIndex, op.toIndex);
    }
}
var MoveDirection;
(function (MoveDirection) {
    MoveDirection[MoveDirection["Forward"] = 0] = "Forward";
    MoveDirection[MoveDirection["Backward"] = 1] = "Backward";
    MoveDirection[MoveDirection["Identity"] = 2] = "Identity";
})(MoveDirection || (MoveDirection = {}));

const ArrayInsertMoveOTF = (s, c) => {
    switch (ArrayMoveHelper.getMoveDirection(c)) {
        case MoveDirection.Forward:
            return transformAgainstForwardMove$5(s, c);
        case MoveDirection.Backward:
            return transformAgainstBackwardMove$5(s, c);
        case MoveDirection.Identity:
            return transformAgainstIdentityMove$5(s, c);
        default:
            throw new Error("Invalid move direction");
    }
};
function transformAgainstForwardMove$5(s, c) {
    switch (ArrayMoveHelper.getRangeIndexRelationship(c, s.index)) {
        case RangeIndexRelationship.Before:
        case RangeIndexRelationship.Start:
            return new OperationPair(s, c.copy({ fromIndex: c.fromIndex + 1, toIndex: c.toIndex + 1 }));
        case RangeIndexRelationship.Within:
        case RangeIndexRelationship.End:
            return new OperationPair(s.copy({ index: s.index - 1 }), c.copy({ toIndex: c.toIndex + 1 }));
        case RangeIndexRelationship.After:
            return new OperationPair(s, c);
        default:
            throw new Error("Invalid range-index relationship");
    }
}
function transformAgainstBackwardMove$5(s, c) {
    switch (ArrayMoveHelper.getRangeIndexRelationship(c, s.index)) {
        case RangeIndexRelationship.Before:
        case RangeIndexRelationship.Start:
            return new OperationPair(s, c.copy({ fromIndex: c.fromIndex + 1, toIndex: c.toIndex + 1 }));
        case RangeIndexRelationship.Within:
        case RangeIndexRelationship.End:
            return new OperationPair(s.copy({ index: s.index + 1 }), c.copy({ fromIndex: c.fromIndex + 1 }));
        case RangeIndexRelationship.After:
            return new OperationPair(s, c);
        default:
            throw new Error("Invalid range-index relationship");
    }
}
function transformAgainstIdentityMove$5(s, c) {
    switch (ArrayMoveHelper.getRangeIndexRelationship(c, s.index)) {
        case RangeIndexRelationship.After:
            return new OperationPair(s, c);
        default:
            return new OperationPair(s, c.copy({ fromIndex: c.fromIndex + 1, toIndex: c.toIndex + 1 }));
    }
}

const ArrayInsertSetOTF = (s, c) => {
    return new OperationPair(s.copy({ noOp: true }), c);
};

const ArrayRemoveInsertOTF = (s, c) => {
    if (s.index < c.index) {
        return new OperationPair(s, c.copy({ index: c.index - 1 }));
    }
    else {
        return new OperationPair(s.copy({ index: s.index + 1 }), c);
    }
};

const ArrayRemoveRemoveOTF = (s, c) => {
    if (s.index === c.index) {
        return new OperationPair(s.copy({ noOp: true }), c.copy({ noOp: true }));
    }
    else if (s.index < c.index) {
        return new OperationPair(s, c.copy({ index: c.index - 1 }));
    }
    else {
        return new OperationPair(s.copy({ index: s.index - 1 }), c);
    }
};

class DiscreteOperation extends Operation {
    constructor(type, id, noOp) {
        super(type);
        this.id = id;
        this.noOp = noOp;
    }
}

class ArrayInsertOperation extends DiscreteOperation {
    constructor(id, noOp, index, value) {
        super(OperationType.ARRAY_INSERT, id, noOp);
        this.index = index;
        this.value = value;
        Object.freeze(this);
    }
    copy(updates) {
        return new ArrayInsertOperation(Immutable.update(this.id, updates.id), Immutable.update(this.noOp, updates.noOp), Immutable.update(this.index, updates.index), Immutable.update(this.value, updates.value));
    }
}

const ArrayRemoveReplaceOTF = (s, c) => {
    if (s.index < c.index) {
        return new OperationPair(s, c.copy({ index: c.index - 1 }));
    }
    else if (s.index === c.index) {
        return new OperationPair(s.copy({ noOp: true }), new ArrayInsertOperation(c.id, c.noOp, c.index, c.value));
    }
    else {
        return new OperationPair(s, c);
    }
};

const ArrayRemoveMoveOTF = (s, c) => {
    switch (ArrayMoveHelper.getMoveDirection(c)) {
        case MoveDirection.Forward:
            return transformAgainstForwardMove$4(s, c);
        case MoveDirection.Backward:
            return transformAgainstBackwardMove$4(s, c);
        case MoveDirection.Identity:
            return transformAgainstIdentityMove$4(s, c);
        default:
            throw new Error("Invalid move direction");
    }
};
function transformAgainstForwardMove$4(s, c) {
    switch (ArrayMoveHelper.getRangeIndexRelationship(c, s.index)) {
        case RangeIndexRelationship.Before:
            return new OperationPair(s, c.copy({ fromIndex: c.fromIndex - 1, toIndex: c.toIndex - 1 }));
        case RangeIndexRelationship.Start:
            return new OperationPair(s.copy({ index: c.toIndex }), c.copy({ noOp: true }));
        case RangeIndexRelationship.Within:
        case RangeIndexRelationship.End:
            return new OperationPair(s.copy({ index: s.index - 1 }), c.copy({ toIndex: c.toIndex - 1 }));
        case RangeIndexRelationship.After:
            return new OperationPair(s, c);
        default:
            throw new Error("Invalid range-index relationship");
    }
}
function transformAgainstBackwardMove$4(s, c) {
    switch (ArrayMoveHelper.getRangeIndexRelationship(c, s.index)) {
        case RangeIndexRelationship.Before:
            return new OperationPair(s, c.copy({ fromIndex: c.fromIndex - 1, toIndex: c.toIndex - 1 }));
        case RangeIndexRelationship.Start:
        case RangeIndexRelationship.Within:
            return new OperationPair(s.copy({ index: s.index + 1 }), c.copy({ fromIndex: c.fromIndex - 1 }));
        case RangeIndexRelationship.End:
            return new OperationPair(s.copy({ index: c.toIndex }), c.copy({ noOp: true }));
        case RangeIndexRelationship.After:
            return new OperationPair(s, c);
        default:
            throw new Error("Invalid range-index relationship");
    }
}
function transformAgainstIdentityMove$4(s, c) {
    switch (ArrayMoveHelper.getRangeIndexRelationship(c, s.index)) {
        case RangeIndexRelationship.Before:
            return new OperationPair(s, c.copy({ fromIndex: c.fromIndex - 1, toIndex: c.toIndex - 1 }));
        case RangeIndexRelationship.Start:
        case RangeIndexRelationship.Within:
        case RangeIndexRelationship.End:
            return new OperationPair(s, c.copy({ noOp: true }));
        default:
            return new OperationPair(s, c);
    }
}

const ArrayRemoveSetOTF = (s, c) => {
    return new OperationPair(s.copy({ noOp: true }), c);
};

const ArrayReplaceInsertOTF = (s, c) => {
    if (s.index < c.index) {
        return new OperationPair(s, c);
    }
    else {
        return new OperationPair(s.copy({ index: s.index + 1 }), c);
    }
};

const ArrayReplaceRemoveOTF = (s, c) => {
    if (s.index < c.index) {
        return new OperationPair(s, c);
    }
    else if (s.index === c.index) {
        return new OperationPair(new ArrayInsertOperation(s.id, s.noOp, s.index, s.value), c.copy({ noOp: true }));
    }
    else {
        return new OperationPair(s.copy({ index: s.index - 1 }), c);
    }
};

class EqualsUtil {
    static deepEquals(x, y) {
        let p;
        if (isNaN(x) && isNaN(y) && typeof x === "number" && typeof y === "number") {
            return true;
        }
        if (x === y) {
            return true;
        }
        if ((typeof x === "function" && typeof y === "function") ||
            (x instanceof Date && y instanceof Date) ||
            (x instanceof RegExp && y instanceof RegExp) ||
            (x instanceof String && y instanceof String) ||
            (x instanceof Number && y instanceof Number)) {
            return x.toString() === y.toString();
        }
        if (!(x instanceof Object && y instanceof Object)) {
            return false;
        }
        if (x.isPrototypeOf(y) || y.isPrototypeOf(x)) {
            return false;
        }
        if (x.constructor !== y.constructor) {
            return false;
        }
        if (x.prototype !== y.prototype) {
            return false;
        }
        for (p in y) {
            if (y.hasOwnProperty(p) !== x.hasOwnProperty(p)) {
                return false;
            }
            else if (typeof y[p] !== typeof x[p]) {
                return false;
            }
        }
        for (p in x) {
            if (y.hasOwnProperty(p) !== x.hasOwnProperty(p)) {
                return false;
            }
            else if (typeof y[p] !== typeof x[p]) {
                return false;
            }
            switch (typeof (x[p])) {
                case "object":
                case "function":
                    if (!this.deepEquals(x[p], y[p])) {
                        return false;
                    }
                    break;
                default:
                    if (x[p] !== y[p]) {
                        return false;
                    }
                    break;
            }
        }
        return true;
    }
}

const ArrayReplaceReplaceOTF = (s, c) => {
    if (s.index !== c.index) {
        return new OperationPair(s, c);
    }
    else if (!EqualsUtil.deepEquals(s.value, c.value)) {
        return new OperationPair(s, c.copy({ noOp: true }));
    }
    else {
        return new OperationPair(s.copy({ noOp: true }), c.copy({ noOp: true }));
    }
};

const ArrayReplaceMoveOTF = (s, c) => {
    switch (ArrayMoveHelper.getMoveDirection(c)) {
        case MoveDirection.Forward:
            return transformAgainstForwardMove$3(s, c);
        case MoveDirection.Backward:
            return transformAgainstBackwardMove$3(s, c);
        case MoveDirection.Identity:
            return transformAgainstIdentityMove$3(s, c);
        default:
            throw new Error("Invalid move direction");
    }
};
function transformAgainstForwardMove$3(s, c) {
    switch (ArrayMoveHelper.getRangeIndexRelationship(c, s.index)) {
        case RangeIndexRelationship.Before:
        case RangeIndexRelationship.After:
            return new OperationPair(s, c);
        case RangeIndexRelationship.Start:
            return new OperationPair(s.copy({ index: c.toIndex }), c);
        case RangeIndexRelationship.Within:
        case RangeIndexRelationship.End:
            return new OperationPair(s.copy({ index: s.index - 1 }), c);
        default:
            throw new Error("Invalid range-index relationship");
    }
}
function transformAgainstBackwardMove$3(s, c) {
    switch (ArrayMoveHelper.getRangeIndexRelationship(c, s.index)) {
        case RangeIndexRelationship.Before:
        case RangeIndexRelationship.After:
            return new OperationPair(s, c);
        case RangeIndexRelationship.End:
            return new OperationPair(s.copy({ index: c.toIndex }), c);
        case RangeIndexRelationship.Within:
        case RangeIndexRelationship.Start:
            return new OperationPair(s.copy({ index: s.index + 1 }), c);
        default:
            throw new Error("Invalid range-index relationship");
    }
}
function transformAgainstIdentityMove$3(s, c) {
    return new OperationPair(s, c);
}

const ArrayReplaceSetOTF = (s, c) => {
    return new OperationPair(s.copy({ noOp: true }), c);
};

const ArrayMoveInsertOTF = (s, c) => {
    switch (ArrayMoveHelper.getMoveDirection(s)) {
        case MoveDirection.Forward:
            return transformAgainstForwardMove$2(s, c);
        case MoveDirection.Backward:
            return transformAgainstBackwardMove$2(s, c);
        case MoveDirection.Identity:
            return transformAgainstIdentityMove$2(s, c);
        default:
            throw new Error("Invalid move direction");
    }
};
function transformAgainstForwardMove$2(s, c) {
    switch (ArrayMoveHelper.getRangeIndexRelationship(s, c.index)) {
        case RangeIndexRelationship.Before:
        case RangeIndexRelationship.Start:
            return new OperationPair(s.copy({ fromIndex: s.fromIndex + 1, toIndex: s.toIndex + 1 }), c);
        case RangeIndexRelationship.Within:
        case RangeIndexRelationship.End:
            return new OperationPair(s.copy({ toIndex: s.toIndex + 1 }), c.copy({ index: c.index - 1 }));
        case RangeIndexRelationship.After:
            return new OperationPair(s, c);
        default:
            throw new Error("Invalid range-index relationship");
    }
}
function transformAgainstBackwardMove$2(s, c) {
    switch (ArrayMoveHelper.getRangeIndexRelationship(s, c.index)) {
        case RangeIndexRelationship.Before:
        case RangeIndexRelationship.Start:
            return new OperationPair(s.copy({ fromIndex: s.fromIndex + 1, toIndex: s.toIndex + 1 }), c);
        case RangeIndexRelationship.Within:
        case RangeIndexRelationship.End:
            return new OperationPair(s.copy({ fromIndex: s.fromIndex + 1 }), c.copy({ index: c.index + 1 }));
        case RangeIndexRelationship.After:
            return new OperationPair(s, c);
        default:
            throw new Error("Invalid range-index relationship");
    }
}
function transformAgainstIdentityMove$2(s, c) {
    switch (ArrayMoveHelper.getRangeIndexRelationship(s, c.index)) {
        case RangeIndexRelationship.After:
            return new OperationPair(s, c);
        default:
            return new OperationPair(s.copy({ fromIndex: s.fromIndex + 1, toIndex: s.toIndex + 1 }), c);
    }
}

const ArrayMoveRemoveOTF = (s, c) => {
    switch (ArrayMoveHelper.getMoveDirection(s)) {
        case MoveDirection.Forward:
            return transformAgainstForwardMove$1(s, c);
        case MoveDirection.Backward:
            return transformAgainstBackwardMove$1(s, c);
        case MoveDirection.Identity:
            return transformAgainstIdentityMove$1(s, c);
        default:
            throw new Error("Invalid move direction");
    }
};
function transformAgainstForwardMove$1(s, c) {
    switch (ArrayMoveHelper.getRangeIndexRelationship(s, c.index)) {
        case RangeIndexRelationship.Before:
            return new OperationPair(s.copy({ fromIndex: s.fromIndex - 1, toIndex: s.toIndex - 1 }), c);
        case RangeIndexRelationship.Start:
            return new OperationPair(s.copy({ noOp: true }), c.copy({ index: s.toIndex }));
        case RangeIndexRelationship.Within:
        case RangeIndexRelationship.End:
            return new OperationPair(s.copy({ toIndex: s.toIndex - 1 }), c.copy({ index: c.index - 1 }));
        case RangeIndexRelationship.After:
            return new OperationPair(s, c);
        default:
            throw new Error("Invalid range-index relationship");
    }
}
function transformAgainstBackwardMove$1(s, c) {
    switch (ArrayMoveHelper.getRangeIndexRelationship(s, c.index)) {
        case RangeIndexRelationship.Before:
            return new OperationPair(s.copy({ fromIndex: s.fromIndex - 1, toIndex: s.toIndex - 1 }), c);
        case RangeIndexRelationship.Start:
        case RangeIndexRelationship.Within:
            return new OperationPair(s.copy({ fromIndex: s.fromIndex - 1 }), c.copy({ index: c.index + 1 }));
        case RangeIndexRelationship.End:
            return new OperationPair(s.copy({ noOp: true }), c.copy({ index: s.toIndex }));
        case RangeIndexRelationship.After:
            return new OperationPair(s, c);
        default:
            throw new Error("Invalid range-index relationship");
    }
}
function transformAgainstIdentityMove$1(s, c) {
    switch (ArrayMoveHelper.getRangeIndexRelationship(s, c.index)) {
        case RangeIndexRelationship.Before:
            return new OperationPair(s.copy({ fromIndex: s.fromIndex - 1, toIndex: s.toIndex - 1 }), c);
        case RangeIndexRelationship.Start:
        case RangeIndexRelationship.Within:
        case RangeIndexRelationship.End:
            return new OperationPair(s.copy({ noOp: true }), c);
        case RangeIndexRelationship.After:
            return new OperationPair(s, c);
        default:
            throw new Error("Invalid range-index relationship");
    }
}

const ArrayMoveReplaceOTF = (s, c) => {
    switch (ArrayMoveHelper.getMoveDirection(s)) {
        case MoveDirection.Forward:
            return transformAgainstForwardMove(s, c);
        case MoveDirection.Backward:
            return transformAgainstBackwardMove(s, c);
        case MoveDirection.Identity:
            return transformAgainstIdentityMove(s, c);
        default:
            throw new Error("Invalid move direction");
    }
};
function transformAgainstForwardMove(s, c) {
    switch (ArrayMoveHelper.getRangeIndexRelationship(s, c.index)) {
        case RangeIndexRelationship.Before:
        case RangeIndexRelationship.After:
            return new OperationPair(s, c);
        case RangeIndexRelationship.Start:
            return new OperationPair(s, c.copy({ index: s.toIndex }));
        case RangeIndexRelationship.Within:
        case RangeIndexRelationship.End:
            return new OperationPair(s, c.copy({ index: c.index - 1 }));
        default:
            throw new Error("Invalid range-index relationship");
    }
}
function transformAgainstBackwardMove(s, c) {
    switch (ArrayMoveHelper.getRangeIndexRelationship(s, c.index)) {
        case RangeIndexRelationship.Before:
        case RangeIndexRelationship.After:
            return new OperationPair(s, c);
        case RangeIndexRelationship.End:
            return new OperationPair(s, c.copy({ index: s.toIndex }));
        case RangeIndexRelationship.Start:
        case RangeIndexRelationship.Within:
            return new OperationPair(s, c.copy({ index: c.index + 1 }));
        default:
            throw new Error("Invalid range-index relationship");
    }
}
function transformAgainstIdentityMove(s, c) {
    return new OperationPair(s, c);
}

const ArrayMoveMoveOTF = (s, c) => {
    const sMoveType = ArrayMoveHelper.getMoveDirection(s);
    const cMoveType = ArrayMoveHelper.getMoveDirection(c);
    if (sMoveType === MoveDirection.Forward) {
        if (cMoveType === MoveDirection.Forward) {
            return transformServerForwardMoveWithClientForwardMove(s, c);
        }
        else if (cMoveType === MoveDirection.Backward) {
            return transformServerForwardMoveWithClientBackwardMove(s, c);
        }
        else {
            return transformServerForwardMoveWithClientIdentityMove(s, c);
        }
    }
    else if (sMoveType === MoveDirection.Backward) {
        if (cMoveType === MoveDirection.Forward) {
            return transformServerBackwardMoveWithClientForwardMove(s, c);
        }
        else if (cMoveType === MoveDirection.Backward) {
            return transformServerBackwardMoveWithClientBackwardMove(s, c);
        }
        else {
            return transformServerBackwardMoveWithClientIdentityMove(s, c);
        }
    }
    else {
        if (cMoveType === MoveDirection.Forward) {
            return transformServerIdentityMoveWithClientForwardMove(s, c);
        }
        else if (cMoveType === MoveDirection.Backward) {
            return transformServerIdentityMoveWithClientBackwardMove(s, c);
        }
        else {
            return transformServerIdentityMoveWithClientIdentityMove(s, c);
        }
    }
};
function transformServerForwardMoveWithClientForwardMove(s, c) {
    switch (ArrayMoveHelper.getRangeRelationship(s, c)) {
        case RangeRangeRelationship.Precedes:
            return new OperationPair(s, c);
        case RangeRangeRelationship.PrecededBy:
            return new OperationPair(s, c);
        case RangeRangeRelationship.Meets:
            return new OperationPair(s.copy({ toIndex: s.toIndex - 1 }), c.copy({ fromIndex: c.fromIndex - 1 }));
        case RangeRangeRelationship.MetBy:
            return new OperationPair(s.copy({ fromIndex: s.fromIndex - 1 }), c.copy({ toIndex: c.toIndex - 1 }));
        case RangeRangeRelationship.Overlaps:
            return new OperationPair(s.copy({ toIndex: s.toIndex - 1 }), c.copy({ fromIndex: c.fromIndex - 1 }));
        case RangeRangeRelationship.OverlappedBy:
            return new OperationPair(s.copy({ fromIndex: s.fromIndex - 1 }), c.copy({ toIndex: c.toIndex - 1 }));
        case RangeRangeRelationship.Starts:
            return new OperationPair(s.copy({ fromIndex: c.toIndex }), c.copy({ noOp: true }));
        case RangeRangeRelationship.StartedBy:
            return new OperationPair(s.copy({ fromIndex: c.toIndex }), c.copy({ noOp: true }));
        case RangeRangeRelationship.Contains:
            return new OperationPair(s, c.copy({ fromIndex: c.fromIndex - 1, toIndex: c.toIndex - 1 }));
        case RangeRangeRelationship.ContainedBy:
            return new OperationPair(s.copy({ fromIndex: s.fromIndex - 1, toIndex: s.toIndex - 1 }), c);
        case RangeRangeRelationship.Finishes:
            return new OperationPair(s.copy({ fromIndex: s.fromIndex - 1 }), c.copy({ toIndex: c.toIndex - 1 }));
        case RangeRangeRelationship.FinishedBy:
            return new OperationPair(s, c.copy({ fromIndex: c.fromIndex - 1, toIndex: c.toIndex - 1 }));
        case RangeRangeRelationship.EqualTo:
            return new OperationPair(s.copy({ noOp: true }), c.copy({ noOp: true }));
        default:
            throw new Error("Invalid range-range relationship");
    }
}
function transformServerForwardMoveWithClientBackwardMove(s, c) {
    switch (ArrayMoveHelper.getRangeRelationship(s, c)) {
        case RangeRangeRelationship.Precedes:
            return new OperationPair(s, c);
        case RangeRangeRelationship.PrecededBy:
            return new OperationPair(s, c);
        case RangeRangeRelationship.Meets:
            return new OperationPair(s.copy({ toIndex: s.toIndex + 1 }), c.copy({ toIndex: c.toIndex - 1 }));
        case RangeRangeRelationship.MetBy:
            return new OperationPair(s.copy({ fromIndex: c.toIndex }), c.copy({ noOp: true }));
        case RangeRangeRelationship.Overlaps:
            return new OperationPair(s.copy({ toIndex: s.toIndex + 1 }), c.copy({ toIndex: c.toIndex - 1 }));
        case RangeRangeRelationship.OverlappedBy:
            return new OperationPair(s.copy({ fromIndex: s.fromIndex + 1 }), c.copy({ fromIndex: c.fromIndex - 1 }));
        case RangeRangeRelationship.Starts:
            return new OperationPair(s.copy({ fromIndex: s.fromIndex + 1, toIndex: s.toIndex + 1 }), c);
        case RangeRangeRelationship.StartedBy:
            return new OperationPair(s.copy({ fromIndex: s.fromIndex + 1 }), c.copy({ fromIndex: c.fromIndex - 1 }));
        case RangeRangeRelationship.Contains:
            return new OperationPair(s, c.copy({ fromIndex: c.fromIndex - 1, toIndex: c.toIndex - 1 }));
        case RangeRangeRelationship.ContainedBy:
            return new OperationPair(s.copy({ fromIndex: s.fromIndex + 1, toIndex: s.toIndex + 1 }), c);
        case RangeRangeRelationship.Finishes:
            return new OperationPair(s.copy({ fromIndex: s.fromIndex + 1 }), c.copy({ fromIndex: c.fromIndex - 1 }));
        case RangeRangeRelationship.FinishedBy:
            return new OperationPair(s, c.copy({ fromIndex: c.fromIndex - 1, toIndex: c.toIndex - 1 }));
        case RangeRangeRelationship.EqualTo:
            return new OperationPair(s.copy({ fromIndex: s.fromIndex + 1 }), c.copy({ fromIndex: c.fromIndex - 1 }));
        default:
            throw new Error("Invalid range-range relationship");
    }
}
function transformServerForwardMoveWithClientIdentityMove(s, c) {
    switch (ArrayMoveHelper.getRangeIndexRelationship(s, c.fromIndex)) {
        case RangeIndexRelationship.Before:
        case RangeIndexRelationship.After:
            return new OperationPair(s, c);
        case RangeIndexRelationship.Start:
            return new OperationPair(s, c.copy({ fromIndex: s.toIndex, toIndex: s.toIndex }));
        case RangeIndexRelationship.Within:
        case RangeIndexRelationship.End:
            return new OperationPair(s, c.copy({ fromIndex: c.fromIndex - 1, toIndex: c.toIndex - 1 }));
        default:
            throw new Error("Invalid range-index relationship");
    }
}
function transformServerBackwardMoveWithClientForwardMove(s, c) {
    switch (ArrayMoveHelper.getRangeRelationship(s, c)) {
        case RangeRangeRelationship.Precedes:
            return new OperationPair(s, c);
        case RangeRangeRelationship.PrecededBy:
            return new OperationPair(s, c);
        case RangeRangeRelationship.Meets:
            return new OperationPair(s.copy({ fromIndex: c.toIndex }), c.copy({ noOp: true }));
        case RangeRangeRelationship.MetBy:
            return new OperationPair(s.copy({ toIndex: s.toIndex - 1 }), c.copy({ toIndex: c.toIndex + 1 }));
        case RangeRangeRelationship.Overlaps:
            return new OperationPair(s.copy({ fromIndex: s.fromIndex - 1 }), c.copy({ fromIndex: c.fromIndex + 1 }));
        case RangeRangeRelationship.OverlappedBy:
            return new OperationPair(s.copy({ toIndex: s.toIndex - 1 }), c.copy({ toIndex: c.toIndex + 1 }));
        case RangeRangeRelationship.Starts:
            return new OperationPair(s.copy({ fromIndex: s.fromIndex - 1 }), c.copy({ fromIndex: c.fromIndex + 1 }));
        case RangeRangeRelationship.StartedBy:
            return new OperationPair(s, c.copy({ fromIndex: c.fromIndex + 1, toIndex: c.toIndex + 1 }));
        case RangeRangeRelationship.Contains:
            return new OperationPair(s, c.copy({ fromIndex: c.fromIndex + 1, toIndex: c.toIndex + 1 }));
        case RangeRangeRelationship.ContainedBy:
            return new OperationPair(s.copy({ fromIndex: s.fromIndex - 1, toIndex: s.toIndex - 1 }), c);
        case RangeRangeRelationship.Finishes:
            return new OperationPair(s.copy({ fromIndex: s.fromIndex - 1, toIndex: s.toIndex - 1 }), c);
        case RangeRangeRelationship.FinishedBy:
            return new OperationPair(s.copy({ fromIndex: s.fromIndex - 1 }), c.copy({ fromIndex: c.fromIndex + 1 }));
        case RangeRangeRelationship.EqualTo:
            return new OperationPair(s.copy({ fromIndex: s.fromIndex - 1 }), c.copy({ fromIndex: c.fromIndex + 1 }));
        default:
            throw new Error("Invalid range-range relationship");
    }
}
function transformServerBackwardMoveWithClientBackwardMove(s, c) {
    switch (ArrayMoveHelper.getRangeRelationship(s, c)) {
        case RangeRangeRelationship.Precedes:
            return new OperationPair(s, c);
        case RangeRangeRelationship.PrecededBy:
            return new OperationPair(s, c);
        case RangeRangeRelationship.Meets:
            return new OperationPair(s.copy({ fromIndex: s.fromIndex + 1 }), c.copy({ toIndex: c.toIndex + 1 }));
        case RangeRangeRelationship.MetBy:
            return new OperationPair(s.copy({ toIndex: s.toIndex + 1 }), c.copy({ fromIndex: c.fromIndex + 1 }));
        case RangeRangeRelationship.Overlaps:
            return new OperationPair(s.copy({ fromIndex: s.fromIndex + 1 }), c.copy({ toIndex: c.toIndex + 1 }));
        case RangeRangeRelationship.OverlappedBy:
            return new OperationPair(s.copy({ toIndex: s.toIndex + 1 }), c.copy({ fromIndex: c.fromIndex + 1 }));
        case RangeRangeRelationship.Starts:
            return new OperationPair(s.copy({ fromIndex: s.fromIndex + 1, toIndex: s.toIndex + 1 }), c);
        case RangeRangeRelationship.StartedBy:
            return new OperationPair(s, c.copy({ fromIndex: c.fromIndex + 1, toIndex: c.toIndex + 1 }));
        case RangeRangeRelationship.Contains:
            return new OperationPair(s, c.copy({ fromIndex: c.fromIndex + 1, toIndex: c.toIndex + 1 }));
        case RangeRangeRelationship.ContainedBy:
            return new OperationPair(s.copy({ fromIndex: s.fromIndex + 1, toIndex: s.toIndex + 1 }), c);
        case RangeRangeRelationship.Finishes:
            return new OperationPair(s.copy({ fromIndex: c.toIndex }), c.copy({ noOp: true }));
        case RangeRangeRelationship.FinishedBy:
            return new OperationPair(s.copy({ fromIndex: c.toIndex }), c.copy({ noOp: true }));
        case RangeRangeRelationship.EqualTo:
            return new OperationPair(s.copy({ noOp: true }), c.copy({ noOp: true }));
        default:
            throw new Error("Invalid range-range relationship");
    }
}
function transformServerBackwardMoveWithClientIdentityMove(s, c) {
    switch (ArrayMoveHelper.getRangeIndexRelationship(s, c.fromIndex)) {
        case RangeIndexRelationship.Before:
        case RangeIndexRelationship.After:
            return new OperationPair(s, c);
        case RangeIndexRelationship.Start:
        case RangeIndexRelationship.Within:
            return new OperationPair(s, c.copy({ fromIndex: c.fromIndex + 1, toIndex: c.toIndex + 1 }));
        case RangeIndexRelationship.End:
            return new OperationPair(s, c.copy({ fromIndex: s.toIndex, toIndex: s.toIndex }));
        default:
            throw new Error("Invalid range-index relationship");
    }
}
function transformServerIdentityMoveWithClientForwardMove(s, c) {
    switch (ArrayMoveHelper.getRangeIndexRelationship(c, s.fromIndex)) {
        case RangeIndexRelationship.Before:
        case RangeIndexRelationship.After:
            return new OperationPair(s, c);
        case RangeIndexRelationship.Start:
            return new OperationPair(s.copy({ fromIndex: c.toIndex, toIndex: c.toIndex }), c);
        case RangeIndexRelationship.Within:
        case RangeIndexRelationship.End:
            return new OperationPair(s.copy({ fromIndex: s.fromIndex - 1, toIndex: s.toIndex - 1 }), c);
        default:
            throw new Error("Invalid range-index relationship");
    }
}
function transformServerIdentityMoveWithClientBackwardMove(s, c) {
    switch (ArrayMoveHelper.getRangeIndexRelationship(c, s.fromIndex)) {
        case RangeIndexRelationship.Before:
        case RangeIndexRelationship.After:
            return new OperationPair(s, c);
        case RangeIndexRelationship.Start:
        case RangeIndexRelationship.Within:
            return new OperationPair(s.copy({ fromIndex: s.fromIndex + 1, toIndex: s.toIndex + 1 }), c);
        case RangeIndexRelationship.End:
            return new OperationPair(s.copy({ fromIndex: c.toIndex, toIndex: c.toIndex }), c);
        default:
            throw new Error("Invalid range-index relationship");
    }
}
function transformServerIdentityMoveWithClientIdentityMove(s, c) {
    return new OperationPair(s, c);
}

const ArrayMoveSetOTF = (s, c) => {
    return new OperationPair(s.copy({ noOp: true }), c);
};

const ArraySetInsertOTF = (s, c) => {
    return new OperationPair(s, c.copy({ noOp: true }));
};

const ArraySetRemoveOTF = (s, c) => {
    return new OperationPair(s, c.copy({ noOp: true }));
};

const ArraySetReplaceOTF = (s, c) => {
    return new OperationPair(s, c.copy({ noOp: true }));
};

const ArraySetMoveOTF = (s, c) => {
    return new OperationPair(s, c.copy({ noOp: true }));
};

const ArraySetSetOTF = (s, c) => {
    if (!EqualsUtil.deepEquals(s.value, c.value)) {
        return new OperationPair(s, c.copy({ noOp: true }));
    }
    else {
        return new OperationPair(s.copy({ noOp: true }), c.copy({ noOp: true }));
    }
};

const StringSpliceSetOTF = (s, c) => {
    return new OperationPair(s.copy({ noOp: true }), c);
};

const StringSetSpliceOTF = (s, c) => {
    return new OperationPair(s, c.copy({ noOp: true }));
};

const StringSetSetOTF = (s, c) => {
    if (s.value === c.value) {
        return new OperationPair(s.copy({ noOp: true }), s.copy({ noOp: true }));
    }
    else {
        return new OperationPair(s, c.copy({ noOp: true }));
    }
};

const NumberAddAddOTF = (s, c) => {
    return new OperationPair(s, c);
};

const NumberAddSetOTF = (s, c) => {
    return new OperationPair(s.copy({ noOp: true }), c);
};

const NumberSetAddOTF = (s, c) => {
    return new OperationPair(s, c.copy({ noOp: true }));
};

const NumberSetSetOTF = (s, c) => {
    if (s.value === c.value) {
        return new OperationPair(s.copy({ noOp: true }), s.copy({ noOp: true }));
    }
    else {
        return new OperationPair(s, c.copy({ noOp: true }));
    }
};

const BooleanSetSetOTF = (s, c) => {
    if (s.value === c.value) {
        return new OperationPair(s.copy({ noOp: true }), s.copy({ noOp: true }));
    }
    else {
        return new OperationPair(s, c.copy({ noOp: true }));
    }
};

class ObjectSetPropertyOperation extends DiscreteOperation {
    constructor(id, noOp, prop, value) {
        super(OperationType.OBJECT_SET, id, noOp);
        this.prop = prop;
        this.value = value;
        Object.freeze(this);
    }
    copy(updates) {
        return new ObjectSetPropertyOperation(Immutable.update(this.id, updates.id), Immutable.update(this.noOp, updates.noOp), Immutable.update(this.prop, updates.prop), Immutable.update(this.value, updates.value));
    }
}

const ObjectAddPropertyAddPropertyOTF = (s, c) => {
    if (s.prop !== c.prop) {
        return new OperationPair(s, c);
    }
    else if (s.value !== c.value) {
        return new OperationPair(new ObjectSetPropertyOperation(s.id, s.noOp, s.prop, s.value), c.copy({ noOp: true }));
    }
    else {
        return new OperationPair(s.copy({ noOp: true }), c.copy({ noOp: true }));
    }
};

const ObjectAddPropertySetPropertyOTF = (s, c) => {
    if (s.prop !== c.prop) {
        return new OperationPair(s, c);
    }
    else {
        throw new Error("Add property and set property can not target the same property");
    }
};

const ObjectAddPropertyRemovePropertyOTF = (s, c) => {
    if (s.prop !== c.prop) {
        return new OperationPair(s, c);
    }
    else {
        throw new Error("Add property and Remove property can not target the same property");
    }
};

const ObjectRemovePropertyAddPropertyOTF = (s, c) => {
    if (s.prop !== c.prop) {
        return new OperationPair(s, c);
    }
    else {
        throw new Error("Remove property and add property can not target the same property");
    }
};

class ObjectAddPropertyOperation extends DiscreteOperation {
    constructor(id, noOp, prop, value) {
        super(OperationType.OBJECT_ADD, id, noOp);
        this.prop = prop;
        this.value = value;
        Object.freeze(this);
    }
    copy(updates) {
        return new ObjectAddPropertyOperation(Immutable.update(this.id, updates.id), Immutable.update(this.noOp, updates.noOp), Immutable.update(this.prop, updates.prop), Immutable.update(this.value, updates.value));
    }
}

const ObjectRemovePropertySetPropertyOTF = (s, c) => {
    if (s.prop !== c.prop) {
        return new OperationPair(s, c);
    }
    else {
        return new OperationPair(s.copy({ noOp: true }), new ObjectAddPropertyOperation(c.id, c.noOp, c.prop, c.value));
    }
};

const ObjectRemovePropertyRemovePropertyOTF = (s, c) => {
    if (s.prop !== c.prop) {
        return new OperationPair(s, c);
    }
    else {
        return new OperationPair(s.copy({ noOp: true }), c.copy({ noOp: true }));
    }
};

const ObjectAddPropertySetOTF = (s, c) => {
    return new OperationPair(s.copy({ noOp: true }), c);
};

const ObjectRemovePropertySetOTF = (s, c) => {
    return new OperationPair(s.copy({ noOp: true }), c);
};

const ObjectSetPropertyAddPropertyOTF = (s, c) => {
    if (s.prop !== c.prop) {
        return new OperationPair(s, c);
    }
    else {
        throw new Error("Set property and add property can not target the same property");
    }
};

const ObjectSetPropertySetPropertyOTF = (s, c) => {
    if (s.prop !== c.prop) {
        return new OperationPair(s, c);
    }
    else if (s.value !== c.value) {
        return new OperationPair(s, c.copy({ noOp: true }));
    }
    else {
        return new OperationPair(s.copy({ noOp: true }), c.copy({ noOp: true }));
    }
};

const ObjectSetPropertyRemovePropertyOTF = (s, c) => {
    if (s.prop !== c.prop) {
        return new OperationPair(s, c);
    }
    else {
        return new OperationPair(new ObjectAddPropertyOperation(s.id, s.noOp, s.prop, s.value), c.copy({ noOp: true }));
    }
};

const ObjectSetPropertySetOTF = (s, c) => {
    return new OperationPair(s.copy({ noOp: true }), c);
};

const ObjectSetAddPropertyOTF = (s, c) => {
    return new OperationPair(s, c.copy({ noOp: true }));
};

const ObjectSetSetPropertyOTF = (s, c) => {
    return new OperationPair(s, c.copy({ noOp: true }));
};

const ObjectSetRemovePropertyOTF = (s, c) => {
    return new OperationPair(s, c.copy({ noOp: true }));
};

const ObjectSetSetOTF = (s, c) => {
    if (!EqualsUtil.deepEquals(s.value, c.value)) {
        return new OperationPair(s, c.copy({ noOp: true }));
    }
    else {
        return new OperationPair(s.copy({ noOp: true }), c.copy({ noOp: true }));
    }
};

class ArrayInsertEvent {
    constructor(element, user, sessionId, local, index, value) {
        this.element = element;
        this.user = user;
        this.sessionId = sessionId;
        this.local = local;
        this.index = index;
        this.value = value;
        this.name = ArrayInsertEvent.NAME;
        Object.freeze(this);
    }
}
ArrayInsertEvent.NAME = "insert";

class ArrayRemoveEvent {
    constructor(element, user, sessionId, local, index, oldValue) {
        this.element = element;
        this.user = user;
        this.sessionId = sessionId;
        this.local = local;
        this.index = index;
        this.oldValue = oldValue;
        this.name = ArrayRemoveEvent.NAME;
        Object.freeze(this);
    }
}
ArrayRemoveEvent.NAME = "remove";

class ArrayReorderEvent {
    constructor(element, user, sessionId, local, fromIndex, toIndex) {
        this.element = element;
        this.user = user;
        this.sessionId = sessionId;
        this.local = local;
        this.fromIndex = fromIndex;
        this.toIndex = toIndex;
        this.name = ArrayReorderEvent.NAME;
        Object.freeze(this);
    }
}
ArrayReorderEvent.NAME = "reorder";

class ArraySetEvent {
    constructor(element, user, sessionId, local, index, value, oldValue) {
        this.element = element;
        this.user = user;
        this.sessionId = sessionId;
        this.local = local;
        this.index = index;
        this.value = value;
        this.oldValue = oldValue;
        this.name = ArraySetEvent.NAME;
        Object.freeze(this);
    }
}
ArraySetEvent.NAME = "set";

class ArraySetValueEvent {
    constructor(element, user, sessionId, local) {
        this.element = element;
        this.user = user;
        this.sessionId = sessionId;
        this.local = local;
        this.name = ArraySetValueEvent.NAME;
        Object.freeze(this);
    }
}
ArraySetValueEvent.NAME = "value";

class ObjectRemoveEvent {
    constructor(element, user, sessionId, local, key, oldValue) {
        this.element = element;
        this.user = user;
        this.sessionId = sessionId;
        this.local = local;
        this.key = key;
        this.oldValue = oldValue;
        this.name = ObjectRemoveEvent.NAME;
        Object.freeze(this);
    }
}
ObjectRemoveEvent.NAME = "remove";

class ObjectSetEvent {
    constructor(element, user, sessionId, local, key, value, oldValue) {
        this.element = element;
        this.user = user;
        this.sessionId = sessionId;
        this.local = local;
        this.key = key;
        this.value = value;
        this.oldValue = oldValue;
        this.name = ObjectSetEvent.NAME;
        Object.freeze(this);
    }
}
ObjectSetEvent.NAME = "set";

class ObjectSetValueEvent {
    constructor(element, user, sessionId, local) {
        this.element = element;
        this.user = user;
        this.sessionId = sessionId;
        this.local = local;
        this.name = ObjectSetValueEvent.NAME;
        Object.freeze(this);
    }
}
ObjectSetValueEvent.NAME = "value";

class BooleanSetValueEvent {
    constructor(element, user, sessionId, local) {
        this.element = element;
        this.user = user;
        this.sessionId = sessionId;
        this.local = local;
        this.name = BooleanSetValueEvent.NAME;
        Object.freeze(this);
    }
}
BooleanSetValueEvent.NAME = "value";

class DateSetValueEvent {
    constructor(element, user, sessionId, local) {
        this.element = element;
        this.user = user;
        this.sessionId = sessionId;
        this.local = local;
        this.name = DateSetValueEvent.NAME;
        Object.freeze(this);
    }
}
DateSetValueEvent.NAME = "value";

class NumberSetValueEvent {
    constructor(element, user, sessionId, local) {
        this.element = element;
        this.user = user;
        this.sessionId = sessionId;
        this.local = local;
        this.name = NumberSetValueEvent.NAME;
        Object.freeze(this);
    }
}
NumberSetValueEvent.NAME = "value";

class NumberDeltaEvent {
    constructor(element, user, sessionId, local, value) {
        this.element = element;
        this.user = user;
        this.sessionId = sessionId;
        this.local = local;
        this.value = value;
        this.name = NumberDeltaEvent.NAME;
        Object.freeze(this);
    }
}
NumberDeltaEvent.NAME = "delta";

class StringInsertEvent {
    constructor(element, user, sessionId, local, index, value) {
        this.element = element;
        this.user = user;
        this.sessionId = sessionId;
        this.local = local;
        this.index = index;
        this.value = value;
        this.name = StringInsertEvent.NAME;
        Object.freeze(this);
    }
}
StringInsertEvent.NAME = "insert";

class StringRemoveEvent {
    constructor(element, user, sessionId, local, index, value) {
        this.element = element;
        this.user = user;
        this.sessionId = sessionId;
        this.local = local;
        this.index = index;
        this.value = value;
        this.name = StringRemoveEvent.NAME;
        Object.freeze(this);
    }
}
StringRemoveEvent.NAME = "remove";

class StringSpliceEvent {
    constructor(element, user, sessionId, local, index, deleteCount, insertValue) {
        this.element = element;
        this.user = user;
        this.sessionId = sessionId;
        this.local = local;
        this.index = index;
        this.deleteCount = deleteCount;
        this.insertValue = insertValue;
        this.name = StringSpliceEvent.NAME;
        Object.freeze(this);
    }
}
StringSpliceEvent.NAME = "splice";

class StringSetValueEvent {
    constructor(element, user, sessionId, local) {
        this.element = element;
        this.user = user;
        this.sessionId = sessionId;
        this.local = local;
        this.name = StringSetValueEvent.NAME;
        Object.freeze(this);
    }
}
StringSetValueEvent.NAME = "value";

class ElementDetachedEvent {
    constructor(src) {
        this.src = src;
        this.name = ElementDetachedEvent.NAME;
    }
}
ElementDetachedEvent.NAME = "detached";

class ModelChangedEvent {
    constructor(element, user, sessionId, local, relativePath, childEvent) {
        this.element = element;
        this.user = user;
        this.sessionId = sessionId;
        this.local = local;
        this.relativePath = relativePath;
        this.childEvent = childEvent;
        this.name = ModelChangedEvent.NAME;
        Object.freeze(this);
    }
}
ModelChangedEvent.NAME = "model_changed";

class ModelClosedEvent {
    constructor(src, local, reason) {
        this.src = src;
        this.local = local;
        this.reason = reason;
        this.name = ModelClosedEvent.NAME;
        Object.freeze(this);
    }
}
ModelClosedEvent.NAME = "closed";

class ModelDeletedEvent {
    constructor(src, local, reason) {
        this.src = src;
        this.local = local;
        this.reason = reason;
        this.name = ModelDeletedEvent.NAME;
        Object.freeze(this);
    }
}
ModelDeletedEvent.NAME = "deleted";

class ModelPermissionsChangedEvent {
    constructor(model, permissions, changes) {
        this.model = model;
        this.permissions = permissions;
        this.changes = changes;
        this.name = ModelPermissionsChangedEvent.NAME;
        Object.freeze(this);
    }
}
ModelPermissionsChangedEvent.NAME = "permissions_changed";

class VersionChangedEvent {
    constructor(src, version) {
        this.src = src;
        this.version = version;
        this.name = VersionChangedEvent.NAME;
        Object.freeze(this);
    }
}
VersionChangedEvent.NAME = "version_changed";

class ModelOnlineEvent {
    constructor(src) {
        this.src = src;
        this.name = ModelOnlineEvent.NAME;
        Object.freeze(this);
    }
}
ModelOnlineEvent.NAME = "online";

class ModelOfflineEvent {
    constructor(src) {
        this.src = src;
        this.name = ModelOfflineEvent.NAME;
        Object.freeze(this);
    }
}
ModelOfflineEvent.NAME = "offline";

class ModelReconnectingEvent {
    constructor(src) {
        this.src = src;
        this.name = ModelReconnectingEvent.NAME;
        Object.freeze(this);
    }
}
ModelReconnectingEvent.NAME = "reconnecting";

class ResyncStartedEvent {
    constructor(src) {
        this.src = src;
        this.name = ResyncStartedEvent.NAME;
        Object.freeze(this);
    }
}
ResyncStartedEvent.NAME = "resync_started";

class ResyncCompletedEvent {
    constructor(src) {
        this.src = src;
        this.name = ResyncCompletedEvent.NAME;
        Object.freeze(this);
    }
}
ResyncCompletedEvent.NAME = "resync_completed";

class ResyncErrorEvent {
    constructor(src, message) {
        this.src = src;
        this.message = message;
        this.name = ResyncErrorEvent.NAME;
        Object.freeze(this);
    }
}
ResyncErrorEvent.NAME = "resync_error";

class RemoteReferenceCreatedEvent {
    constructor(reference, model, element) {
        this.reference = reference;
        this.model = model;
        this.element = element;
        this.name = RemoteReferenceCreatedEvent.NAME;
        Object.freeze(this);
    }
}
RemoteReferenceCreatedEvent.NAME = "reference";

class RemoteResyncStartedEvent {
    constructor(model, sessionId, user) {
        this.model = model;
        this.sessionId = sessionId;
        this.user = user;
        this.name = RemoteResyncStartedEvent.NAME;
        Object.freeze(this);
    }
}
RemoteResyncStartedEvent.NAME = "remote_resync_started";

class RemoteResyncCompletedEvent {
    constructor(model, sessionId, user) {
        this.model = model;
        this.sessionId = sessionId;
        this.user = user;
        this.name = RemoteResyncCompletedEvent.NAME;
        Object.freeze(this);
    }
}
RemoteResyncCompletedEvent.NAME = "remote_resync_completed";

class CollaboratorOpenedEvent {
    constructor(src, collaborator) {
        this.src = src;
        this.collaborator = collaborator;
        this.name = CollaboratorOpenedEvent.NAME;
        Object.freeze(this);
    }
}
CollaboratorOpenedEvent.NAME = "collaborator_opened";

class CollaboratorClosedEvent {
    constructor(src, collaborator) {
        this.src = src;
        this.collaborator = collaborator;
        this.name = CollaboratorClosedEvent.NAME;
        Object.freeze(this);
    }
}
CollaboratorClosedEvent.NAME = "collaborator_closed";

class ModelCommittedEvent {
    constructor(src) {
        this.src = src;
        this.name = ModelCommittedEvent.NAME;
        Object.freeze(this);
    }
}
ModelCommittedEvent.NAME = "committed";

class ModelModifiedEvent {
    constructor(src) {
        this.src = src;
        this.name = ModelModifiedEvent.NAME;
        Object.freeze(this);
    }
}
ModelModifiedEvent.NAME = "modified";

class OfflineModelsDownloadStartedEvent {
    constructor(modelsToDownload) {
        this.modelsToDownload = modelsToDownload;
        this.name = OfflineModelsDownloadStartedEvent.NAME;
        Object.freeze(this);
    }
}
OfflineModelsDownloadStartedEvent.NAME = "offline_models_download_started";

class OfflineModelsDownloadStatusChangedEvent {
    constructor(modelsToDownload, trigger) {
        this.modelsToDownload = modelsToDownload;
        this.trigger = trigger;
        this.name = OfflineModelsDownloadStatusChangedEvent.NAME;
        Object.freeze(this);
    }
}
OfflineModelsDownloadStatusChangedEvent.NAME = "offline_models_download_status_changed";

class OfflineModelsDownloadStoppedEvent {
    constructor() {
        this.name = OfflineModelsDownloadStoppedEvent.NAME;
        Object.freeze(this);
    }
}
OfflineModelsDownloadStoppedEvent.NAME = "offline_models_download_stopped";

class OfflineModelsSyncStartedEvent {
    constructor(modelsToSync) {
        this.modelsToSync = modelsToSync;
        this.name = OfflineModelsSyncStartedEvent.NAME;
        Object.freeze(this);
    }
}
OfflineModelsSyncStartedEvent.NAME = "offline_models_sync_started";

class OfflineModelsSyncProgressEvent {
    constructor(modelsToSync) {
        this.modelsToSync = modelsToSync;
        this.name = OfflineModelsSyncProgressEvent.NAME;
        Object.freeze(this);
    }
}
OfflineModelsSyncProgressEvent.NAME = "offline_models_sync_progress";

class OfflineModelsSyncCompletedEvent {
    constructor() {
        this.name = OfflineModelsSyncCompletedEvent.NAME;
        Object.freeze(this);
    }
}
OfflineModelsSyncCompletedEvent.NAME = "offline_models_sync_completed";

class OfflineModelsSyncAbortedEvent {
    constructor(reason, code) {
        this.reason = reason;
        this.code = code;
        this.name = OfflineModelsSyncAbortedEvent.NAME;
        Object.freeze(this);
    }
}
OfflineModelsSyncAbortedEvent.NAME = "offline_models_sync_aborted";

class OfflineModelSyncStartedEvent {
    constructor(modelId) {
        this.modelId = modelId;
        this.name = OfflineModelSyncStartedEvent.NAME;
        Object.freeze(this);
    }
}
OfflineModelSyncStartedEvent.NAME = "offline_model_sync_started";

class OfflineModelSyncCompletedEvent {
    constructor(modelId) {
        this.modelId = modelId;
        this.name = OfflineModelSyncCompletedEvent.NAME;
        Object.freeze(this);
    }
}
OfflineModelSyncCompletedEvent.NAME = "offline_model_sync_completed";

class OfflineModelSyncErrorEvent {
    constructor(modelId, message, model) {
        this.modelId = modelId;
        this.message = message;
        this.model = model;
        this.name = OfflineModelSyncErrorEvent.NAME;
        Object.freeze(this);
    }
}
OfflineModelSyncErrorEvent.NAME = "offline_model_sync_error";

class OfflineModelDownloadedEvent {
    constructor(id, version, permissions) {
        this.id = id;
        this.version = version;
        this.permissions = permissions;
        this.name = OfflineModelDownloadedEvent.NAME;
        Object.freeze(this);
    }
}
OfflineModelDownloadedEvent.NAME = "offline_model_downloaded";

class OfflineModelUpdatedEvent {
    constructor(id, version, permissions) {
        this.id = id;
        this.version = version;
        this.permissions = permissions;
        this.name = OfflineModelUpdatedEvent.NAME;
        Object.freeze(this);
    }
}
OfflineModelUpdatedEvent.NAME = "offline_model_updated";

class OfflineModelStatusChangedEvent {
    constructor(id, subscribed, available, synchronized, local) {
        this.id = id;
        this.subscribed = subscribed;
        this.available = available;
        this.synchronized = synchronized;
        this.local = local;
        this.name = OfflineModelStatusChangedEvent.NAME;
        Object.freeze(this);
    }
}
OfflineModelStatusChangedEvent.NAME = "offline_model_status_changed";

class OfflineModelDeletedEvent {
    constructor(id) {
        this.id = id;
        this.name = OfflineModelDeletedEvent.NAME;
        Object.freeze(this);
    }
}
OfflineModelDeletedEvent.NAME = "offline_model_deleted";

class OfflineModelPermissionsRevokedEvent {
    constructor(id) {
        this.id = id;
        this.name = OfflineModelPermissionsRevokedEvent.NAME;
        Object.freeze(this);
    }
}
OfflineModelPermissionsRevokedEvent.NAME = "offline_model_permissions_revoked";

class NodeDetachedEvent {
    constructor(src, local) {
        this.src = src;
        this.local = local;
        this.name = NodeDetachedEvent.NAME;
    }
}
NodeDetachedEvent.NAME = "detached";
class NodeChangedEvent {
    constructor(src, local, relativePath, childEvent, sessionId, user) {
        this.src = src;
        this.local = local;
        this.relativePath = relativePath;
        this.childEvent = childEvent;
        this.sessionId = sessionId;
        this.user = user;
        this.name = NodeChangedEvent.NAME;
        Object.freeze(this);
    }
}
NodeChangedEvent.NAME = "node_changed";
class ArrayNodeInsertEvent {
    constructor(src, local, index, value, sessionId, user) {
        this.src = src;
        this.local = local;
        this.index = index;
        this.value = value;
        this.sessionId = sessionId;
        this.user = user;
        this.name = ArrayNodeInsertEvent.NAME;
        Object.freeze(this);
    }
}
ArrayNodeInsertEvent.NAME = "insert";
class ArrayNodeRemoveEvent {
    constructor(src, local, index, oldValue, sessionId, user) {
        this.src = src;
        this.local = local;
        this.index = index;
        this.oldValue = oldValue;
        this.sessionId = sessionId;
        this.user = user;
        this.name = ArrayNodeRemoveEvent.NAME;
        Object.freeze(this);
    }
}
ArrayNodeRemoveEvent.NAME = "remove";
class ArrayNodeSetEvent {
    constructor(src, local, index, value, oldValue, sessionId, user) {
        this.src = src;
        this.local = local;
        this.index = index;
        this.value = value;
        this.oldValue = oldValue;
        this.sessionId = sessionId;
        this.user = user;
        this.name = ArrayNodeSetEvent.NAME;
        Object.freeze(this);
    }
}
ArrayNodeSetEvent.NAME = "set";
class ArrayNodeReorderEvent {
    constructor(src, local, fromIndex, toIndex, sessionId, user) {
        this.src = src;
        this.local = local;
        this.fromIndex = fromIndex;
        this.toIndex = toIndex;
        this.sessionId = sessionId;
        this.user = user;
        this.name = ArrayNodeReorderEvent.NAME;
        Object.freeze(this);
    }
}
ArrayNodeReorderEvent.NAME = "reorder";
class ArrayNodeSetValueEvent {
    constructor(src, local, value, sessionId, user) {
        this.src = src;
        this.local = local;
        this.value = value;
        this.sessionId = sessionId;
        this.user = user;
        this.name = ArrayNodeSetValueEvent.NAME;
        Object.freeze(this);
    }
}
ArrayNodeSetValueEvent.NAME = "value";
class BooleanNodeSetValueEvent {
    constructor(src, local, value, sessionId, user) {
        this.src = src;
        this.local = local;
        this.value = value;
        this.sessionId = sessionId;
        this.user = user;
        this.name = BooleanNodeSetValueEvent.NAME;
        Object.freeze(this);
    }
}
BooleanNodeSetValueEvent.NAME = "value";
class NumberNodeSetValueEvent {
    constructor(src, local, value, sessionId, user) {
        this.src = src;
        this.local = local;
        this.value = value;
        this.sessionId = sessionId;
        this.user = user;
        this.name = NumberNodeSetValueEvent.NAME;
        Object.freeze(this);
    }
}
NumberNodeSetValueEvent.NAME = "value";
class NumberNodeDeltaEvent {
    constructor(src, local, value, sessionId, user) {
        this.src = src;
        this.local = local;
        this.value = value;
        this.sessionId = sessionId;
        this.user = user;
        this.name = NumberNodeDeltaEvent.NAME;
        Object.freeze(this);
    }
}
NumberNodeDeltaEvent.NAME = "delta";
class ObjectNodeSetEvent {
    constructor(src, local, key, value, oldValue, sessionId, user) {
        this.src = src;
        this.local = local;
        this.key = key;
        this.value = value;
        this.oldValue = oldValue;
        this.sessionId = sessionId;
        this.user = user;
        this.name = ObjectNodeSetEvent.NAME;
        Object.freeze(this);
    }
}
ObjectNodeSetEvent.NAME = "set";
class ObjectNodeRemoveEvent {
    constructor(src, local, key, oldValue, sessionId, user) {
        this.src = src;
        this.local = local;
        this.key = key;
        this.oldValue = oldValue;
        this.sessionId = sessionId;
        this.user = user;
        this.name = ObjectNodeRemoveEvent.NAME;
        Object.freeze(this);
    }
}
ObjectNodeRemoveEvent.NAME = "remove";
class ObjectNodeSetValueEvent {
    constructor(src, local, value, sessionId, user) {
        this.src = src;
        this.local = local;
        this.value = value;
        this.sessionId = sessionId;
        this.user = user;
        this.name = ObjectNodeSetValueEvent.NAME;
        Object.freeze(this);
    }
}
ObjectNodeSetValueEvent.NAME = "value";
class StringNodeSpliceEvent {
    constructor(src, local, index, deleteCount, insertValue, sessionId, user) {
        this.src = src;
        this.local = local;
        this.index = index;
        this.deleteCount = deleteCount;
        this.insertValue = insertValue;
        this.sessionId = sessionId;
        this.user = user;
        this.name = StringNodeSpliceEvent.NAME;
        Object.freeze(this);
    }
}
StringNodeSpliceEvent.NAME = "splice";
class StringNodeInsertEvent {
    constructor(src, local, index, value, sessionId, user) {
        this.src = src;
        this.local = local;
        this.index = index;
        this.value = value;
        this.sessionId = sessionId;
        this.user = user;
        this.name = StringNodeInsertEvent.NAME;
        Object.freeze(this);
    }
}
StringNodeInsertEvent.NAME = "insert";
class StringNodeRemoveEvent {
    constructor(src, local, index, value, sessionId, user) {
        this.src = src;
        this.local = local;
        this.index = index;
        this.value = value;
        this.sessionId = sessionId;
        this.user = user;
        this.name = StringNodeRemoveEvent.NAME;
        Object.freeze(this);
    }
}
StringNodeRemoveEvent.NAME = "remove";
class StringNodeSetValueEvent {
    constructor(src, local, value, sessionId, user) {
        this.src = src;
        this.local = local;
        this.value = value;
        this.sessionId = sessionId;
        this.user = user;
        this.name = StringNodeSetValueEvent.NAME;
        Object.freeze(this);
    }
}
StringNodeSetValueEvent.NAME = "value";
class DateNodeSetValueEvent {
    constructor(src, local, value, sessionId, user) {
        this.src = src;
        this.local = local;
        this.value = value;
        this.sessionId = sessionId;
        this.user = user;
        this.name = DateNodeSetValueEvent.NAME;
        Object.freeze(this);
    }
}
DateNodeSetValueEvent.NAME = "value";

class ModelEventConverter {
    static convertEvent(event, wrapperFactory) {
        if (event instanceof NodeDetachedEvent) {
            return new ElementDetachedEvent(wrapperFactory.wrap(event.src));
        }
        else if (event instanceof NodeChangedEvent) {
            return new ModelChangedEvent(wrapperFactory.wrap(event.src), event.user, event.sessionId, event.local, event.relativePath, this.convertEvent(event.childEvent, wrapperFactory));
        }
        else if (event instanceof ArrayNodeInsertEvent) {
            return new ArrayInsertEvent(wrapperFactory.wrap(event.src), event.user, event.sessionId, event.local, event.index, wrapperFactory.wrap(event.value));
        }
        else if (event instanceof ArrayNodeRemoveEvent) {
            return new ArrayRemoveEvent(wrapperFactory.wrap(event.src), event.user, event.sessionId, event.local, event.index, wrapperFactory.wrap(event.oldValue));
        }
        else if (event instanceof ArrayNodeReorderEvent) {
            return new ArrayReorderEvent(wrapperFactory.wrap(event.src), event.user, event.sessionId, event.local, event.fromIndex, event.toIndex);
        }
        else if (event instanceof ArrayNodeSetEvent) {
            return new ArraySetEvent(wrapperFactory.wrap(event.src), event.user, event.sessionId, event.local, event.index, wrapperFactory.wrap(event.value), wrapperFactory.wrap(event.oldValue));
        }
        else if (event instanceof ArrayNodeSetValueEvent) {
            return new ArraySetValueEvent(wrapperFactory.wrap(event.src), event.user, event.sessionId, event.local);
        }
        else if (event instanceof BooleanNodeSetValueEvent) {
            return new BooleanSetValueEvent(wrapperFactory.wrap(event.src), event.user, event.sessionId, event.local);
        }
        else if (event instanceof NumberNodeSetValueEvent) {
            return new NumberSetValueEvent(wrapperFactory.wrap(event.src), event.user, event.sessionId, event.local);
        }
        else if (event instanceof NumberNodeDeltaEvent) {
            return new NumberDeltaEvent(wrapperFactory.wrap(event.src), event.user, event.sessionId, event.local, event.value);
        }
        else if (event instanceof ObjectNodeSetValueEvent) {
            return new ObjectSetValueEvent(wrapperFactory.wrap(event.src), event.user, event.sessionId, event.local);
        }
        else if (event instanceof ObjectNodeRemoveEvent) {
            return new ObjectRemoveEvent(wrapperFactory.wrap(event.src), event.user, event.sessionId, event.local, event.key, wrapperFactory.wrap(event.oldValue));
        }
        else if (event instanceof ObjectNodeSetEvent) {
            return new ObjectSetEvent(wrapperFactory.wrap(event.src), event.user, event.sessionId, event.local, event.key, wrapperFactory.wrap(event.value), wrapperFactory.wrap(event.oldValue));
        }
        else if (event instanceof StringNodeSpliceEvent) {
            return new StringSpliceEvent(wrapperFactory.wrap(event.src), event.user, event.sessionId, event.local, event.index, event.deleteCount, event.insertValue);
        }
        else if (event instanceof StringNodeInsertEvent) {
            return new StringInsertEvent(wrapperFactory.wrap(event.src), event.user, event.sessionId, event.local, event.index, event.value);
        }
        else if (event instanceof StringNodeRemoveEvent) {
            return new StringRemoveEvent(wrapperFactory.wrap(event.src), event.user, event.sessionId, event.local, event.index, event.value);
        }
        else if (event instanceof StringNodeSetValueEvent) {
            return new StringSetValueEvent(wrapperFactory.wrap(event.src), event.user, event.sessionId, event.local);
        }
        else if (event instanceof DateNodeSetValueEvent) {
            return new DateSetValueEvent(wrapperFactory.wrap(event.src), event.user, event.sessionId, event.local);
        }
        else {
            throw new Error("Unable to convert event: " + event.name);
        }
    }
}

class ReferenceMap {
    constructor() {
        this._referencesBySessionId = new Map();
    }
    put(reference) {
        const sessionId = reference.sessionId();
        const key = reference.key();
        let sessionReferences = this._referencesBySessionId.get(sessionId);
        if (TypeChecker.isUndefined(sessionReferences)) {
            sessionReferences = new Map();
            this._referencesBySessionId.set(sessionId, sessionReferences);
        }
        if (sessionReferences.has(key)) {
            throw new Error(`Reference with key "${key}" already exists for session "${sessionId}".`);
        }
        sessionReferences.set(key, reference);
    }
    get(sessionId, key) {
        const sessionReferences = this._referencesBySessionId.get(sessionId);
        if (!TypeChecker.isUndefined(sessionReferences)) {
            return sessionReferences.get(key);
        }
        else {
            return;
        }
    }
    getAll(filter) {
        if (TypeChecker.isUndefined(filter)) {
            filter = {};
        }
        const refs = [];
        const sessionIds = TypeChecker.isSet(filter.sessionId) ?
            [filter.sessionId] :
            Array.from(this._referencesBySessionId.keys());
        sessionIds.forEach((sid) => {
            const sessionRefs = this._referencesBySessionId.get(sid);
            const keys = TypeChecker.isSet(filter.key) ? [filter.key] : Array.from(sessionRefs.keys());
            keys.forEach((k) => {
                const r = sessionRefs.get(k);
                if (!TypeChecker.isUndefined(r)) {
                    refs.push(r);
                }
            });
        });
        return refs;
    }
    removeAll() {
        this._referencesBySessionId.clear();
    }
    remove(sessionId, key) {
        const sessionReferences = this._referencesBySessionId.get(sessionId);
        if (sessionReferences !== undefined) {
            const current = sessionReferences.get(key);
            sessionReferences.delete(key);
            return current;
        }
        else {
            return;
        }
    }
    removeBySession(sessionId) {
        this._referencesBySessionId.delete(sessionId);
    }
    removeByKey(key) {
        this._referencesBySessionId.forEach(referencesForSession => {
            referencesForSession.delete(key);
        });
    }
}

class ReferenceSetEvent {
    constructor(src, oldValues, addedValues, removedValues, synthetic) {
        this.src = src;
        this.oldValues = oldValues;
        this.addedValues = addedValues;
        this.removedValues = removedValues;
        this.synthetic = synthetic;
        this.name = ReferenceSetEvent.NAME;
        if (oldValues.length > 0) {
            this.oldValue = oldValues[0];
        }
        Object.freeze(this);
    }
}
ReferenceSetEvent.NAME = "set";

class ReferenceClearedEvent {
    constructor(src, oldValues) {
        this.src = src;
        this.oldValues = oldValues;
        this.name = ReferenceClearedEvent.NAME;
        if (oldValues.length > 0) {
            this.oldValue = oldValues[0];
        }
        Object.freeze(this);
    }
}
ReferenceClearedEvent.NAME = "cleared";

class ReferenceDisposedEvent {
    constructor(src) {
        this.src = src;
        this.name = ReferenceDisposedEvent.NAME;
        Object.freeze(this);
    }
}
ReferenceDisposedEvent.NAME = "disposed";

class ModelReference extends ConvergenceEventEmitter {
    constructor(referenceManager, type, key, source, user, sessionId, local) {
        super();
        this._referenceManager = referenceManager;
        this._disposed = false;
        this._values = [];
        this._type = type;
        this._key = key;
        this._source = source;
        this._user = user;
        this._sessionId = sessionId;
        this._local = local;
    }
    type() {
        return this._type;
    }
    key() {
        return this._key;
    }
    source() {
        return this._source;
    }
    isLocal() {
        return this._local;
    }
    user() {
        return this._user;
    }
    sessionId() {
        return this._sessionId;
    }
    isDisposed() {
        return this._disposed;
    }
    _dispose() {
        this._disposed = true;
        const event = new ReferenceDisposedEvent(this);
        this._emitEvent(event);
        this.removeAllListeners();
        this._referenceManager._handleReferenceDisposed(this);
    }
    value() {
        return this._values[0];
    }
    values() {
        return this._values;
    }
    isSet() {
        return this._values.length > 0;
    }
    _set(values, synthetic) {
        const oldValues = this._values;
        this._values = values;
        const added = this._values.filter(v => !oldValues.includes(v));
        const removed = oldValues.filter(v => !this._values.includes(v));
        const event = new ReferenceSetEvent(this, oldValues, added, removed, synthetic);
        this._emitEvent(event);
    }
    _clear() {
        const oldValues = this._values;
        this._values = [];
        const event = new ReferenceClearedEvent(this, oldValues);
        this._emitEvent(event);
    }
    _setIfChanged(values, synthetic) {
        if (!EqualsUtil.deepEquals(this._values, values)) {
            this._set(values, synthetic);
        }
    }
}
ModelReference.Events = {
    SET: ReferenceSetEvent.NAME,
    CLEARED: ReferenceClearedEvent.NAME,
    DISPOSED: ReferenceDisposedEvent.NAME
};
ModelReference.Types = {
    INDEX: "index",
    RANGE: "range",
    PROPERTY: "property",
    ELEMENT: "element"
};
Object.freeze(ModelReference.Events);
Object.freeze(ModelReference.Types);

class IndexTransformer {
    static handleSplice(indices, spliceIndex, deleteCount, insertCount) {
        return indices.map(index => {
            if (index >= index) {
                return (index - Math.min(index - spliceIndex, deleteCount)) + insertCount;
            }
            else {
                return index;
            }
        });
    }
    static handleInsert(indices, insertIndex, length) {
        return indices.map((index) => {
            if (index >= insertIndex) {
                return index + length;
            }
            else {
                return index;
            }
        });
    }
    static handleReorder(indices, fromIndex, toIndex) {
        return indices.map((index) => {
            if (index >= toIndex && index < fromIndex) {
                return index + 1;
            }
            else if (index >= fromIndex && index < toIndex) {
                return index - 1;
            }
            else {
                return index;
            }
        });
    }
    static handleRemove(indices, removeIndex, length) {
        return indices.map((index) => {
            if (index > removeIndex) {
                return index - Math.min(index - removeIndex, length);
            }
            else {
                return index;
            }
        });
    }
}

class IndexReference extends ModelReference {
    constructor(referenceManager, key, source, user, sessionId, local) {
        super(referenceManager, ModelReference.Types.INDEX, key, source, user, sessionId, local);
    }
    _handleInsert(index, length) {
        this._setIfChanged(IndexTransformer.handleInsert(this._values, index, length), true);
    }
    _handleRemove(index, length) {
        this._setIfChanged(IndexTransformer.handleRemove(this._values, index, length), true);
    }
    _handleSplice(index, deleteCount, insertCount) {
        this._setIfChanged(IndexTransformer.handleSplice(this._values, index, deleteCount, insertCount), true);
    }
    _handleReorder(fromIndex, toIndex) {
        this._setIfChanged(IndexTransformer.handleReorder(this._values, fromIndex, toIndex), true);
    }
}

class RangeTransformer {
    static handleSplice(ranges, insertIndex, deleteCount, insertCount) {
        return ranges.map(range => {
            const indices = RangeTransformer._rangeToTuple(range);
            const xFormed = IndexTransformer.handleSplice(indices, insertIndex, deleteCount, insertCount);
            return RangeTransformer._tupleToRange(xFormed);
        });
    }
    static handleInsert(ranges, insertIndex, length) {
        return ranges.map(range => {
            const indices = RangeTransformer._rangeToTuple(range);
            const xFormed = IndexTransformer.handleInsert(indices, insertIndex, length);
            return RangeTransformer._tupleToRange(xFormed);
        });
    }
    static handleReorder(ranges, fromIndex, toIndex) {
        return ranges.map(range => {
            const indices = RangeTransformer._rangeToTuple(range);
            const xFormed = IndexTransformer.handleReorder(indices, fromIndex, toIndex);
            return RangeTransformer._tupleToRange(xFormed);
        });
    }
    static handleRemove(ranges, removeIndex, length) {
        return ranges.map(range => {
            const indices = RangeTransformer._rangeToTuple(range);
            const xFormed = IndexTransformer.handleRemove(indices, removeIndex, length);
            return RangeTransformer._tupleToRange(xFormed);
        });
    }
    static _rangeToTuple(range) {
        return [range.start, range.end];
    }
    static _tupleToRange(tuple) {
        return { start: tuple[0], end: tuple[1] };
    }
}

class RangeReference extends ModelReference {
    constructor(referenceManager, key, source, user, sessionId, local) {
        super(referenceManager, ModelReference.Types.RANGE, key, source, user, sessionId, local);
    }
    _handleInsert(index, length) {
        this._setIfChanged(RangeTransformer.handleInsert(this._values, index, length), true);
    }
    _handleRemove(index, length) {
        this._setIfChanged(RangeTransformer.handleRemove(this._values, index, length), true);
    }
    _handleSplice(index, deleteCount, insertCount) {
        this._setIfChanged(RangeTransformer.handleSplice(this._values, index, deleteCount, insertCount), true);
    }
    _handleReorder(fromIndex, toIndex) {
        this._setIfChanged(RangeTransformer.handleReorder(this._values, fromIndex, toIndex), true);
    }
}

class PropertyReference extends ModelReference {
    constructor(referenceManager, key, source, user, sessionId, local) {
        super(referenceManager, ModelReference.Types.PROPERTY, key, source, user, sessionId, local);
    }
    _handlePropertyRemoved(property) {
        const index = this._values.indexOf(property, 0);
        if (index > -1) {
            const newElements = this._values.slice(0);
            newElements.splice(index, 1);
            this._set(newElements, true);
        }
    }
}

class RemoteReferenceEvent {
    constructor(sessionId, resourceId, valueId, key) {
        this.sessionId = sessionId;
        this.resourceId = resourceId;
        this.valueId = valueId;
        this.key = key;
    }
}
class RemoteReferenceShared extends RemoteReferenceEvent {
    constructor(sessionId, resourceId, valueId, key, referenceType, values) {
        super(sessionId, resourceId, valueId, key);
        this.referenceType = referenceType;
        this.values = values;
    }
}
class RemoteReferenceUnshared extends RemoteReferenceEvent {
    constructor(sessionId, resourceId, valueId, key) {
        super(sessionId, resourceId, valueId, key);
    }
}
class RemoteReferenceSet extends RemoteReferenceEvent {
    constructor(sessionId, resourceId, valueId, key, values) {
        super(sessionId, resourceId, valueId, key);
        this.values = values;
    }
}
class RemoteReferenceCleared extends RemoteReferenceEvent {
    constructor(sessionId, resourceId, valueId, key) {
        super(sessionId, resourceId, valueId, key);
    }
}

class ReferenceManager {
    constructor(source, validTypes, onRemoteReference, identityCache) {
        this._referenceMap = new ReferenceMap();
        this._localReferences = new Map();
        this._validTypes = validTypes;
        this._source = source;
        this._onRemoteReference = onRemoteReference;
        this._identityCache = identityCache;
    }
    get(sessionId, key) {
        return this._referenceMap.get(sessionId, key);
    }
    getAll(filter) {
        return this._referenceMap.getAll(filter);
    }
    removeAll() {
        this.removeAllLocalReferences();
        this.getAll().forEach(ref => ref._dispose());
    }
    addLocalReference(reference) {
        const key = reference.reference().key();
        if (this._localReferences.has(key)) {
            throw new Error(`Local reference already set for key: ${key}`);
        }
        this._localReferences.set(key, reference);
        this._referenceMap.put(reference.reference());
    }
    removeLocalReference(key) {
        const current = this._localReferences.get(key);
        if (current !== undefined) {
            current._dispose();
        }
    }
    removeAllLocalReferences() {
        this._localReferences.forEach((reference, key) => {
            this.removeLocalReference(key);
        });
    }
    getLocalReference(key) {
        return this._localReferences.get(key);
    }
    handleRemoteReferenceEvent(event) {
        if (event instanceof RemoteReferenceShared) {
            this._handleRemoteReferenceShared(event);
        }
        else if (event instanceof RemoteReferenceSet) {
            this._handleRemoteReferenceSet(event);
        }
        else if (event instanceof RemoteReferenceCleared) {
            this._handleRemoteReferenceCleared(event);
        }
        else if (event instanceof RemoteReferenceUnshared) {
            this._handleRemoteReferenceUnshared(event);
        }
        else {
            throw new Error("Invalid reference event.");
        }
    }
    reShare() {
        this._localReferences.forEach(reference => {
            if (reference.isShared()) {
                reference.share();
            }
        });
    }
    _handleReferenceDisposed(reference) {
        this._referenceMap.remove(reference.sessionId(), reference.key());
        if (reference.isLocal()) {
            this._localReferences.delete(reference.key());
        }
    }
    _handleRemoteReferenceShared(event) {
        const user = this._identityCache.getUserForSession(event.sessionId);
        let reference;
        const values = event.values;
        this._assertValidType(event.referenceType);
        if (event.referenceType === "index") {
            reference = new IndexReference(this, event.key, this._source, user, event.sessionId, false);
        }
        else if (event.referenceType === "range") {
            reference = new RangeReference(this, event.key, this._source, user, event.sessionId, false);
        }
        else if (event.referenceType === "element") {
            reference = new ElementReference(this, event.key, this._source, user, event.sessionId, false);
        }
        else if (event.referenceType === "property") {
            reference = new PropertyReference(this, event.key, this._source, user, event.sessionId, false);
        }
        else {
            throw new ConvergenceError("Invalid reference type: " + event);
        }
        if (values !== null) {
            this._setReferenceValues(reference, values);
        }
        this._referenceMap.put(reference);
        this._onRemoteReference(reference);
    }
    _assertValidType(referenceType) {
        if (!this._validTypes.includes(referenceType)) {
            throw new Error(`Invalid reference type: ${referenceType}`);
        }
    }
    _handleRemoteReferenceUnshared(event) {
        const reference = this._referenceMap.remove(event.sessionId, event.key);
        reference._dispose();
    }
    _handleRemoteReferenceCleared(event) {
        const reference = this._referenceMap.get(event.sessionId, event.key);
        reference._clear();
    }
    _handleRemoteReferenceSet(event) {
        const reference = this._referenceMap.get(event.sessionId, event.key);
        this._setReferenceValues(reference, event.values);
    }
    _setReferenceValues(reference, values) {
        if (reference.type() === ModelReference.Types.ELEMENT) {
            const rtvs = [];
            for (const id of values) {
                const value = this._source._getRegisteredValue(id);
                if (value !== undefined) {
                    rtvs.push(value);
                }
            }
            reference._set(rtvs, false);
        }
        else {
            reference._set(values, false);
        }
    }
}

const ObservableElementEventConstants = {
    VALUE: "value",
    DETACHED: "detached",
    REFERENCE: "reference",
    MODEL_CHANGED: "model_changed"
};
Object.freeze(ObservableElementEventConstants);

class RealTimeElement extends ConvergenceEventEmitter {
    constructor(delegate, callbacks, wrapperFactory, model, referenceTypes, identityCache) {
        super();
        this._delegate = delegate;
        this._callbacks = callbacks;
        this._wrapperFactory = wrapperFactory;
        this._model = model;
        const onRemoteReference = (ref) => {
            this._fireReferenceCreated(ref);
        };
        this._referenceManager = new ReferenceManager(this, referenceTypes, onRemoteReference, identityCache);
        this._delegate.events().pipe(filter(event => {
            return this._model.emitLocalEvents() || !event.local || event instanceof NodeDetachedEvent;
        })).subscribe(event => {
            if (event instanceof NodeDetachedEvent) {
                this._referenceManager.removeAll();
            }
            const convertedEvent = ModelEventConverter.convertEvent(event, this._wrapperFactory);
            this._emitEvent(convertedEvent);
        });
    }
    model() {
        return this._model;
    }
    id() {
        return this._delegate.id();
    }
    type() {
        return this._delegate.type();
    }
    path() {
        return this._delegate.path();
    }
    parent() {
        const parentPath = this._delegate.path().slice(0);
        parentPath.pop();
        const parent = this._model.elementAt(parentPath);
        return parent;
    }
    relativePath() {
        const parentPath = this._delegate.path().slice(0);
        if (parentPath.length > 0) {
            return parentPath.pop();
        }
        else {
            return null;
        }
    }
    removeFromParent() {
        this._assertWritable();
        const parentPath = this._delegate.path().slice(0);
        if (parentPath.length === 0) {
            throw new Error("Can not remove the root object from the model");
        }
        const relPath = parentPath.pop();
        const parent = this._model.elementAt(parentPath);
        parent._removeChild(relPath);
    }
    isDetached() {
        return this._delegate.isDetached();
    }
    isAttached() {
        return !this._delegate.isDetached();
    }
    value(value) {
        if (arguments.length === 0) {
            return this._delegate.data();
        }
        else {
            this._assertWritable();
            this._delegate.data(value);
            return;
        }
    }
    toJSON() {
        return this._delegate.toJson();
    }
    reference(sessionId, key) {
        return this._referenceManager.get(sessionId, key);
    }
    references(referenceFilter) {
        return this._referenceManager.getAll(referenceFilter);
    }
    _handleRemoteReferenceEvent(event) {
        this._referenceManager.handleRemoteReferenceEvent(event);
    }
    _sendOperation(operation) {
        this._callbacks.sendOperationCallback(operation);
    }
    _assertWritable() {
        if (!this._model.permissions().write) {
            throw new Error("The user does not have write permissions for the model.");
        }
        this._assertAttached();
    }
    _assertAttached() {
        if (this.isDetached()) {
            throw Error("Can not perform actions on a detached RealTimeElement.");
        }
    }
    _fireReferenceCreated(reference) {
        this._emitEvent(new RemoteReferenceCreatedEvent(reference, this.model(), this));
    }
}
RealTimeElement.Events = ObservableElementEventConstants;
Object.freeze(RealTimeElement.Events);

class ElementReference extends ModelReference {
    constructor(referenceManager, key, source, user, sessionId, local) {
        super(referenceManager, ModelReference.Types.ELEMENT, key, source, user, sessionId, local);
        this._detachedListener = (event) => {
            this._handleElementRemoved(event.src);
        };
    }
    _set(values, synthetic) {
        for (const oldElement of this.values()) {
            oldElement.removeListener(RealTimeElement.Events.DETACHED, this._detachedListener);
        }
        for (const newElement of values) {
            newElement.addListener(RealTimeElement.Events.DETACHED, this._detachedListener);
        }
        super._set(values, synthetic);
    }
    _clear() {
        for (const oldElement of this.values()) {
            oldElement.removeListener(RealTimeElement.Events.DETACHED, this._detachedListener);
        }
        super._clear();
    }
    _handleElementRemoved(element) {
        const index = this._values.indexOf(element, 0);
        if (index > -1) {
            const newElements = this._values.slice(0);
            newElements.splice(index, 1);
            this._set(newElements, true);
        }
    }
}

class ArrayReplaceOperation extends DiscreteOperation {
    constructor(id, noOp, index, value) {
        super(OperationType.ARRAY_SET, id, noOp);
        this.index = index;
        this.value = value;
        Object.freeze(this);
    }
    copy(updates) {
        return new ArrayReplaceOperation(Immutable.update(this.id, updates.id), Immutable.update(this.noOp, updates.noOp), Immutable.update(this.index, updates.index), Immutable.update(this.value, updates.value));
    }
}

class ArrayRemoveOperation extends DiscreteOperation {
    constructor(id, noOp, index) {
        super(OperationType.ARRAY_REMOVE, id, noOp);
        this.index = index;
        Object.freeze(this);
    }
    copy(updates) {
        return new ArrayRemoveOperation(Immutable.update(this.id, updates.id), Immutable.update(this.noOp, updates.noOp), Immutable.update(this.index, updates.index));
    }
}

class ArrayMoveOperation extends DiscreteOperation {
    constructor(id, noOp, fromIndex, toIndex) {
        super(OperationType.ARRAY_REORDER, id, noOp);
        this.fromIndex = fromIndex;
        this.toIndex = toIndex;
        Object.freeze(this);
    }
    copy(updates) {
        return new ArrayMoveOperation(Immutable.update(this.id, updates.id), Immutable.update(this.noOp, updates.noOp), Immutable.update(this.fromIndex, updates.fromIndex), Immutable.update(this.toIndex, updates.toIndex));
    }
}

class ArraySetOperation extends DiscreteOperation {
    constructor(id, noOp, value) {
        super(OperationType.ARRAY_VALUE, id, noOp);
        this.value = value;
        Object.freeze(this);
    }
    copy(updates) {
        return new ArraySetOperation(Immutable.update(this.id, updates.id), Immutable.update(this.noOp, updates.noOp), Immutable.update(this.value, updates.value));
    }
}

const ObservableArrayEventConstants = Object.assign(Object.assign({}, ObservableElementEventConstants), { INSERT: "insert", REMOVE: "remove", SET: "set", REORDER: "reorder" });
Object.freeze(ObservableArrayEventConstants);

class RealTimeArray extends RealTimeElement {
    constructor(delegate, callbacks, wrapperFactory, model, identityCache) {
        super(delegate, callbacks, wrapperFactory, model, [], identityCache);
        this._delegate.events().subscribe((event) => {
            if (event.local) {
                if (event instanceof ArrayNodeInsertEvent) {
                    this._sendOperation(new ArrayInsertOperation(this.id(), false, event.index, event.src.get(event.index).dataValue()));
                }
                else if (event instanceof ArrayNodeRemoveEvent) {
                    this._sendOperation(new ArrayRemoveOperation(this.id(), false, event.index));
                }
                else if (event instanceof ArrayNodeReorderEvent) {
                    this._sendOperation(new ArrayMoveOperation(this.id(), false, event.fromIndex, event.toIndex));
                }
                else if (event instanceof ArrayNodeSetEvent) {
                    const index = event.index;
                    this._sendOperation(new ArrayReplaceOperation(this.id(), false, index, event.src.get(index).dataValue()));
                }
                else if (event instanceof ArrayNodeSetValueEvent) {
                    this._sendOperation(new ArraySetOperation(this.id(), false, event.src.dataValue().value));
                }
            }
        });
    }
    get(index) {
        return this._wrapperFactory.wrap(this._delegate.get(index));
    }
    set(index, value) {
        this._assertWritable();
        return this._wrapperFactory.wrap(this._delegate.set(index, value));
    }
    insert(index, value) {
        this._assertWritable();
        return this._wrapperFactory.wrap(this._delegate.insert(index, value));
    }
    remove(index) {
        this._assertWritable();
        return this._wrapperFactory.wrap(this._delegate.remove(index));
    }
    reorder(fromIndex, toIndex) {
        this._assertWritable();
        this._delegate.reorder(fromIndex, toIndex);
    }
    push(value) {
        this._assertWritable();
        return this._wrapperFactory.wrap(this._delegate.push(value));
    }
    pop() {
        this._assertWritable();
        return this._wrapperFactory.wrap(this._delegate.pop());
    }
    unshift(value) {
        this._assertWritable();
        return this._wrapperFactory.wrap(this._delegate.unshift(value));
    }
    shift() {
        this._assertWritable();
        return this._wrapperFactory.wrap(this._delegate.shift());
    }
    length() {
        return this._delegate.length();
    }
    some(callback) {
        return this._delegate.some((modelNode, index) => {
            return callback(this._wrapperFactory.wrap(modelNode), index);
        });
    }
    every(callback) {
        return this._delegate.every((modelNode, index) => {
            return callback(this._wrapperFactory.wrap(modelNode), index);
        });
    }
    find(callback) {
        const node = this._delegate.find((modelNode, index) => {
            return callback(this._wrapperFactory.wrap(modelNode), index);
        });
        if (node === undefined) {
            return undefined;
        }
        else {
            return this._wrapperFactory.wrap(node);
        }
    }
    findIndex(callback) {
        return this._delegate.findIndex((modelNode, index) => {
            return callback(this._wrapperFactory.wrap(modelNode), index);
        });
    }
    forEach(callback) {
        this._delegate.forEach((modelNode, index) => {
            callback(this._wrapperFactory.wrap(modelNode), index);
        });
    }
    elementAt(...path) {
        return this._wrapperFactory.wrap(this._delegate.valueAt(...path));
    }
    _removeChild(relPath) {
        if (typeof relPath !== "number") {
            throw new Error("The relative path of a child must be a number: " + (typeof relPath));
        }
        this.remove(relPath);
    }
    _handleRemoteReferenceEvent(event) {
        throw new Error("Arrays to do have references yet.");
    }
}
RealTimeArray.Events = ObservableArrayEventConstants;

class BooleanSetOperation extends DiscreteOperation {
    constructor(id, noOp, value) {
        super(OperationType.BOOLEAN_VALUE, id, noOp);
        this.value = value;
        Object.freeze(this);
    }
    copy(updates) {
        return new BooleanSetOperation(Immutable.update(this.id, updates.id), Immutable.update(this.noOp, updates.noOp), Immutable.update(this.value, updates.value));
    }
}

class RealTimeBoolean extends RealTimeElement {
    constructor(delegate, callbacks, wrapperFactory, model, identityCache) {
        super(delegate, callbacks, wrapperFactory, model, [], identityCache);
        this._delegate.events().subscribe((event) => {
            if (event.local) {
                if (event instanceof BooleanNodeSetValueEvent) {
                    this._sendOperation(new BooleanSetOperation(this.id(), false, event.value));
                }
            }
        });
    }
    _handleRemoteReferenceEvent(event) {
        throw new Error("Boolean values do not process references");
    }
}
RealTimeBoolean.Events = ObservableElementEventConstants;

class DataValueFactory {
    constructor(_idGenerator, _undefinedObjectValues, _undefinedArrayValues) {
        this._idGenerator = _idGenerator;
        this._undefinedObjectValues = _undefinedObjectValues;
        this._undefinedArrayValues = _undefinedArrayValues;
    }
    createDataValue(data) {
        const id = this._idGenerator();
        if (TypeChecker.isNull(data)) {
            return { id, type: "null", value: null };
        }
        else if (TypeChecker.isString(data)) {
            return { id, type: "string", value: data };
        }
        else if (TypeChecker.isDate(data)) {
            return { id, type: "date", value: data };
        }
        else if (TypeChecker.isArray(data)) {
            const list = data.map((child) => {
                if (child === undefined) {
                    if (this._undefinedArrayValues === "null") {
                        return this.createDataValue(null);
                    }
                    else {
                        throw new ConvergenceError("Found an undefined value within an array. " +
                            "To convert undefined values in arrays to null, set the " +
                            "options.model.data.undefinedArrayValues option to 'null'");
                    }
                }
                else {
                    return this.createDataValue(child);
                }
            });
            return { id, type: "array", value: list };
        }
        else if (TypeChecker.isObject(data)) {
            if (data.hasOwnProperty("$convergenceType")) {
                const convergenceType = data["$convergenceType"];
                if (convergenceType === "date") {
                    if (data.hasOwnProperty("value")) {
                        return { id, type: "date", value: new Date(data["value"]) };
                    }
                    else {
                        throw new ConvergenceError("Invalid convergence data type 'data': The value field missing.");
                    }
                }
                else {
                    throw new ConvergenceError("Invalid convergence data type: " + convergenceType + " supported types are [date].");
                }
            }
            else {
                const props = {};
                Object.getOwnPropertyNames(data).forEach((prop) => {
                    const child = data[prop];
                    if (child === undefined) {
                        if (this._undefinedObjectValues !== "omit") {
                            throw new ConvergenceError("Found an undefined value within an object. " +
                                "To simply omit undefined values within objects, set the " +
                                "options.model.data.undefinedArrayValues " +
                                "option to 'omit'");
                        }
                    }
                    else {
                        props[prop] = this.createDataValue(child);
                    }
                });
                return { id, type: "object", value: props };
            }
        }
        else if (TypeChecker.isNumber(data)) {
            return { id, type: "number", value: data };
        }
        else if (TypeChecker.isBoolean(data)) {
            return { id, type: "boolean", value: data };
        }
        else {
            throw new ConvergenceError("Unsupported data type found within model data: " + typeof data);
        }
    }
}

class ModelElementType {
}
ModelElementType.OBJECT = "object";
ModelElementType.ARRAY = "array";
ModelElementType.STRING = "string";
ModelElementType.NUMBER = "number";
ModelElementType.BOOLEAN = "boolean";
ModelElementType.NULL = "null";
ModelElementType.UNDEFINED = "undefined";
ModelElementType.DATE = "date";

class ModelNode extends ConvergenceEventEmitter {
    constructor(modelType, id, path, model, session) {
        super();
        this._id = id;
        this._session = session;
        this._modelType = modelType;
        this._model = model;
        this._path = path;
        if (this._model) {
            this._model._registerValue(this);
        }
    }
    session() {
        return this._session;
    }
    id() {
        return this._id;
    }
    type() {
        return this._modelType;
    }
    path() {
        return this._path();
    }
    model() {
        return this._model;
    }
    isDetached() {
        return this._model === null;
    }
    _detach(local) {
        this._model._unregisterValue(this);
        this._model = null;
        const event = new NodeDetachedEvent(this, local);
        this._emitEvent(event);
    }
    data(value) {
        if (arguments.length === 0) {
            return this._getData();
        }
        else {
            this._setData(value);
            return;
        }
    }
    _emitValueEvent(event) {
        this._emitEvent(event);
        this._emitEvent(new NodeChangedEvent(this, event.local, [], event, this._session.sessionId(), this._session.user()));
    }
    _exceptionIfDetached() {
        if (this.isDetached()) {
            throw Error("Can not perform actions on a detached ModelNode.");
        }
    }
}
ModelNode.Events = {
    DETACHED: "detached",
    NODE_CHANGED: "node_changed",
    OPERATION: "operation"
};

class UndefinedNode extends ModelNode {
    constructor(id, path, session) {
        super(ModelElementType.UNDEFINED, id, path, undefined, session);
    }
    dataValue() {
        return undefined;
    }
    toJson() {
        return undefined;
    }
    _handleModelOperationEvent(operationEvent) {
        throw new Error("Undefined values do not process operations");
    }
    _getData() {
        return undefined;
    }
    _setData(data) {
        throw new Error("Can not set the delta on a Undefined type.");
    }
}
UndefinedNode.Events = {
    DETACHED: ModelNode.Events.DETACHED
};

class NullNode extends ModelNode {
    constructor(id, path, model, session) {
        super(ModelElementType.NULL, id, path, model, session);
    }
    dataValue() {
        return {
            id: this.id(),
            type: "null",
            value: this.data()
        };
    }
    toJson() {
        return null;
    }
    _handleModelOperationEvent(operationEvent) {
        throw new Error("Null values do not process operations");
    }
    _getData() {
        return null;
    }
    _setData(data) {
        throw new Error("Can not set the delta on a Null type.");
    }
}
NullNode.Events = {
    DETACHED: ModelNode.Events.DETACHED
};

class StringNode extends ModelNode {
    constructor(data, path, model, session) {
        super(ModelElementType.STRING, data.id, path, model, session);
        this._data = data.value;
    }
    dataValue() {
        return {
            id: this.id(),
            type: "string",
            value: this.data()
        };
    }
    toJson() {
        return this._data;
    }
    splice(index, deleteCount, inserValue) {
        this._applySplice(index, deleteCount, inserValue, true, this._session.sessionId(), this._session.user());
    }
    insert(index, value) {
        this._applySplice(index, 0, value, true, this._session.sessionId(), this._session.user());
    }
    remove(index, length) {
        this._applySplice(index, length, "", true, this._session.sessionId(), this._session.user());
    }
    length() {
        return this._data.length;
    }
    _handleModelOperationEvent(operationEvent) {
        const type = operationEvent.operation.type;
        if (type === OperationType.STRING_SPLICE) {
            this._handleSpliceOperation(operationEvent);
        }
        else if (type === OperationType.STRING_VALUE) {
            this._handleSetOperation(operationEvent);
        }
        else {
            throw new Error("Invalid operation type for a String: " + operationEvent.operation.type);
        }
    }
    _setData(data) {
        this._applySetValue(data, true, this._session.sessionId(), this._session.user());
    }
    _getData() {
        return this._data;
    }
    _applySplice(index, deleteCount, insertValue, local, sessionId, user) {
        Validation.assertValidStringIndex(index, this._data, true, "index");
        Validation.assertValidStringIndex(index + deleteCount, this._data, true);
        const removedVal = this._data.slice(index, index + deleteCount);
        this._data = this._data.slice(0, index) + insertValue + this._data.slice(index + deleteCount, this._data.length);
        if (deleteCount > 0 && insertValue.length === 0) {
            const event = new StringNodeRemoveEvent(this, local, index, removedVal, sessionId, user);
            this._emitValueEvent(event);
        }
        else if (deleteCount === 0 && insertValue.length > 0) {
            const event = new StringNodeInsertEvent(this, local, index, insertValue, sessionId, user);
            this._emitValueEvent(event);
        }
        else if (deleteCount > 0 && insertValue.length > 0) {
            const event = new StringNodeSpliceEvent(this, local, index, deleteCount, insertValue, sessionId, user);
            this._emitValueEvent(event);
        }
        else ;
    }
    _applySetValue(value, local, sessionId, user) {
        Validation.assertString(value);
        this._data = value;
        const event = new StringNodeSetValueEvent(this, local, value, sessionId, user);
        this._emitValueEvent(event);
    }
    _handleSpliceOperation(operationEvent) {
        const operation = operationEvent.operation;
        this._applySplice(operation.index, operation.deleteCount, operation.insertValue, false, operationEvent.sessionId, operationEvent.user);
    }
    _handleSetOperation(operationEvent) {
        const operation = operationEvent.operation;
        this._applySetValue(operation.value, false, operationEvent.sessionId, operationEvent.user);
    }
}
StringNode.Events = {
    INSERT: "insert",
    REMOVE: "remove",
    VALUE: "value",
    DETACHED: ModelNode.Events.DETACHED,
    MODEL_CHANGED: ModelNode.Events.MODEL_CHANGED
};

class ContainerNode extends ModelNode {
    constructor(modelType, id, path, model, session) {
        super(modelType, id, path, model, session);
        this._nodeChangedHandler = (event) => {
            const newPath = event.relativePath.slice(0);
            newPath.unshift(this._idToPathElement.get(event.src.id()));
            const newEvent = new NodeChangedEvent(this, event.local, newPath, event.childEvent, this._session.sessionId(), this._session.user());
            this._emitEvent(newEvent);
        };
        this._idToPathElement = new Map();
    }
    valueAt(...path) {
        const resolved = (path.length === 1 && Array.isArray(path[0])) ?
            path[0] :
            path;
        if (path.length === 0) {
            return this;
        }
        return this._valueAt(resolved);
    }
    _detach(local) {
        this._detachChildren(local);
        super._detach(local);
    }
    _createUndefinedNode() {
        return ModelNodeFactory.create(undefined, () => null, undefined, this._session);
    }
}
ContainerNode.Events = {
    NODE_CHANGED: ModelNode.Events.NODE_CHANGED
};

class ArrayNode extends ContainerNode {
    constructor(data, path, model, session, dataValueFactory) {
        super(ModelElementType.ARRAY, data.id, path, model, session);
        this._children = [];
        this._dataValueFactory = dataValueFactory;
        for (let i = 0; i < data.value.length; i++) {
            const child = data.value[i];
            this._idToPathElement.set(child.id, i);
            this._children.push(ModelNodeFactory.create(child, this._pathCB(child.id), model, session, dataValueFactory));
        }
        this._children.forEach((child) => {
            child.on(ArrayNode.Events.NODE_CHANGED, this._nodeChangedHandler);
        });
    }
    dataValue() {
        const values = this._children.map((node) => {
            return node.dataValue();
        });
        return {
            id: this.id(),
            type: "array",
            value: values
        };
    }
    toJson() {
        const jsonArray = [];
        this.forEach(node => {
            jsonArray.push(node.toJson());
        });
        return jsonArray;
    }
    get(index) {
        this._validateIndex(index, false);
        return this._children[index];
    }
    set(index, value) {
        this._validateIndex(index, false);
        const dataValue = this._dataValueFactory.createDataValue(value);
        this._applySet(index, dataValue, true, this._session.sessionId(), this._session.user());
        return this.get(index);
    }
    insert(index, value) {
        this._validateIndex(index, true);
        const dataValue = this._dataValueFactory.createDataValue(value);
        this._applyInsert(index, dataValue, true, this._session.sessionId(), this._session.user());
        return this.get(index);
    }
    remove(index) {
        this._validateIndex(index, false);
        const oldValue = this.get(index);
        this._applyRemove(index, true, this._session.sessionId(), this._session.user());
        return oldValue;
    }
    reorder(fromIndex, toIndex) {
        this._validateIndex(fromIndex, false, "fromIndex");
        this._validateIndex(toIndex, false, "toIndex");
        this._applyReorder(fromIndex, toIndex, true, this._session.sessionId(), this._session.user());
    }
    push(value) {
        return this.insert(this._children.length, value);
    }
    pop() {
        return this.remove(this._children.length - 1);
    }
    unshift(value) {
        return this.insert(0, value);
    }
    shift() {
        return this.remove(0);
    }
    length() {
        return this._children.length;
    }
    some(callback) {
        return this._children.some(callback);
    }
    every(callback) {
        return this._children.every(callback);
    }
    find(callback) {
        return this._children.find(callback);
    }
    findIndex(callback) {
        return this._children.findIndex(callback);
    }
    forEach(callback) {
        this._children.forEach(callback);
    }
    _handleModelOperationEvent(operationEvent) {
        const type = operationEvent.operation.type;
        if (type === OperationType.ARRAY_INSERT) {
            this._handleInsertOperation(operationEvent);
        }
        else if (type === OperationType.ARRAY_REORDER) {
            this._handleReorderOperation(operationEvent);
        }
        else if (type === OperationType.ARRAY_REMOVE) {
            this._handleRemoveOperation(operationEvent);
        }
        else if (type === OperationType.ARRAY_SET) {
            this._handleSetOperation(operationEvent);
        }
        else if (type === OperationType.ARRAY_VALUE) {
            this._handleSetValueOperation(operationEvent);
        }
        else {
            throw new Error("Invalid operation type for an Array: " + operationEvent.operation.type);
        }
    }
    _getData() {
        const returnVal = [];
        this.forEach((child) => {
            returnVal.push(child.data());
        });
        return returnVal;
    }
    _setData(data) {
        const dataValues = data.map((value) => {
            return this._dataValueFactory.createDataValue(value);
        });
        this._applySetValue(dataValues, true, this._session.sessionId(), this._session.user());
    }
    _detachChildren(local) {
        this._idToPathElement.clear();
        this.forEach((child) => {
            child._detach(local);
            child.removeListener(ArrayNode.Events.NODE_CHANGED, this._nodeChangedHandler);
        });
    }
    _valueAt(pathArgs) {
        if (pathArgs.length === 0) {
            return this;
        }
        const index = pathArgs[0];
        const child = this._children[index];
        if (child === undefined) {
            return this._createUndefinedNode();
        }
        if (pathArgs.length > 1) {
            if (child.type() === ModelElementType.OBJECT || child.type() === ModelElementType.ARRAY) {
                return child.valueAt(pathArgs.slice(1, pathArgs.length));
            }
            else {
                return this._createUndefinedNode();
            }
        }
        else {
            return child;
        }
    }
    _validateIndex(index, includeEnd, name) {
        if (index < 0) {
            const iName = name ? name : "index";
            throw new Error(`${iName} must be >= 0: ${index}`);
        }
        if ((!includeEnd && index >= this._children.length) || (includeEnd && index > this._children.length)) {
            const iName = name ? name : "index";
            const comparison = includeEnd ? "<=" : "<";
            throw new Error(`${iName} must be ${comparison} the length of the array: ${index}`);
        }
    }
    _applyInsert(index, value, local, sessionId, user) {
        this._validateInsert(index, value);
        this._idToPathElement.set(value.id, index);
        const child = ModelNodeFactory.create(value, this._pathCB(value.id), this._model, this._session, this._dataValueFactory);
        child.on(ArrayNode.Events.NODE_CHANGED, this._nodeChangedHandler);
        this._children.splice(index, 0, child);
        this._updateIdToPathElementMap(index);
        const event = new ArrayNodeInsertEvent(this, local, index, child, sessionId, user);
        this._emitValueEvent(event);
    }
    _applySet(index, value, local, sessionId, user) {
        this._validateReplace(index, value);
        const oldChild = this._valueAt([index]);
        if (oldChild.type() !== ModelElementType.UNDEFINED) {
            oldChild.removeListener(ArrayNode.Events.NODE_CHANGED, this._nodeChangedHandler);
            oldChild._detach(local);
        }
        this._idToPathElement.set(value.id, index);
        const newChild = ModelNodeFactory.create(value, this._pathCB(value.id), this.model(), this._session, this._dataValueFactory);
        newChild.on(ArrayNode.Events.NODE_CHANGED, this._nodeChangedHandler);
        this._children[index] = newChild;
        this._updateIdToPathElementMap(index);
        const event = new ArrayNodeSetEvent(this, local, index, newChild, oldChild, sessionId, user);
        this._emitValueEvent(event);
    }
    _applyRemove(index, local, sessionId, user) {
        this._validateRemove(index);
        const oldChild = this._valueAt([index]);
        if (oldChild.type() !== ModelElementType.UNDEFINED) {
            oldChild.removeListener(ArrayNode.Events.NODE_CHANGED, this._nodeChangedHandler);
            oldChild._detach(local);
        }
        this._children.splice(index, 1);
        this._updateIdToPathElementMap(index);
        const event = new ArrayNodeRemoveEvent(this, local, index, oldChild, sessionId, user);
        this._emitValueEvent(event);
    }
    _applySetValue(data, local, sessionId, user) {
        Validation.assertArray(data, "data");
        this._detachChildren(local);
        this._children = data.map((value, i) => {
            this._idToPathElement.set(value.id, i);
            return ModelNodeFactory.create(value, this._pathCB(value.id), this.model(), this._session, this._dataValueFactory);
        });
        this._children.forEach((child) => {
            child.on(ArrayNode.Events.NODE_CHANGED, this._nodeChangedHandler);
        });
        const event = new ArrayNodeSetValueEvent(this, local, this.data(), sessionId, user);
        this._emitValueEvent(event);
    }
    _applyReorder(fromIndex, toIndex, local, sessionId, user) {
        this._validateMove(fromIndex, toIndex);
        const child = this._children[fromIndex];
        this._children.splice(fromIndex, 1);
        this._children.splice(toIndex, 0, child);
        this._updateIdToPathElementMap(Math.min(fromIndex, toIndex));
        const event = new ArrayNodeReorderEvent(this, local, fromIndex, toIndex, sessionId, user);
        this._emitValueEvent(event);
    }
    _handleInsertOperation(operationEvent) {
        const operation = operationEvent.operation;
        this._applyInsert(operation.index, operation.value, false, operationEvent.sessionId, operationEvent.user);
    }
    _handleReorderOperation(operationEvent) {
        const operation = operationEvent.operation;
        this._applyReorder(operation.fromIndex, operation.toIndex, false, operationEvent.sessionId, operationEvent.user);
    }
    _handleRemoveOperation(operationEvent) {
        const operation = operationEvent.operation;
        this._applyRemove(operation.index, false, operationEvent.sessionId, operationEvent.user);
    }
    _handleSetOperation(operationEvent) {
        const operation = operationEvent.operation;
        this._applySet(operation.index, operation.value, false, operationEvent.sessionId, operationEvent.user);
    }
    _handleSetValueOperation(operationEvent) {
        const operation = operationEvent.operation;
        this._applySetValue(operation.value, false, operationEvent.sessionId, operationEvent.user);
    }
    _pathCB(id) {
        return () => {
            const path = this.path();
            path.push(this._idToPathElement.get(id));
            return path;
        };
    }
    _validateInsert(index, value) {
        if (this._children.length < index || index < 0) {
            throw new Error("Index out of bounds!");
        }
        if (typeof value === "undefined" || typeof value === "function") {
            throw new Error("Invalid value for insert!");
        }
    }
    _validateMove(fromIndex, toIndex) {
        if (this._children.length <= fromIndex || fromIndex < 0 || this._children.length <= toIndex || toIndex < 0) {
            throw new Error("Index out of bounds!");
        }
    }
    _validateRemove(index) {
        if (this._children.length <= index || index < 0) {
            throw new Error("Index out of bounds!");
        }
    }
    _validateReplace(index, value) {
        if (this._children.length <= index || index < 0) {
            throw new Error("Index out of bounds!");
        }
        if (typeof value === "undefined" || typeof value === "function") {
            throw new Error("Illegal argument!");
        }
    }
    _updateIdToPathElementMap(start) {
        for (let i = start; i < this._children.length; i++) {
            const child = this._children[i];
            this._idToPathElement.set(child.id(), i);
        }
    }
}
ArrayNode.Events = {
    INSERT: "insert",
    REMOVE: "remove",
    SET: "set",
    REORDER: "reorder",
    VALUE: "value",
    DETACHED: ModelNode.Events.DETACHED,
    NODE_CHANGED: ContainerNode.Events.NODE_CHANGED
};

class NumberNode extends ModelNode {
    constructor(data, path, model, session) {
        super(ModelElementType.NUMBER, data.id, path, model, session);
        this._data = data.value;
    }
    dataValue() {
        return {
            id: this.id(),
            type: "number",
            value: this.data()
        };
    }
    toJson() {
        return this._data;
    }
    add(value) {
        this._applyDelta(value, true, this._session.sessionId(), this._session.user());
    }
    subtract(value) {
        this.add(-value);
    }
    increment() {
        this.add(1);
    }
    decrement() {
        this.add(-1);
    }
    _handleModelOperationEvent(operationEvent) {
        const type = operationEvent.operation.type;
        if (type === OperationType.NUMBER_DELTA) {
            this._handleAddOperation(operationEvent);
        }
        else if (type === OperationType.NUMBER_VALUE) {
            this._handleSetOperation(operationEvent);
        }
        else {
            throw new Error("Invalid operation type for a Number: " + operationEvent.operation.type);
        }
    }
    _setData(data) {
        this._applySet(data, true, this._session.sessionId(), this._session.user());
    }
    _getData() {
        return this._data;
    }
    _applyDelta(delta, local, sessionId, user) {
        this._validateNumber(delta);
        if (delta !== 0) {
            this._data += delta;
            const event = new NumberNodeDeltaEvent(this, local, delta, sessionId, user);
            this._emitValueEvent(event);
        }
    }
    _applySet(value, local, sessionId, user) {
        this._validateNumber(value);
        this._data = value;
        const event = new NumberNodeSetValueEvent(this, local, value, sessionId, user);
        this._emitValueEvent(event);
    }
    _handleAddOperation(operationEvent) {
        const operation = operationEvent.operation;
        this._applyDelta(operation.delta, false, operationEvent.sessionId, operationEvent.user);
    }
    _handleSetOperation(operationEvent) {
        const operation = operationEvent.operation;
        this._applySet(operation.value, false, operationEvent.sessionId, operationEvent.user);
    }
    _validateNumber(value) {
        if (typeof value !== "number") {
            throw new Error(`The value must be a number but was: ${typeof value}`);
        }
        if (isNaN(value)) {
            throw new Error("The delta must not be NaN");
        }
    }
}
NumberNode.Events = {
    DELTA: "delta",
    VALUE: "value",
    DETACHED: ModelNode.Events.DETACHED,
    MODEL_CHANGED: ModelNode.Events.MODEL_CHANGED
};

class BooleanNode extends ModelNode {
    constructor(data, path, model, session) {
        super(ModelElementType.BOOLEAN, data.id, path, model, session);
        this._data = data.value;
    }
    dataValue() {
        return {
            id: this.id(),
            type: "boolean",
            value: this.data()
        };
    }
    toJson() {
        return this._data;
    }
    _handleModelOperationEvent(operationEvent) {
        const type = operationEvent.operation.type;
        if (type === OperationType.BOOLEAN_VALUE) {
            this._handleSetOperation(operationEvent);
        }
        else {
            throw new Error("Invalid operation type for a Boolean: " + operationEvent.operation.type);
        }
    }
    _setData(value) {
        this._applySetValue(value, true, this._session.sessionId(), this._session.user());
    }
    _getData() {
        return this._data;
    }
    _applySetValue(value, local, sessionId, user) {
        Validation.assertBoolean(value);
        this._data = value;
        const event = new BooleanNodeSetValueEvent(this, local, value, sessionId, user);
        this._emitValueEvent(event);
    }
    _handleSetOperation(operationEvent) {
        const operation = operationEvent.operation;
        this._applySetValue(operation.value, false, operationEvent.sessionId, operationEvent.user);
    }
}
BooleanNode.Events = {
    VALUE: "value",
    DETACHED: ModelNode.Events.DETACHED,
    MODEL_CHANGED: ModelNode.Events.MODEL_CHANGED
};

class DateNode extends ModelNode {
    constructor(data, path, model, session) {
        super(ModelElementType.DATE, data.id, path, model, session);
        this._data = data.value;
    }
    dataValue() {
        return {
            id: this.id(),
            type: "date",
            value: this.data()
        };
    }
    toJson() {
        return {
            $convergenceType: "date",
            value: this._data.toISOString()
        };
    }
    _handleModelOperationEvent(operationEvent) {
        const type = operationEvent.operation.type;
        if (type === OperationType.DATE_VALUE) {
            this._handleSetOperation(operationEvent);
        }
        else {
            throw new Error("Invalid operation type for a Date: " + operationEvent.operation.type);
        }
    }
    _setData(value) {
        this._applySetValue(value, true, this._session.sessionId(), this._session.user());
    }
    _getData() {
        return this._data;
    }
    _applySetValue(value, local, sessionId, user) {
        Validation.assertDate(value);
        this._data = value;
        const event = new DateNodeSetValueEvent(this, local, value, sessionId, user);
        this._emitValueEvent(event);
    }
    _handleSetOperation(operationEvent) {
        const operation = operationEvent.operation;
        this._applySetValue(operation.value, false, operationEvent.sessionId, operationEvent.user);
    }
}
DateNode.Events = {
    VALUE: "value",
    DETACHED: ModelNode.Events.DETACHED,
    MODEL_CHANGED: ModelNode.Events.MODEL_CHANGED
};

class ModelNodeFactory {
    static create(data, path, model, session, dataValueFactory) {
        if (data === undefined) {
            return new UndefinedNode(undefined, path, session);
        }
        const type = data.type;
        if (type === "null") {
            return new NullNode(data.id, path, model, session);
        }
        else if (type === "string") {
            return new StringNode(data, path, model, session);
        }
        else if (type === "array") {
            return new ArrayNode(data, path, model, session, dataValueFactory);
        }
        else if (type === "object") {
            return new ObjectNode(data, path, model, session, dataValueFactory);
        }
        else if (type === "number") {
            return new NumberNode(data, path, model, session);
        }
        else if (type === "boolean") {
            return new BooleanNode(data, path, model, session);
        }
        else if (type === "date") {
            return new DateNode(data, path, model, session);
        }
        else {
            throw new Error("Invalid data type: " + type);
        }
    }
}

class ObjectNode extends ContainerNode {
    constructor(data, path, model, session, dataValueFactory) {
        super(ModelElementType.OBJECT, data.id, path, model, session);
        this.dataValueFactory = dataValueFactory;
        this._children = new Map();
        Object.getOwnPropertyNames(data.value).forEach((prop) => {
            const child = data.value[prop];
            this._idToPathElement.set(child.id, prop);
            this._children.set(prop, ModelNodeFactory.create(child, this._pathCB(child.id), model, this._session, this.dataValueFactory));
        });
        this._children.forEach((child) => {
            child.on(ObjectNode.Events.NODE_CHANGED, this._nodeChangedHandler);
        });
    }
    dataValue() {
        const values = {};
        this._children.forEach((value, key) => {
            values[key] = value.dataValue();
        });
        return {
            id: this.id(),
            type: "object",
            value: values
        };
    }
    toJson() {
        const jsonObject = {};
        this.forEach((node, key) => {
            jsonObject[key] = node.toJson();
        });
        return jsonObject;
    }
    get(key) {
        Validation.assertString(key, "key");
        return this._valueAt([key]);
    }
    set(key, value) {
        const dataValue = this.dataValueFactory.createDataValue(value);
        this._applySet(key, dataValue, true, this._session.sessionId(), this._session.user());
        return this.get(key);
    }
    remove(key) {
        const oldValue = this._valueAt([key]);
        this._applyRemove(key, true, this._session.sessionId(), this._session.user());
        return oldValue;
    }
    keys() {
        const keys = [];
        this._children.forEach((v, k) => {
            keys.push(k);
        });
        return keys;
    }
    hasKey(key) {
        return this._children.has(key);
    }
    forEach(callback) {
        this._children.forEach((value, key) => {
            callback(value, key);
        });
    }
    _handleModelOperationEvent(operationEvent) {
        switch (operationEvent.operation.type) {
            case OperationType.OBJECT_ADD:
                this._handleAddPropertyOperation(operationEvent);
                break;
            case OperationType.OBJECT_SET:
                this._handleSetPropertyOperation(operationEvent);
                break;
            case OperationType.OBJECT_REMOVE:
                this._handleRemovePropertyOperation(operationEvent);
                break;
            case OperationType.OBJECT_VALUE:
                this._handleSetOperation(operationEvent);
                break;
            default:
                throw new Error("Invalid operation type for an Object: " + operationEvent.operation.type);
        }
    }
    _getData() {
        const returnObject = {};
        this.forEach((model, key) => {
            returnObject[key] = model.data();
        });
        return returnObject;
    }
    _setData(data) {
        const values = {};
        for (const prop in data) {
            if (data.hasOwnProperty(prop)) {
                values[prop] = this.dataValueFactory.createDataValue(data[prop]);
            }
        }
        this._applySetValue(values, true, this._session.sessionId(), this._session.user());
    }
    _valueAt(pathArgs) {
        if (pathArgs.length === 0) {
            return this;
        }
        const prop = pathArgs[0];
        const child = this._children.get(prop);
        if (child === undefined) {
            return this._createUndefinedNode();
        }
        if (pathArgs.length > 1) {
            if (child.type() === ModelElementType.OBJECT || child.type() === ModelElementType.ARRAY) {
                return child.valueAt(pathArgs.slice(1, pathArgs.length));
            }
            else {
                return this._createUndefinedNode();
            }
        }
        else {
            return child;
        }
    }
    _detachChildren(local) {
        this.forEach((child) => {
            child._detach(local);
            child.removeListener(ObjectNode.Events.NODE_CHANGED, this._nodeChangedHandler);
        });
    }
    _pathCB(id) {
        return () => {
            const path = this.path();
            path.push(this._idToPathElement.get(id));
            return path;
        };
    }
    _applySet(key, value, local, sessionId, user) {
        Validation.assertString(key, "key");
        const oldValue = this._valueAt([key]);
        if (oldValue.type() !== ModelElementType.UNDEFINED) {
            oldValue.removeListener(ObjectNode.Events.NODE_CHANGED, this._nodeChangedHandler);
            oldValue._detach(local);
        }
        const child = ModelNodeFactory.create(value, this._pathCB(value.id), this._model, this._session, this.dataValueFactory);
        child.on(ObjectNode.Events.NODE_CHANGED, this._nodeChangedHandler);
        this._children.set(key, child);
        this._idToPathElement.set(child.id(), key);
        const event = new ObjectNodeSetEvent(this, local, key, child, oldValue, sessionId, user);
        this._emitValueEvent(event);
    }
    _applyRemove(key, local, sessionId, user) {
        Validation.assertString(key, "key");
        if (this._children.has(key)) {
            this._idToPathElement.delete(key);
            const oldChild = this._children.get(key);
            oldChild.removeListener(ObjectNode.Events.NODE_CHANGED, this._nodeChangedHandler);
            oldChild._detach(local);
            this._children.delete(key);
            const event = new ObjectNodeRemoveEvent(this, local, key, oldChild, sessionId, user);
            this._emitValueEvent(event);
        }
    }
    _applySetValue(values, local, sessionId, user) {
        this._detachChildren(local);
        this._children = new Map();
        for (const prop in values) {
            if (values.hasOwnProperty(prop)) {
                const dataValue = values[prop];
                this._idToPathElement.set(dataValue.id, prop);
                this._children.set(prop, ModelNodeFactory.create(dataValue, this._pathCB(dataValue.id), this.model(), this._session, this.dataValueFactory));
            }
        }
        this._children.forEach((child) => {
            child.on(ObjectNode.Events.NODE_CHANGED, this._nodeChangedHandler);
        });
        const event = new ObjectNodeSetValueEvent(this, local, this.data(), sessionId, user);
        this._emitValueEvent(event);
    }
    _handleAddPropertyOperation(operationEvent) {
        const operation = operationEvent.operation;
        this._applySet(operation.prop, operation.value, false, operationEvent.sessionId, operationEvent.user);
    }
    _handleSetPropertyOperation(operationEvent) {
        const operation = operationEvent.operation;
        this._applySet(operation.prop, operation.value, false, operationEvent.sessionId, operationEvent.user);
    }
    _handleRemovePropertyOperation(operationEvent) {
        const operation = operationEvent.operation;
        this._applyRemove(operation.prop, false, operationEvent.sessionId, operationEvent.user);
    }
    _handleSetOperation(operationEvent) {
        const operation = operationEvent.operation;
        this._applySetValue(operation.value, false, operationEvent.sessionId, operationEvent.user);
    }
}
ObjectNode.Events = {
    SET: "set",
    REMOVE: "remove",
    VALUE: "value",
    DETACHED: ModelNode.Events.DETACHED,
    NODE_CHANGED: ModelNode.Events.NODE_CHANGED,
    OPERATION: ModelNode.Events.OPERATION
};

const VALUE_SEPARATOR = ":";
class Model extends ConvergenceEventEmitter {
    constructor(session, valueIdPrefix, data, undefinedObjectValues, undefinedArrayValues) {
        super();
        this._session = session;
        this._valueIdPrefix = valueIdPrefix;
        this._idToValue = new Map();
        this._vidCounter = 0;
        const idGenerator = () => this._valueIdPrefix + VALUE_SEPARATOR + this._vidCounter++;
        const dataValueFactory = new DataValueFactory(idGenerator, undefinedObjectValues, undefinedArrayValues);
        this._data = new ObjectNode(data, () => [], this, session, dataValueFactory);
    }
    root() {
        return this._data;
    }
    valueAt(...path) {
        return this._data.valueAt(...path);
    }
    setValueIdPrefix(prefix) {
        this._valueIdPrefix = prefix;
    }
    _getRegisteredValue(id) {
        return this._idToValue.get(id);
    }
    _registerValue(value) {
        this._idToValue.set(value.id(), value);
    }
    _unregisterValue(value) {
        this._idToValue.delete(value.id());
    }
    handleModelOperationEvent(modelEvent) {
        const child = this._idToValue.get(modelEvent.operation.id);
        if (child) {
            child._handleModelOperationEvent(modelEvent);
        }
    }
    valueIdPrefix() {
        return this._valueIdPrefix;
    }
}
Model.Events = {
    DELETED: "deleted",
    MODIFIED: "modified"
};
Object.freeze(Model.Events);
var ModelForcedCloseReasonCodes;
(function (ModelForcedCloseReasonCodes) {
    ModelForcedCloseReasonCodes[ModelForcedCloseReasonCodes["UNKNOWN"] = 0] = "UNKNOWN";
    ModelForcedCloseReasonCodes[ModelForcedCloseReasonCodes["UNAUTHORIZED"] = 1] = "UNAUTHORIZED";
    ModelForcedCloseReasonCodes[ModelForcedCloseReasonCodes["DELETED"] = 2] = "DELETED";
    ModelForcedCloseReasonCodes[ModelForcedCloseReasonCodes["ERROR_APPLYING_OPERATION"] = 3] = "ERROR_APPLYING_OPERATION";
    ModelForcedCloseReasonCodes[ModelForcedCloseReasonCodes["INVALID_REFERENCE_EVENT"] = 4] = "INVALID_REFERENCE_EVENT";
    ModelForcedCloseReasonCodes[ModelForcedCloseReasonCodes["PERMISSION_ERROR"] = 5] = "PERMISSION_ERROR";
    ModelForcedCloseReasonCodes[ModelForcedCloseReasonCodes["UNEXPECTED_COMMITTED_VERSION"] = 6] = "UNEXPECTED_COMMITTED_VERSION";
})(ModelForcedCloseReasonCodes || (ModelForcedCloseReasonCodes = {}));

class ClientOperationEvent {
    constructor(sessionId, seqNo, contextVersion, timestamp, operation) {
        this.sessionId = sessionId;
        this.seqNo = seqNo;
        this.contextVersion = contextVersion;
        this.timestamp = timestamp;
        this.operation = operation;
        Object.freeze(this);
    }
    copy(mods) {
        mods = Immutable.getOrDefault(mods, {});
        return new ClientOperationEvent(Immutable.getOrDefault(mods.sessionId, this.sessionId), Immutable.getOrDefault(mods.seqNo, this.seqNo), Immutable.getOrDefault(mods.contextVersion, this.contextVersion), Immutable.getOrDefault(mods.timestamp, this.timestamp), Immutable.getOrDefault(mods.operation, this.operation));
    }
}

class ClientConcurrencyControl extends ConvergenceEventEmitter {
    constructor(sessionId, contextVersion, lastSequenceNumber, transformer, referenceTransformer) {
        super();
        this._sessionId = sessionId;
        this._lastSequenceNumber = lastSequenceNumber;
        this._contextVersion = contextVersion;
        this._unappliedOperations = [];
        this._inflightOperations = [];
        this._transformer = transformer;
        this._referenceTransformer = referenceTransformer;
        this._compoundOpInProgress = false;
        this._pendingCompoundOperation = [];
    }
    lastSequenceNumber() {
        return this._lastSequenceNumber;
    }
    setState(contextVersion, lastSequenceNumber, inFlight) {
        this._contextVersion = contextVersion;
        this._lastSequenceNumber = lastSequenceNumber;
        this._inflightOperations.length = 0;
        this._inflightOperations.push(...inFlight);
    }
    contextVersion() {
        return this._contextVersion;
    }
    isCommitted() {
        return !this.hasInflightOperation() || this._pendingCompoundOperation.length > 0;
    }
    hasInflightOperation() {
        return this._inflightOperations.length > 0;
    }
    firstInFlightOperation() {
        return this._inflightOperations[0];
    }
    getInFlightOperations() {
        return this._inflightOperations.slice(0);
    }
    hasNextIncomingOperation() {
        return this._unappliedOperations.length !== 0;
    }
    getNextIncomingOperation() {
        if (this._unappliedOperations.length === 0) {
            return null;
        }
        else {
            return this._unappliedOperations.shift();
        }
    }
    hasNextRemoteReference() {
        return this._unappliedOperations.length !== 0;
    }
    getNextRemoteReferenceSetEvent() {
        if (this._unappliedOperations.length === 0) {
            return null;
        }
        else {
            return this._unappliedOperations.shift();
        }
    }
    startBatchOperation() {
        if (this._compoundOpInProgress) {
            throw new Error("Batch operation already in progress.");
        }
        this._pendingCompoundOperation = [];
        this._compoundOpInProgress = true;
    }
    cancelBatchOperation() {
        if (!this._compoundOpInProgress) {
            throw new Error("Batch operation not in progress.");
        }
        if (this._pendingCompoundOperation.length !== 0) {
            throw new Error("Can not cancel a batch operation if operations have been issued.");
        }
        this._compoundOpInProgress = false;
    }
    completeBatchOperation() {
        if (!this._compoundOpInProgress) {
            throw new Error("Batch operation not in progress.");
        }
        this._compoundOpInProgress = false;
        if (this._pendingCompoundOperation.length === 0) {
            throw new Error("A Batch operation must have at least one operation.");
        }
        const compoundOp = new CompoundOperation(this._pendingCompoundOperation);
        this._pendingCompoundOperation = [];
        const seqNo = ++this._lastSequenceNumber;
        const outgoingOperation = new ClientOperationEvent(this._sessionId(), seqNo, this._contextVersion, new Date(), compoundOp);
        this._inflightOperations.push(outgoingOperation);
        return outgoingOperation;
    }
    batchSize() {
        return this._pendingCompoundOperation.length;
    }
    isBatchOperationInProgress() {
        return this._compoundOpInProgress;
    }
    processOutgoingOperation(operation) {
        if (this._compoundOpInProgress && !(operation instanceof DiscreteOperation)) {
            throw new Error("Can't process a compound operation that is in progress");
        }
        const outgoingOperation = this.transformOutgoing(this._unappliedOperations, operation);
        const wasCommitted = this._inflightOperations.length === 0 && this._pendingCompoundOperation.length === 0;
        let outgoingEvent = null;
        if (this._compoundOpInProgress) {
            this._pendingCompoundOperation.push(outgoingOperation);
        }
        else {
            outgoingEvent = new ClientOperationEvent(this._sessionId(), ++this._lastSequenceNumber, this._contextVersion, new Date(), outgoingOperation);
            this._inflightOperations.push(outgoingEvent);
        }
        if (wasCommitted) {
            const evt = {
                name: ClientConcurrencyControl.Events.COMMIT_STATE_CHANGED,
                committed: false
            };
            this._emitEvent(evt);
        }
        return outgoingEvent;
    }
    processOutgoingSetReference(r) {
        for (let i = 0; i < this._unappliedOperations.length && r; i++) {
            r = this._referenceTransformer.transform(this._unappliedOperations[i].operation, r);
        }
        return r;
    }
    dispose() {
    }
    processAcknowledgement(version, seqNo) {
        if (this._inflightOperations.length === 0) {
            throw new Error("Received an acknowledgment, but had no uncommitted operations.");
        }
        if (this._contextVersion !== version) {
            throw new Error(`Acknowledgement did not meet expected context version of ${this._contextVersion}: ${version}`);
        }
        const acked = this._inflightOperations.shift();
        if (seqNo !== undefined) {
            if (acked.seqNo !== seqNo) {
                throw new Error(`Acknowledgement did not meet expected sequence number of ${acked.seqNo}: ${seqNo}`);
            }
        }
        this._contextVersion++;
        if (this._inflightOperations.length === 0 && this._pendingCompoundOperation.length === 0) {
            const evt = {
                name: ClientConcurrencyControl.Events.COMMIT_STATE_CHANGED,
                committed: true
            };
            this._emitEvent(evt);
        }
        return acked;
    }
    processRemoteOperation(incomingOperation) {
        if (incomingOperation.version !== this._contextVersion) {
            throw new Error(`Invalid version of ${incomingOperation.version}, expected ${this._contextVersion}.`);
        }
        incomingOperation = this.transformInflightOperations(incomingOperation);
        incomingOperation = this.transformPendingCompoundOperations(incomingOperation);
        this._contextVersion++;
        this._unappliedOperations.push(incomingOperation);
    }
    processRemoteReferenceSet(r) {
        for (let i = 0; i < this._inflightOperations.length && r; i++) {
            r = this._referenceTransformer.transform(this._inflightOperations[i].operation, r);
        }
        return r;
    }
    transformInflightOperations(serverOp) {
        let sPrime = serverOp.operation;
        for (let i = 0; i < this._inflightOperations.length; i++) {
            const opEvent = this._inflightOperations[i];
            const opPair = this._transformer.transform(sPrime, opEvent.operation);
            sPrime = opPair.serverOp;
            this._inflightOperations[i] = opEvent.copy({ contextVersion: serverOp.version + 1, operation: opPair.clientOp });
        }
        return serverOp.copy({ operation: sPrime });
    }
    transformPendingCompoundOperations(serverOp) {
        let sPrime = serverOp.operation;
        for (let i = 0; i < this._pendingCompoundOperation.length; i++) {
            const op = this._pendingCompoundOperation[i];
            const opPair = this._transformer.transform(sPrime, op);
            sPrime = opPair.serverOp;
            this._pendingCompoundOperation[i] = opPair.clientOp;
        }
        return serverOp.copy({ operation: sPrime });
    }
    transformOutgoing(serverOps, clientOp) {
        let cPrime = clientOp;
        for (let i = 0; i < serverOps.length; i++) {
            const serverOp = serverOps[i];
            const opPair = this._transformer.transform(serverOp.operation, cPrime);
            serverOps[i] = serverOp.copy({ operation: opPair.serverOp });
            cPrime = opPair.clientOp;
        }
        return cPrime;
    }
}
ClientConcurrencyControl.Events = {
    COMMIT_STATE_CHANGED: "commit_state_changed"
};

class NodeWrapperFactory {
    constructor() {
        this._wrappers = new Map();
    }
    wrap(node) {
        let wrapper = this._wrappers.get(node.id());
        if (wrapper === undefined) {
            wrapper = this._createWrapper(node);
            this._wrappers.set(node.id(), wrapper);
            node.on(ModelNode.Events.DETACHED, () => {
                this._wrappers.delete(node.id());
            });
        }
        return wrapper;
    }
}

class ObjectRemovePropertyOperation extends DiscreteOperation {
    constructor(id, noOp, prop) {
        super(OperationType.OBJECT_REMOVE, id, noOp);
        this.prop = prop;
        Object.freeze(this);
    }
    copy(updates) {
        return new ObjectRemovePropertyOperation(Immutable.update(this.id, updates.id), Immutable.update(this.noOp, updates.noOp), Immutable.update(this.prop, updates.prop));
    }
}

class ObjectSetOperation extends DiscreteOperation {
    constructor(id, noOp, value) {
        super(OperationType.OBJECT_VALUE, id, noOp);
        this.value = value;
        Object.freeze(this);
    }
    copy(updates) {
        return new ObjectSetOperation(Immutable.update(this.id, updates.id), Immutable.update(this.noOp, updates.noOp), Immutable.update(this.value, updates.value));
    }
}

const ObservableObjectEventConstants = Object.assign({ SET: "set", REMOVE: "remove" }, ObservableElementEventConstants);
Object.freeze(ObservableObjectEventConstants);

class RealTimeObject extends RealTimeElement {
    constructor(delegate, callbacks, wrapperFactory, model, identityCache) {
        super(delegate, callbacks, wrapperFactory, model, [ModelReference.Types.PROPERTY], identityCache);
        this._delegate.events().subscribe(event => this._handleReferenceEvents(event));
    }
    get(key) {
        return this._wrapperFactory.wrap(this._delegate.get(key));
    }
    set(key, value) {
        this._assertWritable();
        const propSet = this._delegate.hasKey(key);
        const delegateChild = this._delegate.set(key, value);
        const operation = propSet ?
            new ObjectSetPropertyOperation(this.id(), false, key, delegateChild.dataValue()) :
            new ObjectAddPropertyOperation(this.id(), false, key, delegateChild.dataValue());
        this._sendOperation(operation);
        return this._wrapperFactory.wrap(delegateChild);
    }
    remove(key) {
        this._assertWritable();
        return this._wrapperFactory.wrap(this._delegate.remove(key));
    }
    keys() {
        return this._delegate.keys();
    }
    hasKey(key) {
        return this._delegate.hasKey(key);
    }
    forEach(callback) {
        this._delegate.forEach((modelNode, key) => {
            callback(this._wrapperFactory.wrap(modelNode), key);
        });
    }
    elementAt(...path) {
        return this._wrapperFactory.wrap(this._delegate.valueAt(...path));
    }
    propertyReference(key) {
        const existing = this._referenceManager.getLocalReference(key);
        if (existing !== undefined) {
            if (existing.reference().type() !== ModelReference.Types.PROPERTY) {
                throw new Error("A reference with this key already exists, but is not an index reference");
            }
            else {
                return existing;
            }
        }
        else {
            const reference = new PropertyReference(this._referenceManager, key, this, this._delegate.session().user(), this._delegate.session().sessionId(), true);
            const local = new LocalPropertyReference(reference, this._callbacks.referenceEventCallbacks);
            this._referenceManager.addLocalReference(local);
            return local;
        }
    }
    _removeChild(relPath) {
        if (typeof relPath !== "string") {
            throw new Error("The relative path of a child must be a string: " + (typeof relPath));
        }
        this.remove(relPath);
    }
    _handleReferenceEvents(event) {
        if (event instanceof ObjectNodeSetValueEvent) {
            if (event.local) {
                this._sendOperation(new ObjectSetOperation(this.id(), false, this._delegate.dataValue().value));
            }
            this._referenceManager.removeAll();
        }
        else if (event instanceof ObjectNodeRemoveEvent) {
            if (event.local) {
                this._sendOperation(new ObjectRemovePropertyOperation(this.id(), false, event.key));
            }
            this._referenceManager.getAll().forEach((ref) => {
                if (ref instanceof PropertyReference) {
                    ref._handlePropertyRemoved(event.key);
                }
            });
        }
        else if (event instanceof ObjectNodeSetEvent) {
            if (event.local) ;
            this._referenceManager.getAll().forEach((ref) => {
                if (ref instanceof PropertyReference) {
                    ref._handlePropertyRemoved(event.key);
                }
            });
        }
    }
}
RealTimeObject.Events = ObservableObjectEventConstants;

class RealTimeNull extends RealTimeElement {
    constructor(delegate, callbacks, wrapperFactory, model, identityCache) {
        super(delegate, callbacks, wrapperFactory, model, [], identityCache);
    }
    handleRemoteReferenceEvent(event) {
        throw new Error("Null values do not process references");
    }
}
RealTimeNull.Events = ObservableElementEventConstants;

class NumberSetOperation extends DiscreteOperation {
    constructor(id, noOp, value) {
        super(OperationType.NUMBER_VALUE, id, noOp);
        this.value = value;
        Object.freeze(this);
    }
    copy(updates) {
        return new NumberSetOperation(Immutable.update(this.id, updates.id), Immutable.update(this.noOp, updates.noOp), Immutable.update(this.value, updates.value));
    }
}

class NumberDeltaOperation extends DiscreteOperation {
    constructor(id, noOp, delta) {
        super(OperationType.NUMBER_DELTA, id, noOp);
        this.delta = delta;
        Object.freeze(this);
    }
    copy(updates) {
        return new NumberDeltaOperation(Immutable.update(this.id, updates.id), Immutable.update(this.noOp, updates.noOp), Immutable.update(this.delta, updates.delta));
    }
}

const ObservableNumberEventConstants = Object.assign({ DELTA: "delta" }, ObservableElementEventConstants);
Object.freeze(ObservableNumberEventConstants);

class RealTimeNumber extends RealTimeElement {
    constructor(delegate, callbacks, wrapperFactory, model, identityCache) {
        super(delegate, callbacks, wrapperFactory, model, [], identityCache);
        this._delegate.events().subscribe((event) => {
            if (event.local) {
                if (event instanceof NumberNodeSetValueEvent) {
                    this._sendOperation(new NumberSetOperation(this.id(), false, event.value));
                }
                else if (event instanceof NumberNodeDeltaEvent) {
                    this._sendOperation(new NumberDeltaOperation(this.id(), false, event.value));
                }
            }
        });
    }
    add(value) {
        this._assertWritable();
        this._delegate.add(value);
    }
    subtract(value) {
        this._assertWritable();
        this._delegate.subtract(value);
    }
    increment() {
        this._assertWritable();
        this._delegate.increment();
    }
    decrement() {
        this._assertWritable();
        this._delegate.decrement();
    }
    _handleRemoteReferenceEvent(event) {
        throw new Error("Number values do not process references");
    }
}
RealTimeNumber.Events = ObservableNumberEventConstants;

class StringSetOperation extends DiscreteOperation {
    constructor(id, noOp, value) {
        super(OperationType.STRING_VALUE, id, noOp);
        this.value = value;
        Object.freeze(this);
    }
    copy(updates) {
        return new StringSetOperation(Immutable.update(this.id, updates.id), Immutable.update(this.noOp, updates.noOp), Immutable.update(this.value, updates.value));
    }
}

const ObservableStringEventConstants = Object.assign({ INSERT: "insert", REMOVE: "remove", SPLICE: "splice" }, ObservableElementEventConstants);
Object.freeze(ObservableStringEventConstants);

class StringSpliceOperation extends DiscreteOperation {
    constructor(id, noOp, index, deleteCount, insertValue) {
        super(OperationType.STRING_SPLICE, id, noOp);
        this.index = index;
        this.deleteCount = deleteCount;
        this.insertValue = insertValue;
        Object.freeze(this);
    }
    copy(updates) {
        return new StringSpliceOperation(Immutable.update(this.id, updates.id), Immutable.update(this.noOp, updates.noOp), Immutable.update(this.index, updates.index), Immutable.update(this.deleteCount, updates.deleteCount), Immutable.update(this.insertValue, updates.insertValue));
    }
}

class RealTimeString extends RealTimeElement {
    constructor(delegate, callbacks, wrapperFactory, model, identityCache) {
        super(delegate, callbacks, wrapperFactory, model, [ModelReference.Types.INDEX, ModelReference.Types.RANGE], identityCache);
        this._delegate.events().subscribe(e => this._handleDelegateModelEvents(e));
    }
    insert(index, value) {
        this._assertWritable();
        this._string.insert(index, value);
    }
    remove(index, length) {
        this._assertWritable();
        this._string.remove(index, length);
    }
    splice(index, deleteCount, insertValue) {
        this._assertWritable();
        this._string.splice(index, deleteCount, insertValue);
    }
    length() {
        return this._string.length();
    }
    indexReference(key) {
        const existing = this._referenceManager.getLocalReference(key);
        if (existing !== undefined) {
            if (existing.reference().type() !== ModelReference.Types.INDEX) {
                throw new Error("A reference with this key already exists, but is not an index reference");
            }
            else {
                return existing;
            }
        }
        else {
            const reference = new IndexReference(this._referenceManager, key, this, this._delegate.session().user(), this._delegate.session().sessionId(), true);
            const local = new LocalIndexReference(reference, this._callbacks.referenceEventCallbacks);
            this._referenceManager.addLocalReference(local);
            return local;
        }
    }
    rangeReference(key) {
        const existing = this._referenceManager.getLocalReference(key);
        if (existing !== undefined) {
            if (existing.reference().type() !== ModelReference.Types.RANGE) {
                throw new Error("A reference with this key already exists, but is not a range reference");
            }
            else {
                return existing;
            }
        }
        else {
            const reference = new RangeReference(this._referenceManager, key, this, this._delegate.session().user(), this._delegate.session().sessionId(), true);
            const local = new LocalRangeReference(reference, this._callbacks.referenceEventCallbacks);
            this._referenceManager.addLocalReference(local);
            return local;
        }
    }
    _handleDelegateModelEvents(event) {
        if (event instanceof StringNodeInsertEvent) {
            if (event.local) {
                this._sendOperation(new StringSpliceOperation(this.id(), false, event.index, 0, event.value));
            }
            this._referenceManager.getAll().forEach((ref) => {
                if (ref instanceof IndexReference) {
                    ref._handleInsert(event.index, event.value.length);
                }
                else if (ref instanceof RangeReference) {
                    ref._handleInsert(event.index, event.value.length);
                }
            });
        }
        else if (event instanceof StringNodeRemoveEvent) {
            if (event.local) {
                this._sendOperation(new StringSpliceOperation(this.id(), false, event.index, event.value.length, ""));
            }
            this._referenceManager.getAll().forEach((ref) => {
                if (ref instanceof IndexReference) {
                    ref._handleRemove(event.index, event.value.length);
                }
                else if (ref instanceof RangeReference) {
                    ref._handleRemove(event.index, event.value.length);
                }
            });
        }
        else if (event instanceof StringNodeSpliceEvent) {
            if (event.local) {
                this._sendOperation(new StringSpliceOperation(this.id(), false, event.index, event.deleteCount, event.insertValue));
            }
            this._referenceManager.getAll().forEach((ref) => {
                if (ref instanceof IndexReference) {
                    ref._handleSplice(event.index, event.deleteCount, event.insertValue.length);
                }
                else if (ref instanceof RangeReference) {
                    ref._handleSplice(event.index, event.deleteCount, event.insertValue.length);
                }
            });
        }
        else if (event instanceof StringNodeSetValueEvent) {
            if (event.local) {
                this._sendOperation(new StringSetOperation(this.id(), false, event.value));
            }
            this._referenceManager.removeAll();
        }
    }
    get _string() {
        return this._delegate;
    }
}
RealTimeString.Events = ObservableStringEventConstants;
Object.freeze(RealTimeString.Events);

class RealTimeUndefined extends RealTimeElement {
    constructor(delegate, callbacks, wrapperFactory, model, identityCache) {
        super(delegate, callbacks, wrapperFactory, model, [], identityCache);
    }
    path() {
        return null;
    }
    relativePath() {
        return null;
    }
    parent() {
        return null;
    }
    removeFromParent() {
    }
    handleRemoteReferenceEvent(event) {
        throw new Error("Undefined values do not process references");
    }
    setData(data) {
        throw new Error("Can not set the delta on a Undefined type.");
    }
}
RealTimeUndefined.Events = ObservableElementEventConstants;

class DateSetOperation extends DiscreteOperation {
    constructor(id, noOp, value) {
        super(OperationType.DATE_VALUE, id, noOp);
        this.value = value;
        Object.freeze(this);
    }
    copy(updates) {
        return new DateSetOperation(Immutable.update(this.id, updates.id), Immutable.update(this.noOp, updates.noOp), Immutable.update(this.value, updates.value));
    }
}

class RealTimeDate extends RealTimeElement {
    constructor(delegate, callbacks, wrapperFactory, model, identityCache) {
        super(delegate, callbacks, wrapperFactory, model, [], identityCache);
        this._delegate.events().subscribe((event) => {
            if (event.local) {
                if (event instanceof DateNodeSetValueEvent) {
                    this._sendOperation(new DateSetOperation(this.id(), false, event.value));
                }
            }
        });
    }
    _handleRemoteReferenceEvent(event) {
        throw new Error("Date values do not process references");
    }
}
RealTimeDate.Events = ObservableElementEventConstants;

class RealTimeWrapperFactory extends NodeWrapperFactory {
    constructor(_callbacks, _model, _identityCache) {
        super();
        this._callbacks = _callbacks;
        this._model = _model;
        this._identityCache = _identityCache;
    }
    _createWrapper(node) {
        switch (node.type()) {
            case ModelElementType.ARRAY:
                return new RealTimeArray(node, this._callbacks, this, this._model, this._identityCache);
            case ModelElementType.OBJECT:
                return new RealTimeObject(node, this._callbacks, this, this._model, this._identityCache);
            case ModelElementType.BOOLEAN:
                return new RealTimeBoolean(node, this._callbacks, this, this._model, this._identityCache);
            case ModelElementType.NULL:
                return new RealTimeNull(node, this._callbacks, this, this._model, this._identityCache);
            case ModelElementType.NUMBER:
                return new RealTimeNumber(node, this._callbacks, this, this._model, this._identityCache);
            case ModelElementType.STRING:
                return new RealTimeString(node, this._callbacks, this, this._model, this._identityCache);
            case ModelElementType.UNDEFINED:
                return new RealTimeUndefined(node, this._callbacks, this, this._model, this._identityCache);
            case ModelElementType.DATE:
                return new RealTimeDate(node, this._callbacks, this, this._model, this._identityCache);
            default:
                return null;
        }
    }
}

class ServerOperationEvent {
    constructor(clientId, version, timestamp, operation) {
        this.clientId = clientId;
        this.version = version;
        this.timestamp = timestamp;
        this.operation = operation;
        Object.freeze(this);
    }
    copy(mods) {
        mods = Immutable.getOrDefault(mods, {});
        return new ServerOperationEvent(Immutable.getOrDefault(mods.clientId, this.clientId), Immutable.getOrDefault(mods.version, this.version), Immutable.getOrDefault(mods.timestamp, this.timestamp), Immutable.getOrDefault(mods.operation, this.operation));
    }
}

class ModelOperationEvent {
    constructor(sessionId, user, version, timestamp, operation) {
        this.sessionId = sessionId;
        this.user = user;
        this.version = version;
        this.timestamp = timestamp;
        this.operation = operation;
        Object.freeze(this);
    }
}

class ModelCollaborator {
    constructor(user, sessionId) {
        this.user = user;
        this.sessionId = sessionId;
        Object.freeze(this);
    }
}

const ObservableModelEventConstants = {
    CLOSED: "closed",
    DELETED: "deleted",
    VERSION_CHANGED: "version_changed"
};
Object.freeze(ObservableModelEventConstants);

class ModelPermissions {
    constructor(read, write, remove, manage) {
        this.read = read;
        this.write = write;
        this.remove = remove;
        this.manage = manage;
        Object.freeze(this);
    }
    static fromJSON(json) {
        return new ModelPermissions(json.read, json.write, json.remove, json.manage);
    }
    toJSON() {
        return { read: this.read, write: this.write, remove: this.remove, manage: this.manage };
    }
}
ModelPermissions.NONE = new ModelPermissions(false, false, false, false);

class ModelResult {
    constructor(data, collectionId, modelId, created, modified, version) {
        this.data = data;
        this.collectionId = collectionId;
        this.modelId = modelId;
        this.created = created;
        this.modified = modified;
        this.version = version;
        Object.freeze(this);
    }
}

function toDataValue(val) {
    if (val.arrayValue) {
        const { id, children } = val.arrayValue;
        return {
            type: "array",
            id,
            value: getOrDefaultArray(children).map(toDataValue)
        };
    }
    else if (val.objectValue) {
        return toObjectValue(val.objectValue);
    }
    else if (val.booleanValue) {
        const { id, value } = val.booleanValue;
        return { type: "boolean", id, value: getOrDefaultBoolean(value) };
    }
    else if (val.dateValue) {
        const { id, value } = val.dateValue;
        return { type: "date", id, value: timestampToDate(value) };
    }
    else if (val.doubleValue) {
        const { id, value } = val.doubleValue;
        return { type: "number", id, value: getOrDefaultNumber(value) };
    }
    else if (val.stringValue) {
        const { id, value } = val.stringValue;
        return { type: "string", id, value: getOrDefaultString(value) };
    }
    else if (val.nullValue) {
        const { id } = val.nullValue;
        return { type: "null", id, value: null };
    }
    else {
        throw new ConvergenceError("Invalid data delta type: " + JSON.stringify(val));
    }
}
function toIDataValue(val) {
    if (val.type === "array") {
        const { id, value } = val;
        return { arrayValue: { id, children: value.map(toIDataValue) } };
    }
    else if (val.type === "object") {
        const { id, value } = val;
        return { objectValue: { id, children: mapObjectValues(value, toIDataValue) } };
    }
    else if (val.type === "boolean") {
        const { id, value } = val;
        return { booleanValue: { id, value } };
    }
    else if (val.type === "date") {
        const { id, value } = val;
        return { dateValue: { id, value: dateToTimestamp(value) } };
    }
    else if (val.type === "number") {
        const { id, value } = val;
        return { doubleValue: { id, value } };
    }
    else if (val.type === "null") {
        const { id } = val;
        return { nullValue: { id } };
    }
    else if (val.type === "string") {
        const { id, value } = val;
        return { stringValue: { id, value } };
    }
    else {
        throw new ConvergenceError("Invalid data delta type: " + JSON.stringify(val));
    }
}
function toIObjectValue(objectValue) {
    return {
        id: objectValue.id,
        children: mapObjectValues(objectValue.value, toIDataValue)
    };
}
function toObjectValue(objectValue) {
    return {
        type: "object",
        id: objectValue.id,
        value: mapObjectValues(getOrDefaultObject(objectValue.children), toDataValue)
    };
}
function toModelPermissions(permissionsData) {
    return permissionsData === undefined ?
        undefined :
        new ModelPermissions(getOrDefaultBoolean(permissionsData.read), getOrDefaultBoolean(permissionsData.write), getOrDefaultBoolean(permissionsData.remove), getOrDefaultBoolean(permissionsData.manage));
}
function toModelResult(result) {
    const fields = getOrDefaultObject(result.data.fields);
    return new ModelResult(mapObjectValues(fields, protoValueToJson), result.collectionId, result.modelId, timestampToDate(result.createdTime), timestampToDate(result.modifiedTime), getOrDefaultNumber(result.version));
}
function modelUserPermissionMapToProto(perms) {
    if (perms === undefined || perms === null) {
        return [];
    }
    else {
        const mapped = [];
        perms.forEach((permissions, userId) => {
            mapped.push({
                user: domainUserIdToProto(userId),
                permissions
            });
        });
        return mapped;
    }
}
function protoToModelUserPermissionMap(perms) {
    const map = new DomainUserIdMap();
    if (TypeChecker.isArray(perms)) {
        perms.forEach(entry => {
            const domainUserId = protoToDomainUserId(entry.user);
            map.set(domainUserId, toModelPermissions(entry.permissions));
        });
    }
    return map;
}
function getModelMessageResourceId(message) {
    const modelMessage = toModelMessage(message);
    return modelMessage !== null ? getOrDefaultNumber(modelMessage.resourceId) : null;
}
function toModelMessage(message) {
    return message.forceCloseRealTimeModel ||
        message.remoteOperation ||
        message.referenceShared ||
        message.referenceSet ||
        message.referenceCleared ||
        message.referenceUnshared ||
        message.operationAck ||
        message.remoteClientOpenedModel ||
        message.remoteClientClosedModel ||
        message.modelPermissionsChanged ||
        message.remoteClientResyncStarted ||
        message.remoteClientResyncCompleted ||
        message.modelResyncServerComplete ||
        null;
}

class ModelPermissionManager {
    constructor(modelId, connection) {
        this._modelId = modelId;
        this._connection = connection;
    }
    get modelId() {
        return this._modelId;
    }
    getPermissions() {
        return this.getAllUserPermissions().then(permissionMap => {
            const mine = permissionMap.get(this._connection.session().user().userId.toGuid());
            return mine !== undefined ? mine : ModelPermissions.NONE;
        });
    }
    setOverridesCollection(overrideCollection) {
        const request = {
            setModelPermissionsRequest: {
                modelId: this._modelId,
                overrideCollection: { value: overrideCollection },
                removeAllUserPermissionsBeforeSet: false,
                setUserPermissions: [],
                removedUserPermissions: []
            }
        };
        return this._connection.request(request).then(() => {
            return;
        });
    }
    getOverridesCollection() {
        const request = {
            getModelPermissionsRequest: {
                modelId: this._modelId
            }
        };
        return this._connection.request(request).then((response) => {
            const { getModelPermissionsResponse } = response;
            return getModelPermissionsResponse.overridesCollection;
        });
    }
    getWorldPermissions() {
        const request = {
            getModelPermissionsRequest: {
                modelId: this._modelId
            }
        };
        return this._connection.request(request).then((response) => {
            const { getModelPermissionsResponse } = response;
            return toModelPermissions(getModelPermissionsResponse.worldPermissions);
        });
    }
    setWorldPermissions(worldPermissions) {
        const request = {
            setModelPermissionsRequest: {
                modelId: this._modelId,
                worldPermissions,
                removeAllUserPermissionsBeforeSet: false,
                setUserPermissions: [],
                removedUserPermissions: []
            }
        };
        return this._connection.request(request).then(() => {
            return;
        });
    }
    getAllUserPermissions() {
        const request = {
            getModelPermissionsRequest: {
                modelId: this._modelId
            }
        };
        return this._connection.request(request).then((response) => {
            const { getModelPermissionsResponse } = response;
            return protoToModelUserPermissionMap(getModelPermissionsResponse.userPermissions);
        });
    }
    setAllUserPermissions(permissions) {
        permissions = DomainUserIdMap.of(permissions);
        const request = {
            setModelPermissionsRequest: {
                modelId: this._modelId,
                setUserPermissions: modelUserPermissionMapToProto(permissions),
                removeAllUserPermissionsBeforeSet: true
            }
        };
        return this._connection.request(request).then(() => {
            return;
        });
    }
    getUserPermissions(userId) {
        return this.getAllUserPermissions().then(perms => {
            return perms.get(userId);
        });
    }
    setUserPermissions(user, permissions) {
        const userId = DomainUserId.of(user);
        const request = {
            setModelPermissionsRequest: {
                modelId: this._modelId,
                worldPermissions: null,
                setUserPermissions: [{ user: domainUserIdToProto(userId), permissions }],
                removeAllUserPermissionsBeforeSet: false,
                removedUserPermissions: []
            }
        };
        return this._connection.request(request).then(() => {
            return;
        });
    }
    removeUserPermissions(user) {
        const userId = DomainUserId.of(user);
        const request = {
            setModelPermissionsRequest: {
                modelId: this._modelId,
                removeAllUserPermissionsBeforeSet: false,
                removedUserPermissions: [domainUserIdToProto(userId)]
            }
        };
        return this._connection.request(request).then(() => {
            return;
        });
    }
}

function toOperation(operationData) {
    if (operationData.compoundOperation) {
        return toCompoundOperation$1(operationData.compoundOperation);
    }
    else if (operationData.discreteOperation) {
        return toDiscreteOperation$1(operationData.discreteOperation);
    }
    else {
        throw new ConvergenceError("Invalid operation data: " + JSON.stringify(operationData));
    }
}
function toCompoundOperation$1(compoundOperationData) {
    const discreteOps = getOrDefaultArray(compoundOperationData.operations).map(toDiscreteOperation$1);
    return new CompoundOperation(discreteOps);
}
function toDiscreteOperation$1(discreteOperationData) {
    if (discreteOperationData.arrayInsertOperation) {
        const { id, noOp, index, value } = discreteOperationData.arrayInsertOperation;
        return new ArrayInsertOperation(id, getOrDefaultBoolean(noOp), getOrDefaultNumber(index), toDataValue(value));
    }
    else if (discreteOperationData.arrayRemoveOperation) {
        const { id, noOp, index } = discreteOperationData.arrayRemoveOperation;
        return new ArrayRemoveOperation(id, getOrDefaultBoolean(noOp), getOrDefaultNumber(index));
    }
    else if (discreteOperationData.arrayMoveOperation) {
        const { id, noOp, fromIndex, toIndex } = discreteOperationData.arrayMoveOperation;
        return new ArrayMoveOperation(id, getOrDefaultBoolean(noOp), getOrDefaultNumber(fromIndex), getOrDefaultNumber(toIndex));
    }
    else if (discreteOperationData.arrayReplaceOperation) {
        const { id, noOp, index, value } = discreteOperationData.arrayReplaceOperation;
        return new ArrayReplaceOperation(id, getOrDefaultBoolean(noOp), getOrDefaultNumber(index), toDataValue(value));
    }
    else if (discreteOperationData.arraySetOperation) {
        const { id, noOp, values } = discreteOperationData.arraySetOperation;
        return new ArraySetOperation(id, getOrDefaultBoolean(noOp), getOrDefaultArray(values).map(toDataValue));
    }
    else if (discreteOperationData.objectAddPropertyOperation) {
        const { id, noOp, key, value } = discreteOperationData.objectAddPropertyOperation;
        return new ObjectAddPropertyOperation(id, getOrDefaultBoolean(noOp), getOrDefaultString(key), toDataValue(value));
    }
    else if (discreteOperationData.objectSetPropertyOperation) {
        const { id, noOp, key, value } = discreteOperationData.objectSetPropertyOperation;
        return new ObjectSetPropertyOperation(id, getOrDefaultBoolean(noOp), getOrDefaultString(key), toDataValue(value));
    }
    else if (discreteOperationData.objectRemovePropertyOperation) {
        const { id, noOp, key } = discreteOperationData.objectRemovePropertyOperation;
        return new ObjectRemovePropertyOperation(id, getOrDefaultBoolean(noOp), getOrDefaultString(key));
    }
    else if (discreteOperationData.objectSetOperation) {
        const { id, noOp, values } = discreteOperationData.objectSetOperation;
        return new ObjectSetOperation(id, getOrDefaultBoolean(noOp), mapObjectValues(values, toDataValue));
    }
    else if (discreteOperationData.stringSpliceOperation) {
        const { id, noOp, index, deleteCount, insertedValue } = discreteOperationData.stringSpliceOperation;
        return new StringSpliceOperation(id, getOrDefaultBoolean(noOp), getOrDefaultNumber(index), getOrDefaultNumber(deleteCount), getOrDefaultString(insertedValue));
    }
    else if (discreteOperationData.stringSetOperation) {
        const { id, noOp, value } = discreteOperationData.stringSetOperation;
        return new StringSetOperation(id, getOrDefaultBoolean(noOp), getOrDefaultString(value));
    }
    else if (discreteOperationData.numberDeltaOperation) {
        const { id, noOp, delta } = discreteOperationData.numberDeltaOperation;
        return new NumberDeltaOperation(id, getOrDefaultBoolean(noOp), getOrDefaultNumber(delta));
    }
    else if (discreteOperationData.numberSetOperation) {
        const { id, noOp, value } = discreteOperationData.numberSetOperation;
        return new NumberSetOperation(id, getOrDefaultBoolean(noOp), getOrDefaultNumber(value));
    }
    else if (discreteOperationData.booleanSetOperation) {
        const { id, noOp, value } = discreteOperationData.booleanSetOperation;
        return new BooleanSetOperation(id, getOrDefaultBoolean(noOp), getOrDefaultBoolean(value));
    }
    else if (discreteOperationData.dateSetOperation) {
        const { id, noOp, value } = discreteOperationData.dateSetOperation;
        return new DateSetOperation(id, getOrDefaultBoolean(noOp), timestampToDate(value));
    }
}
function toIOperationData(operation) {
    if (operation instanceof CompoundOperation) {
        return { compoundOperation: toICompoundOperationData(operation) };
    }
    else if (operation instanceof DiscreteOperation) {
        return { discreteOperation: toIDiscreteOperationData(operation) };
    }
    else {
        throw new ConvergenceError("Invalid operation data: " + JSON.stringify(operation));
    }
}
function toICompoundOperationData(compoundOperation) {
    const operations = compoundOperation.ops.map(toIDiscreteOperationData);
    return { operations };
}
function toIDiscreteOperationData(op) {
    if (op instanceof ArrayInsertOperation) {
        const { id, noOp, index, value } = op;
        return { arrayInsertOperation: { id, noOp, index, value: toIDataValue(value) } };
    }
    else if (op instanceof ArrayRemoveOperation) {
        const { id, noOp, index } = op;
        return { arrayRemoveOperation: { id, noOp, index } };
    }
    else if (op instanceof ArrayMoveOperation) {
        const { id, noOp, fromIndex, toIndex } = op;
        return { arrayMoveOperation: { id, noOp, fromIndex, toIndex } };
    }
    else if (op instanceof ArrayReplaceOperation) {
        const { id, noOp, index, value } = op;
        return { arrayReplaceOperation: { id, noOp, index, value: toIDataValue(value) } };
    }
    else if (op instanceof ArraySetOperation) {
        const { id, noOp, value } = op;
        return { arraySetOperation: { id, noOp, values: value.map(toIDataValue) } };
    }
    else if (op instanceof ObjectAddPropertyOperation) {
        const { id, noOp, prop, value } = op;
        return { objectAddPropertyOperation: { id, noOp, key: prop, value: toIDataValue(value) } };
    }
    else if (op instanceof ObjectSetPropertyOperation) {
        const { id, noOp, prop, value } = op;
        return { objectSetPropertyOperation: { id, noOp, key: prop, value: toIDataValue(value) } };
    }
    else if (op instanceof ObjectRemovePropertyOperation) {
        const { id, noOp, prop } = op;
        return { objectRemovePropertyOperation: { id, noOp, key: prop } };
    }
    else if (op instanceof ObjectSetOperation) {
        const { id, noOp, value } = op;
        return { objectSetOperation: { id, noOp, values: mapObjectValues(value, toIDataValue) } };
    }
    else if (op instanceof StringSpliceOperation) {
        const { id, noOp, index, deleteCount, insertValue } = op;
        return { stringSpliceOperation: { id, noOp, index, deleteCount, insertedValue: insertValue } };
    }
    else if (op instanceof StringSetOperation) {
        const { id, noOp, value } = op;
        return { stringSetOperation: { id, noOp, value } };
    }
    else if (op instanceof NumberDeltaOperation) {
        const { id, noOp, delta } = op;
        return { numberDeltaOperation: { id, noOp, delta } };
    }
    else if (op instanceof NumberSetOperation) {
        const { id, noOp, value } = op;
        return { numberSetOperation: { id, noOp, value } };
    }
    else if (op instanceof BooleanSetOperation) {
        const { id, noOp, value } = op;
        return { booleanSetOperation: { id, noOp, value } };
    }
    else if (op instanceof DateSetOperation) {
        const { id, noOp, value } = op;
        return { dateSetOperation: { id, noOp, value: dateToTimestamp(value) } };
    }
}

function toRemoteReferenceEvent(message) {
    if (message.referenceShared) {
        const { sessionId, key, references, valueId, resourceId } = message.referenceShared;
        const { referenceType, values } = extractValueAndType(references);
        return new RemoteReferenceShared(sessionId, resourceId, fromOptional(valueId), key, referenceType, values);
    }
    else if (message.referenceSet) {
        const { sessionId, key, references, valueId, resourceId } = message.referenceSet;
        const { values } = extractValueAndType(references);
        return new RemoteReferenceSet(sessionId, resourceId, fromOptional(valueId), key, values);
    }
    else if (message.referenceCleared) {
        const { sessionId, key, valueId, resourceId } = message.referenceCleared;
        return new RemoteReferenceCleared(sessionId, resourceId, fromOptional(valueId), key);
    }
    else if (message.referenceUnshared) {
        const { sessionId, key, valueId, resourceId } = message.referenceUnshared;
        return new RemoteReferenceUnshared(sessionId, resourceId, fromOptional(valueId), key);
    }
}
function extractValueAndType(values) {
    if (values.indices) {
        const vals = getOrDefaultArray(values.indices.values);
        return { referenceType: "index", values: vals.map(getOrDefaultNumber) };
    }
    else if (values.ranges) {
        const ranges = getOrDefaultArray(values.ranges.values);
        const val = ranges.map(({ startIndex, endIndex }) => {
            return { start: getOrDefaultNumber(startIndex), end: getOrDefaultNumber(endIndex) };
        });
        return { referenceType: "range", values: val };
    }
    else if (values.properties) {
        const vals = getOrDefaultArray(values.properties.values);
        return { referenceType: "property", values: vals.map(getOrDefaultString) };
    }
    else if (values.elements) {
        const vals = getOrDefaultArray(values.elements.values);
        return { referenceType: "element", values: vals.map(getOrDefaultString) };
    }
}
function toIReferenceValues(type, values) {
    switch (type) {
        case ModelReference.Types.INDEX:
            return { indices: { values } };
        case ModelReference.Types.PROPERTY:
            return { properties: { values } };
        case ModelReference.Types.RANGE:
            const ranges = values.map((r) => {
                return { startIndex: r.start, endIndex: r.end };
            });
            return { ranges: { values: ranges } };
        case ModelReference.Types.ELEMENT:
            const elementIds = [];
            for (const element of values) {
                elementIds.push(element.id());
            }
            return { elements: { values: elementIds } };
        default:
            throw new Error("Invalid reference type");
    }
}

function toOfflineOperationData(op) {
    if (op instanceof CompoundOperation) {
        const opData = op.ops.map(o => toOfflineOperationData(o));
        return { type: "compound", ops: opData };
    }
    else if (op instanceof ArrayInsertOperation) {
        return {
            type: "array_insert",
            id: op.id,
            noOp: op.noOp,
            index: op.index,
            value: op.value
        };
    }
    else if (op instanceof ArrayReplaceOperation) {
        return {
            type: "array_replace",
            id: op.id,
            noOp: op.noOp,
            index: op.index,
            value: op.value
        };
    }
    else if (op instanceof ArrayRemoveOperation) {
        return {
            type: "array_remove",
            id: op.id,
            noOp: op.noOp,
            index: op.index
        };
    }
    else if (op instanceof ArrayMoveOperation) {
        return {
            type: "array_move",
            id: op.id,
            noOp: op.noOp,
            fromIndex: op.fromIndex,
            toIndex: op.toIndex
        };
    }
    else if (op instanceof ArraySetOperation) {
        return {
            type: "array_set",
            id: op.id,
            noOp: op.noOp,
            value: op.value
        };
    }
    else if (op instanceof ObjectAddPropertyOperation) {
        return {
            type: "object_add_property",
            id: op.id,
            noOp: op.noOp,
            key: op.prop,
            value: op.value
        };
    }
    else if (op instanceof ObjectSetPropertyOperation) {
        return {
            type: "object_set_property",
            id: op.id,
            noOp: op.noOp,
            key: op.prop,
            value: op.value
        };
    }
    else if (op instanceof ObjectRemovePropertyOperation) {
        return {
            type: "object_remove_property",
            id: op.id,
            noOp: op.noOp,
            key: op.prop
        };
    }
    else if (op instanceof ObjectSetOperation) {
        return {
            type: "object_set",
            id: op.id,
            noOp: op.noOp,
            value: op.value
        };
    }
    else if (op instanceof StringSpliceOperation) {
        return {
            type: "string_splice",
            id: op.id,
            noOp: op.noOp,
            index: op.index,
            deleteCount: op.deleteCount,
            insertedValue: op.insertValue
        };
    }
    else if (op instanceof StringSetOperation) {
        return {
            type: "string_set",
            id: op.id,
            noOp: op.noOp,
            value: op.value
        };
    }
    else if (op instanceof NumberDeltaOperation) {
        return {
            type: "number_delta",
            id: op.id,
            noOp: op.noOp,
            value: op.delta
        };
    }
    else if (op instanceof NumberSetOperation) {
        return {
            type: "number_set",
            id: op.id,
            noOp: op.noOp,
            value: op.value
        };
    }
    else if (op instanceof BooleanSetOperation) {
        return {
            type: "boolean_set",
            id: op.id,
            noOp: op.noOp,
            value: op.value
        };
    }
    else if (op instanceof DateSetOperation) {
        return {
            type: "date_set",
            id: op.id,
            noOp: op.noOp,
            value: op.value
        };
    }
}
function fromOfflineOperationData(op) {
    switch (op.type) {
        case "array_insert":
            const ai = op;
            return new ArrayInsertOperation(ai.id, ai.noOp, ai.index, ai.value);
        case "array_remove":
            const ar = op;
            return new ArrayRemoveOperation(ar.id, ar.noOp, ar.index);
        case "array_replace":
            const ap = op;
            return new ArrayReplaceOperation(ap.id, ap.noOp, ap.index, ap.value);
        case "array_move":
            const am = op;
            return new ArrayMoveOperation(am.id, am.noOp, am.fromIndex, am.toIndex);
        case "array_set":
            const as = op;
            return new ArraySetOperation(as.id, as.noOp, as.value);
        case "object_add_property":
            const oap = op;
            return new ObjectAddPropertyOperation(oap.id, oap.noOp, oap.key, oap.value);
        case "object_set_property":
            const osp = op;
            return new ObjectSetPropertyOperation(osp.id, osp.noOp, osp.key, osp.value);
        case "object_remove_property":
            const orp = op;
            return new ObjectRemovePropertyOperation(orp.id, orp.noOp, orp.key);
        case "object_set":
            const os = op;
            return new ObjectSetOperation(os.id, os.noOp, os.value);
        case "string_splice":
            const sp = op;
            return new StringSpliceOperation(sp.id, sp.noOp, sp.index, sp.deleteCount, sp.insertedValue);
        case "string_set":
            const ss = op;
            return new StringSetOperation(ss.id, ss.noOp, ss.value);
        case "number_delta":
            const nd = op;
            return new NumberDeltaOperation(nd.id, nd.noOp, nd.value);
        case "number_set":
            const ns = op;
            return new NumberSetOperation(ns.id, ns.noOp, ns.value);
        case "boolean_set":
            const bs = op;
            return new BooleanSetOperation(bs.id, bs.noOp, bs.value);
        case "date_set":
            const ds = op;
            return new DateSetOperation(ds.id, ds.noOp, ds.value);
        case "compound":
            const co = op;
            const ops = co.ops.map(o => fromOfflineOperationData(o));
            return new CompoundOperation(ops);
    }
}

class ReplayDeferred extends AbstractDeferred {
    constructor() {
        super();
        this._deferreds = [];
        this._value = undefined;
        this._error = undefined;
    }
    static resolved(value) {
        const d = new ReplayDeferred();
        d.resolve(value);
        return d;
    }
    resolve(value) {
        super.resolve(value);
        this._value = value;
        this._deferreds.forEach(d => d.resolve(value));
    }
    reject(error) {
        super.reject(error);
        this._error = error;
        this._deferreds.forEach(d => d.reject(error));
    }
    promise() {
        if (this.isPending()) {
            const d = new Deferred();
            this._deferreds.push(d);
            return d.promise();
        }
        else if (this.isRejected()) {
            return Promise.reject(this._error);
        }
        else {
            return Promise.resolve(this._value);
        }
    }
}

class ConnectedEvent {
    constructor(domain) {
        this.domain = domain;
        this.name = ConnectedEvent.NAME;
        Object.freeze(this);
    }
}
ConnectedEvent.NAME = "connected";

class ConnectionFailedEvent {
    constructor(domain, code, authMethod, message) {
        this.domain = domain;
        this.code = code;
        this.authMethod = authMethod;
        this.message = message;
        this.name = ConnectionFailedEvent.NAME;
        Object.freeze(this);
    }
}
ConnectionFailedEvent.NAME = "connection_failed";

class ConnectionScheduledEvent {
    constructor(domain, delay) {
        this.domain = domain;
        this.delay = delay;
        this.name = ConnectionScheduledEvent.NAME;
        Object.freeze(this);
    }
}
ConnectionScheduledEvent.NAME = "connection_scheduled";

class ConnectingEvent {
    constructor(domain) {
        this.domain = domain;
        this.name = ConnectingEvent.NAME;
        Object.freeze(this);
    }
}
ConnectingEvent.NAME = "connecting";

class AuthenticatingEvent {
    constructor(domain, method) {
        this.domain = domain;
        this.method = method;
        this.name = AuthenticatingEvent.NAME;
        Object.freeze(this);
    }
}
AuthenticatingEvent.NAME = "authenticating";

class DisconnectedEvent {
    constructor(domain) {
        this.domain = domain;
        this.name = DisconnectedEvent.NAME;
        Object.freeze(this);
    }
}
DisconnectedEvent.NAME = "disconnected";

class InterruptedEvent {
    constructor(domain) {
        this.domain = domain;
        this.name = InterruptedEvent.NAME;
        Object.freeze(this);
    }
}
InterruptedEvent.NAME = "interrupted";

class ErrorEvent {
    constructor(domain, message, cause) {
        this.domain = domain;
        this.message = message;
        this.cause = cause;
        this.name = ErrorEvent.NAME;
        Object.freeze(this);
    }
}
ErrorEvent.NAME = "error";

const RealTimeModelEventConstants = Object.assign(Object.assign({}, ObservableModelEventConstants), { MODIFIED: ModelModifiedEvent.NAME, COMMITTED: ModelCommittedEvent.NAME, RESYNC_STARTED: ResyncStartedEvent.NAME, RESYNC_COMPLETED: ResyncCompletedEvent.NAME, RESYNC_ERROR: ResyncErrorEvent.NAME, REMOTE_RESYNC_STARTED: RemoteResyncStartedEvent.NAME, REMOTE_RESYNC_COMPLETED: RemoteResyncCompletedEvent.NAME, OFFLINE: ModelOfflineEvent.NAME, ONLINE: ModelOnlineEvent.NAME, RECONNECTING: ModelReconnectingEvent.NAME, COLLABORATOR_OPENED: CollaboratorOpenedEvent.NAME, COLLABORATOR_CLOSED: CollaboratorClosedEvent.NAME, REFERENCE: RemoteReferenceCreatedEvent.NAME, PERMISSIONS_CHANGED: ModelPermissionsChangedEvent.NAME });
class RealTimeModel extends ConvergenceEventEmitter {
    constructor(resourceId, valueIdPrefix, data, local, resyncOnly, sessions, resyncingSessions, references, permissions, createdTime, modifiedTime, modelId, collectionId, concurrencyControl, connection, identityCache, modelService, modelOfflineManager, undefinedObjectValues, undefinedArrayValues) {
        super();
        this._log = Logging.logger("models");
        this._emitLocal = false;
        this._resourceId = resourceId;
        this._local = local;
        this._createdTime = createdTime;
        this._time = modifiedTime;
        this._modelId = modelId;
        this._collectionId = collectionId;
        this._concurrencyControl = concurrencyControl;
        this._connection = connection;
        this._modelService = modelService;
        this._permissions = permissions;
        this._identityCache = identityCache;
        this._offlineManager = modelOfflineManager;
        this._resyncOnly = resyncOnly;
        this._offline = false;
        this._resyncData = null;
        this._closingData = {
            deferred: new ReplayDeferred(),
            closing: false,
            waitingForServer: false
        };
        this._model = new Model(this.session(), valueIdPrefix, data, undefinedObjectValues, undefinedArrayValues);
        this._referencesBySession = new Map();
        this._sessions = [...sessions];
        this._resyncingSessions = [...resyncingSessions];
        this._collaboratorsSubject = new BehaviorSubject(this.collaborators());
        const onRemoteReference = (ref) => this._onRemoteReferenceShared(ref);
        this._referenceManager = new ReferenceManager(this, [ModelReference.Types.ELEMENT], onRemoteReference, this._identityCache);
        this._concurrencyControl
            .on(ClientConcurrencyControl.Events.COMMIT_STATE_CHANGED, (event) => {
            const evt = event.committed ?
                new ModelCommittedEvent(this) : new ModelModifiedEvent(this);
            this._emitEvent(evt);
        });
        const referenceCallbacks = {
            onShare: this._onShareReference.bind(this),
            onUnShare: this._onUnshareReference.bind(this),
            onSet: this._onSetReference.bind(this),
            onClear: this._onClearReference.bind(this)
        };
        this._callbacks = {
            sendOperationCallback: (operation) => {
                const opEvent = this._concurrencyControl.processOutgoingOperation(operation);
                if (!this._concurrencyControl.isBatchOperationInProgress()) {
                    this._handleLocalOperation(opEvent);
                }
            },
            referenceEventCallbacks: referenceCallbacks
        };
        this._wrapperFactory = new RealTimeWrapperFactory(this._callbacks, this, this._identityCache);
        this._open = true;
        this._initializeReferences(references);
        this._storeOffline = this._offlineManager.isOfflineEnabled();
    }
    permissions() {
        return this._permissions;
    }
    permissionsManager() {
        return new ModelPermissionManager(this._modelId, this._connection);
    }
    session() {
        return this._connection.session();
    }
    emitLocalEvents(emit) {
        if (arguments.length === 0) {
            return this._emitLocal;
        }
        else {
            this._emitLocal = emit;
            return;
        }
    }
    collaborators() {
        return this._sessions.map((sessionId) => {
            const user = this._identityCache.getUserForSession(sessionId);
            return new ModelCollaborator(user, sessionId);
        });
    }
    resynchronizingCollaborators() {
        return this._resyncingSessions.map((sessionId) => {
            const user = this._identityCache.getUserForSession(sessionId);
            return new ModelCollaborator(user, sessionId);
        });
    }
    collaboratorsAsObservable() {
        return this._collaboratorsSubject.asObservable();
    }
    collectionId() {
        return this._collectionId;
    }
    modelId() {
        return this._modelId;
    }
    time() {
        return this._time;
    }
    minTime() {
        return this._createdTime;
    }
    maxTime() {
        return this.time();
    }
    createdTime() {
        return this._createdTime;
    }
    version() {
        return this._concurrencyControl.contextVersion();
    }
    minVersion() {
        return 0;
    }
    maxVersion() {
        return this.version();
    }
    isCommitted() {
        return this._concurrencyControl.isCommitted();
    }
    root() {
        return this._wrapperFactory.wrap(this._model.root());
    }
    elementAt(...path) {
        return this._wrapperFactory.wrap(this._model.valueAt(...path));
    }
    isOpen() {
        return this._open;
    }
    isLocal() {
        return this._local;
    }
    close() {
        this._log.debug("RealtimeModel.close() called: " + this._modelId);
        if (this._closingData.closing) {
            return Promise.reject(new ConvergenceError(`The model '${this._modelId}' is already closing`));
        }
        if (!this._open) {
            return Promise.reject(new ConvergenceError(`The model '${this._modelId}' has already been closed`));
        }
        if (this._resyncData !== null) {
            this._debug("Close called on a resyncing model");
            this._resyncOnly = true;
            return Promise.resolve();
        }
        else {
            this._debug("Close called on a non-resyncing model");
            const event = new ModelClosedEvent(this, true);
            this._initiateClose(true, event);
            return this._closingData.deferred.promise();
        }
    }
    isClosing() {
        return this._closingData.closing || this._mustCloseAfterResync();
    }
    whenClosed() {
        return this._closingData.deferred.promise();
    }
    startBatch() {
        this._concurrencyControl.startBatchOperation();
    }
    endBatch() {
        this.completeBatch();
    }
    completeBatch() {
        const opEvent = this._concurrencyControl.completeBatchOperation();
        this._handleLocalOperation(opEvent);
    }
    batchSize() {
        return this._concurrencyControl.batchSize();
    }
    cancelBatch() {
        return this._concurrencyControl.cancelBatchOperation();
    }
    isBatchStarted() {
        return this._concurrencyControl.isBatchOperationInProgress();
    }
    elementReference(key) {
        const existing = this._referenceManager.getLocalReference(key);
        if (existing !== undefined) {
            if (existing.reference().type() !== ModelReference.Types.ELEMENT) {
                throw new Error("A reference with this key already exists, but is not an observable reference");
            }
            else {
                return existing;
            }
        }
        else {
            const session = this.session();
            const reference = new ElementReference(this._referenceManager, key, this, session.user(), session.sessionId(), true);
            const local = new LocalElementReference(reference, this._callbacks.referenceEventCallbacks);
            this._referenceManager.addLocalReference(local);
            return local;
        }
    }
    reference(sessionId, key) {
        return this._referenceManager.get(sessionId, key);
    }
    references(filter) {
        return this._referenceManager.getAll(filter);
    }
    subscribeOffline() {
        return this._offlineManager.subscribe([this._modelId]);
    }
    unsubscribeOffline() {
        return this._offlineManager.unsubscribe([this._modelId]);
    }
    isSubscribedOffline() {
        return this._offlineManager.isModelSubscribed(this._modelId);
    }
    _getResourceId() {
        return this._resourceId;
    }
    _initiateClose(closeWithServer, event) {
        if (this._closingData.closing) {
            throw new ConvergenceError(`The model '${this._modelId}' is already closing.`, "already_closing");
        }
        this._closingData.closing = true;
        this._closingData.event = event;
        if (this._connection.isConnected() && closeWithServer) {
            this._debug("Close initiated (online)");
            this._closingData.waitingForServer = true;
            const request = {
                closeRealTimeModelRequest: {
                    resourceId: this._resourceId
                }
            };
            this._connection
                .request(request)
                .then(() => {
                this._debug("Closed with server");
                this._closingData.waitingForServer = false;
                this._checkIfCanClose();
            })
                .catch(err => {
                this._log.error(`Unexpected error closing a model: ${this._modelId}`, err);
                this._closingData.deferred.reject(err);
            });
        }
        else {
            this._debug("Close initiated (offline)");
            this._checkIfCanClose();
        }
    }
    _valueIdPrefix() {
        return this._model.valueIdPrefix();
    }
    _checkIfCanClose() {
        if (this._closingData.closing && !this._closingData.waitingForServer &&
            (this._concurrencyControl.isCommitted() || !this._connection.isConnected())) {
            this._close();
        }
    }
    _openAfterResync() {
        if (this._closingData.closing) {
            throw new ConvergenceError("Can't open after resynchronization if the mode is closing: " + this._modelId);
        }
        this._resyncOnly = false;
    }
    _isResyncOnly() {
        return this._resyncOnly;
    }
    _getConcurrencyControlStateSnapshot() {
        return {
            data: this._model.root().dataValue(),
            lastSequenceNumber: this._concurrencyControl.lastSequenceNumber(),
            uncommittedOperations: [...this._concurrencyControl.getInFlightOperations()]
        };
    }
    _rehydrateFromOfflineState(targetVersion, serverOps, localOps) {
        const serverEvents = serverOps.map(serverOp => {
            const op = fromOfflineOperationData(serverOp.operation);
            return new ServerOperationEvent(serverOp.sessionId, serverOp.version, serverOp.timestamp, op);
        }).sort((a, b) => a.version - b.version);
        const clientEvents = localOps.map(localOp => {
            const op = fromOfflineOperationData(localOp.operation);
            return new ClientOperationEvent(localOp.sessionId, localOp.sequenceNumber, localOp.contextVersion, localOp.timestamp, op);
        }).sort((a, b) => {
            if (a.contextVersion === b.contextVersion) {
                return a.seqNo - b.seqNo;
            }
            else {
                return a.contextVersion - b.contextVersion;
            }
        });
        let contextVersion = this._concurrencyControl.contextVersion();
        let lastSequenceNumber = this._concurrencyControl.lastSequenceNumber();
        let inFlight = [];
        while (clientEvents.length > 0 && clientEvents[0].seqNo <= lastSequenceNumber) {
            clientEvents.shift();
        }
        const canApplyServerEvent = () => {
            return serverEvents.length > 0 &&
                (clientEvents.length === 0 || serverEvents[0].version <= clientEvents[0].contextVersion);
        };
        const canApplyClientEvent = () => {
            return clientEvents.length > 0 && clientEvents[0].contextVersion === contextVersion;
        };
        while (canApplyServerEvent() || canApplyClientEvent()) {
            while (canApplyServerEvent()) {
                const serverEvent = serverEvents.shift();
                this._applyOperation(serverEvent.operation, serverEvent.clientId, serverEvent.version, serverEvent.timestamp);
                contextVersion++;
            }
            while (canApplyClientEvent()) {
                const clientEvent = clientEvents.shift();
                this._applyOperation(clientEvent.operation, this._connection.session().sessionId(), clientEvent.contextVersion, clientEvent.timestamp);
                lastSequenceNumber = clientEvent.seqNo;
                inFlight.push(clientEvent);
            }
        }
        if (serverEvents.length > 0 || clientEvents.length > 0) {
            throw new Error(`Unable to apply all events while rehydrating model "${this._modelId}" from offline state.`);
        }
        if (contextVersion !== targetVersion) {
            throw new Error(`Did not arrive at the proper version (${targetVersion}) when rehydrating model: ${contextVersion}`);
        }
        this._concurrencyControl.setState(contextVersion, lastSequenceNumber, inFlight);
    }
    _getRegisteredValue(id) {
        return this._wrapperFactory.wrap(this._model._getRegisteredValue(id));
    }
    _handleMessage(event) {
        const { forceCloseRealTimeModel, remoteOperation, referenceShared, referenceSet, referenceCleared, referenceUnshared, operationAck, remoteClientOpenedModel, remoteClientClosedModel, modelPermissionsChanged, remoteClientResyncStarted, remoteClientResyncCompleted, modelResyncServerComplete } = event.message;
        if (forceCloseRealTimeModel) {
            this._handleForceClose(forceCloseRealTimeModel);
        }
        else if (remoteOperation) {
            this._handleRemoteOperation(remoteOperation);
        }
        else if (operationAck) {
            this._handleOperationAck(operationAck);
        }
        else if (referenceShared || referenceSet || referenceCleared || referenceUnshared) {
            const remoteRefEvent = toRemoteReferenceEvent(event.message);
            this._handleRemoteReferenceEvent(remoteRefEvent);
        }
        else if (remoteClientOpenedModel) {
            this._handleClientOpen(getOrDefaultString(remoteClientOpenedModel.sessionId));
        }
        else if (remoteClientClosedModel) {
            this._handleClientClosed(getOrDefaultString(remoteClientClosedModel.sessionId));
        }
        else if (modelPermissionsChanged) {
            this._handleModelPermissionsChanged(modelPermissionsChanged);
        }
        else if (remoteClientResyncStarted) {
            this._handleRemoteClientResyncStarted(remoteClientResyncStarted);
        }
        else if (remoteClientResyncCompleted) {
            this._handleRemoteClientResyncCompleted(remoteClientResyncCompleted);
        }
        else if (modelResyncServerComplete) {
            this._handleModelResyncServerComplete(modelResyncServerComplete);
        }
        else {
            throw new Error("Unexpected message" + JSON.stringify(event.message));
        }
    }
    _isOffline() {
        return this._offline;
    }
    _setOffline() {
        this._debug("Offline");
        this._offline = true;
        this._emitEvent(new ModelOfflineEvent(this));
        this._sessions.forEach(sessionId => this._handleClientClosed(sessionId));
        this._sessions = [];
        this._resyncData = null;
        this._resourceId = null;
        if (this._closingData.closing) {
            this._close();
        }
    }
    _setOnline() {
        this._offline = false;
        this._resyncData = {
            bufferedOperations: [],
            resyncCompleted: false,
            upToDate: false
        };
        if (this._local) {
            this._createLocalModelAtServer().catch(e => {
                this._log.error("An unexpected error occurred while synchronizing offline model on reconnect.", e);
            });
        }
        else {
            this._resynchronize();
        }
    }
    _createLocalModelAtServer() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const metaData = yield this._offlineManager.getModelMetaData(this._modelId);
                yield this._deleteBeforeCreateIfNeeded(metaData);
                const creationData = yield this._offlineManager.getModelCreationData(this._modelId);
                yield this._createLocalModel(creationData)
                    .catch(e => this._handleCreationErrorOnResync(creationData, e));
                this._local = false;
                yield this._offlineManager.modelCreated(this._modelId);
                this._resynchronize();
            }
            catch (e) {
                this._resyncError(e.message);
            }
        });
    }
    _handleCreationErrorOnResync(creationData, e) {
        if (e instanceof ConvergenceServerError && e.code !== ConvergenceErrorCodes.MODEL_ALREADY_EXISTS) {
            const fp = e.details["fingerprint"];
            if (creationData.createdTime.getTime().toString() === fp) {
                return;
            }
        }
        return Promise.reject(e);
    }
    _createLocalModel(creation) {
        this._debug("Creating offline model at the server");
        const userPermissions = creation.userPermissions &&
            DomainUserIdMap.fromGuidObjectMap(creation.userPermissions);
        const options = {
            id: creation.modelId,
            collection: creation.collection,
            overrideCollectionWorldPermissions: creation.overrideCollectionWorldPermissions,
            worldPermissions: creation.worldPermissions,
            userPermissions
        };
        return this._modelService._create(options, creation.initialData);
    }
    _deleteBeforeCreateIfNeeded(metaData) {
        if (metaData.deleted) {
            return this._modelService.remove(this._modelId)
                .catch((e) => {
                console.log(e);
            }).then(() => {
                return this._offlineManager.modelDeleted(this._modelId);
            });
        }
        else {
            return Promise.resolve();
        }
    }
    _handleLocallyDeleted() {
        this._storeOffline = false;
        const event = new ModelClosedEvent(this, true, "The model was locally deleted");
        this._initiateClose(true, event);
        const message = `The model '${this._modelId}' was locally deleted while offline.`;
        const deletedEvent = new ModelDeletedEvent(this, true, message);
        this._emitEvent(deletedEvent);
    }
    _resynchronize() {
        this._debug("Requesting model resynchronization");
        this._emitEvent(new ModelReconnectingEvent(this));
        const request = {
            modelResyncRequest: {
                modelId: this._modelId,
                contextVersion: this._concurrencyControl.contextVersion()
            }
        };
        this._connection.request(request).then((response) => {
            this._debug("Starting model resynchronization");
            this._emitEvent(new ResyncStartedEvent(this));
            const { modelResyncResponse } = response;
            this._permissions = toModelPermissions(modelResyncResponse.permissions);
            this._resyncData.reconnectVersion = getOrDefaultNumber(modelResyncResponse.currentVersion);
            this._resourceId = getOrDefaultNumber(modelResyncResponse.resourceId);
            this._modelService._resyncStarted(this._modelId, this._resourceId);
            this._checkForReconnectUpToDate();
        }).catch((e) => this._resyncError(e.message));
    }
    _resyncError(message) {
        this._emitEvent(new ResyncErrorEvent(this, message));
        this._modelService._resyncError(this._modelId, message, this);
    }
    _handleModelPermissionsChanged(message) {
        const oldPermissions = this._permissions;
        this._permissions = toModelPermissions(message.permissions);
        const changes = [];
        if (this._permissions.read !== oldPermissions.read) {
            changes.push("read");
        }
        if (this._permissions.write !== oldPermissions.write) {
            changes.push("write");
        }
        if (this._permissions.remove !== oldPermissions.remove) {
            changes.push("remove");
        }
        if (this._permissions.manage !== oldPermissions.manage) {
            changes.push("manage");
        }
        const event = new ModelPermissionsChangedEvent(this, this._permissions, changes);
        this._emitEvent(event);
    }
    _handleRemoteClientResyncStarted(message) {
        const sessionId = getOrDefaultString(message.sessionId);
        this._resyncingSessions.push(sessionId);
        const user = this._identityCache.getUserForSession(sessionId);
        const event = new RemoteResyncStartedEvent(this, sessionId, user);
        this._emitEvent(event);
    }
    _handleRemoteClientResyncCompleted(message) {
        const sessionId = getOrDefaultString(message.sessionId);
        this._resyncingSessions.push(sessionId);
        const index = this._resyncingSessions.indexOf(sessionId);
        if (index >= 0) {
            this._resyncingSessions.splice(index, 1);
        }
        const user = this._identityCache.getUserForSession(sessionId);
        const event = new RemoteResyncCompletedEvent(this, sessionId, user);
        this._emitEvent(event);
    }
    _handleClientOpen(sessionId) {
        this._sessions.push(sessionId);
        this._referencesBySession.set(sessionId, []);
        const user = this._identityCache.getUserForSession(sessionId);
        const event = new CollaboratorOpenedEvent(this, new ModelCollaborator(user, sessionId));
        this._emitEvent(event);
        this._collaboratorsSubject.next(this.collaborators());
    }
    _handleClientClosed(sessionId) {
        this._sessions = this._sessions.filter(s => s !== sessionId);
        const refs = this._referencesBySession.get(sessionId);
        this._referencesBySession.delete(sessionId);
        refs.forEach((ref) => {
            ref._dispose();
        });
        const user = this._identityCache.getUserForSession(sessionId);
        const event = new CollaboratorClosedEvent(this, new ModelCollaborator(user, sessionId));
        this._emitEvent(event);
        this._collaboratorsSubject.next(this.collaborators());
    }
    _handleRemoteReferenceEvent(event) {
        if (event.valueId === null) {
            this._modelReferenceEvent(event);
        }
        else {
            const value = this._getRegisteredValue(event.valueId);
            if (!value) {
                return;
            }
            if (event instanceof RemoteReferenceShared) {
                if (event.values) {
                    let data = {
                        type: event.referenceType,
                        valueId: event.valueId,
                        values: event.values
                    };
                    data = this._concurrencyControl.processRemoteReferenceSet(data);
                    event = new RemoteReferenceShared(event.sessionId, event.resourceId, event.valueId, event.key, event.referenceType, data.values);
                }
            }
            else if (event instanceof RemoteReferenceSet) {
                const reference = value.reference(event.sessionId, event.key);
                if (!reference) {
                    this._log.warn("received an update for a non-existent reference.");
                    return;
                }
                const type = reference.type();
                let data = {
                    type,
                    valueId: event.valueId,
                    values: event.values
                };
                data = this._concurrencyControl.processRemoteReferenceSet(data);
                event = new RemoteReferenceSet(event.sessionId, event.resourceId, event.valueId, event.key, data.values);
            }
            if (event instanceof RemoteReferenceUnshared) {
                const r = value.reference(event.sessionId, event.key);
                const index = this._referencesBySession.get(event.sessionId).indexOf(r);
                this._referencesBySession.get(event.sessionId).splice(index, 1);
            }
            value._handleRemoteReferenceEvent(event);
            if (event instanceof RemoteReferenceShared) {
                const r = value.reference(event.sessionId, event.key);
                this._referencesBySession.get(event.sessionId).push(r);
            }
        }
    }
    _handleForceClose(message) {
        if (this.isClosing()) {
            return;
        }
        const event = new ModelClosedEvent(this, false, message.reason);
        this._initiateClose(false, event);
        if (message.reasonCode === ModelForcedCloseReasonCodes.DELETED) {
            const deletedEvent = new ModelDeletedEvent(this, false, message.reason);
            this._emitEvent(deletedEvent);
        }
        else {
            this._log.error(`The model with id '${this._modelId}' was forcefully closed by the server: ${message.reason}`);
        }
    }
    _close() {
        this._debug("Finishing close");
        const { deferred, event } = this._closingData;
        this._modelService._close(this);
        if (this._offlineManager.isOfflineEnabled()) {
            this._offlineManager.modelClosed(this);
        }
        this._model.root()._detach(false);
        this._open = false;
        this._connection = null;
        this._resourceId = null;
        this._closingData.closing = false;
        deferred.resolve();
        if (event) {
            this._emitEvent(event);
        }
    }
    _handleOperationAck(message) {
        const version = getOrDefaultNumber(message.version);
        const sequenceNumber = getOrDefaultNumber(message.sequenceNumber);
        let acknowledgedOperation;
        try {
            acknowledgedOperation = this._concurrencyControl.processAcknowledgement(version, sequenceNumber);
        }
        catch (e) {
            this._handleConcurrencyControlError(e);
            return;
        }
        this._time = timestampToDate(message.timestamp);
        if (this._storeOffline) {
            const operation = toOfflineOperationData(acknowledgedOperation.operation);
            const serverOp = {
                modelId: this._modelId,
                sessionId: this._connection.session().sessionId(),
                version,
                timestamp: this._time,
                operation
            };
            this._offlineManager
                .processOperationAck(this.modelId(), sequenceNumber, serverOp)
                .catch(e => {
                this._emitErrorEvent(`Error storing operation ack: ${this._modelId}`, e);
            });
        }
        this._checkIfCanClose();
        this._emitVersionChanged();
    }
    _handleRemoteOperation(message) {
        if (this._resyncData !== null && !this._resyncData.upToDate) {
            if (getOrDefaultNumber(message.contextVersion) > this._resyncData.reconnectVersion) {
                this._resyncData.bufferedOperations.push(message);
            }
            else if (this._concurrencyControl.hasInflightOperation() &&
                this._concurrencyControl.firstInFlightOperation().sessionId === message.sessionId) {
                const syntheticAck = {
                    resourceId: message.resourceId,
                    version: message.contextVersion,
                    timestamp: message.timestamp
                };
                this._handleOperationAck(syntheticAck);
                this._checkForReconnectUpToDate();
            }
            else {
                this._processRemoteOperation(message);
                this._checkForReconnectUpToDate();
            }
        }
        else {
            this._processRemoteOperation(message);
        }
    }
    _processRemoteOperation(message) {
        const unprocessed = new ServerOperationEvent(getOrDefaultString(message.sessionId), getOrDefaultNumber(message.contextVersion), timestampToDate(message.timestamp), toOperation(message.operation));
        this._processServerOperationEvent(unprocessed);
    }
    _processServerOperationEvent(unprocessed) {
        try {
            this._concurrencyControl.processRemoteOperation(unprocessed);
        }
        catch (e) {
            this._handleConcurrencyControlError(e);
            return;
        }
        const processed = this._concurrencyControl.getNextIncomingOperation();
        const operation = processed.operation;
        const clientId = processed.clientId;
        const contextVersion = processed.version;
        const timestamp = processed.timestamp;
        this._time = new Date(timestamp);
        this._applyOperation(operation, clientId, contextVersion, timestamp);
        this._emitVersionChanged();
        if (this._storeOffline) {
            const inflight = this._concurrencyControl.getInFlightOperations();
            this._offlineManager
                .processServerOperationEvent(this._modelId, processed, inflight)
                .catch(e => {
                this._emitErrorEvent(`Error storing remote operation: ${this._modelId}`, e);
            });
        }
    }
    _handleConcurrencyControlError(e) {
        const message = `A concurrency control error occurred within model "${this._modelId}".`;
        this._emitErrorEvent(message, e);
        const closedEvent = new ModelClosedEvent(this, false, "A concurrency control error occurred.");
        this._initiateClose(true, closedEvent);
    }
    _applyOperation(operation, sessionId, version, timestamp) {
        const user = this._identityCache.getUserForSession(sessionId);
        if (operation.type === OperationType.COMPOUND) {
            const compoundOp = operation;
            compoundOp.ops.forEach((discreteOp) => {
                this._applyDiscreteOperation(discreteOp, user, sessionId, version, timestamp);
            });
        }
        else {
            const discreteOp = operation;
            this._applyDiscreteOperation(discreteOp, user, sessionId, version, timestamp);
        }
    }
    _applyDiscreteOperation(discreteOp, user, sessionId, version, timestamp) {
        if (!discreteOp.noOp) {
            const modelEvent = new ModelOperationEvent(sessionId, user, version, timestamp, discreteOp);
            this._deliverToChild(modelEvent);
        }
    }
    _checkForReconnectUpToDate() {
        if (this._resyncData !== null &&
            !this._resyncData.upToDate &&
            this._resyncData.reconnectVersion === this._concurrencyControl.contextVersion()) {
            this._resyncData.upToDate = true;
            this._resyncData.bufferedOperations.forEach(m => {
                this._processRemoteOperation(m);
            });
            this._debug("All server operations applied during resynchronization");
            const resend = this._concurrencyControl.getInFlightOperations();
            resend.forEach(op => this._sendOperation(op));
            this._debug("All local operations resent during resynchronization");
            this._completeResynchronization();
        }
    }
    _completeResynchronization() {
        this._debug("Requesting resynchronization completion");
        this._resyncData.resyncCompleted = true;
        this._resyncData.openAfterComplete = !this._resyncOnly;
        const completeRequest = {
            modelResyncClientComplete: {
                resourceId: this._resourceId,
                open: this._resyncData.openAfterComplete
            }
        };
        this._connection.send(completeRequest);
    }
    _handleModelResyncServerComplete(message) {
        this._debug("Resynchronization completed");
        const openAfterComplete = this._resyncData.openAfterComplete;
        const open = openAfterComplete && !this._resyncOnly;
        this._resyncData = null;
        this._emitEvent(new ResyncCompletedEvent(this));
        this._modelService._resyncCompleted(this._modelId);
        if (open) {
            this._debug("Opening after reconnect");
            const sessions = getOrDefaultArray(message.connectedClients);
            sessions.forEach(sessionId => this._handleClientOpen(sessionId));
            this._initializeReferences(getOrDefaultArray(message.references));
            this._referenceManager.reShare();
            this._debug(`Online`);
            this._emitEvent(new ModelOnlineEvent(this));
        }
        else {
            this._initiateClose(openAfterComplete);
        }
    }
    _mustCloseAfterResync() {
        return this._resyncData !== null &&
            this._resyncData.resyncCompleted &&
            !this._resyncData.openAfterComplete;
    }
    _debug(message) {
        this._log.debug(`Model("${this._modelId}"): ${message}`);
    }
    _deliverToChild(modelEvent) {
        this._model.handleModelOperationEvent(modelEvent);
    }
    _handleLocalOperation(opEvent) {
        if (this._storeOffline) {
            this._offlineManager
                .processLocalOperation(this._modelId, opEvent)
                .catch(e => {
                this._emitErrorEvent(`Error storing local operation: ${this._modelId}`, e);
            });
        }
        this._sendOperation(opEvent);
    }
    _sendOperation(opEvent) {
        if (this._sendEvents()) {
            const opSubmission = {
                operationSubmission: {
                    resourceId: this._resourceId,
                    sequenceNumber: opEvent.seqNo,
                    contextVersion: opEvent.contextVersion,
                    operation: toIOperationData(opEvent.operation)
                }
            };
            this._connection.send(opSubmission);
        }
    }
    _emitVersionChanged() {
        const event = new VersionChangedEvent(this, this._concurrencyControl.contextVersion());
        this._emitEvent(event);
    }
    _modelReferenceEvent(message) {
        this._referenceManager.handleRemoteReferenceEvent(message);
    }
    _onRemoteReferenceShared(reference) {
        this._referencesBySession.get(reference.sessionId()).push(reference);
        this._emitEvent(new RemoteReferenceCreatedEvent(reference, this));
    }
    _initializeReferences(references) {
        this._referencesBySession.clear();
        this._sessions.forEach(sessionId => {
            this._referencesBySession.set(sessionId, []);
        });
        references.forEach((ref) => {
            let element;
            const valueId = fromOptional(ref.valueId);
            if (valueId !== null) {
                element = this._wrapperFactory.wrap(this._model._getRegisteredValue(valueId));
            }
            const { referenceType, values } = extractValueAndType(ref.reference);
            const shared = new RemoteReferenceShared(ref.sessionId, this._resourceId, valueId, ref.key, referenceType, values);
            if (element !== undefined) {
                element._handleRemoteReferenceEvent(shared);
                const r = element.reference(ref.sessionId, ref.key);
                this._referencesBySession.get(ref.sessionId).push(r);
            }
            else {
                this._modelReferenceEvent(shared);
            }
        });
    }
    _onShareReference(reference) {
        if (this._sendEvents()) {
            const source = reference.reference().source();
            const vid = (source instanceof RealTimeElement) ? source.id() : null;
            let refData = {
                type: reference.reference().type(),
                valueId: vid,
                values: reference.reference().values()
            };
            if (vid !== undefined) {
                refData = this._concurrencyControl.processOutgoingSetReference(refData);
            }
            const event = {
                shareReference: {
                    resourceId: this._resourceId,
                    key: reference.reference().key(),
                    valueId: toOptional(vid),
                    references: toIReferenceValues(reference.reference().type(), refData.values),
                    version: this._concurrencyControl.contextVersion()
                }
            };
            this._connection.send(event);
        }
    }
    _onUnshareReference(reference) {
        if (this._sendEvents()) {
            const source = reference.reference().source();
            const vid = (source instanceof RealTimeElement) ? source.id() : null;
            const event = {
                unshareReference: {
                    resourceId: this._resourceId,
                    valueId: toOptional(vid),
                    key: reference.reference().key(),
                }
            };
            this._connection.send(event);
        }
    }
    _onSetReference(reference) {
        if (this._sendEvents()) {
            const source = reference.reference().source();
            const vid = (source instanceof RealTimeElement) ? source.id() : null;
            let refData = {
                type: reference.reference().type(),
                valueId: vid,
                values: reference.reference().values()
            };
            if (vid !== undefined) {
                refData = this._concurrencyControl.processOutgoingSetReference(refData);
            }
            if (refData) {
                const event = {
                    setReference: {
                        resourceId: this._resourceId,
                        key: reference.reference().key(),
                        valueId: toOptional(refData.valueId),
                        references: toIReferenceValues(reference.reference().type(), refData.values),
                        version: this._concurrencyControl.contextVersion()
                    }
                };
                this._connection.send(event);
            }
        }
    }
    _onClearReference(reference) {
        if (this._sendEvents()) {
            const source = reference.reference().source();
            const vid = (source instanceof RealTimeElement) ? source.id() : null;
            const event = {
                clearReference: {
                    resourceId: this._resourceId,
                    key: reference.reference().key(),
                    valueId: toOptional(vid)
                }
            };
            this._connection.send(event);
        }
    }
    _sendEvents() {
        return this._connection.isConnected() &&
            !this._offline &&
            (this._resyncData === null || this._resyncData.upToDate);
    }
    _emitErrorEvent(message, e) {
        this._log.error(message, e);
        const errorEvent = new ErrorEvent(this._connection.session().domain(), message, e);
        this._emitEvent(errorEvent);
        this._modelService._emitError(errorEvent);
    }
}
RealTimeModel.Events = RealTimeModelEventConstants;
Object.freeze(RealTimeModel.Events);

class LocalModelReference extends ConvergenceEventEmitter {
    constructor(reference, callbacks) {
        super();
        this._emitFrom(reference.events());
        this._reference = reference;
        this._shared = false;
        this._callbacks = callbacks;
    }
    type() {
        return this._reference.type();
    }
    key() {
        return this._reference.key();
    }
    source() {
        return this._reference.source();
    }
    isLocal() {
        return true;
    }
    user() {
        return this._reference.user();
    }
    sessionId() {
        return this._reference.sessionId();
    }
    isDisposed() {
        return this._reference.isDisposed();
    }
    value() {
        return this._reference.value();
    }
    values() {
        return this._reference.values();
    }
    reference() {
        return this._reference;
    }
    share() {
        this._ensureAttached();
        if (!this.isShared()) {
            this._callbacks.onShare(this);
            this._shared = true;
        }
    }
    unshare() {
        this._ensureAttached();
        if (this.isShared()) {
            this._callbacks.onUnShare(this);
            this._shared = false;
        }
    }
    isShared() {
        return this._shared;
    }
    set(value) {
        this._ensureAttached();
        if (value instanceof Array) {
            this._reference._set(value, false);
        }
        else {
            this._reference._set([value], false);
        }
        if (this.isShared()) {
            this._callbacks.onSet(this);
        }
    }
    clear() {
        this._ensureAttached();
        this._reference._clear();
        this._callbacks.onClear(this);
    }
    isSet() {
        return this._reference.isSet();
    }
    dispose() {
        this._ensureAttached();
        this._dispose();
    }
    _ensureAttached() {
        if (this.type() !== ModelReference.Types.ELEMENT) {
            if (this.reference().source().isDetached()) {
                throw new Error("The source model is detached");
            }
        }
    }
    _dispose() {
        const source = this.source();
        const needToUnshare = (source instanceof RealTimeElement && source.isAttached()) ||
            (source instanceof RealTimeModel && source.isOpen());
        if (needToUnshare) {
            this.unshare();
        }
        this._reference._dispose();
        this._callbacks = null;
    }
}
LocalModelReference.Events = ModelReference.Events;

class LocalElementReference extends LocalModelReference {
    constructor(reference, referenceCallbacks) {
        super(reference, referenceCallbacks);
    }
}

class LocalIndexReference extends LocalModelReference {
    constructor(reference, referenceCallbacks) {
        super(reference, referenceCallbacks);
    }
}

class LocalPropertyReference extends LocalModelReference {
    constructor(reference, referenceCallbacks) {
        super(reference, referenceCallbacks);
    }
}

class LocalRangeReference extends LocalModelReference {
    constructor(reference, referenceCallbacks) {
        super(reference, referenceCallbacks);
    }
}

const StringSpliceIndexTransformationFunction = (o, r) => {
    const values = IndexTransformer.handleSplice(r.values, o.index, o.deleteCount, o.insertValue.length);
    return Immutable.copy(r, { values });
};
const StringSetIndexTransformationFunction = (o, r) => {
    return null;
};

const StringSpliceRangeTransformationFunction = (o, r) => {
    const values = RangeTransformer.handleSplice(r.values, o.index, o.deleteCount, o.insertValue.length);
    return Immutable.copy(r, { values });
};
const StringSetRangeTransformationFunction = (o, r) => {
    return null;
};

const DateSetSetOTF = (s, c) => {
    if (s.value.getTime() === c.value.getTime()) {
        return new OperationPair(s.copy({ noOp: true }), s.copy({ noOp: true }));
    }
    else {
        return new OperationPair(s, c.copy({ noOp: true }));
    }
};

var SpliceType;
(function (SpliceType) {
    SpliceType[SpliceType["Insert"] = 0] = "Insert";
    SpliceType[SpliceType["Remove"] = 1] = "Remove";
    SpliceType[SpliceType["Splice"] = 2] = "Splice";
    SpliceType[SpliceType["NoOp"] = 3] = "NoOp";
})(SpliceType || (SpliceType = {}));
function getSpliceType(op) {
    if (op.deleteCount === 0 && op.insertValue.length > 0) {
        return SpliceType.Insert;
    }
    else if (op.deleteCount > 0 && op.insertValue.length === 0) {
        return SpliceType.Remove;
    }
    else if (op.deleteCount > 0 && op.insertValue.length > 0) {
        return SpliceType.Splice;
    }
    else {
        return SpliceType.NoOp;
    }
}
const StringSpliceSpliceOTF = (s, c) => {
    const sType = getSpliceType(s);
    const cType = getSpliceType(c);
    if (sType === SpliceType.NoOp || cType === SpliceType.NoOp) {
        return new OperationPair(s, c);
    }
    else if (sType === SpliceType.Insert && cType === SpliceType.Insert) {
        return transformInsertInsert(s, c);
    }
    else if (sType === SpliceType.Insert && cType === SpliceType.Remove) {
        return transformInsertRemove(s, c);
    }
    else if (sType === SpliceType.Insert && cType === SpliceType.Splice) {
        return transformInsertSplice(s, c);
    }
    else if (sType === SpliceType.Remove && cType === SpliceType.Insert) {
        return transformRemoveInsert(s, c);
    }
    else if (sType === SpliceType.Remove && cType === SpliceType.Remove) {
        return transformRemoveRemove(s, c);
    }
    else if (sType === SpliceType.Remove && cType === SpliceType.Splice) {
        return transformRemoveSplice(s, c);
    }
    else if (sType === SpliceType.Splice && cType === SpliceType.Insert) {
        return transformSpliceInsert(s, c);
    }
    else if (sType === SpliceType.Splice && cType === SpliceType.Remove) {
        return transformSpliceRemove(s, c);
    }
    else if (sType === SpliceType.Splice && cType === SpliceType.Splice) {
        return transformSpliceSplice(s, c);
    }
};
function transformInsertInsert(s, c) {
    if (s.index <= c.index) {
        return new OperationPair(s, c.copy({ index: c.index + s.insertValue.length }));
    }
    else {
        return new OperationPair(s.copy({ index: s.index + c.insertValue.length }), c);
    }
}
function transformInsertRemove(s, c) {
    if (s.index <= c.index) {
        return new OperationPair(s, c.copy({ index: c.index + s.insertValue.length }));
    }
    else if (s.index >= c.index + c.deleteCount) {
        return new OperationPair(s.copy({ index: s.index - c.deleteCount }), c);
    }
    else {
        return new OperationPair(s.copy({ noOp: true }), c.copy({ deleteCount: c.deleteCount + s.insertValue.length }));
    }
}
function transformInsertSplice(s, c) {
    if (s.index <= c.index) {
        return new OperationPair(s, c.copy({ index: c.index + s.insertValue.length }));
    }
    else if (c.index + c.deleteCount > s.index) {
        return new OperationPair(s.copy({ noOp: true }), c.copy({ deleteCount: c.deleteCount + s.insertValue.length }));
    }
    else {
        return new OperationPair(s.copy({ index: s.index - c.deleteCount + c.insertValue.length }), c);
    }
}
function transformRemoveInsert(s, c) {
    if (c.index <= s.index) {
        return new OperationPair(s.copy({ index: s.index + c.insertValue.length }), c);
    }
    else if (c.index >= s.index + s.deleteCount) {
        return new OperationPair(s, c.copy({ index: c.index - s.deleteCount }));
    }
    else {
        return new OperationPair(s.copy({ deleteCount: s.deleteCount + c.insertValue.length }), c.copy({ noOp: true }));
    }
}
function transformRemoveRemove(s, c) {
    const cStart = c.index;
    const cEnd = c.index + c.deleteCount;
    const sStart = s.index;
    const sEnd = s.index + s.deleteCount;
    const relationship = RangeRelationshipUtil.getRangeRangeRelationship(sStart, sEnd, cStart, cEnd);
    switch (relationship) {
        case RangeRangeRelationship.Precedes:
            return new OperationPair(s, c.copy({ index: c.index - s.deleteCount }));
        case RangeRangeRelationship.PrecededBy:
            return new OperationPair(s.copy({ index: s.index - c.deleteCount }), c);
        case RangeRangeRelationship.Meets:
        case RangeRangeRelationship.Overlaps: {
            const overlapLength = s.index + s.deleteCount - c.index;
            return new OperationPair(s.copy({ deleteCount: s.deleteCount - overlapLength }), c.copy({ index: s.index, deleteCount: c.deleteCount - overlapLength }));
        }
        case RangeRangeRelationship.MetBy:
        case RangeRangeRelationship.OverlappedBy: {
            const overlapLength = c.index + c.deleteCount - s.index;
            return new OperationPair(s.copy({ index: c.index, deleteCount: s.deleteCount - overlapLength }), c.copy({ deleteCount: c.deleteCount - overlapLength }));
        }
        case RangeRangeRelationship.Starts:
        case RangeRangeRelationship.ContainedBy:
        case RangeRangeRelationship.Finishes:
            return new OperationPair(s.copy({ noOp: true }), c.copy({ deleteCount: c.deleteCount - s.deleteCount }));
        case RangeRangeRelationship.StartedBy:
        case RangeRangeRelationship.Contains:
        case RangeRangeRelationship.FinishedBy:
            return new OperationPair(s.copy({ deleteCount: s.deleteCount - c.deleteCount }), c.copy({ noOp: true }));
        case RangeRangeRelationship.EqualTo:
            return new OperationPair(s.copy({ noOp: true }), c.copy({ noOp: true }));
    }
}
function transformRemoveSplice(s, c) {
    const cStart = c.index;
    const cEnd = c.index + c.deleteCount;
    const sStart = s.index;
    const sEnd = s.index + s.deleteCount;
    const relationship = RangeRelationshipUtil.getRangeRangeRelationship(sStart, sEnd, cStart, cEnd);
    switch (relationship) {
        case RangeRangeRelationship.Precedes:
            return new OperationPair(s, c.copy({ index: c.index - s.deleteCount }));
        case RangeRangeRelationship.PrecededBy:
            return new OperationPair(s.copy({ index: s.index - c.deleteCount + c.insertValue.length }), c);
        case RangeRangeRelationship.Meets:
        case RangeRangeRelationship.Overlaps: {
            const overlapLength = s.index + s.deleteCount - c.index;
            return new OperationPair(s.copy({ deleteCount: s.deleteCount - overlapLength }), c.copy({ index: s.index, deleteCount: c.deleteCount - overlapLength }));
        }
        case RangeRangeRelationship.MetBy:
        case RangeRangeRelationship.OverlappedBy: {
            const overlapLength = c.index + c.deleteCount - s.index;
            return new OperationPair(s.copy({ index: c.index + c.insertValue.length, deleteCount: s.deleteCount - overlapLength }), c.copy({ deleteCount: c.deleteCount - overlapLength }));
        }
        case RangeRangeRelationship.Starts:
        case RangeRangeRelationship.Finishes:
        case RangeRangeRelationship.ContainedBy:
            return new OperationPair(s.copy({ noOp: true }), c.copy({ deleteCount: c.deleteCount - s.deleteCount }));
        case RangeRangeRelationship.StartedBy:
            return new OperationPair(s.copy({ index: s.index + c.insertValue.length, deleteCount: s.deleteCount - c.deleteCount }), c.copy({ index: s.index, deleteCount: 0 }));
        case RangeRangeRelationship.FinishedBy:
            return new OperationPair(s.copy({ deleteCount: s.deleteCount - c.deleteCount }), c.copy({ index: s.index, deleteCount: 0 }));
        case RangeRangeRelationship.Contains:
            return new OperationPair(s.copy({ deleteCount: s.deleteCount - c.deleteCount + c.insertValue.length }), c.copy({ noOp: true }));
        case RangeRangeRelationship.EqualTo:
            return new OperationPair(s.copy({ noOp: true }), c.copy({ deleteCount: 0 }));
    }
}
function transformSpliceInsert(s, c) {
    if (c.index <= s.index) {
        return new OperationPair(s.copy({ index: s.index + c.insertValue.length }), c);
    }
    else if (s.index + s.deleteCount > c.index) {
        return new OperationPair(s.copy({ deleteCount: s.deleteCount + c.insertValue.length }), c.copy({ noOp: true }));
    }
    else {
        return new OperationPair(s, c.copy({ index: c.index - s.deleteCount + s.insertValue.length }));
    }
}
function transformSpliceRemove(s, c) {
    const cStart = c.index;
    const cEnd = c.index + c.deleteCount;
    const sStart = s.index;
    const sEnd = s.index + s.deleteCount;
    const relationship = RangeRelationshipUtil.getRangeRangeRelationship(sStart, sEnd, cStart, cEnd);
    switch (relationship) {
        case RangeRangeRelationship.Precedes:
            return new OperationPair(s, c.copy({ index: c.index - s.deleteCount + s.insertValue.length }));
        case RangeRangeRelationship.PrecededBy:
            return new OperationPair(s.copy({ index: s.index - c.deleteCount }), c);
        case RangeRangeRelationship.Meets:
        case RangeRangeRelationship.Overlaps: {
            const overlapLength = s.index + s.deleteCount - c.index;
            return new OperationPair(s.copy({ deleteCount: s.deleteCount - overlapLength }), c.copy({ index: s.index + s.insertValue.length, deleteCount: c.deleteCount - overlapLength }));
        }
        case RangeRangeRelationship.MetBy:
        case RangeRangeRelationship.OverlappedBy: {
            const overlapLength = c.index + c.deleteCount - s.index;
            return new OperationPair(s.copy({ index: c.index, deleteCount: s.deleteCount - overlapLength }), c.copy({ deleteCount: c.deleteCount - overlapLength }));
        }
        case RangeRangeRelationship.Starts:
            return new OperationPair(s.copy({ deleteCount: 0 }), c.copy({ index: c.index + s.insertValue.length, deleteCount: c.deleteCount - s.deleteCount }));
        case RangeRangeRelationship.Finishes:
            return new OperationPair(s.copy({ index: c.index, deleteCount: 0 }), c.copy({ deleteCount: c.deleteCount - s.deleteCount }));
        case RangeRangeRelationship.ContainedBy:
            return new OperationPair(s.copy({ noOp: true }), c.copy({ deleteCount: c.deleteCount - s.deleteCount + s.insertValue.length }));
        case RangeRangeRelationship.StartedBy:
        case RangeRangeRelationship.FinishedBy:
        case RangeRangeRelationship.Contains:
            return new OperationPair(s.copy({ deleteCount: s.deleteCount - c.deleteCount }), c.copy({ deleteCount: 0 }));
        case RangeRangeRelationship.EqualTo:
            return new OperationPair(s.copy({ deleteCount: 0 }), c.copy({ noOp: true }));
    }
}
function transformSpliceSplice(s, c) {
    const cStart = c.index;
    const cEnd = c.index + c.deleteCount;
    const sStart = s.index;
    const sEnd = s.index + s.deleteCount;
    const relationship = RangeRelationshipUtil.getRangeRangeRelationship(sStart, sEnd, cStart, cEnd);
    switch (relationship) {
        case RangeRangeRelationship.Precedes:
            return new OperationPair(s, c.copy({ index: c.index - s.deleteCount + s.insertValue.length }));
        case RangeRangeRelationship.PrecededBy:
            return new OperationPair(s.copy({ index: s.index - c.deleteCount + s.insertValue.length }), c);
        case RangeRangeRelationship.Meets:
        case RangeRangeRelationship.Overlaps: {
            const overlapLength = s.index + s.deleteCount - c.index;
            return new OperationPair(s.copy({ deleteCount: s.deleteCount - overlapLength }), c.copy({ index: s.index + s.insertValue.length, deleteCount: c.deleteCount - overlapLength }));
        }
        case RangeRangeRelationship.MetBy:
        case RangeRangeRelationship.OverlappedBy: {
            const overlapLength = c.index + c.deleteCount - s.index;
            return new OperationPair(s.copy({ index: c.index + c.insertValue.length, deleteCount: s.deleteCount - overlapLength }), c.copy({ deleteCount: c.deleteCount - overlapLength }));
        }
        case RangeRangeRelationship.Starts:
            return new OperationPair(s.copy({ deleteCount: 0 }), c.copy({ index: c.index + s.insertValue.length, deleteCount: c.deleteCount - s.deleteCount }));
        case RangeRangeRelationship.ContainedBy:
            return new OperationPair(s.copy({ noOp: true }), c.copy({ deleteCount: c.deleteCount - s.deleteCount + s.insertValue.length }));
        case RangeRangeRelationship.Finishes:
            return new OperationPair(s.copy({ index: c.index + c.insertValue.length, deleteCount: 0 }), c.copy({ deleteCount: c.deleteCount - s.deleteCount }));
        case RangeRangeRelationship.StartedBy:
            return new OperationPair(s.copy({ index: s.index + c.insertValue.length, deleteCount: s.deleteCount - c.deleteCount }), c.copy({ deleteCount: 0 }));
        case RangeRangeRelationship.Contains:
            return new OperationPair(s.copy({ deleteCount: s.deleteCount - c.deleteCount + c.insertValue.length }), c.copy({ noOp: true }));
        case RangeRangeRelationship.FinishedBy:
            return new OperationPair(s.copy({ deleteCount: s.deleteCount - c.deleteCount }), c.copy({ deleteCount: 0, index: s.index + s.insertValue.length }));
        case RangeRangeRelationship.EqualTo:
            return new OperationPair(s.copy({ deleteCount: 0 }), c.copy({ index: c.index + s.insertValue.length, deleteCount: 0 }));
    }
}

class TransformationFunctionRegistry {
    constructor() {
        this._otfs = {};
        this._rtfs = {};
        this.registerOtf(OperationType.STRING_SPLICE, OperationType.STRING_SPLICE, StringSpliceSpliceOTF);
        this.registerOtf(OperationType.STRING_SPLICE, OperationType.STRING_VALUE, StringSpliceSetOTF);
        this.registerOtf(OperationType.STRING_VALUE, OperationType.STRING_SPLICE, StringSetSpliceOTF);
        this.registerOtf(OperationType.STRING_VALUE, OperationType.STRING_VALUE, StringSetSetOTF);
        this.registerOtf(OperationType.OBJECT_ADD, OperationType.OBJECT_ADD, ObjectAddPropertyAddPropertyOTF);
        this.registerOtf(OperationType.OBJECT_ADD, OperationType.OBJECT_SET, ObjectAddPropertySetPropertyOTF);
        this.registerOtf(OperationType.OBJECT_ADD, OperationType.OBJECT_REMOVE, ObjectAddPropertyRemovePropertyOTF);
        this.registerOtf(OperationType.OBJECT_ADD, OperationType.OBJECT_VALUE, ObjectAddPropertySetOTF);
        this.registerOtf(OperationType.OBJECT_REMOVE, OperationType.OBJECT_ADD, ObjectRemovePropertyAddPropertyOTF);
        this.registerOtf(OperationType.OBJECT_REMOVE, OperationType.OBJECT_SET, ObjectRemovePropertySetPropertyOTF);
        this.registerOtf(OperationType.OBJECT_REMOVE, OperationType.OBJECT_REMOVE, ObjectRemovePropertyRemovePropertyOTF);
        this.registerOtf(OperationType.OBJECT_REMOVE, OperationType.OBJECT_VALUE, ObjectRemovePropertySetOTF);
        this.registerOtf(OperationType.OBJECT_SET, OperationType.OBJECT_ADD, ObjectSetPropertyAddPropertyOTF);
        this.registerOtf(OperationType.OBJECT_SET, OperationType.OBJECT_SET, ObjectSetPropertySetPropertyOTF);
        this.registerOtf(OperationType.OBJECT_SET, OperationType.OBJECT_REMOVE, ObjectSetPropertyRemovePropertyOTF);
        this.registerOtf(OperationType.OBJECT_SET, OperationType.OBJECT_VALUE, ObjectSetPropertySetOTF);
        this.registerOtf(OperationType.OBJECT_VALUE, OperationType.OBJECT_ADD, ObjectSetAddPropertyOTF);
        this.registerOtf(OperationType.OBJECT_VALUE, OperationType.OBJECT_SET, ObjectSetSetPropertyOTF);
        this.registerOtf(OperationType.OBJECT_VALUE, OperationType.OBJECT_REMOVE, ObjectSetRemovePropertyOTF);
        this.registerOtf(OperationType.OBJECT_VALUE, OperationType.OBJECT_VALUE, ObjectSetSetOTF);
        this.registerOtf(OperationType.ARRAY_INSERT, OperationType.ARRAY_INSERT, ArrayInsertInsertOTF);
        this.registerOtf(OperationType.ARRAY_INSERT, OperationType.ARRAY_REMOVE, ArrayInsertRemoveOTF);
        this.registerOtf(OperationType.ARRAY_INSERT, OperationType.ARRAY_SET, ArrayInsertReplaceOTF);
        this.registerOtf(OperationType.ARRAY_INSERT, OperationType.ARRAY_REORDER, ArrayInsertMoveOTF);
        this.registerOtf(OperationType.ARRAY_INSERT, OperationType.ARRAY_VALUE, ArrayInsertSetOTF);
        this.registerOtf(OperationType.ARRAY_REMOVE, OperationType.ARRAY_INSERT, ArrayRemoveInsertOTF);
        this.registerOtf(OperationType.ARRAY_REMOVE, OperationType.ARRAY_REMOVE, ArrayRemoveRemoveOTF);
        this.registerOtf(OperationType.ARRAY_REMOVE, OperationType.ARRAY_SET, ArrayRemoveReplaceOTF);
        this.registerOtf(OperationType.ARRAY_REMOVE, OperationType.ARRAY_REORDER, ArrayRemoveMoveOTF);
        this.registerOtf(OperationType.ARRAY_REMOVE, OperationType.ARRAY_VALUE, ArrayRemoveSetOTF);
        this.registerOtf(OperationType.ARRAY_SET, OperationType.ARRAY_INSERT, ArrayReplaceInsertOTF);
        this.registerOtf(OperationType.ARRAY_SET, OperationType.ARRAY_REMOVE, ArrayReplaceRemoveOTF);
        this.registerOtf(OperationType.ARRAY_SET, OperationType.ARRAY_SET, ArrayReplaceReplaceOTF);
        this.registerOtf(OperationType.ARRAY_SET, OperationType.ARRAY_REORDER, ArrayReplaceMoveOTF);
        this.registerOtf(OperationType.ARRAY_SET, OperationType.ARRAY_VALUE, ArrayReplaceSetOTF);
        this.registerOtf(OperationType.ARRAY_REORDER, OperationType.ARRAY_INSERT, ArrayMoveInsertOTF);
        this.registerOtf(OperationType.ARRAY_REORDER, OperationType.ARRAY_REMOVE, ArrayMoveRemoveOTF);
        this.registerOtf(OperationType.ARRAY_REORDER, OperationType.ARRAY_SET, ArrayMoveReplaceOTF);
        this.registerOtf(OperationType.ARRAY_REORDER, OperationType.ARRAY_REORDER, ArrayMoveMoveOTF);
        this.registerOtf(OperationType.ARRAY_REORDER, OperationType.ARRAY_VALUE, ArrayMoveSetOTF);
        this.registerOtf(OperationType.ARRAY_VALUE, OperationType.ARRAY_INSERT, ArraySetInsertOTF);
        this.registerOtf(OperationType.ARRAY_VALUE, OperationType.ARRAY_REMOVE, ArraySetRemoveOTF);
        this.registerOtf(OperationType.ARRAY_VALUE, OperationType.ARRAY_SET, ArraySetReplaceOTF);
        this.registerOtf(OperationType.ARRAY_VALUE, OperationType.ARRAY_REORDER, ArraySetMoveOTF);
        this.registerOtf(OperationType.ARRAY_VALUE, OperationType.ARRAY_VALUE, ArraySetSetOTF);
        this.registerOtf(OperationType.NUMBER_DELTA, OperationType.NUMBER_DELTA, NumberAddAddOTF);
        this.registerOtf(OperationType.NUMBER_DELTA, OperationType.NUMBER_VALUE, NumberAddSetOTF);
        this.registerOtf(OperationType.NUMBER_VALUE, OperationType.NUMBER_DELTA, NumberSetAddOTF);
        this.registerOtf(OperationType.NUMBER_VALUE, OperationType.NUMBER_VALUE, NumberSetSetOTF);
        this.registerOtf(OperationType.BOOLEAN_VALUE, OperationType.BOOLEAN_VALUE, BooleanSetSetOTF);
        this.registerOtf(OperationType.DATE_VALUE, OperationType.DATE_VALUE, DateSetSetOTF);
        this.registerRtf(ModelReference.Types.INDEX, OperationType.STRING_SPLICE, StringSpliceIndexTransformationFunction);
        this.registerRtf(ModelReference.Types.INDEX, OperationType.STRING_VALUE, StringSetIndexTransformationFunction);
        this.registerRtf(ModelReference.Types.RANGE, OperationType.STRING_SPLICE, StringSpliceRangeTransformationFunction);
        this.registerRtf(ModelReference.Types.RANGE, OperationType.STRING_VALUE, StringSetRangeTransformationFunction);
    }
    registerOtf(s, c, otf) {
        const key = s + c;
        if (this._otfs[key]) {
            throw new Error("Function already registered for " + s + ", " + c);
        }
        else {
            this._otfs[key] = otf;
        }
    }
    registerRtf(r, s, rtf) {
        const key = s + r;
        if (this._rtfs[key]) {
            throw new Error("Function already registered for " + s + ", " + r);
        }
        else {
            this._rtfs[key] = rtf;
        }
    }
    getOperationTransformationFunction(s, c) {
        const key = s.type + c.type;
        return this._otfs[key];
    }
    getReferenceTransformationFunction(o, r) {
        const key = o.type + r.type;
        return this._rtfs[key];
    }
}

class ReferenceTransformer {
    constructor(tfr) {
        this._tfr = tfr;
    }
    transform(o, r) {
        if (o instanceof CompoundOperation) {
            return this.transformCompoundOperation(o, r);
        }
        else {
            return this.transformDiscreteOperation(o, r);
        }
    }
    transformCompoundOperation(o, r) {
        let result = r;
        for (let i = 0; i < o.ops.length && result; i++) {
            result = this.transform(o.ops[i], result);
        }
        return result;
    }
    transformDiscreteOperation(s, r) {
        if (s.noOp) {
            return r;
        }
        else if (s.id === r.valueId) {
            return this.transformWithIdenticalPathOperation(s, r);
        }
        else {
            return r;
        }
    }
    transformWithIdenticalPathOperation(o, r) {
        const tf = this._tfr.getReferenceTransformationFunction(o, r);
        if (tf) {
            return tf(o, r);
        }
        else {
            throw new Error(`No function found for: (${o.type},${r.type})`);
        }
    }
}

class HistoricalElement extends ConvergenceEventEmitter {
    constructor(delegate, wrapperFactory, model) {
        super();
        this._delegate = delegate;
        this._wrapperFactory = wrapperFactory;
        this._model = model;
        this._delegate.events().subscribe((event) => {
            const convertedEvent = ModelEventConverter.convertEvent(event, this._wrapperFactory);
            this._emitEvent(convertedEvent);
        });
    }
    id() {
        return this._delegate.id();
    }
    type() {
        return this._delegate.type();
    }
    path() {
        return this._delegate.path();
    }
    relativePath() {
        const parentPath = this._delegate.path().slice(0);
        if (parentPath.length > 0) {
            return parentPath.pop();
        }
        else {
            return null;
        }
    }
    parent() {
        const parentPath = this._delegate.path().slice(0);
        parentPath.pop();
        const parent = this._model.elementAt(parentPath);
        return parent;
    }
    isAttached() {
        return !this._delegate.isDetached();
    }
    isDetached() {
        return this._delegate.isDetached();
    }
    value() {
        return this._delegate.data();
    }
    toJSON() {
        return this._delegate.toJson();
    }
    model() {
        return this._model;
    }
}
HistoricalElement.Events = ObservableElementEventConstants;

class HistoricalArray extends HistoricalElement {
    constructor(delegate, wrapperFactory, model) {
        super(delegate, wrapperFactory, model);
    }
    get(index) {
        return this._wrapperFactory.wrap(this._delegate.get(index));
    }
    length() {
        return this._delegate.length();
    }
    forEach(callback) {
        this._delegate.forEach((modelNode, index) => {
            callback(this._wrapperFactory.wrap(modelNode), index);
        });
    }
    elementAt(...path) {
        return this._wrapperFactory.wrap(this._delegate.valueAt(...path));
    }
}
HistoricalArray.Events = ObservableArrayEventConstants;

class HistoricalBoolean extends HistoricalElement {
    constructor(delegate, wrapperFactory, model) {
        super(delegate, wrapperFactory, model);
    }
}
HistoricalBoolean.Events = ObservableElementEventConstants;

class HistoricalObject extends HistoricalElement {
    constructor(delegate, wrapperFactory, model) {
        super(delegate, wrapperFactory, model);
    }
    get(key) {
        return this._wrapperFactory.wrap(this._delegate.get(key));
    }
    keys() {
        return this._delegate.keys();
    }
    hasKey(key) {
        return this._delegate.hasKey(key);
    }
    forEach(callback) {
        this._delegate.forEach((modelNode, key) => {
            callback(this._wrapperFactory.wrap(modelNode), key);
        });
    }
    elementAt(...path) {
        return this._wrapperFactory.wrap(this._delegate.valueAt(...path));
    }
}
HistoricalObject.Events = ObservableObjectEventConstants;

class HistoricalNull extends HistoricalElement {
    constructor(delegate, wrapperFactory, model) {
        super(delegate, wrapperFactory, model);
    }
}
HistoricalNull.Events = ObservableElementEventConstants;

class HistoricalNumber extends HistoricalElement {
    constructor(delegate, wrapperFactory, model) {
        super(delegate, wrapperFactory, model);
    }
}
HistoricalNumber.Events = ObservableNumberEventConstants;

class HistoricalString extends HistoricalElement {
    constructor(delegate, wrapperFactory, model) {
        super(delegate, wrapperFactory, model);
    }
    length() {
        return this._delegate.length();
    }
}
HistoricalString.Events = ObservableStringEventConstants;

class HistoricalUndefined extends HistoricalElement {
    constructor(delegate, wrapperFactory, model) {
        super(delegate, wrapperFactory, model);
    }
}
HistoricalUndefined.Events = ObservableElementEventConstants;

class HistoricalDate extends HistoricalElement {
    constructor(delegate, wrapperFactory, model) {
        super(delegate, wrapperFactory, model);
    }
}
HistoricalDate.Events = ObservableElementEventConstants;

class HistoricalWrapperFactory extends NodeWrapperFactory {
    constructor(_model) {
        super();
        this._model = _model;
    }
    _createWrapper(node) {
        switch (node.type()) {
            case ModelElementType.ARRAY:
                return new HistoricalArray(node, this, this._model);
            case ModelElementType.OBJECT:
                return new HistoricalObject(node, this, this._model);
            case ModelElementType.BOOLEAN:
                return new HistoricalBoolean(node, this, this._model);
            case ModelElementType.NULL:
                return new HistoricalNull(node, this, this._model);
            case ModelElementType.NUMBER:
                return new HistoricalNumber(node, this, this._model);
            case ModelElementType.STRING:
                return new HistoricalString(node, this, this._model);
            case ModelElementType.UNDEFINED:
                return new HistoricalUndefined(node, this, this._model);
            case ModelElementType.DATE:
                return new HistoricalDate(node, this, this._model);
            default:
                return null;
        }
    }
}

class ModelOperation {
    constructor(modelId, version, timestamp, user, sessionId, operation) {
        this.modelId = modelId;
        this.version = version;
        this.timestamp = timestamp;
        this.user = user;
        this.sessionId = sessionId;
        this.operation = operation;
        Object.freeze(this);
    }
}

class AppliedOperation {
    constructor(type) {
        this.type = type;
    }
}

class AppliedCompoundOperation extends AppliedOperation {
    constructor(ops) {
        super(OperationType.COMPOUND);
        this.ops = ops;
        Object.freeze(this);
    }
    inverse() {
        return new AppliedCompoundOperation(this.ops.map((op) => {
            return op.inverse();
        }));
    }
}

class AppliedDiscreteOperation extends AppliedOperation {
    constructor(type, id, noOp) {
        super(type);
        this.id = id;
        this.noOp = noOp;
    }
}

class AppliedArrayRemoveOperation extends AppliedDiscreteOperation {
    constructor(id, noOp, index, oldValue) {
        super(OperationType.ARRAY_REMOVE, id, noOp);
        this.index = index;
        this.oldValue = oldValue;
        Object.freeze(this);
    }
    inverse() {
        return new AppliedArrayInsertOperation(this.id, this.noOp, this.index, this.oldValue);
    }
}

class AppliedArrayInsertOperation extends AppliedDiscreteOperation {
    constructor(id, noOp, index, value) {
        super(OperationType.ARRAY_INSERT, id, noOp);
        this.index = index;
        this.value = value;
        Object.freeze(this);
    }
    inverse() {
        return new AppliedArrayRemoveOperation(this.id, this.noOp, this.index, this.value);
    }
}

class AppliedArrayMoveOperation extends AppliedDiscreteOperation {
    constructor(id, noOp, fromIndex, toIndex) {
        super(OperationType.ARRAY_REORDER, id, noOp);
        this.fromIndex = fromIndex;
        this.toIndex = toIndex;
        Object.freeze(this);
    }
    inverse() {
        return new AppliedArrayMoveOperation(this.id, this.noOp, this.toIndex, this.fromIndex);
    }
}

class AppliedArrayReplaceOperation extends AppliedDiscreteOperation {
    constructor(id, noOp, index, value, oldValue) {
        super(OperationType.ARRAY_SET, id, noOp);
        this.index = index;
        this.value = value;
        this.oldValue = oldValue;
        Object.freeze(this);
    }
    inverse() {
        return new AppliedArrayReplaceOperation(this.id, this.noOp, this.index, this.oldValue, this.value);
    }
}

class AppliedArraySetOperation extends AppliedDiscreteOperation {
    constructor(id, noOp, value, oldValue) {
        super(OperationType.ARRAY_VALUE, id, noOp);
        this.value = value;
        this.oldValue = oldValue;
        Object.freeze(this);
    }
    inverse() {
        return new AppliedArraySetOperation(this.id, this.noOp, this.oldValue, this.value);
    }
}

class AppliedObjectRemovePropertyOperation extends AppliedDiscreteOperation {
    constructor(id, noOp, prop, oldValue) {
        super(OperationType.OBJECT_REMOVE, id, noOp);
        this.prop = prop;
        this.oldValue = oldValue;
        Object.freeze(this);
    }
    inverse() {
        return new AppliedObjectAddPropertyOperation(this.id, this.noOp, this.prop, this.oldValue);
    }
}

class AppliedObjectAddPropertyOperation extends AppliedDiscreteOperation {
    constructor(id, noOp, prop, value) {
        super(OperationType.OBJECT_ADD, id, noOp);
        this.prop = prop;
        this.value = value;
        Object.freeze(this);
    }
    inverse() {
        return new AppliedObjectRemovePropertyOperation(this.id, this.noOp, this.prop, this.value);
    }
}

class AppliedObjectSetPropertyOperation extends AppliedDiscreteOperation {
    constructor(id, noOp, prop, value, oldValue) {
        super(OperationType.OBJECT_SET, id, noOp);
        this.prop = prop;
        this.value = value;
        this.oldValue = oldValue;
        Object.freeze(this);
    }
    inverse() {
        return new AppliedObjectSetPropertyOperation(this.id, this.noOp, this.prop, this.oldValue, this.value);
    }
}

class AppliedObjectSetOperation extends AppliedDiscreteOperation {
    constructor(id, noOp, value, oldValue) {
        super(OperationType.OBJECT_VALUE, id, noOp);
        this.value = value;
        this.oldValue = oldValue;
        Object.freeze(this);
    }
    inverse() {
        return new AppliedObjectSetOperation(this.id, this.noOp, this.oldValue, this.value);
    }
}

class AppliedStringSpliceOperation extends AppliedDiscreteOperation {
    constructor(id, noOp, index, deletedValue, deleteCount, insertValue) {
        super(OperationType.STRING_SPLICE, id, noOp);
        this.index = index;
        this.deletedValue = deletedValue;
        this.deleteCount = deleteCount;
        this.insertValue = insertValue;
        Object.freeze(this);
    }
    inverse() {
        return new AppliedStringSpliceOperation(this.id, this.noOp, this.index, this.insertValue, this.insertValue.length, this.deletedValue);
    }
}

class AppliedStringSetOperation extends AppliedDiscreteOperation {
    constructor(id, noOp, value, oldValue) {
        super(OperationType.STRING_VALUE, id, noOp);
        this.value = value;
        this.oldValue = oldValue;
        Object.freeze(this);
    }
    inverse() {
        return new AppliedStringSetOperation(this.id, this.noOp, this.oldValue, this.value);
    }
}

class AppliedNumberDeltaOperation extends AppliedDiscreteOperation {
    constructor(id, noOp, delta) {
        super(OperationType.NUMBER_DELTA, id, noOp);
        this.delta = delta;
        Object.freeze(this);
    }
    inverse() {
        return new AppliedNumberDeltaOperation(this.id, this.noOp, -this.delta);
    }
}

class AppliedNumberSetOperation extends AppliedDiscreteOperation {
    constructor(id, noOp, value, oldValue) {
        super(OperationType.NUMBER_VALUE, id, noOp);
        this.value = value;
        this.oldValue = oldValue;
        Object.freeze(this);
    }
    inverse() {
        return new AppliedNumberSetOperation(this.id, this.noOp, this.oldValue, this.value);
    }
}

class AppliedBooleanSetOperation extends AppliedDiscreteOperation {
    constructor(id, noOp, value, oldValue) {
        super(OperationType.BOOLEAN_VALUE, id, noOp);
        this.value = value;
        this.oldValue = oldValue;
        Object.freeze(this);
    }
    inverse() {
        return new AppliedBooleanSetOperation(this.id, this.noOp, this.oldValue, this.value);
    }
}

class AppliedDateSetOperation extends AppliedDiscreteOperation {
    constructor(id, noOp, value, oldValue) {
        super(OperationType.DATE_VALUE, id, noOp);
        this.value = value;
        this.oldValue = oldValue;
        Object.freeze(this);
    }
    inverse() {
        return new AppliedDateSetOperation(this.id, this.noOp, this.oldValue, this.value);
    }
}

function toModelOperation(operationData, identityCache) {
    let appliedOp;
    if (operationData.operation.compoundOperation) {
        appliedOp = toCompoundOperation(operationData.operation.compoundOperation);
    }
    else if (operationData.operation.discreteOperation) {
        appliedOp = toDiscreteOperation(operationData.operation.discreteOperation);
    }
    else {
        throw new ConvergenceError("Invalid model operation: " + JSON.stringify(operationData));
    }
    return new ModelOperation(operationData.modelId, getOrDefaultNumber(operationData.version), timestampToDate(operationData.timestamp), identityCache.getUserForSession(operationData.sessionId), operationData.sessionId, appliedOp);
}
function toCompoundOperation(compoundOperationData) {
    const discreteOps = getOrDefaultArray(compoundOperationData.operations).map(toDiscreteOperation);
    return new AppliedCompoundOperation(discreteOps);
}
function toDiscreteOperation(discreteOperationData) {
    if (discreteOperationData.arrayInsertOperation) {
        const { id, noOp, index, value } = discreteOperationData.arrayInsertOperation;
        return new AppliedArrayInsertOperation(id, getOrDefaultBoolean(noOp), getOrDefaultNumber(index), toDataValue(value));
    }
    else if (discreteOperationData.arrayRemoveOperation) {
        const { id, noOp, index, oldValue } = discreteOperationData.arrayRemoveOperation;
        const oldVal = noOp ? undefined : toDataValue(oldValue);
        return new AppliedArrayRemoveOperation(id, getOrDefaultBoolean(noOp), getOrDefaultNumber(index), oldVal);
    }
    else if (discreteOperationData.arrayMoveOperation) {
        const { id, noOp, fromIndex, toIndex } = discreteOperationData.arrayMoveOperation;
        return new AppliedArrayMoveOperation(id, getOrDefaultBoolean(noOp), getOrDefaultNumber(fromIndex), getOrDefaultNumber(toIndex));
    }
    else if (discreteOperationData.arrayReplaceOperation) {
        const { id, noOp, index, value, oldValue } = discreteOperationData.arrayReplaceOperation;
        const oldVal = noOp ? undefined : toDataValue(oldValue);
        return new AppliedArrayReplaceOperation(id, getOrDefaultBoolean(noOp), getOrDefaultNumber(index), toDataValue(value), oldVal);
    }
    else if (discreteOperationData.arraySetOperation) {
        const { id, noOp, values, oldValues } = discreteOperationData.arraySetOperation;
        const oldVal = noOp ? undefined : getOrDefaultArray(oldValues).map(toDataValue);
        return new AppliedArraySetOperation(id, getOrDefaultBoolean(noOp), getOrDefaultArray(values).map(toDataValue), oldVal);
    }
    else if (discreteOperationData.objectAddPropertyOperation) {
        const { id, noOp, key, value } = discreteOperationData.objectAddPropertyOperation;
        return new AppliedObjectAddPropertyOperation(id, getOrDefaultBoolean(noOp), getOrDefaultString(key), toDataValue(value));
    }
    else if (discreteOperationData.objectSetPropertyOperation) {
        const { id, noOp, key, value, oldValue } = discreteOperationData.objectSetPropertyOperation;
        const oldVal = noOp ? undefined : toDataValue(oldValue);
        return new AppliedObjectSetPropertyOperation(id, getOrDefaultBoolean(noOp), getOrDefaultString(key), toDataValue(value), oldVal);
    }
    else if (discreteOperationData.objectRemovePropertyOperation) {
        const { id, noOp, key, oldValue } = discreteOperationData.objectSetPropertyOperation;
        const oldVal = noOp ? undefined : toDataValue(oldValue);
        return new AppliedObjectRemovePropertyOperation(id, getOrDefaultBoolean(noOp), getOrDefaultString(key), oldVal);
    }
    else if (discreteOperationData.objectSetOperation) {
        const { id, noOp, values, oldValues } = discreteOperationData.objectSetOperation;
        const oldVal = noOp ? undefined : mapObjectValues(getOrDefaultObject(oldValues), toDataValue);
        return new AppliedObjectSetOperation(id, getOrDefaultBoolean(noOp), mapObjectValues(getOrDefaultObject(values), toDataValue), oldVal);
    }
    else if (discreteOperationData.stringSpliceOperation) {
        const { id, noOp, index, deletedValue, insertedValue } = discreteOperationData.stringSpliceOperation;
        return new AppliedStringSpliceOperation(id, getOrDefaultBoolean(noOp), getOrDefaultNumber(index), getOrDefaultString(deletedValue), getOrDefaultString(deletedValue).length, getOrDefaultString(insertedValue));
    }
    else if (discreteOperationData.stringSetOperation) {
        const { id, noOp, value, oldValue } = discreteOperationData.stringSetOperation;
        const oldVal = noOp ? undefined : getOrDefaultString(oldValue);
        return new AppliedStringSetOperation(id, getOrDefaultBoolean(noOp), getOrDefaultString(value), oldVal);
    }
    else if (discreteOperationData.numberDeltaOperation) {
        const { id, noOp, delta } = discreteOperationData.numberDeltaOperation;
        return new AppliedNumberDeltaOperation(id, getOrDefaultBoolean(noOp), getOrDefaultNumber(delta));
    }
    else if (discreteOperationData.numberSetOperation) {
        const { id, noOp, value, oldValue } = discreteOperationData.numberSetOperation;
        const oldVal = noOp ? undefined : getOrDefaultNumber(oldValue);
        return new AppliedNumberSetOperation(id, getOrDefaultBoolean(noOp), getOrDefaultNumber(value), oldVal);
    }
    else if (discreteOperationData.booleanSetOperation) {
        const { id, noOp, value, oldValue } = discreteOperationData.booleanSetOperation;
        const oldVal = noOp ? undefined : getOrDefaultBoolean(oldValue);
        return new AppliedBooleanSetOperation(id, getOrDefaultBoolean(noOp), getOrDefaultBoolean(value), oldVal);
    }
    else if (discreteOperationData.dateSetOperation) {
        const { id, noOp, value, oldValue } = discreteOperationData.dateSetOperation;
        const oldVal = noOp ? undefined : timestampToDate(oldValue);
        return new AppliedDateSetOperation(id, getOrDefaultBoolean(noOp), timestampToDate(value), oldVal);
    }
}

const HistoricalModelEventConstants = Object.assign(Object.assign({}, ObservableModelEventConstants), { TARGET_VERSION_CHANGED: "target_changed", TRANSITION_START: "transition_start", TRANSITION_END: "transition_end" });
Object.freeze(HistoricalModelEventConstants);
class HistoricalModel {
    constructor(data, version, modifiedTime, createdTime, modelId, collectionId, connection, session, identityCache) {
        this._session = session;
        this._connection = connection;
        this._identityCache = identityCache;
        this._modelId = modelId;
        this._collectionId = collectionId;
        this._model = new Model(this.session(), null, data, "error", "error");
        this._wrapperFactory = new HistoricalWrapperFactory(this);
        this._version = version;
        this._targetVersion = version;
        this._maxVersion = version;
        this._currentTime = modifiedTime;
        this._modifiedTime = modifiedTime;
        this._createdTime = createdTime;
        this._opRequests = [];
    }
    session() {
        return this._session;
    }
    collectionId() {
        return this._collectionId;
    }
    modelId() {
        return this._modelId;
    }
    time() {
        return this._currentTime;
    }
    minTime() {
        return this.createdTime();
    }
    maxTime() {
        return this._modifiedTime;
    }
    createdTime() {
        return this._createdTime;
    }
    version() {
        return this._version;
    }
    minVersion() {
        return 0;
    }
    maxVersion() {
        return this._maxVersion;
    }
    targetVersion() {
        return this._targetVersion;
    }
    isTransitioning() {
        return this._targetVersion === this._version;
    }
    root() {
        return this._wrapperFactory.wrap(this._model.root());
    }
    elementAt(...path) {
        return this._wrapperFactory.wrap(this._model.valueAt(...path));
    }
    playTo(version) {
        if (version < 0) {
            throw new Error(`Version must be >= 0: ${version}`);
        }
        else if (version > this._maxVersion) {
            throw new Error(`Version must be <= maxVersion: ${version}`);
        }
        let firstVersion;
        let lastVersion;
        let forward = null;
        if (version === this._targetVersion) {
            return Promise.resolve();
        }
        else if (version > this._targetVersion) {
            firstVersion = this._targetVersion + 1;
            lastVersion = version;
            forward = true;
        }
        else {
            firstVersion = version;
            lastVersion = this._targetVersion;
            forward = false;
        }
        this._targetVersion = version;
        const request = {
            historicalOperationsRequest: {
                modelId: this._modelId,
                first: firstVersion,
                last: lastVersion
            }
        };
        const opRequest = {
            forward,
            completed: false,
            operations: null
        };
        this._opRequests.push(opRequest);
        return this._connection.request(request).then((response) => {
            const { historicalOperationsResponse } = response;
            opRequest.completed = true;
            opRequest.operations = getOrDefaultArray(historicalOperationsResponse.operations)
                .map(op => toModelOperation(op, this._identityCache));
            if (!opRequest.forward && firstVersion !== 1) {
                opRequest.precedingOp = opRequest.operations.shift();
            }
            this._checkAndProcess();
            return;
        });
    }
    playToTime(time) {
        const request = {
            modelGetVersionAtTimeRequest: {
                modelId: this._modelId,
                targetTime: dateToTimestamp(time)
            }
        };
        return this._connection.request(request).then((response) => {
            const { modelGetVersionAtTimeResponse } = response;
            const version = getOrDefaultNumber(modelGetVersionAtTimeResponse.versionAtTime);
            return this.playTo(version);
        });
    }
    forward(delta = 1) {
        if (delta < 1) {
            throw new Error("delta must be > 0");
        }
        else if (this._targetVersion + delta > this._maxVersion) {
            throw new Error(`Cannot move forward by ${delta}, because that would exceed the model's maxVersion.`);
        }
        const desiredVersion = this._targetVersion + delta;
        return this.playTo(desiredVersion);
    }
    backward(delta = 1) {
        if (delta < 1) {
            throw new Error("delta must be > 0");
        }
        else if (this._targetVersion - delta < 0) {
            throw new Error(`Cannot move backward by ${delta}, because that would move beyond version 0.`);
        }
        const desiredVersion = this._targetVersion - delta;
        return this.playTo(desiredVersion);
    }
    _checkAndProcess() {
        if (this._opRequests.length === 0) {
            throw new Error("There are no operation requests to process");
        }
        while (this._opRequests.length > 0 && this._opRequests[0].completed) {
            const opRequest = this._opRequests[0];
            this._playOperations(opRequest.operations, opRequest.forward, opRequest.precedingOp);
            this._opRequests.shift();
        }
    }
    _playOperations(operations, forward, precedingOp) {
        if (!forward) {
            const backwardsOps = operations.reverse();
            backwardsOps.forEach((op, index) => {
                const before = index === backwardsOps.length - 1 ? precedingOp : backwardsOps[index + 1];
                if (op.operation.type === OperationType.COMPOUND) {
                    const compoundOp = op.operation;
                    compoundOp.ops.reverse().forEach((discreteOp) => {
                        this._playDiscreteOp(op, discreteOp, true, before);
                    });
                }
                else {
                    this._playDiscreteOp(op, op.operation, true, before);
                }
            });
        }
        else {
            operations.forEach((op) => {
                if (op.operation.type === OperationType.COMPOUND) {
                    const compoundOp = op.operation;
                    compoundOp.ops.forEach((discreteOp) => {
                        this._playDiscreteOp(op, discreteOp, false);
                    });
                }
                else {
                    this._playDiscreteOp(op, op.operation, false);
                }
            });
        }
    }
    _playDiscreteOp(op, discreteOp, inverse, precedingOp) {
        if (!discreteOp.noOp) {
            const dOp = inverse ?
                discreteOp.inverse() :
                discreteOp;
            this._model.handleModelOperationEvent(new ModelOperationEvent(op.sessionId, op.user, op.version, op.timestamp, dOp));
        }
        if (inverse) {
            if (op.version === 2) {
                this._version = 1;
                this._currentTime = this._createdTime;
            }
            else if (precedingOp !== undefined) {
                this._version = precedingOp.version;
                this._currentTime = precedingOp.timestamp;
            }
            else {
                throw new Error("no preceding operation was supplied to _playDiscreteOp");
            }
        }
        else {
            this._version = op.version;
            this._currentTime = new Date(op.timestamp);
        }
    }
}
HistoricalModel.Events = HistoricalModelEventConstants;

const ModelServiceEventConstants = {
    OFFLINE_MODEL_SYNC_STARTED: "offline_model_sync_started",
    OFFLINE_MODEL_SYNC_COMPLETED: "offline_model_sync_completed",
    OFFLINE_MODEL_SYNC_ERROR: "offline_model_sync_error",
    OFFLINE_MODEL_STATUS_CHANGED: "offline_model_status_changed",
    OFFLINE_MODEL_UPDATED: "offline_model_updated",
    OFFLINE_MODEL_DELETED: "offline_model_deleted",
    OFFLINE_MODEL_PERMISSIONS_REVOKED: "offline_model_permissions_revoked",
    OFFLINE_MODELS_DOWNLOAD_PENDING: "offline_models_download_pending",
    OFFLINE_MODELS_DOWNLOAD_PROGRESS: "offline_models_download_progress",
    OFFLINE_MODELS_DOWNLOAD_COMPLETED: "offline_models_download_completed",
    OFFLINE_MODELS_SYNC_STARTED: "offline_models_sync_started",
    OFFLINE_MODELS_SYNC_PROGRESS: "offline_models_sync_progress",
    OFFLINE_MODELS_SYNC_COMPLETED: "offline_models_sync_completed",
    OFFLINE_MODELS_SYNC_ABORTED: "offline_models_sync_aborted",
};
Object.freeze(ModelServiceEventConstants);
class ModelService extends ConvergenceEventEmitter {
    constructor(connection, identityCache, modelOfflineManager, undefinedObjectValues, undefinedArrayValues) {
        super();
        this._openModelRequests = new Map();
        this._openModels = new Map();
        this._resourceIdToModelId = new Map();
        this._resyncingModels = new Map();
        this._setOffline = () => {
            this._openModels.forEach((model) => {
                model._setOffline();
                if (this._resyncingModels.has(model.modelId())) {
                    this._resyncingModels.delete(model.modelId());
                }
            });
            this._resyncingModels.forEach(entry => {
                if (entry.resyncModel) {
                    entry.resyncModel._setOffline();
                    entry.resyncModel._initiateClose(false);
                }
            });
            this._resyncingModels.clear();
            this._modelResyncQueue.length = 0;
            if (this._offlineSyncStartedDeferred !== null) {
                this._offlineSyncStartedDeferred.resolve();
                this._offlineSyncStartedDeferred = null;
            }
            if (this._syncCompletedDeferred !== null) {
                this._emitEvent(new OfflineModelsSyncAbortedEvent("Convergence went offline during the sync", "offline"));
                this._syncCompletedDeferred.resolve();
                this._syncCompletedDeferred = null;
            }
            this._modelOfflineManager.setOffline();
            this._resourceIdToModelId.clear();
            this._checkResyncQueue();
        };
        this._setOnline = () => {
            this._openModels.forEach((model) => {
                this._resyncOpenModel(model);
            });
            if (this._resyncingModels.size !== 0) {
                this._syncCompletedDeferred = new Deferred();
            }
            if (this._modelOfflineManager.isOfflineEnabled()) {
                this._offlineSyncStartedDeferred = new ReplayDeferred();
                this._modelOfflineManager
                    .ready()
                    .then(() => this._initiateOfflineModelSync())
                    .catch((e) => this._log.error("Error resynchronizing models after reconnect", e));
            }
        };
        this._connection = connection;
        this._identityCache = identityCache;
        this._connection
            .messages()
            .subscribe(message => this._handleMessage(message));
        this._autoRequestId = 0;
        this._autoCreateRequests = new Map();
        this._connection.on(ConvergenceConnection.Events.INTERRUPTED, this._setOffline);
        this._connection.on(ConvergenceConnection.Events.DISCONNECTED, this._setOffline);
        this._connection.on(ConvergenceConnection.Events.CONNECTED, this._setOnline);
        this._modelResyncQueue = [];
        this._syncCompletedDeferred = null;
        this._offlineSyncStartedDeferred = null;
        this._modelOfflineManager = modelOfflineManager;
        this._emitFrom(modelOfflineManager.events());
        this._undefinedArrayValues = undefinedArrayValues;
        this._undefinedObjectValues = undefinedObjectValues;
        this._modelIdGenerator = new RandomStringGenerator(32, RandomStringGenerator.AlphaNumeric);
        this._log = Logging.logger("models");
    }
    session() {
        return this._connection.session();
    }
    isResyncing() {
        return this._syncCompletedDeferred !== null;
    }
    query(query) {
        Validation.assertNonEmptyString(query, "query");
        const request = {
            modelsQueryRequest: {
                query
            }
        };
        return this._connection
            .request(request)
            .then((response) => {
            const { modelsQueryResponse } = response;
            const data = getOrDefaultArray(modelsQueryResponse.models).map(toModelResult);
            const offset = getOrDefaultNumber(modelsQueryResponse.offset);
            const totalResults = getOrDefaultNumber(modelsQueryResponse.totalResults);
            return new PagedData(data, offset, totalResults);
        });
    }
    isOpen(id) {
        Validation.assertNonEmptyString(id, "id");
        return this._openModels.has(id);
    }
    isOpening(id) {
        Validation.assertNonEmptyString(id, "id");
        return this._openModelRequests.has(id);
    }
    open(id) {
        Validation.assertNonEmptyString(id, "id");
        return this._checkAndOpen(id);
    }
    openAutoCreate(options) {
        if (Validation.isNotSet(options)) {
            throw new ConvergenceError("'options' is a required parameter");
        }
        if (options.id === "") {
            throw new ConvergenceError("'options.id' can not be an empty string");
        }
        if (!Validation.nonEmptyString(options.collection)) {
            return Promise.reject(new Error("options.collection must be a non-null, non empty string."));
        }
        return this._checkAndOpen(undefined, options);
    }
    create(options) {
        if (Validation.isNotSet(options)) {
            throw new ConvergenceError("'options' is a required parameter");
        }
        if (!Validation.nonEmptyString(options.collection)) {
            return Promise.reject(new Error("options.collection must be a non-null, non empty string."));
        }
        return this._checkAndCreate(options);
    }
    remove(id) {
        return __awaiter(this, void 0, void 0, function* () {
            Validation.assertNonEmptyString(id, "id");
            if (this._offlineSyncStartedDeferred !== null) {
                yield this._offlineSyncStartedDeferred.promise();
            }
            if (this._resyncingModels.has(id)) {
                const entry = this._resyncingModels.get(id);
                if (entry.action === "delete") {
                    return Promise.reject(new Error("The model has already been deleted locally and resynchronization is in process."));
                }
                if (entry.inProgress) {
                    this._log.debug(`Removing model "${id}" the is resynchronizing. Waiting for resynchronization to complete before removing`);
                    yield entry.done.promise();
                }
                else {
                    entry.action = "delete";
                }
            }
            if (this._openModels.get(id)) {
                const model = this._openModels.get(id);
                model._handleLocallyDeleted();
                yield model.whenClosed();
            }
            if (this._modelOfflineManager.isOfflineEnabled()) {
                yield this._modelOfflineManager
                    .getModelMetaData(id)
                    .then(meta => {
                    if (meta) {
                        return this._modelOfflineManager.markModelForDeletion(id);
                    }
                })
                    .catch(e => {
                    this._log.error("Could not mark model for deletion", e);
                });
            }
            if (!this._connection.isConnected() && !this._modelOfflineManager.isOfflineEnabled()) {
                throw new ConvergenceError("Can not delete a model while not connected and without offline support enabled.");
            }
            else if (this._connection.isConnected()) {
                return this._removeOnline(id);
            }
        });
    }
    history(id) {
        Validation.assertNonEmptyString(id, "id");
        const request = {
            historicalDataRequest: {
                modelId: id
            }
        };
        return this._connection.request(request).then((response) => {
            const { historicalDataResponse } = response;
            return new HistoricalModel(toObjectValue(historicalDataResponse.data), getOrDefaultNumber(historicalDataResponse.version), timestampToDate(historicalDataResponse.modifiedTime), timestampToDate(historicalDataResponse.createdTime), id, historicalDataResponse.collectionId, this._connection, this.session(), this._identityCache);
        });
    }
    permissions(id) {
        Validation.assertNonEmptyString(id, "id");
        return new ModelPermissionManager(id, this._connection);
    }
    subscribeOffline(modelId) {
        const modelIds = TypeChecker.isArray(modelId) ? modelId : [modelId];
        return this._modelOfflineManager.subscribe(modelIds);
    }
    unsubscribeOffline(modelId) {
        const modelIds = TypeChecker.isArray(modelId) ? modelId : [modelId];
        return this._modelOfflineManager.unsubscribe(modelIds);
    }
    setOfflineSubscription(modelIds) {
        return this._modelOfflineManager.setSubscriptions(modelIds);
    }
    getOfflineSubscriptions() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this._modelOfflineManager.ready();
            const ids = yield this._modelOfflineManager.getSubscribedModelIds();
            return Array.from(ids);
        });
    }
    getOfflineModelMetaData() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this._modelOfflineManager.ready();
            return this._modelOfflineManager.getAllModelMetaData();
        });
    }
    _create(options, data) {
        return __awaiter(this, void 0, void 0, function* () {
            const collection = options.collection;
            if (this._connection.isConnected()) {
                const userPermissions = modelUserPermissionMapToProto(options.userPermissions && DomainUserIdMap.of(options.userPermissions));
                const dataValue = data || this._getDataFromCreateOptions(options);
                const request = {
                    createRealTimeModelRequest: {
                        collectionId: collection,
                        modelId: toOptional(options.id),
                        data: toIObjectValue(dataValue),
                        overrideWorldPermissions: options.overrideCollectionWorldPermissions,
                        worldPermissions: options.worldPermissions,
                        userPermissions
                    }
                };
                return this._connection.request(request).then((response) => {
                    const { createRealTimeModelResponse } = response;
                    return createRealTimeModelResponse.modelId;
                });
            }
            else if (this._modelOfflineManager.isOfflineEnabled()) {
                const id = options.id || this._modelIdGenerator.nextString();
                const dataValue = data || this._getDataFromCreateOptions(options);
                const userPermissions = options.userPermissions &&
                    DomainUserIdMap.of(options.userPermissions).toGuidObjectMap();
                const creationData = {
                    modelId: id,
                    collection: options.collection,
                    initialData: dataValue,
                    createdTime: new Date(),
                    valueIdPrefix: "@",
                    overrideCollectionWorldPermissions: options.overrideCollectionWorldPermissions,
                    worldPermissions: options.worldPermissions,
                    userPermissions
                };
                yield this._modelOfflineManager.createOfflineModel(creationData);
                if (this._connection.isConnected()) {
                    const presentlySyncing = this._resyncingModels.size > 0 || this._modelResyncQueue.length > 0;
                    this._addResyncModel(id, "resync");
                    if (!presentlySyncing) {
                        this._checkResyncQueue();
                    }
                }
                return id;
            }
            else {
                throw new ConvergenceError("Can not create a model while not connected and without offline support enabled.");
            }
        });
    }
    _close(model) {
        const modelId = model.modelId();
        this._log.debug("Model closed: " + model.modelId());
        if (this._openModels.has(modelId)) {
            this._openModels.delete(modelId);
        }
        const resourceId = model._getResourceId();
        if (resourceId !== null) {
            this._resourceIdToModelId.delete(resourceId);
        }
    }
    _resyncStarted(modelId, resourceId) {
        this._registerResourceId(modelId, resourceId);
    }
    _resyncCompleted(modelId) {
        this._emitEvent(new OfflineModelSyncCompletedEvent(modelId));
        this._log.debug(`Resync completed for model: "${modelId}"`);
        const entry = this._resyncingModels.get(modelId);
        entry.done.resolve();
        this._resyncingModels.delete(modelId);
        this._checkResyncQueue();
    }
    _resyncError(modelId, message, model) {
        this._log.debug(`Resync error for model: "${modelId}"... ${message}`);
        const event = new OfflineModelSyncErrorEvent(modelId, message, model);
        this._emitEvent(event);
        const entry = this._resyncingModels.get(modelId);
        entry.ready.reject(new Error(message));
        this._resyncCompleted(modelId);
    }
    _emitError(errorEvent) {
        this._emitEvent(errorEvent);
        this._connection.session().domain()._emitError(errorEvent);
    }
    _dispose() {
        this._openModels.forEach(model => model
            .close()
            .catch(err => this._log.error(err)));
    }
    _registerResourceId(modelId, resourceId) {
        this._log.debug(`Registering resource id: {modelId: "${modelId}", resourceId: ${resourceId}}`);
        this._resourceIdToModelId.set(resourceId, modelId);
    }
    _checkAndOpen(id, options) {
        return __awaiter(this, void 0, void 0, function* () {
            if (id === undefined && options === undefined) {
                throw new Error("Internal error, id or options must be defined.");
            }
            if (this._offlineSyncStartedDeferred !== null) {
                yield this._offlineSyncStartedDeferred.promise();
            }
            if (TypeChecker.isNotSet(id)) {
                id = options.id;
            }
            if (id && this._openModels.has(id)) {
                const model = this._openModels.get(id);
                if (model.isClosing()) {
                    return model.whenClosed().then(() => this._open(id, options));
                }
                else {
                    this._log.debug(`Opening model '${id}' that is already open`);
                    return Promise.resolve(model);
                }
            }
            if (id && this._openModelRequests.has(id)) {
                this._log.debug(`Opening model '${id}' that is already opening`);
                return this._openModelRequests.get(id).promise();
            }
            const resyncEntry = this._resyncingModels.get(id);
            if (resyncEntry && resyncEntry.resyncModel) {
                this._log.debug(`Opening model '${id}' that is resyncing`);
                if (resyncEntry.resyncModel.isClosing()) {
                    return resyncEntry.resyncModel.whenClosed().then(() => this._open(id, options));
                }
                else {
                    resyncEntry.resyncModel._openAfterResync();
                    return Promise.resolve(resyncEntry.resyncModel);
                }
            }
            return this._open(id, options);
        });
    }
    _open(id, options) {
        const autoRequestId = options ? this._autoRequestId++ : undefined;
        if (options !== undefined) {
            this._autoCreateRequests.set(autoRequestId, options);
        }
        const deferred = new Deferred();
        if (id !== undefined) {
            this._openModelRequests.set(id, deferred);
        }
        let open;
        if (this._connection.isConnected()) {
            open = this._openOnline(id, autoRequestId);
        }
        else if (this._modelOfflineManager.isOfflineEnabled()) {
            open = this._openOffline(id, autoRequestId);
        }
        else {
            open = Promise.reject(new ConvergenceError("Can not open a model while not online and without offline enabled."));
        }
        const result = open.then(model => {
            this._clearOpenRequestRecords(id, autoRequestId);
            this._openModels.set(model.modelId(), model);
            if (model._isOffline() && this._connection.isConnected()) {
                this._resyncOpenModel(model);
            }
            return model;
        }).catch((error) => {
            this._clearOpenRequestRecords(id, autoRequestId);
            return Promise.reject(error);
        });
        deferred.resolveFromPromise(result);
        return deferred.promise();
    }
    _resyncOpenModel(model) {
        this._addResyncModel(model.modelId(), "resync", model);
        model._setOnline();
    }
    _addResyncModel(modelId, action, model) {
        const entry = {
            modelId,
            action,
            inProgress: model !== undefined,
            done: new ReplayDeferred(),
            ready: model !== undefined ? ReplayDeferred.resolved() : new ReplayDeferred(),
            resyncModel: model || null
        };
        this._resyncingModels.set(modelId, entry);
        if (!entry.inProgress) {
            this._modelResyncQueue.push(entry);
        }
    }
    _clearOpenRequestRecords(id, autoRequestId) {
        if (id !== undefined) {
            this._openModelRequests.delete(id);
        }
        if (autoRequestId !== undefined) {
            this._autoCreateRequests.delete(autoRequestId);
        }
    }
    _openOnline(id, autoRequestId) {
        if (TypeChecker.isString(id) && this._modelOfflineManager.isOfflineEnabled()) {
            this._log.debug(`Opening model '${id}' while online`);
            return this._openOnlineWithOfflineEnabled(id, autoRequestId);
        }
        else {
            return this._requestOpenFromServer(id, autoRequestId);
        }
    }
    _openOnlineWithOfflineEnabled(id, autoRequestId) {
        return __awaiter(this, void 0, void 0, function* () {
            const index = this._modelResyncQueue.findIndex(entry => entry.modelId === id);
            if (index >= 0) {
                const [entry] = this._modelResyncQueue.splice(index, 1);
                yield this._initiateModelResync(entry.modelId);
            }
            if (this._resyncingModels.has(id)) {
                const entry = this._resyncingModels.get(id);
                yield entry.ready.promise();
                if (entry.action === "resync") {
                    return entry.resyncModel;
                }
                else {
                    this._requestOpenFromServer(id, autoRequestId);
                }
            }
            else {
                return this._requestOpenFromServer(id, autoRequestId);
            }
        });
    }
    _requestOpenFromServer(id, autoRequestId) {
        const request = {
            openRealTimeModelRequest: {
                modelId: toOptional(id),
                autoCreateId: toOptional(autoRequestId)
            }
        };
        return this._connection.request(request)
            .then((response) => {
            const { openRealTimeModelResponse } = response;
            const data = toObjectValue(openRealTimeModelResponse.data);
            const model = this._createModel(getOrDefaultNumber(openRealTimeModelResponse.resourceId), getOrDefaultString(openRealTimeModelResponse.modelId), getOrDefaultString(openRealTimeModelResponse.collection), false, false, getOrDefaultNumber(openRealTimeModelResponse.version), 0, timestampToDate(openRealTimeModelResponse.createdTime), timestampToDate(openRealTimeModelResponse.modifiedTime), getOrDefaultString(openRealTimeModelResponse.valueIdPrefix), getOrDefaultArray(openRealTimeModelResponse.connectedClients), getOrDefaultArray(openRealTimeModelResponse.resyncingClients), getOrDefaultArray(openRealTimeModelResponse.references), toModelPermissions(openRealTimeModelResponse.permissions), data);
            this._registerResourceId(model.modelId(), getOrDefaultNumber(openRealTimeModelResponse.resourceId));
            if (this._modelOfflineManager.isOfflineEnabled()) {
                return this._modelOfflineManager
                    .storeOpenModelOffline(model)
                    .then(() => {
                    this._modelOfflineManager.modelOpened(model, 0);
                    return model;
                });
            }
            else {
                return model;
            }
        });
    }
    _openOffline(id, autoRequestId) {
        return __awaiter(this, void 0, void 0, function* () {
            if (TypeChecker.isUndefined(id)) {
                id = this._modelIdGenerator.nextString();
            }
            this._log.debug(`Opening model '${id}' while offline`);
            const modelState = yield this._modelOfflineManager.getOfflineModelState(id);
            if (TypeChecker.isSet(modelState)) {
                const vidPrefix = yield this._modelOfflineManager.claimValueIdPrefix(id);
                const model = this._creteModelFromOfflineState(modelState, vidPrefix, false);
                return Promise.resolve(model);
            }
            else if (this._autoCreateRequests.has(autoRequestId)) {
                const options = this._autoCreateRequests.get(autoRequestId);
                const model = this._createNewModelOffline(id, options);
                const snapshot = model._getConcurrencyControlStateSnapshot();
                const userPermissions = options.userPermissions &&
                    DomainUserIdMap.of(options.userPermissions).toGuidObjectMap();
                const creationData = {
                    modelId: id,
                    collection: options.collection,
                    initialData: snapshot.data,
                    createdTime: new Date(),
                    valueIdPrefix: "@",
                    overrideCollectionWorldPermissions: options.overrideCollectionWorldPermissions,
                    worldPermissions: options.worldPermissions,
                    userPermissions
                };
                return this._modelOfflineManager.createOfflineModel(creationData).then(() => model);
            }
            else {
                return Promise.reject(new Error("Model not available offline, and not auto create options were provided"));
            }
        });
    }
    _creteModelFromOfflineState(state, valueIdPrefix, resyncOnly) {
        this._log.debug(`Creating model '${state.modelId}' from offline state`);
        const resourceId = null;
        const model = this._createModel(resourceId, state.modelId, state.collection, state.local, resyncOnly, state.snapshot.version, state.snapshot.sequenceNumber, state.createdTime, state.modifiedTime, `${valueIdPrefix.prefix}-${valueIdPrefix.increment}`, [], [], [], state.permissions, state.snapshot.data);
        model._setOffline();
        model._rehydrateFromOfflineState(state.version, state.snapshot.serverOperations, state.snapshot.localOperations);
        const opsSinceSnapshot = (state.version - state.snapshot.version) + (state.lastSequenceNumber - state.snapshot.sequenceNumber);
        this._modelOfflineManager.modelOpened(model, opsSinceSnapshot);
        return model;
    }
    _removeOnline(id) {
        this._log.debug(`Removing model online: "${id}"`);
        const request = {
            deleteRealtimeModelRequest: {
                modelId: id
            }
        };
        return this._connection
            .request(request)
            .then(() => {
            return this._handleOnlineDeletion(id);
        })
            .catch(e => {
            if (!(e instanceof ConvergenceError && e.code === ConvergenceErrorCodes.REQUEST_TIMEOUT)) {
                this._handleOnlineDeletion(id);
            }
            return Promise.reject(e);
        });
    }
    _handleOnlineDeletion(id) {
        if (this._modelOfflineManager.isOfflineEnabled()) {
            return this._modelOfflineManager
                .getModelMetaData(id)
                .then(meta => {
                if (meta) {
                    return this._modelOfflineManager.modelDeleted(id);
                }
            })
                .catch((e) => {
                this._emitEvent(new ErrorEvent(this._connection.session().domain(), `There was an error removing the model with id '${id}' from the offline store: ${e.message}`));
                this._log.error("Error removing model from offline store.", e);
            });
        }
        else {
            return Promise.resolve();
        }
    }
    _createNewModelOffline(id, options) {
        this._log.debug(`Creating new offline model '${id}' using auto-create options.`);
        const dataValue = this._getDataFromCreateOptions(options);
        const resourceId = null;
        const valueIdPrefix = "0";
        const currentTime = new Date();
        const permissions = new ModelPermissions(true, true, true, true);
        const version = 1;
        const local = true;
        const resyncOnly = false;
        const model = this._createModel(resourceId, id, options.collection, local, resyncOnly, version, 0, currentTime, currentTime, valueIdPrefix, [], [], [], permissions, dataValue);
        model._setOffline();
        this._modelOfflineManager.modelOpened(model, 0);
        return model;
    }
    _createModel(resourceId, modelId, collection, local, resyncModel, version, sequenceNumber, createdTime, modifiedTime, valueIdPrefix, connectedClients, resyncingClients, references, permissions, data) {
        const transformer = new OperationTransformer(new TransformationFunctionRegistry());
        const referenceTransformer = new ReferenceTransformer(new TransformationFunctionRegistry());
        const clientConcurrencyControl = new ClientConcurrencyControl(() => this._connection.session().sessionId(), version, sequenceNumber, transformer, referenceTransformer);
        const model = new RealTimeModel(resourceId, valueIdPrefix, data, local, resyncModel, connectedClients, resyncingClients, references, permissions, createdTime, modifiedTime, modelId, collection, clientConcurrencyControl, this._connection, this._identityCache, this, this._modelOfflineManager, this._undefinedObjectValues, this._undefinedArrayValues);
        return model;
    }
    _handleMessage(messageEvent) {
        const message = messageEvent.message;
        const resourceId = getModelMessageResourceId(message);
        if (resourceId !== null) {
            const model = this._getModelForResourceId(resourceId);
            if (model) {
                model._handleMessage(messageEvent);
            }
            else {
                this._log.warn("Received a message for a model that is not open or resynchronizing: " + JSON.stringify(message));
            }
        }
        else if (message.modelAutoCreateConfigRequest) {
            this._handleModelDataRequest(message.modelAutoCreateConfigRequest, messageEvent.callback);
        }
    }
    _getModelForResourceId(resourceId) {
        const modelId = this._resourceIdToModelId.get(resourceId);
        if (modelId === undefined) {
            return null;
        }
        const openModel = this._openModels.get(modelId);
        if (openModel !== undefined) {
            return openModel;
        }
        const resyncModel = this._resyncingModels.get(modelId);
        if (resyncModel !== undefined) {
            if (resyncModel.resyncModel) {
                return resyncModel.resyncModel;
            }
            else {
                this._log.warn("Received a message for model that is resyncing but not ready: " + modelId);
                return null;
            }
        }
        return null;
    }
    _handleModelDataRequest(request, replyCallback) {
        const autoCreateId = request.autoCreateId || 0;
        if (!this._autoCreateRequests.has(autoCreateId)) {
            const message = `Received a request for an auto create id that was not expected: ${autoCreateId}`;
            this._log.error(message);
            replyCallback.expectedError("unknown_model", message);
        }
        else {
            const options = this._autoCreateRequests.get(autoCreateId);
            this._autoCreateRequests.delete(autoCreateId);
            const dataValue = this._getDataFromCreateOptions(options);
            const userPermissions = modelUserPermissionMapToProto(options.userPermissions && DomainUserIdMap.of(options.userPermissions));
            const response = {
                modelAutoCreateConfigResponse: {
                    data: toIObjectValue(dataValue),
                    ephemeral: options.ephemeral,
                    collection: options.collection,
                    overridePermissions: options.overrideCollectionWorldPermissions,
                    worldPermissions: options.worldPermissions,
                    userPermissions
                }
            };
            replyCallback.reply(response);
        }
    }
    _getDataFromCreateOptions(options) {
        let data = options.data;
        if (TypeChecker.isFunction(data)) {
            data = data();
        }
        else if (TypeChecker.isNotSet(data)) {
            data = {};
        }
        data = Object.assign({}, data);
        const idGen = new InitialIdGenerator();
        const dataValueFactory = new DataValueFactory(() => idGen.id(), this._undefinedObjectValues, this._undefinedArrayValues);
        return dataValueFactory.createDataValue(data);
    }
    _initiateOfflineModelSync() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this._connection.isConnected()) {
                yield this._syncDirtyModelsToServer();
            }
            if (this._connection.isConnected()) {
                yield this._modelOfflineManager.setOnline();
            }
        });
    }
    _syncDirtyModelsToServer() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this._syncCompletedDeferred === null) {
                this._syncCompletedDeferred = new Deferred();
            }
            const syncNeeded = yield this._modelOfflineManager.getModelsRequiringSync();
            this._emitEvent(new OfflineModelsSyncStartedEvent(syncNeeded.length));
            syncNeeded.forEach(metaData => {
                if (!this._openModels.has(metaData.modelId) &&
                    !this._openModelRequests.has(metaData.modelId) &&
                    !this._resyncingModels.has(metaData.modelId)) {
                    this._addResyncModel(metaData.modelId, metaData.available ? "resync" : "delete");
                }
            });
            const promise = this._syncCompletedDeferred.promise();
            if (this._offlineSyncStartedDeferred !== null) {
                this._offlineSyncStartedDeferred.resolve();
                this._offlineSyncStartedDeferred = null;
            }
            this._checkResyncQueue();
            return promise;
        });
    }
    _checkResyncQueue() {
        if (!this._connection.isConnected()) {
            return;
        }
        const inProgress = Array.from(this._resyncingModels.values()).filter(m => m.inProgress);
        this._log.debug("Checking resync status");
        this._log.debug(() => {
            const modelIds = inProgress.map(e => e.modelId);
            return `In Progress Resync Models (${inProgress.length}): ${JSON.stringify(modelIds)}`;
        });
        this._log.debug(() => {
            const modelIds = this._modelResyncQueue.map(e => e.modelId);
            return `Queued Resync Models (${this._modelResyncQueue.length}): ${JSON.stringify(modelIds)}`;
        });
        if (this._offlineSyncStartedDeferred === null) {
            this._emitEvent(new OfflineModelsSyncProgressEvent(inProgress.length + this._modelResyncQueue.length));
        }
        if (inProgress.length === 0) {
            if (this._modelResyncQueue.length === 0) {
                if (this._offlineSyncStartedDeferred !== null) {
                    this._log.debug(`Resync queue is empty, but waiting for offline sync to start.`);
                    this._offlineSyncStartedDeferred.promise().then(() => this._checkResyncQueue());
                }
                else if (this._syncCompletedDeferred !== null) {
                    this._log.debug(`Resync queue is empty, completing resync process`);
                    this._emitEvent(new OfflineModelsSyncCompletedEvent());
                    this._syncCompletedDeferred.resolve();
                    this._syncCompletedDeferred = null;
                }
            }
            else {
                const entry = this._modelResyncQueue.pop();
                this._initiateModelResync(entry.modelId).catch((e) => {
                    this._log.error("Error resyncing models", e);
                });
            }
        }
    }
    _initiateModelResync(modelId) {
        return __awaiter(this, void 0, void 0, function* () {
            this._log.debug(`Initiating sync for model: "${modelId}"`);
            const entry = this._resyncingModels.get(modelId);
            this._emitEvent(new OfflineModelSyncStartedEvent(entry.modelId));
            if (entry.action === "resync") {
                const modelState = yield this._modelOfflineManager.getOfflineModelState(entry.modelId);
                if (TypeChecker.isSet(modelState)) {
                    const prefix = yield this._modelOfflineManager.claimValueIdPrefix(entry.modelId);
                    const model = this._createAndSyncModel(modelState, prefix);
                    entry.resyncModel = model;
                    entry.ready.resolve();
                }
                else {
                    const message = `The state for model "${modelId}" could not be found to resynchronize the model`;
                    this._resyncError(modelId, message);
                    return Promise.reject(new Error(message));
                }
            }
            else {
                return this._deleteResyncModel(entry.modelId)
                    .then(() => entry.ready.resolve())
                    .catch((e) => {
                    this._resyncError(modelId, e.message);
                    return Promise.reject(new Error(e.message));
                });
            }
        });
    }
    _deleteResyncModel(modelId) {
        this._log.debug(`Deleting resync model from the server: "${modelId}"`);
        return this._removeOnline(modelId)
            .then(() => this._resyncCompleted(modelId))
            .catch((e) => {
            if (e instanceof ConvergenceServerError && e.code === "model_not_found") {
                this._resyncCompleted(modelId);
                return Promise.resolve();
            }
            else {
                return Promise.reject(e);
            }
        });
    }
    _createAndSyncModel(modelState, valueIdPrefix) {
        this._log.debug(`Creating and resynchronizing model: ${modelState.modelId}`);
        const model = this._creteModelFromOfflineState(modelState, valueIdPrefix, true);
        model._setOnline();
        return model;
    }
    _checkAndCreate(options) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this._openModels.has(options.id)) {
                const message = `A model with id '${options.id}' is open locally.`;
                return Promise.reject(new ConvergenceError(message));
            }
            if (this._offlineSyncStartedDeferred !== null) {
                yield this._offlineSyncStartedDeferred.promise();
            }
            if (this._resyncingModels.has(options.id)) {
                const entry = this._resyncingModels.get(options.id);
                if (entry.action === "resync") {
                    const message = `A model with id '${options.id}' is resynchronizing.`;
                    return Promise.reject(new ConvergenceError(message));
                }
                else {
                    yield entry.ready;
                }
            }
            return this._create(options);
        });
    }
}
ModelService.Events = ModelServiceEventConstants;
class InitialIdGenerator {
    constructor() {
        this._prefix = "@";
        this._id = 0;
    }
    id() {
        return this._prefix + ":" + this._id++;
    }
}

class ActivityParticipant {
    constructor(activity, user, sessionId, local, state) {
        this.activity = activity;
        this.user = user;
        this.sessionId = sessionId;
        this.local = local;
        this._state = deepClone(state);
        Object.freeze(this);
    }
    get state() {
        return deepClone(this._state);
    }
    clone(modifications = {}) {
        return new ActivityParticipant(modifications.activity !== undefined ? modifications.activity : this.activity, modifications.user !== undefined ? modifications.user : this.user, modifications.sessionId !== undefined ? modifications.sessionId : this.sessionId, modifications.local !== undefined ? modifications.local : this.local, modifications.state !== undefined ? modifications.state : this.state);
    }
}

class ActivitySessionJoinedEvent {
    constructor(activity, user, sessionId, local, participant) {
        this.activity = activity;
        this.user = user;
        this.sessionId = sessionId;
        this.local = local;
        this.participant = participant;
        this.name = ActivitySessionJoinedEvent.EVENT_NAME;
        Object.freeze(this);
    }
}
ActivitySessionJoinedEvent.EVENT_NAME = "session_joined";

class ActivitySessionLeftEvent {
    constructor(activity, user, sessionId, local) {
        this.activity = activity;
        this.user = user;
        this.sessionId = sessionId;
        this.local = local;
        this.name = ActivitySessionLeftEvent.EVENT_NAME;
        Object.freeze(this);
    }
}
ActivitySessionLeftEvent.EVENT_NAME = "session_left";

class ActivityStateSetEvent {
    constructor(activity, user, sessionId, local, key, value, oldValue) {
        this.activity = activity;
        this.user = user;
        this.sessionId = sessionId;
        this.local = local;
        this.key = key;
        this.value = value;
        this.oldValue = oldValue;
        this.name = ActivityStateSetEvent.EVENT_NAME;
        Object.freeze(this);
    }
}
ActivityStateSetEvent.EVENT_NAME = "state_set";

class ActivityStateRemovedEvent {
    constructor(activity, user, sessionId, local, key, oldValue) {
        this.activity = activity;
        this.user = user;
        this.sessionId = sessionId;
        this.local = local;
        this.key = key;
        this.oldValue = oldValue;
        this.name = ActivityStateRemovedEvent.EVENT_NAME;
        Object.freeze(this);
    }
}
ActivityStateRemovedEvent.EVENT_NAME = "state_removed";

class ActivityStateClearedEvent {
    constructor(activity, user, sessionId, local, oldValues) {
        this.activity = activity;
        this.user = user;
        this.sessionId = sessionId;
        this.local = local;
        this.oldValues = oldValues;
        this.name = ActivityStateClearedEvent.EVENT_NAME;
        Object.freeze(this);
    }
}
ActivityStateClearedEvent.EVENT_NAME = "state_cleared";

class ActivityStateDeltaEvent {
    constructor(activity, user, sessionId, local, values, complete, removed, oldValues) {
        this.activity = activity;
        this.user = user;
        this.sessionId = sessionId;
        this.local = local;
        this.values = values;
        this.complete = complete;
        this.removed = removed;
        this.oldValues = oldValues;
        this.name = ActivityStateDeltaEvent.EVENT_NAME;
        Object.freeze(this);
    }
}
ActivityStateDeltaEvent.EVENT_NAME = "state_delta";

class ActivityLeftEvent {
    constructor(activity, user, sessionId, local) {
        this.activity = activity;
        this.user = user;
        this.sessionId = sessionId;
        this.local = local;
        this.name = ActivityLeftEvent.EVENT_NAME;
        Object.freeze(this);
    }
}
ActivityLeftEvent.EVENT_NAME = "left";

class ActivityPermissionUtils {
    static userPermissions(userPermissions) {
        const result = [];
        if (userPermissions instanceof DomainUserIdMap) {
            userPermissions.forEach((permissions, userId) => {
                result.push(ActivityPermissionUtils.toUserPermissionEntry(userId, permissions));
            });
        }
        else if (TypeChecker.isMap(userPermissions)) {
            userPermissions.forEach((permissions, userId) => {
                userId = DomainUserId.of(userId);
                result.push(ActivityPermissionUtils.toUserPermissionEntry(userId, permissions));
            });
        }
        else if (TypeChecker.isObject(userPermissions)) {
            objectForEach(userPermissions, (username, permissions) => {
                const userId = DomainUserId.of(username);
                result.push(ActivityPermissionUtils.toUserPermissionEntry(userId, permissions));
            });
        }
        return result;
    }
    static toGroupPermissionsProto(groupPermissions) {
        const results = {};
        if (TypeChecker.isMap(groupPermissions) || TypeChecker.isObject(groupPermissions)) {
            StringMap.coerceToMap(groupPermissions).forEach((permissions, groupId) => {
                results[groupId] = permissions;
            });
        }
        return results;
    }
    static toUserPermissionEntry(userId, permissions) {
        return {
            user: domainUserIdToProto(userId),
            permissions
        };
    }
}

class AbstractPermissionManager {
    constructor(connection) {
        this._connection = connection;
    }
    resolveSessionPermissions() {
        this._connection.session().assertOnline();
        const request = {
            resolvePermissionsForConnectedSessionRequest: {
                target: this._getTarget()
            }
        };
        return this._connection.request(request).then((response) => {
            const { resolvePermissionsForConnectedSessionResponse } = response;
            return new Set(getOrDefaultArray(resolvePermissionsForConnectedSessionResponse.permissions));
        });
    }
    getPermissions() {
        this._connection.session().assertOnline();
        const request = {
            getPermissionsRequest: {
                target: this._getTarget()
            }
        };
        return this._connection
            .request(request)
            .then((response) => {
            const { getPermissionsResponse } = response;
            const worldPermissions = new Set(getOrDefaultArray(getPermissionsResponse.world));
            const userPermissions = this._permissionsEntriesToUserMap(getOrDefaultArray(getPermissionsResponse.user));
            const groupPermissions = StringMap.coerceToMap(mapObjectValues(getOrDefaultObject(getPermissionsResponse.group), permissionsList => {
                return new Set(getOrDefaultArray(permissionsList.values));
            }));
            return {
                worldPermissions,
                groupPermissions,
                userPermissions
            };
        });
    }
    addWorldPermissions(permissions) {
        this._connection.session().assertOnline();
        const request = {
            addPermissionsRequest: {
                target: this._getTarget(),
                world: Array.from(permissions)
            }
        };
        return this._connection.request(request).then(() => {
            return;
        });
    }
    removeWorldPermissions(permissions) {
        this._connection.session().assertOnline();
        const request = {
            removePermissionsRequest: {
                target: this._getTarget(),
                world: Array.from(permissions)
            }
        };
        return this._connection.request(request).then(() => {
            return;
        });
    }
    setWorldPermissions(permissions) {
        this._connection.session().assertOnline();
        const request = {
            setPermissionsRequest: {
                target: this._getTarget(),
                world: { permissions: Array.from(permissions) }
            }
        };
        return this._connection.request(request).then(() => {
            return;
        });
    }
    getWorldPermissions() {
        this._connection.session().assertOnline();
        return this.getPermissions().then(permissions => permissions.worldPermissions);
    }
    addUserPermissions(permissions) {
        this._connection.session().assertOnline();
        const userIdMap = DomainUserIdMap.of(permissions);
        const request = {
            addPermissionsRequest: {
                target: this._getTarget(),
                user: this._permissionsMapToPermissionEntries(userIdMap)
            }
        };
        return this._connection.request(request).then(() => {
            return;
        });
    }
    removeUserPermissions(permissions) {
        this._connection.session().assertOnline();
        const userIdMap = DomainUserIdMap.of(permissions);
        const request = {
            removePermissionsRequest: {
                target: this._getTarget(),
                user: this._permissionsMapToPermissionEntries(userIdMap)
            }
        };
        return this._connection.request(request).then(() => {
            return;
        });
    }
    setUserPermissions(permissions, replaceAll = false) {
        this._connection.session().assertOnline();
        const userIdMap = DomainUserIdMap.of(permissions);
        const request = {
            setPermissionsRequest: {
                target: this._getTarget(),
                user: {
                    permissions: this._permissionsMapToPermissionEntries(userIdMap),
                    replaceAll
                }
            }
        };
        return this._connection.request(request).then(() => {
            return;
        });
    }
    getAllUserPermissions() {
        this._connection.session().assertOnline();
        return this.getPermissions().then(p => p.userPermissions);
    }
    getUserPermissions(username) {
        this._connection.session().assertOnline();
        return this.getPermissions()
            .then(p => p.userPermissions.get(DomainUserId.normal(username)) || new Set());
    }
    addGroupPermissions(permissions) {
        this._connection.session().assertOnline();
        let permissionsByGroup = this._coercePermissionsToGroupedProtoPermissionList(permissions);
        const request = {
            addPermissionsRequest: {
                target: this._getTarget(),
                group: permissionsByGroup
            }
        };
        return this._connection.request(request).then(() => {
            return;
        });
    }
    removeGroupPermissions(permissions) {
        this._connection.session().assertOnline();
        let permissionsByGroup = this._coercePermissionsToGroupedProtoPermissionList(permissions);
        const request = {
            removePermissionsRequest: {
                target: this._getTarget(),
                group: permissionsByGroup
            }
        };
        return this._connection.request(request).then(() => {
            return;
        });
    }
    setGroupPermissions(permissions, replaceAll = false) {
        this._connection.session().assertOnline();
        let permissionsByGroup = this._coercePermissionsToGroupedProtoPermissionList(permissions);
        const request = {
            setPermissionsRequest: {
                target: this._getTarget(),
                group: {
                    permissions: permissionsByGroup,
                    replaceAll
                }
            }
        };
        return this._connection.request(request).then(() => {
            return;
        });
    }
    getAllGroupPermissions() {
        this._connection.session().assertOnline();
        return this.getPermissions().then(p => p.groupPermissions);
    }
    getGroupPermissions(groupId) {
        this._connection.session().assertOnline();
        return this.getPermissions().then(p => p.groupPermissions.get(groupId) || new Set());
    }
    _permissionsMapToPermissionEntries(userIdMap) {
        const userPermissions = [];
        userIdMap.forEach((userPerms, userId) => {
            userPermissions.push({
                user: domainUserIdToProto(userId),
                permissions: Array.from(userPerms)
            });
        });
        return userPermissions;
    }
    _permissionsEntriesToUserMap(entries) {
        const userPermissions = new DomainUserIdMap();
        entries.forEach((entry) => {
            const userId = protoToDomainUserId(entry.user);
            let permissions = new Set(getOrDefaultArray(entry.permissions));
            userPermissions.set(userId, permissions);
        });
        return userPermissions;
    }
    _coercePermissionsToGroupedProtoPermissionList(permissions) {
        let groupedPermissions = StringMap.coerceToObject(permissions);
        return mapObjectValues(groupedPermissions, permissionsArr => {
            return {
                values: Array.from(permissionsArr)
            };
        });
    }
}

class ActivityPermissionManager extends AbstractPermissionManager {
    constructor(type, id, connection) {
        super(connection);
        this._target = {
            activity: { type, id }
        };
    }
    type() {
        return this._target.activity.type;
    }
    id() {
        return this._target.activity.id;
    }
    _getTarget() {
        return this._target;
    }
}

class ActivityForceLeaveEvent {
    constructor(activity, user, sessionId, local, reason) {
        this.activity = activity;
        this.user = user;
        this.sessionId = sessionId;
        this.local = local;
        this.reason = reason;
        this.name = ActivityForceLeaveEvent.EVENT_NAME;
        Object.freeze(this);
    }
}
ActivityForceLeaveEvent.EVENT_NAME = "force_leave";

class ActivityDeletedEvent {
    constructor(activity, user, sessionId, local) {
        this.activity = activity;
        this.user = user;
        this.sessionId = sessionId;
        this.local = local;
        this.name = ActivityDeletedEvent.EVENT_NAME;
        Object.freeze(this);
    }
}
ActivityDeletedEvent.EVENT_NAME = "deleted";

class Activity extends ConvergenceEventEmitter {
    constructor(type, id, identityCache, connection) {
        super();
        this._setOnline = () => {
            this._logger.debug(() => `Activity '${this._id}' is online`);
            const initialState = this._lurking ? new Map() : this._localParticipant.state;
            const deferred = new Deferred();
            this._joinWhileOnline(deferred, initialState);
        };
        this._setOffline = () => {
            this._logger.debug(() => `Activity '${this._id}' is offline`);
            this._mutateParticipants((participants) => {
                var _a;
                for (const [sessionId, participant] of participants) {
                    if (sessionId !== ((_a = this._localParticipant) === null || _a === void 0 ? void 0 : _a.sessionId)) {
                        participants.delete(sessionId);
                        const event = new ActivitySessionLeftEvent(this, participant.user, participant.sessionId, false);
                        this._emitEvent(event);
                    }
                }
            });
        };
        this._identityCache = identityCache;
        this._type = type;
        this._id = id;
        this._resource = null;
        this._permissionsManager = new ActivityPermissionManager(type, id, connection);
        this._participants = new BehaviorSubject(new Map());
        this._joined = true;
        this._connection = connection;
        this._logger = Logging.logger("activities.activity");
        this._joinPromise = null;
        this._lurking = false;
        this._connection.on(ConvergenceConnection.Events.INTERRUPTED, this._setOffline);
        this._connection.on(ConvergenceConnection.Events.DISCONNECTED, this._setOffline);
        this._connection.on(ConvergenceConnection.Events.CONNECTED, this._setOnline);
        this._connectionMessageSubscription = this._connection
            .messages()
            .subscribe((event) => {
            const message = event.message;
            if (message.activitySessionJoined &&
                getOrDefaultNumber(message.activitySessionJoined.resourceId) === this._resource) {
                this._onSessionJoined(message.activitySessionJoined);
            }
            else if (message.activitySessionLeft &&
                getOrDefaultNumber(message.activitySessionLeft.resourceId) === this._resource) {
                this._onSessionLeft(message.activitySessionLeft);
            }
            else if (message.activityStateUpdated &&
                getOrDefaultNumber(message.activityStateUpdated.resourceId) === this._resource) {
                this._onRemoteStateUpdated(message.activityStateUpdated);
            }
            else if (message.activityDeleted &&
                getOrDefaultNumber(message.activityDeleted.resourceId) === this._resource) {
                this._onDeleted(message.activityDeleted);
            }
            else if (message.activityForceLeave &&
                getOrDefaultNumber(message.activityForceLeave.resourceId) === this._resource) {
                this._onForceLeave(message.activityForceLeave);
            }
            else ;
        });
    }
    session() {
        return this._connection.session();
    }
    type() {
        return this._type;
    }
    id() {
        return this._id;
    }
    createdTime() {
        return this._created;
    }
    isEphemeral() {
        return this._ephemeral;
    }
    permissions() {
        return this._permissionsManager;
    }
    leave() {
        if (this.isJoined()) {
            if (this._connection.isConnected()) {
                return this._connection
                    .request({
                    activityLeaveRequest: { resourceId: this._resource }
                })
                    .then(() => {
                    this._completeLeave();
                    return;
                });
            }
            else {
                this._completeLeave();
                return Promise.resolve();
            }
        }
        else {
            return Promise.reject(new ConvergenceError(`The activity was not joined: ${this._id}`));
        }
    }
    isJoined() {
        return this._joined;
    }
    state() {
        if (!this._lurking) {
            return this._localParticipant.state;
        }
        else {
            return new Map();
        }
    }
    setState() {
        this._assertNotLurking();
        if (this.isJoined()) {
            let state;
            if (arguments.length === 1) {
                state = StringMap.coerceToMap(arguments[0]);
            }
            else if (arguments.length === 2) {
                state = new Map();
                state.set(arguments[0], arguments[1]);
            }
            if (state.size > 0) {
                this._sendStateUpdate(state, false, []);
                const oldValues = new Map();
                const newState = this.state();
                state.forEach((value, key) => {
                    oldValues.set(key, newState.get(key));
                    newState.set(key, value);
                });
                this._mutateLocalParticipant((local) => local.clone({ state: newState }));
                const session = this._connection.session();
                const deltaEvent = new ActivityStateDeltaEvent(this, session.user(), session.sessionId(), true, state, false, [], oldValues);
                this._emitEvent(deltaEvent);
                state.forEach((v, k) => {
                    const oldValue = oldValues.get(k);
                    const event = new ActivityStateSetEvent(this, session.user(), session.sessionId(), true, k, v, oldValue);
                    this._emitEvent(event);
                });
            }
        }
    }
    removeState(keys) {
        this._assertNotLurking();
        if (this.isJoined()) {
            if (TypeChecker.isString(keys)) {
                keys = [keys];
            }
            if (keys.length > 0) {
                this._sendStateUpdate(null, false, keys);
                const newState = this.state();
                const oldValues = new Map();
                keys.forEach(key => {
                    oldValues.set(key, newState.get(key));
                    newState.delete(key);
                });
                this._mutateLocalParticipant((local) => local.clone({ state: newState }));
                const session = this._connection.session();
                const deltaEvent = new ActivityStateDeltaEvent(this, session.user(), session.sessionId(), true, new Map(), false, keys, oldValues);
                this._emitEvent(deltaEvent);
                keys.forEach(key => {
                    const oldValue = oldValues.get(key);
                    const event = new ActivityStateRemovedEvent(this, session.user(), session.sessionId(), true, key, oldValue);
                    this._emitEvent(event);
                });
            }
        }
    }
    clearState() {
        this._assertNotLurking();
        if (this.isJoined()) {
            if (this._localParticipant.state.size > 0) {
                this._sendStateUpdate(new Map(), true, null);
                const oldValues = this.state();
                this._mutateLocalParticipant((local) => local.clone({ state: new Map() }));
                const session = this._connection.session();
                const deltaEvent = new ActivityStateDeltaEvent(this, session.user(), session.sessionId(), true, new Map(), true, [], oldValues);
                this._emitEvent(deltaEvent);
                const clearedEvent = new ActivityStateClearedEvent(this, session.user(), session.sessionId(), true, oldValues);
                this._emitEvent(clearedEvent);
            }
        }
    }
    participant(sessionId) {
        return this._participants.getValue().get(sessionId);
    }
    participants() {
        return Array.from(this._participants.getValue().values());
    }
    participantsAsObservable() {
        return this._participants
            .asObservable()
            .pipe(map(mappedValues => Array.from(mappedValues.values())));
    }
    _join(options) {
        if (this._joinPromise === null) {
            options = options || {};
            options = Object.assign({ lurk: false }, options);
            const deferred = new Deferred();
            const initialState = options.state ?
                StringMap.coerceToMap(options.state) :
                new Map();
            this._lurking = options.lurk;
            this._autoCreateOptions = options.autoCreate || null;
            if (this._connection.isConnected()) {
                this._joinWhileOnline(deferred, initialState);
            }
            else {
                this._joinWhileOffline(deferred, initialState);
            }
            this._joinPromise = deferred.promise();
        }
    }
    _whenJoined() {
        return this._joinPromise;
    }
    _completeLeave() {
        this._joined = false;
        this._resource = null;
        const user = this._connection.session().user();
        const sessionId = this._connection.session().sessionId();
        const event = new ActivityLeftEvent(this, user, sessionId, true);
        this._emitEvent(event);
        this._connectionMessageSubscription.unsubscribe();
        this._completeEventStream();
    }
    _onSessionJoined(joined) {
        const user = this._identityCache.getUserForSession(joined.sessionId);
        const state = mapObjectValues(getOrDefaultObject(joined.state), protoValueToJson);
        const participant = new ActivityParticipant(this, user, joined.sessionId, false, StringMap.objectToMap(state));
        this._mutateParticipants((participants) => participants.set(participant.sessionId, participant));
        this._emitParticipantJoined(participant);
    }
    _emitParticipantJoined(participant) {
        const event = new ActivitySessionJoinedEvent(this, participant.user, participant.sessionId, false, participant);
        this._emitEvent(event);
    }
    _onSessionLeft(left) {
        const participant = this._participants.getValue().get(left.sessionId);
        this._emitParticipantLeft(participant);
        this._mutateParticipants((participants) => participants.delete(participant.sessionId));
    }
    _emitParticipantLeft(participant) {
        const event = new ActivitySessionLeftEvent(this, participant.user, participant.sessionId, participant.local);
        this._emitEvent(event);
    }
    _assertNotLurking() {
        if (this._lurking) {
            throw new ConvergenceError("Can not mutate local activity state when lurking.");
        }
    }
    _onRemoteStateUpdated(updated) {
        const sessionId = getOrDefaultString(updated.sessionId);
        const set = StringMap.objectToMap(getOrDefaultObject(updated.set));
        const complete = getOrDefaultBoolean(updated.complete);
        const removedKeys = getOrDefaultArray(updated.removed);
        if (complete) {
            if (set.size === 0) {
                this._handleStateCleared(sessionId);
            }
            else {
                this._handleStateReplaced(sessionId, set);
            }
        }
        else {
            if (removedKeys.length > 0) {
                this._handleStateRemoved(sessionId, removedKeys);
            }
            if (set.size > 0) {
                this._handleStateSet(sessionId, set);
            }
        }
    }
    _onDeleted(_) {
        const event = new ActivityDeletedEvent(this, this._connection.session().user(), this._connection.session().sessionId(), true);
        this._emitEvent(event);
        this._completeLeave();
    }
    _onForceLeave(updated) {
        const event = new ActivityForceLeaveEvent(this, this._connection.session().user(), this._connection.session().sessionId(), true, getOrDefaultString(updated.reason));
        this._emitEvent(event);
        this._completeLeave();
    }
    _handleStateSet(sessionId, stateSet) {
        const user = this._identityCache.getUserForSession(sessionId);
        const mapped = new Map();
        stateSet.forEach((protoValue, key) => {
            const value = protoValueToJson(protoValue);
            mapped.set(key, value);
        });
        const oldValues = new Map();
        this._mutateParticipants((participants) => {
            const existing = participants.get(sessionId);
            const state = new Map(existing.state);
            mapped.forEach((value, key) => {
                oldValues.set(key, state.get(key));
                state.set(key, value);
            });
            participants.set(sessionId, existing.clone({ state }));
        });
        const deltaEvent = new ActivityStateDeltaEvent(this, user, sessionId, true, mapped, false, [], oldValues);
        this._emitEvent(deltaEvent);
        mapped.forEach((v, k) => {
            const oldValue = oldValues.get(k);
            const event = new ActivityStateSetEvent(this, user, sessionId, false, k, v, oldValue);
            this._emitEvent(event);
        });
    }
    _handleStateReplaced(sessionId, state) {
        const user = this._identityCache.getUserForSession(sessionId);
        const newState = new Map();
        Object.keys(state).forEach(key => {
            const value = protoValueToJson(state[key]);
            newState.set(key, value);
        });
        const oldValues = this._participants.getValue().get(sessionId).state;
        this._mutateParticipants((participants) => {
            const existing = participants.get(sessionId);
            participants.set(sessionId, existing.clone({ state: newState }));
        });
        const removed = [];
        oldValues.forEach((value, key) => {
            if (!newState.has(key)) {
                removed.push(key);
            }
        });
        const deltaEvent = new ActivityStateDeltaEvent(this, user, sessionId, true, newState, false, removed, oldValues);
        this._emitEvent(deltaEvent);
        removed.forEach((key) => {
            const oldValue = oldValues.get(key);
            const event = new ActivityStateRemovedEvent(this, user, sessionId, false, key, oldValue);
            this._emitEvent(event);
        });
        newState.forEach((v, k) => {
            const oldValue = oldValues.get(k);
            const event = new ActivityStateSetEvent(this, user, sessionId, false, k, v, oldValue);
            this._emitEvent(event);
        });
    }
    _handleStateCleared(sessionId) {
        const user = this._identityCache.getUserForSession(sessionId);
        const oldValues = this._participants.getValue().get(sessionId).state;
        this._mutateParticipants((participants) => {
            const existing = participants.get(sessionId);
            const state = new Map();
            participants.set(sessionId, existing.clone({ state }));
        });
        const deltaEvent = new ActivityStateDeltaEvent(this, user, sessionId, true, new Map(), true, [], oldValues);
        this._emitEvent(deltaEvent);
        const event = new ActivityStateClearedEvent(this, user, sessionId, false, oldValues);
        this._emitEvent(event);
    }
    _handleStateRemoved(sessionId, keys) {
        const user = this._identityCache.getUserForSession(sessionId);
        const oldValues = new Map();
        this._mutateParticipants((participants) => {
            const existing = participants.get(sessionId);
            const state = new Map(existing.state);
            keys.forEach((key) => {
                oldValues.set(key, state.get(key));
                state.delete(key);
            });
            participants.set(sessionId, existing.clone({ state }));
        });
        const deltaEvent = new ActivityStateDeltaEvent(this, user, sessionId, true, new Map(), false, keys, oldValues);
        this._emitEvent(deltaEvent);
        keys.forEach((key) => {
            const oldValue = oldValues.get(key);
            const event = new ActivityStateRemovedEvent(this, user, sessionId, false, key, oldValue);
            this._emitEvent(event);
        });
    }
    _mutateParticipants(mutator) {
        const participants = new Map(this._participants.getValue());
        mutator(participants);
        this._participants.next(participants);
    }
    _mutateLocalParticipant(mutator) {
        this._mutateParticipants(participants => {
            this._localParticipant = mutator(this._localParticipant);
            participants.set(this._localParticipant.sessionId, this._localParticipant);
        });
    }
    _joinWhileOnline(deferred, initialState) {
        let autoCreateData;
        if (this._autoCreateOptions) {
            const worldPermissions = this._autoCreateOptions.worldPermissions || [];
            const userPermissions = ActivityPermissionUtils.userPermissions(this._autoCreateOptions.userPermissions);
            const groupPermissions = ActivityPermissionUtils.toGroupPermissionsProto(this._autoCreateOptions.groupPermissions);
            autoCreateData = {
                worldPermissions,
                userPermissions,
                groupPermissions,
                ephemeral: this._autoCreateOptions.ephemeral
            };
        }
        const mappedState = mapObjectValues(StringMap.mapToObject(initialState), jsonToProtoValue);
        const message = {
            activityJoinRequest: {
                activityType: this._type,
                activityId: this._id,
                lurk: this._lurking,
                state: mappedState,
                autoCreateData
            }
        };
        this._resource = null;
        this._connection
            .request(message)
            .then((response) => {
            const joinResponse = response.activityJoinResponse;
            const localSessionId = this._connection.session().sessionId();
            this._resource = getOrDefaultNumber(joinResponse.resourceId);
            this._ephemeral = getOrDefaultBoolean(joinResponse.ephemeral);
            this._created = timestampToDate(joinResponse.created);
            const participants = new Map();
            const responseState = getOrDefaultObject(joinResponse.state);
            objectForEach(responseState, (sessionId) => {
                const activityState = responseState[sessionId] || {};
                const user = this._identityCache.getUserForSession(sessionId);
                const local = sessionId === localSessionId;
                const rawState = getOrDefaultObject(activityState.state);
                const jsonState = mapObjectValues(rawState, protoValueToJson);
                const stateMap = StringMap.objectToMap(jsonState);
                const participant = new ActivityParticipant(this, user, sessionId, local, stateMap);
                participants.set(sessionId, participant);
            });
            if (this._localParticipant !== undefined) {
                this._emitParticipantLeft(this._localParticipant);
                const joinedLocalParticipant = participants.get(localSessionId);
                const updatedJoinedLocalParticipant = joinedLocalParticipant.clone({ state: this._localParticipant.state });
                this._syncStateAfterJoinOnline(joinedLocalParticipant.state, this._localParticipant.state);
                participants.set(localSessionId, updatedJoinedLocalParticipant);
            }
            this._localParticipant = participants.get(localSessionId);
            this._participants.next(participants);
            participants.forEach(p => this._emitParticipantJoined(p));
            deferred.resolve();
        })
            .catch(e => {
            deferred.reject(e);
        });
    }
    _syncStateAfterJoinOnline(serverState, localState) {
        const removedKeys = [];
        serverState.forEach((value, key) => {
            if (!localState.has(key)) {
                removedKeys.push(key);
            }
        });
        const updated = new Map();
        localState.forEach((value, key) => {
            if (!EqualsUtil.deepEquals(!serverState.get(key), value)) {
                updated.set(key, value);
            }
        });
        if (removedKeys.length > 0 || updated.size > 0) {
            this._sendStateUpdate(updated, false, removedKeys);
        }
    }
    _joinWhileOffline(deferred, initialState) {
        this._joined = true;
        const session = this._connection.session();
        this._localParticipant =
            new ActivityParticipant(undefined, session.user(), session.sessionId(), true, initialState);
        const participants = new Map();
        participants.set(this._localParticipant.sessionId, this._localParticipant);
        this._participants.next(participants);
        deferred.resolve();
    }
    _sendStateUpdate(state, complete, removedKeys) {
        const set = state !== null ? mapObjectValues(StringMap.mapToObject(state), jsonToProtoValue) : {};
        const removed = removedKeys !== null ? removedKeys : [];
        if (this._connection.isConnected()) {
            const message = {
                activityUpdateState: { resourceId: this._resource, set, complete, removed }
            };
            this._connection.send(message);
        }
    }
}
Activity.Events = {
    SESSION_JOINED: ActivitySessionJoinedEvent.EVENT_NAME,
    SESSION_LEFT: ActivitySessionLeftEvent.EVENT_NAME,
    STATE_SET: ActivityStateSetEvent.EVENT_NAME,
    STATE_CLEARED: ActivityStateClearedEvent.EVENT_NAME,
    STATE_REMOVED: ActivityStateRemovedEvent.EVENT_NAME,
    STATE_DELTA: ActivityStateDeltaEvent.EVENT_NAME,
    LEFT: ActivityLeftEvent.EVENT_NAME,
    DELETED: ActivityLeftEvent.EVENT_NAME,
    FORCE_LEAVE: ActivityForceLeaveEvent.EVENT_NAME
};
Object.freeze(Activity.Events);

class ActivityService extends ConvergenceEventEmitter {
    constructor(connection, identityCache) {
        super();
        this._logger = Logging.logger("activities.service");
        this._connection = connection;
        this._identityCache = identityCache;
        this._joinedActivities = new Map();
    }
    session() {
        return this._connection.session();
    }
    create(options) {
        Validation.assertNonEmptyString(options.activityType, "options.activityType");
        const worldPermissions = options.worldPermissions || [];
        const userPermissions = ActivityPermissionUtils.userPermissions(options.userPermissions);
        const groupPermissions = ActivityPermissionUtils.toGroupPermissionsProto(options.groupPermissions);
        const activityCreateRequest = {
            activityId: options.activityId,
            activityType: options.activityType,
            worldPermissions,
            userPermissions,
            groupPermissions
        };
        const requestMessage = { activityCreateRequest };
        return this._connection.request(requestMessage).then(() => undefined);
    }
    remove(activityId, activityType) {
        Validation.assertNonEmptyString(activityType, "activityType");
        const activityDeleteRequest = {
            activityId,
            activityType
        };
        const requestMessage = { activityDeleteRequest };
        return this._connection.request(requestMessage).then(() => undefined);
    }
    join(type, id, options) {
        let activity = this._joinedActivities.get(ActivityService._activityToIdString(type, id));
        if (activity === undefined) {
            activity = new Activity(type, id, this._identityCache, this._connection);
            this._joinedActivities.set(ActivityService._activityToIdString(type, id), activity);
            const activityEvents = activity
                .events()
                .pipe(filter((evt) => evt.name === ActivityLeftEvent.EVENT_NAME), tap(() => this._onActivityLeave(type, id)));
            activity._join(options);
            activity._whenJoined()
                .then(() => {
                this._emitFrom(activityEvents);
                return Promise.resolve(activity);
            })
                .catch(error => {
                this._joinedActivities.delete(id);
                return Promise.reject(error);
            });
        }
        return activity._whenJoined().then(() => activity);
    }
    joined() {
        return new Map(this._joinedActivities);
    }
    isJoined(type, id) {
        return this._joinedActivities.has(ActivityService._activityToIdString(type, id));
    }
    _onActivityLeave(type, id) {
        this._joinedActivities.delete(ActivityService._activityToIdString(type, id));
    }
    static _activityToIdString(type, id) {
        return `${type}/${id}`;
    }
}

class UserPresence {
    constructor(user, available, state) {
        this.user = user;
        this.available = available;
        this._state = deepClone(state);
        Object.freeze(this);
    }
    get state() {
        return deepClone(this._state);
    }
}

class PresenceAvailabilityChangedEvent {
    constructor(user, available) {
        this.user = user;
        this.available = available;
        this.name = PresenceAvailabilityChangedEvent.NAME;
        Object.freeze(this);
    }
}
PresenceAvailabilityChangedEvent.NAME = "availability_changed";

class PresenceStateClearedEvent {
    constructor(user) {
        this.user = user;
        this.name = PresenceStateClearedEvent.NAME;
        Object.freeze(this);
    }
}
PresenceStateClearedEvent.NAME = "state_cleared";

class PresenceStateRemovedEvent {
    constructor(user, keys) {
        this.user = user;
        this.keys = keys;
        this.name = PresenceStateRemovedEvent.NAME;
        Object.freeze(this);
    }
}
PresenceStateRemovedEvent.NAME = "state_removed";

class PresenceStateSetEvent {
    constructor(user, state) {
        this.user = user;
        this.state = state;
        this.name = PresenceStateSetEvent.NAME;
        Object.freeze(this);
    }
}
PresenceStateSetEvent.NAME = "state_set";

class UserPresenceSubscription extends ConvergenceEventEmitter {
    constructor(delegate) {
        super();
        this._manager = delegate;
        this._emitFrom(delegate.events());
    }
    get user() {
        return this._manager.user();
    }
    get available() {
        return this._manager.isAvailable();
    }
    get state() {
        return this._manager.state();
    }
    asObservable() {
        return this._manager.asObservable();
    }
    unsubscribe() {
        if (this._manager !== null) {
            this._manager.unsubscribe(this);
        }
        this._manager = null;
    }
}
UserPresenceSubscription.Events = {
    STATE_SET: PresenceStateSetEvent.NAME,
    STATE_REMOVED: PresenceStateRemovedEvent.NAME,
    STATE_CLEARED: PresenceStateClearedEvent.NAME,
    AVAILABILITY_CHANGED: PresenceAvailabilityChangedEvent.NAME,
};

class UserPresenceManager extends ConvergenceEventEmitter {
    constructor(initialPresence, eventStream, onUnsubscribe) {
        super();
        this._presence = new UserPresence(initialPresence.user, initialPresence.available, initialPresence.state);
        this._subscriptions = [];
        this._onUnsubscribe = onUnsubscribe;
        this._setStream(eventStream);
        this._subject = new BehaviorSubject(this._presence);
    }
    user() {
        return this._presence.user;
    }
    isAvailable() {
        return this._presence.available;
    }
    state() {
        return this._presence.state;
    }
    asObservable() {
        return this._subject.asObservable();
    }
    subscribe() {
        const subscription = new UserPresenceSubscription(this);
        this._subscriptions.push(subscription);
        return subscription;
    }
    unsubscribe(subscription) {
        this._subscriptions = this._subscriptions.filter(s => s !== subscription);
        if (this._subscriptions.length === 0) {
            this._messageSubscription.unsubscribe();
            this._subject.complete();
            this._onUnsubscribe(this.user().userId);
        }
    }
    availability(availability, emitSubject = true) {
        this._presence =
            new UserPresence(this._presence.user, availability, this._presence.state);
        const event = new PresenceAvailabilityChangedEvent(this._presence.user, availability);
        this._emitEvent(event);
        if (emitSubject) {
            this._subject.next(this._presence);
        }
    }
    set(state, emitSubject = true) {
        const newState = this._presence.state;
        state.forEach((v, k) => newState.set(k, deepClone(v)));
        this._presence = new UserPresence(this._presence.user, this._presence.available, newState);
        const event = new PresenceStateSetEvent(this._presence.user, newState);
        this._emitEvent(event);
        if (emitSubject) {
            this._subject.next(this._presence);
        }
    }
    remove(keys, emitSubject = true) {
        const newState = this._presence.state;
        keys.forEach(k => newState.delete(k));
        this._presence = new UserPresence(this._presence.user, this._presence.available, newState);
        const event = new PresenceStateRemovedEvent(this._presence.user, keys);
        this._emitEvent(event);
        if (emitSubject) {
            this._subject.next(this._presence);
        }
    }
    clear(emitSubject = true) {
        this._presence = new UserPresence(this._presence.user, this._presence.available, new Map());
        const event = new PresenceStateClearedEvent(this._presence.user);
        this._emitEvent(event);
        if (emitSubject) {
            this._subject.next(this._presence);
        }
    }
    hasKey(key) {
        return this._presence.state.has(key);
    }
    _setOnline(newPresence) {
        this.availability(newPresence.available, false);
        this.clear(false);
        this.set(newPresence.state);
        this._subject.next(newPresence);
    }
    _setStream(stream) {
        if (this._messageSubscription) {
            this._messageSubscription.unsubscribe();
        }
        this._messageSubscription = stream.subscribe(message => this._handleMessage(message));
    }
    _setOffline() {
        this.availability(false, false);
    }
    _handleMessage(messageEvent) {
        const message = messageEvent.message;
        if (message.presenceAvailabilityChanged) {
            const { available } = message.presenceAvailabilityChanged;
            this.availability(getOrDefaultBoolean(available));
        }
        else if (message.presenceStateSet) {
            const { state } = message.presenceStateSet;
            const jsonState = StringMap.objectToMap(mapObjectValues(getOrDefaultObject(state), protoValueToJson));
            this.set(jsonState);
        }
        else if (message.presenceStateCleared) {
            this.clear();
        }
        else if (message.presenceStateRemoved) {
            const { keys } = message.presenceStateRemoved;
            this.remove(getOrDefaultArray(keys));
        }
    }
}

class PresenceService extends ConvergenceEventEmitter {
    constructor(connection, identityCache, localUser) {
        super();
        this._logger = Logging.logger("connection");
        this._setOnline = () => {
            const localUser = this.session().user();
            const localStream = this._streamForUsername(localUser.userId);
            this._localManager._setStream(localStream);
            const resubscribe = [];
            this._subscribedManagers.forEach(manager => {
                resubscribe.push(manager.user().userId);
            });
            this._subscribeToServer(resubscribe)
                .then((userPresences) => {
                userPresences.forEach(userPresence => {
                    const manager = this._getManager(userPresence.user.userId);
                    if (manager) {
                        manager._setOnline(userPresence);
                    }
                });
            })
                .catch((error) => {
                this._logger.error("Error resubscribing to presence", error);
            });
        };
        this._setOffline = () => {
            this._localManager._setOffline();
            this._subscribedManagers.forEach((manager) => {
                manager._setOffline();
            });
        };
        this._connection = connection;
        this._identityCache = identityCache;
        this._subscribedManagers = new Map();
        this._messageStream = this._connection.messages().pipe(share());
        const user = localUser !== undefined ? localUser : new DomainUser(DomainUserType.ANONYMOUS, "");
        const localStream = this._streamForUsername(user.userId);
        const initialPresence = new UserPresence(user, false, new Map());
        this._localManager = new UserPresenceManager(initialPresence, localStream, () => {
        });
        this._localPresence = this._localManager.subscribe();
        this._emitFrom(this._localPresence.events());
        this._connection.on(ConvergenceConnection.Events.INTERRUPTED, this._setOffline);
        this._connection.on(ConvergenceConnection.Events.DISCONNECTED, this._setOffline);
        this._connection.on(ConvergenceConnection.Events.CONNECTED, this._setOnline);
    }
    session() {
        return this._connection.session();
    }
    isAvailable() {
        return this._localPresence.available;
    }
    setState() {
        let state;
        if (arguments.length === 1) {
            state = StringMap.objectToMap(arguments[0]);
        }
        else if (arguments.length === 2) {
            state = new Map();
            state.set(arguments[0], arguments[1]);
        }
        else if (arguments.length === 0) {
            return;
        }
        this._localManager.set(state);
        if (this._connection.isConnected()) {
            const message = {
                presenceSetState: {
                    state: mapObjectValues(StringMap.mapToObject(state), jsonToProtoValue)
                }
            };
            this._connection.send(message);
        }
    }
    removeState(keys) {
        const stateKeys = typeof keys === "string" ? [keys] : keys;
        const existingKeys = stateKeys.filter(k => this._localManager.hasKey(k));
        if (existingKeys.length > 0) {
            this._localManager.remove(existingKeys);
            if (this._connection.isConnected()) {
                const message = {
                    presenceRemoveState: {
                        keys: existingKeys
                    }
                };
                this._connection.send(message);
            }
        }
    }
    clearState() {
        this._localManager.clear();
        if (this._connection.isConnected()) {
            const message = {
                presenceClearState: {}
            };
            this._connection.send(message);
        }
    }
    state() {
        return this._localPresence.state;
    }
    presence(users) {
        this._connection.session().assertOnline();
        if (!Array.isArray(users)) {
            return this._get([DomainUserId.toDomainUserId(users)]).then(result => {
                return result[0];
            });
        }
        else {
            return this._get(users.map(DomainUserId.toDomainUserId));
        }
    }
    subscribe(users) {
        const requested = Array.isArray(users) ?
            users.map(DomainUserId.toDomainUserId) :
            [DomainUserId.toDomainUserId(users)];
        return this._subscribe(requested).then(() => {
            const subscriptions = requested.map(userId => this._subscribedManagers.get(userId.toGuid()).subscribe());
            if (!Array.isArray(users)) {
                return subscriptions[0];
            }
            else {
                return subscriptions;
            }
        });
    }
    _setInternalState(state) {
        this._localManager.clear(false);
        this._localManager.set(state);
    }
    _get(userIds) {
        const users = userIds.map(domainUserIdToProto);
        const message = {
            presenceRequest: {
                users
            }
        };
        return this._connection.request(message).then((response) => {
            const { userPresences } = response.presenceResponse;
            return getOrDefaultArray(userPresences).map(p => {
                return this._mapUserPresence(p);
            });
        });
    }
    _streamForUsername(user) {
        return this._messageStream.pipe(filter(e => {
            const message = e.message;
            const incomingUserIdData = (message.presenceAvailabilityChanged && message.presenceAvailabilityChanged.user) ||
                (message.presenceStateSet && message.presenceStateSet.user) ||
                (message.presenceStateRemoved && message.presenceStateRemoved.user) ||
                (message.presenceStateCleared && message.presenceStateCleared.user);
            return incomingUserIdData && protoToDomainUserId(incomingUserIdData).equals(user);
        }));
    }
    _subscribe(users) {
        const notSubscribed = users.filter(userId => {
            return this._subscribedManagers.get(userId.toGuid()) === undefined;
        });
        if (notSubscribed.length > 0) {
            return this._subscribeToServer(notSubscribed).then(userPresences => {
                userPresences.forEach(userPresence => {
                    const guid = userPresence.user.userId.toGuid();
                    const stream = this._streamForUsername(userPresence.user.userId);
                    const unsubscribe = (userId) => this._unsubscribe(userId);
                    const manager = new UserPresenceManager(userPresence, stream, unsubscribe);
                    this._subscribedManagers.set(guid, manager);
                });
                return;
            });
        }
        else {
            return Promise.resolve();
        }
    }
    _subscribeToServer(users) {
        const message = {
            presenceSubscribeRequest: {
                users: users.map(domainUserIdToProto)
            }
        };
        return this._connection.request(message).then((response) => {
            const { userPresences } = response.presenceSubscribeResponse;
            return getOrDefaultArray(userPresences).map(presence => this._mapUserPresence(presence));
        });
    }
    _unsubscribe(userId) {
        if (this._connection.isConnected()) {
            const message = {
                presenceUnsubscribe: {
                    users: [domainUserIdToProto(userId)]
                }
            };
            this._connection.send(message);
        }
        const guid = userId.toGuid();
        this._subscribedManagers.delete(guid);
    }
    _mapUserPresence(p) {
        const userId = protoToDomainUserId(p.user);
        let user = this._identityCache.getUser(userId);
        if (!user) {
            user = new DomainUser(userId.userType, userId.username);
        }
        const available = getOrDefaultBoolean(p.available);
        const state = StringMap.objectToMap(mapObjectValues(getOrDefaultObject(p.state), protoValueToJson));
        return new UserPresence(user, available, state);
    }
    _getManager(userId) {
        return userId.equals(this._localManager.user().userId) ?
            this._localManager :
            this._subscribedManagers.get(userId.toGuid());
    }
}
PresenceService.Events = {
    STATE_SET: PresenceStateSetEvent.NAME,
    STATE_REMOVED: PresenceStateRemovedEvent.NAME,
    STATE_CLEARED: PresenceStateClearedEvent.NAME,
    AVAILABILITY_CHANGED: PresenceAvailabilityChangedEvent.NAME
};
Object.freeze(PresenceService.Events);

class ChatEvent {
    constructor(_chatId, _eventNumber, _timestamp, _user) {
        this.chatId = _chatId;
        this.eventNumber = _eventNumber;
        this.timestamp = _timestamp;
        this.user = _user;
    }
}

class ChatJoinedEvent {
    constructor(chatId) {
        this.chatId = chatId;
        this.name = ChatJoinedEvent.NAME;
        Object.freeze(this);
    }
}
ChatJoinedEvent.NAME = "joined";

class ChatLeftEvent {
    constructor(chatId) {
        this.chatId = chatId;
        this.name = ChatLeftEvent.NAME;
        Object.freeze(this);
    }
}
ChatLeftEvent.NAME = "left";

class ChatMessageEvent extends ChatEvent {
    constructor(chatId, eventNumber, timestamp, user, message) {
        super(chatId, eventNumber, timestamp, user);
        this.message = message;
        this.name = ChatMessageEvent.NAME;
        Object.freeze(this);
    }
}
ChatMessageEvent.NAME = "message";

class ChatNameChangedEvent extends ChatEvent {
    constructor(chatId, eventNumber, timestamp, user, chatName) {
        super(chatId, eventNumber, timestamp, user);
        this.chatName = chatName;
        this.name = ChatNameChangedEvent.NAME;
        Object.freeze(this);
    }
}
ChatNameChangedEvent.NAME = "name_changed";

class ChatRemovedEvent {
    constructor(chatId) {
        this.chatId = chatId;
        this.name = ChatRemovedEvent.NAME;
        Object.freeze(this);
    }
}
ChatRemovedEvent.NAME = "removed";

class ChatTopicChangedEvent extends ChatEvent {
    constructor(chatId, eventNumber, timestamp, user, topic) {
        super(chatId, eventNumber, timestamp, user);
        this.topic = topic;
        this.name = ChatTopicChangedEvent.NAME;
        Object.freeze(this);
    }
}
ChatTopicChangedEvent.NAME = "topic_changed";

class UserAddedEvent extends ChatEvent {
    constructor(chatId, eventNumber, timestamp, user, addedUser) {
        super(chatId, eventNumber, timestamp, user);
        this.addedUser = addedUser;
        this.name = UserAddedEvent.NAME;
        Object.freeze(this);
    }
}
UserAddedEvent.NAME = "user_added";

class UserJoinedEvent extends ChatEvent {
    constructor(chatId, eventNumber, timestamp, user) {
        super(chatId, eventNumber, timestamp, user);
        this.name = UserJoinedEvent.NAME;
        Object.freeze(this);
    }
}
UserJoinedEvent.NAME = "user_joined";

class UserLeftEvent extends ChatEvent {
    constructor(chatId, eventNumber, timestamp, user) {
        super(chatId, eventNumber, timestamp, user);
        this.name = UserLeftEvent.NAME;
        Object.freeze(this);
    }
}
UserLeftEvent.NAME = "user_left";

class UserRemovedEvent extends ChatEvent {
    constructor(chatId, eventNumber, timestamp, user, removedUser) {
        super(chatId, eventNumber, timestamp, user);
        this.removedUser = removedUser;
        this.name = UserRemovedEvent.NAME;
        Object.freeze(this);
    }
}
UserRemovedEvent.NAME = "user_removed";

class ChatEventsMarkedSeenEvent {
    constructor(chatId, maxSeenEventNumber, user) {
        this.chatId = chatId;
        this.maxSeenEventNumber = maxSeenEventNumber;
        this.user = user;
        this.name = ChatEventsMarkedSeenEvent.NAME;
        Object.freeze(this);
    }
}
ChatEventsMarkedSeenEvent.NAME = "events_seen";

function isChatMessage(message) {
    return !!message.remoteChatMessage ||
        !!message.userJoinedChat ||
        !!message.userLeftChat ||
        !!message.userAddedToChat ||
        !!message.userRemovedFromChat ||
        !!message.chatRemoved ||
        !!message.chatNameChanged ||
        !!message.chatTopicChanged ||
        !!message.chatEventsMarkedSeen;
}
function processChatMessage(message, identityCache) {
    if (message.userJoinedChat) {
        const userJoined = message.userJoinedChat;
        return new UserJoinedEvent(userJoined.chatId, getOrDefaultNumber(userJoined.eventNumber), timestampToDate(userJoined.timestamp), identityCache.getUser(protoToDomainUserId(userJoined.user)));
    }
    else if (message.userLeftChat) {
        const userLeft = message.userLeftChat;
        return new UserLeftEvent(userLeft.chatId, getOrDefaultNumber(userLeft.eventNumber), timestampToDate(userLeft.timestamp), identityCache.getUser(protoToDomainUserId(userLeft.user)));
    }
    else if (message.userAddedToChat) {
        const userAdded = message.userAddedToChat;
        return new UserAddedEvent(userAdded.chatId, getOrDefaultNumber(userAdded.eventNumber), timestampToDate(userAdded.timestamp), identityCache.getUser(protoToDomainUserId(userAdded.user)), identityCache.getUser(protoToDomainUserId(userAdded.addedUser)));
    }
    else if (message.userRemovedFromChat) {
        const userRemoved = message.userRemovedFromChat;
        return new UserRemovedEvent(userRemoved.chatId, getOrDefaultNumber(userRemoved.eventNumber), timestampToDate(userRemoved.timestamp), identityCache.getUser(protoToDomainUserId(userRemoved.user)), identityCache.getUser(protoToDomainUserId(userRemoved.removedUser)));
    }
    else if (message.chatRemoved) {
        const removedMsg = message.chatRemoved;
        return new ChatRemovedEvent(removedMsg.chatId);
    }
    else if (message.chatNameChanged) {
        const nameSet = message.chatNameChanged;
        return new ChatNameChangedEvent(nameSet.chatId, getOrDefaultNumber(nameSet.eventNumber), timestampToDate(nameSet.timestamp), identityCache.getUser(protoToDomainUserId(nameSet.user)), getOrDefaultString(nameSet.name));
    }
    else if (message.chatTopicChanged) {
        const topicSet = message.chatTopicChanged;
        return new ChatTopicChangedEvent(topicSet.chatId, getOrDefaultNumber(topicSet.eventNumber), timestampToDate(topicSet.timestamp), identityCache.getUser(protoToDomainUserId(topicSet.user)), getOrDefaultString(topicSet.topic));
    }
    else if (message.remoteChatMessage) {
        const chatMsg = message.remoteChatMessage;
        return new ChatMessageEvent(chatMsg.chatId, getOrDefaultNumber(chatMsg.eventNumber), timestampToDate(chatMsg.timestamp), identityCache.getUser(protoToDomainUserId(chatMsg.user)), getOrDefaultString(chatMsg.message));
    }
    else if (message.chatEventsMarkedSeen) {
        const chatMsg = message.chatEventsMarkedSeen;
        return new ChatEventsMarkedSeenEvent(chatMsg.chatId, getOrDefaultNumber(chatMsg.eventNumber), identityCache.getUser(protoToDomainUserId(chatMsg.user)));
    }
    else {
        throw new ConvergenceError("Invalid chat event");
    }
}

class ChatHistoryEntry {
    constructor(_type, _chatId, _eventNumber, _timestamp, _user) {
        this.type = _type;
        this.chatId = _chatId;
        this.eventNumber = _eventNumber;
        this.timestamp = _timestamp;
        this.user = _user;
    }
}
ChatHistoryEntry.TYPES = {
    CREATED: "created",
    MESSAGE: "message",
    USER_JOINED: "user_joined",
    USER_LEFT: "user_left",
    USER_ADDED: "user_added",
    USER_REMOVED: "user_removed",
    NAME_CHANGED: "name_changed",
    TOPIC_CHANGED: "topic_changed"
};
Immutable.make(ChatHistoryEntry.TYPES);

class CreatedChatHistoryEntry extends ChatHistoryEntry {
    constructor(chatId, eventNumber, timestamp, user, name, topic, members) {
        super(CreatedChatHistoryEntry.TYPE, chatId, eventNumber, timestamp, user);
        this.name = name;
        this.topic = topic;
        this.members = members;
        Immutable.make(this);
    }
}
CreatedChatHistoryEntry.TYPE = ChatHistoryEntry.TYPES.CREATED;

class MessageChatHistoryEntry extends ChatHistoryEntry {
    constructor(chatId, eventNumber, timestamp, user, message) {
        super(MessageChatHistoryEntry.TYPE, chatId, eventNumber, timestamp, user);
        this.message = message;
        Immutable.make(this);
    }
}
MessageChatHistoryEntry.TYPE = ChatHistoryEntry.TYPES.MESSAGE;

class UserAddedChatHistoryEntry extends ChatHistoryEntry {
    constructor(chatId, eventNumber, timestamp, user, addedUser) {
        super(UserAddedChatHistoryEntry.TYPE, chatId, eventNumber, timestamp, user);
        this.addedUser = addedUser;
        Immutable.make(this);
    }
}
UserAddedChatHistoryEntry.TYPE = ChatHistoryEntry.TYPES.USER_ADDED;

class UserRemovedChatHistoryEntry extends ChatHistoryEntry {
    constructor(chatId, eventNumber, timestamp, user, removedUser) {
        super(UserRemovedChatHistoryEntry.TYPE, chatId, eventNumber, timestamp, user);
        this.removedUser = removedUser;
        Immutable.make(this);
    }
}
UserRemovedChatHistoryEntry.TYPE = ChatHistoryEntry.TYPES.USER_REMOVED;

class UserJoinedChatHistoryEntry extends ChatHistoryEntry {
    constructor(chatId, eventNumber, timestamp, user) {
        super(UserJoinedChatHistoryEntry.TYPE, chatId, eventNumber, timestamp, user);
        Immutable.make(this);
    }
}
UserJoinedChatHistoryEntry.TYPE = ChatHistoryEntry.TYPES.USER_JOINED;

class UserLeftChatHistoryEntry extends ChatHistoryEntry {
    constructor(chatId, eventNumber, timestamp, user) {
        super(UserLeftChatHistoryEntry.TYPE, chatId, eventNumber, timestamp, user);
        Immutable.make(this);
    }
}
UserLeftChatHistoryEntry.TYPE = ChatHistoryEntry.TYPES.USER_LEFT;

class NameChangedChatHistoryEntry extends ChatHistoryEntry {
    constructor(chatId, eventNumber, timestamp, user, name) {
        super(NameChangedChatHistoryEntry.TYPE, chatId, eventNumber, timestamp, user);
        this.name = name;
        Immutable.make(this);
    }
}
NameChangedChatHistoryEntry.TYPE = ChatHistoryEntry.TYPES.NAME_CHANGED;

class TopicChangedChatHistoryEntry extends ChatHistoryEntry {
    constructor(chatId, eventNumber, timestamp, user, topic) {
        super(TopicChangedChatHistoryEntry.TYPE, chatId, eventNumber, timestamp, user);
        this.topic = topic;
        Immutable.make(this);
    }
}
TopicChangedChatHistoryEntry.TYPE = ChatHistoryEntry.TYPES.TOPIC_CHANGED;

class ChatHistoryEventMapper {
    static toChatHistoryEntry(data, identityCache) {
        if (data.created) {
            const { chatId, eventNumber, timestamp, user, name, topic, members } = data.created;
            const memberUsers = getOrDefaultArray(members).map(userId => identityCache.getUser(protoToDomainUserId(userId)));
            return new CreatedChatHistoryEntry(chatId, getOrDefaultNumber(eventNumber), timestampToDate(timestamp), identityCache.getUser(protoToDomainUserId(user)), name, topic, memberUsers);
        }
        else if (data.message) {
            const { chatId, eventNumber, timestamp, user, message } = data.message;
            return new MessageChatHistoryEntry(chatId, getOrDefaultNumber(eventNumber), timestampToDate(timestamp), identityCache.getUser(protoToDomainUserId(user)), message);
        }
        else if (data.userAdded) {
            const { chatId, eventNumber, timestamp, user, addedUser } = data.userAdded;
            return new UserAddedChatHistoryEntry(chatId, getOrDefaultNumber(eventNumber), timestampToDate(timestamp), identityCache.getUser(protoToDomainUserId(user)), identityCache.getUser(protoToDomainUserId(addedUser)));
        }
        else if (data.userRemoved) {
            const { chatId, eventNumber, timestamp, user, removedUser } = data.userRemoved;
            return new UserRemovedChatHistoryEntry(chatId, getOrDefaultNumber(eventNumber), timestampToDate(timestamp), identityCache.getUser(protoToDomainUserId(user)), identityCache.getUser(protoToDomainUserId(removedUser)));
        }
        else if (data.userJoined) {
            const { chatId, eventNumber, timestamp, user } = data.userJoined;
            return new UserJoinedChatHistoryEntry(chatId, getOrDefaultNumber(eventNumber), timestampToDate(timestamp), identityCache.getUser(protoToDomainUserId(user)));
        }
        else if (data.userLeft) {
            const { chatId, eventNumber, timestamp, user } = data.userLeft;
            return new UserLeftChatHistoryEntry(chatId, getOrDefaultNumber(eventNumber), timestampToDate(timestamp), identityCache.getUser(protoToDomainUserId(user)));
        }
        else if (data.topicChanged) {
            const { chatId, eventNumber, timestamp, user, topic } = data.topicChanged;
            return new TopicChangedChatHistoryEntry(chatId, getOrDefaultNumber(eventNumber), timestampToDate(timestamp), identityCache.getUser(protoToDomainUserId(user)), getOrDefaultString(topic));
        }
        else if (data.nameChanged) {
            const { chatId, eventNumber, timestamp, user, name } = data.nameChanged;
            return new NameChangedChatHistoryEntry(chatId, getOrDefaultNumber(eventNumber), timestampToDate(timestamp), identityCache.getUser(protoToDomainUserId(user)), getOrDefaultString(name));
        }
        else {
            throw new ConvergenceError("Invalid chat event: " + JSON.stringify(data));
        }
    }
}

var ChatTypes;
(function (ChatTypes) {
    ChatTypes["DIRECT"] = "direct";
    ChatTypes["CHANNEL"] = "channel";
    ChatTypes["ROOM"] = "room";
})(ChatTypes || (ChatTypes = {}));
function createChatInfo(session, identityCache, chatData) {
    let maxEvent = -1;
    const localUserId = session.user().userId;
    const members = getOrDefaultArray(chatData.members).map(member => {
        const userId = protoToDomainUserId(member.user);
        if (userId.equals(localUserId)) {
            maxEvent = getOrDefaultNumber(member.maxSeenEventNumber);
        }
        const user = identityCache.getUser(userId);
        return { user, maxSeenEventNumber: getOrDefaultNumber(member.maxSeenEventNumber) };
    });
    return {
        chatId: chatData.id,
        chatType: chatData.chatType,
        membership: chatData.membership,
        name: getOrDefaultString(chatData.name),
        topic: getOrDefaultString(chatData.topic),
        createdTime: timestampToDate(chatData.createdTime),
        lastEventTime: timestampToDate(chatData.lastEventTime),
        lastEventNumber: getOrDefaultNumber(chatData.lastEventNumber),
        maxSeenEventNumber: maxEvent,
        members,
        joined: members.find(m => m.user.userId.equals(localUserId)) !== undefined
    };
}

class ChatPermissionManager extends AbstractPermissionManager {
    constructor(id, connection) {
        super(connection);
        this._target = {
            chat: { id }
        };
    }
    id() {
        return this._target.activity.id;
    }
    _getTarget() {
        return this._target;
    }
}

const Events$1 = {
    MESSAGE: ChatMessageEvent.NAME,
    USER_JOINED: UserJoinedEvent.NAME,
    USER_LEFT: UserLeftEvent.NAME,
    USER_ADDED: UserAddedEvent.NAME,
    USER_REMOVED: UserRemovedEvent.NAME,
};
Object.freeze(Events$1);
class Chat extends ConvergenceEventEmitter {
    constructor(connection, identityCache, messageStream, chatInfo) {
        super();
        this._connection = connection;
        this._identityCache = identityCache;
        this._info = chatInfo;
        messageStream.subscribe(event => {
            this._processEvent(event);
            this._emitEvent(event);
        });
    }
    session() {
        return this._connection.session();
    }
    info() {
        return Object.assign({}, this._info);
    }
    isJoined() {
        return this._info.joined;
    }
    send(message) {
        this._connection.session().assertOnline();
        this._assertJoined();
        return this._connection.request({
            publishChatMessageRequest: {
                chatId: this._info.chatId,
                message
            }
        }).then((response) => {
            const { publishChatMessageResponse } = response;
            const eventNumber = getOrDefaultNumber(publishChatMessageResponse.eventNumber);
            const timestamp = timestampToDate(publishChatMessageResponse.timestamp);
            return { eventNumber, timestamp };
        });
    }
    setName(name) {
        this._connection.session().assertOnline();
        this._assertJoined();
        return this._connection.request({
            setChatNameRequest: {
                chatId: this._info.chatId,
                name
            }
        }).then(() => undefined);
    }
    setTopic(topic) {
        this._connection.session().assertOnline();
        this._assertJoined();
        return this._connection.request({
            setChatTopicRequest: {
                chatId: this._info.chatId,
                topic
            }
        }).then(() => undefined);
    }
    markSeen(eventNumber) {
        this._connection.session().assertOnline();
        this._assertJoined();
        return this._connection.request({
            markChatEventsSeenRequest: {
                chatId: this._info.chatId,
                eventNumber
            }
        }).then(() => {
            return;
        });
    }
    getHistory(options) {
        this._connection.session().assertOnline();
        this._assertJoined();
        return this._connection.request({
            getChatHistoryRequest: {
                chatId: this._info.chatId,
                startEvent: toOptional(options.startEvent),
                limit: toOptional(options.limit),
                forward: toOptional(options.forward),
                eventFilter: options.eventFilter
            }
        }).then((message) => {
            const response = message.getChatHistoryResponse;
            const data = getOrDefaultArray(response.data)
                .map(d => ChatHistoryEventMapper.toChatHistoryEntry(d, this._identityCache));
            return new PagedData(data, getOrDefaultNumber(response.startIndex), getOrDefaultNumber(response.totalResults));
        });
    }
    permissions() {
        return new ChatPermissionManager(this._info.chatId, this._connection);
    }
    _updateWithData(chatData) {
        this._info = createChatInfo(this._connection.session(), this._identityCache, chatData);
    }
    _join() {
        return this._connection.request({
            joinChatRequest: {
                chatId: this._info.chatId
            }
        }).then((response) => {
            const { joinChatResponse } = response;
            this._updateWithData(joinChatResponse.chatInfo);
        });
    }
    _assertJoined() {
        if (!this.isJoined()) {
            const message = `Chat channel not joined: ${this._info.chatId}`;
            throw new ConvergenceError(message, ConvergenceErrorCodes.CHAT_NOT_JOINED);
        }
    }
    _processEvent(event) {
        if (event instanceof ChatEvent) {
            this._info = Object.assign(Object.assign({}, this._info), { lastEventNumber: event.eventNumber, lastEventTime: event.timestamp });
        }
        if (event instanceof UserJoinedEvent || event instanceof UserAddedEvent) {
            const user = (event instanceof UserJoinedEvent) ? event.user : event.addedUser;
            const members = this._info.members.slice(0);
            const member = { user, maxSeenEventNumber: -1 };
            members.push(member);
            if (event.user.username === this.session().user().username) {
                this._info = Object.assign(Object.assign({}, this._info), { joined: true });
            }
            this._info = Object.assign(Object.assign({}, this._info), { members });
        }
        else if (event instanceof UserLeftEvent || event instanceof UserRemovedEvent) {
            const removedUser = (event instanceof UserLeftEvent) ? event.user : event.removedUser;
            if (this.session().user().userId.equals(removedUser.userId)) {
                this._info = Object.assign(Object.assign({}, this._info), { joined: false });
            }
            const members = this._info.members.filter(member => !member.user.userId.equals(removedUser.userId));
            this._info = Object.assign(Object.assign({}, this._info), { members });
        }
        else if (event instanceof ChatNameChangedEvent) {
            this._info = Object.assign(Object.assign({}, this._info), { name: event.chatName });
        }
        else if (event instanceof ChatTopicChangedEvent) {
            this._info = Object.assign(Object.assign({}, this._info), { topic: event.topic });
        }
        else if (event instanceof ChatEventsMarkedSeenEvent) {
            const members = this._info.members.slice(0);
            const index = members.findIndex(m => m.user.userId.equals(event.user.userId));
            if (index >= 0) {
                const member = members[index];
                if (member) {
                    const newMember = Object.assign(Object.assign({}, member), { eventNumber: event.maxSeenEventNumber });
                    members[index] = newMember;
                    this._info = Object.assign(Object.assign({}, this._info), { members });
                    if (this.session().user().userId.equals(member.user.userId)) {
                        this._info = Object.assign(Object.assign({}, this._info), { maxSeenEventNumber: event.maxSeenEventNumber });
                    }
                }
            }
        }
        Immutable.make(this._info);
    }
}
Chat.Events = Events$1;

class DirectChat extends Chat {
    constructor(connection, identityCache, messageStream, info) {
        super(connection, identityCache, messageStream, info);
    }
    info() {
        const info = super.info();
        const localUserId = this.session().user().userId;
        const otherUsers = info.members.filter(member => !member.user.userId.equals(localUserId));
        return Object.assign(Object.assign({}, info), { otherUsers });
    }
}

class MembershipChat extends Chat {
    constructor(connection, identityCache, messageStream, info) {
        super(connection, identityCache, messageStream, info);
    }
    info() {
        return super.info();
    }
    leave() {
        this._connection.session().assertOnline();
        this._assertJoined();
        return this._connection.request({
            leaveChatRequest: {
                chatId: this._info.chatId
            }
        }).then(() => undefined);
    }
    remove(user) {
        this._connection.session().assertOnline();
        this._assertJoined();
        return this._connection.request({
            removeUserFromChatRequest: {
                chatId: this._info.chatId,
                userToRemove: domainUserIdToProto(DomainUserId.toDomainUserId(user))
            }
        }).then(() => undefined);
    }
}

class ChatChannel extends MembershipChat {
    constructor(connection, identityCache, messageStream, info) {
        super(connection, identityCache, messageStream, info);
    }
    add(user) {
        this._connection.session().assertOnline();
        this._assertJoined();
        return this._connection.request({
            addUserToChatRequest: {
                chatId: this._info.chatId,
                userToAdd: domainUserIdToProto(DomainUserId.toDomainUserId(user))
            }
        }).then(() => undefined);
    }
}

class ChatRoom extends MembershipChat {
    constructor(connection, identityCache, messageStream, info) {
        super(connection, identityCache, messageStream, info);
        this._setOnline = () => {
            this._join().catch(e => console.log(e));
        };
        this._setOffline = () => {
            this._info = Object.assign(Object.assign({}, this._info), { joined: false });
        };
        connection.on(ConvergenceConnection.Events.INTERRUPTED, this._setOffline);
        connection.on(ConvergenceConnection.Events.DISCONNECTED, this._setOffline);
        connection.on(ConvergenceConnection.Events.CONNECTED, this._setOnline);
    }
}

const Events = {
    MESSAGE: ChatMessageEvent.NAME,
    USER_JOINED: UserJoinedEvent.NAME,
    USER_LEFT: UserLeftEvent.NAME,
    USER_ADDED: UserAddedEvent.NAME,
    USER_REMOVED: UserRemovedEvent.NAME,
    CHANNEL_JOINED: ChatJoinedEvent.NAME,
    CHANNEL_LEFT: ChatLeftEvent.NAME
};
Object.freeze(Events);
class ChatService extends ConvergenceEventEmitter {
    constructor(connection, identityCache) {
        super();
        this._connection = connection;
        this._identityCache = identityCache;
        this._messageStream = this._connection
            .messages()
            .pipe(filter(message => isChatMessage(message.message)), map(message => processChatMessage(message.message, this._identityCache)), tap(event => {
            if (event instanceof UserJoinedEvent && event.user.username === this.session().user().username) {
                const joined = new ChatJoinedEvent(event.chatId);
                this._emitEvent(joined);
            }
            else if (event instanceof UserLeftEvent && event.user.username === this.session().user().username) {
                const left = new ChatLeftEvent(event.chatId);
                this._emitEvent(left);
            }
        }), share());
        this._emitFrom(this._messageStream);
    }
    static _toChatIdsArray(chatIds) {
        if (TypeChecker.isString(chatIds)) {
            return [chatIds];
        }
        else if (TypeChecker.isArray(chatIds)) {
            return chatIds;
        }
        else {
            throw new ConvergenceError("chatIds must be a string or string array: " + chatIds);
        }
    }
    session() {
        return this._connection.session();
    }
    search(criteria) {
        this.session().assertOnline();
        return this._connection.request({
            chatsSearchRequest: {
                searchTerm: criteria.searchTerm,
                searchFields: criteria.searchFields,
                types: criteria.types,
                membership: criteria.membership,
                limit: jsonToProtoValue(criteria.limit),
                offset: jsonToProtoValue(criteria.offset)
            }
        }).then((response) => {
            const { chatsSearchResponse } = response;
            const data = getOrDefaultArray(chatsSearchResponse.data).map(chatInfoData => {
                return createChatInfo(this._connection.session(), this._identityCache, chatInfoData);
            });
            return new PagedData(data, getOrDefaultNumber(chatsSearchResponse.startIndex), getOrDefaultNumber(chatsSearchResponse.totalResults));
        });
    }
    exists(chatIds) {
        let ids = ChatService._toChatIdsArray(chatIds);
        this.session().assertOnline();
        return this._connection.request({
            chatsExistRequest: {
                chatIds: ids
            }
        }).then((response) => {
            const { chatsExistResponse } = response;
            const exists = getOrDefaultArray(chatsExistResponse.exists);
            if (TypeChecker.isString(chatIds)) {
                return getOrDefaultBoolean(exists[0]);
            }
            else if (TypeChecker.isArray(chatIds)) {
                return new Map(chatIds.map((chatId, index) => [chatId, getOrDefaultBoolean(exists[index])]));
            }
        });
    }
    get(chatIds) {
        let ids = ChatService._toChatIdsArray(chatIds);
        this.session().assertOnline();
        return this._connection
            .request({
            getChatsRequest: {
                chatIds: ids
            }
        })
            .then((response) => {
            const { getChatsResponse } = response;
            const chatData = getOrDefaultArray(getChatsResponse.chatInfo);
            const chats = chatData
                .map(d => {
                const c = createChatInfo(this._connection.session(), this._identityCache, d);
                return this._createChat(c);
            });
            if (TypeChecker.isString(chatIds)) {
                return chats[0];
            }
            else if (TypeChecker.isArray(chatIds)) {
                return new Map(chats.map(chat => [chat.info().chatId, chat]));
            }
        });
    }
    joined() {
        if (!this.session().isConnected()) {
            return Promise.resolve([]);
        }
        return this._connection
            .request({
            getJoinedChatsRequest: {}
        })
            .then((response) => {
            const { getJoinedChatsResponse } = response;
            return getOrDefaultArray(getJoinedChatsResponse.chatInfo).map(chatInfo => {
                return createChatInfo(this._connection.session(), this._identityCache, chatInfo);
            });
        });
    }
    create(options) {
        if (!options) {
            throw new Error("create options must be supplied");
        }
        if (options.type !== "channel" && options.type !== "room") {
            throw new Error(`type must be 'channel' or 'room': ${options.type}`);
        }
        if (options.membership !== "public" && options.membership !== "private") {
            throw new Error(`membership must be 'public' or 'private': ${options.membership}`);
        }
        if (options.type === "room" && options.membership === "private") {
            throw new Error(`membership must be 'public' for a 'room': ${options.membership}`);
        }
        if (options.id !== undefined) {
            Validation.assertNonEmptyString(options.id, "id");
        }
        this.session().assertOnline();
        const { id, type: chatType, name, topic, membership, members } = options;
        const memberIds = (members || []).map(member => {
            if (member instanceof DomainUserId) {
                return domainUserIdToProto(member);
            }
            else {
                return domainUserIdToProto(DomainUserId.normal(member));
            }
        });
        return this._connection
            .request({
            createChatRequest: {
                chatId: toOptional(id),
                chatType,
                membership,
                name,
                topic,
                members: memberIds
            }
        })
            .then((response) => {
            const { createChatResponse } = response;
            return createChatResponse.chatId;
        })
            .catch(error => {
            if (error instanceof ConvergenceServerError &&
                error.code === "chat_already_exists" &&
                options.ignoreExistsError) {
                return Promise.resolve(id);
            }
            else {
                return Promise.reject(error);
            }
        });
    }
    remove(chatId) {
        Validation.assertNonEmptyString(chatId, "chatId");
        this.session().assertOnline();
        return this._connection.request({
            removeChatRequest: {
                chatId
            }
        }).then(() => undefined);
    }
    join(chatId) {
        Validation.assertNonEmptyString(chatId, "chatId");
        this.session().assertOnline();
        return this._connection.request({
            joinChatRequest: {
                chatId
            }
        }).then((response) => {
            const { joinChatResponse } = response;
            const chatInfo = createChatInfo(this._connection.session(), this._identityCache, joinChatResponse.chatInfo);
            return this._createChat(chatInfo);
        });
    }
    leave(chatId) {
        Validation.assertNonEmptyString(chatId, "chatId");
        this.session().assertOnline();
        return this._connection.request({
            leaveChatRequest: {
                chatId
            }
        }).then(() => undefined);
    }
    direct(users) {
        this.session().assertOnline();
        const { directIds, single } = this.processDirectChatArgs(users);
        const userLists = directIds.map(userList => {
            const values = userList.map(user => {
                if (user instanceof DomainUserId) {
                    return domainUserIdToProto(user);
                }
                else {
                    return domainUserIdToProto(DomainUserId.normal(user));
                }
            });
            return { values };
        });
        return this._connection.request({
            getDirectChatsRequest: {
                userLists
            }
        }).then((response) => {
            const { getDirectChatsResponse } = response;
            const results = getOrDefaultArray(getDirectChatsResponse.chatInfo).map(chatInfoData => {
                const chatInfo = createChatInfo(this._connection.session(), this._identityCache, chatInfoData);
                return this._createChat(chatInfo);
            });
            if (single) {
                return results[0];
            }
            else {
                return results;
            }
        });
    }
    permissions(chatId) {
        return new ChatPermissionManager(chatId, this._connection);
    }
    processDirectChatArgs(users) {
        let directIds;
        let single = true;
        if (typeof users === "string" || users instanceof DomainUserId) {
            directIds = [[users]];
        }
        else if (Array.isArray(users)) {
            if (users.length === 0) {
                throw new ConvergenceError("users can not be an empty array");
            }
            const firstIsArray = Array.isArray(users[0]);
            const allArrays = users.every(u => Array.isArray(u));
            if (firstIsArray !== allArrays) {
                throw new ConvergenceError("When passing an array, all elements must be single usernames / DomainUserIds or they must all be arrays.");
            }
            if (firstIsArray) {
                directIds = users;
                single = false;
            }
            else {
                directIds = [users];
            }
        }
        else {
            throw new ConvergenceError("users must be a string, DomainUserId, or an Array");
        }
        return { directIds, single };
    }
    _createChat(chatInfo) {
        const messageStream = this._messageStream.pipe(filter(msg => msg.chatId === chatInfo.chatId));
        switch (chatInfo.chatType) {
            case ChatTypes.DIRECT:
                return new DirectChat(this._connection, this._identityCache, messageStream, chatInfo);
            case ChatTypes.CHANNEL:
                return new ChatChannel(this._connection, this._identityCache, messageStream, chatInfo);
            case ChatTypes.ROOM:
                return new ChatRoom(this._connection, this._identityCache, messageStream, chatInfo);
            default:
                throw new Error(`Invalid chat chat type: ${chatInfo.chatType}`);
        }
    }
}
ChatService.Events = Events;

class IdentityCache {
    constructor(connection, storage) {
        this._users = new Map();
        this._sessions = new Map();
        connection.messages().subscribe((event) => {
            if (event.message.identityCacheUpdate) {
                this._processIdentityUpdate(event.message.identityCacheUpdate);
            }
        });
        this._storage = storage;
        this._log = Logging.logger("identity");
    }
    init() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this._storage.isEnabled()) {
                const users = yield this._storage.identityStore().getUsers();
                users.forEach(user => {
                    this._users.set(user.userId.toGuid(), user);
                });
                const sessions = yield this._storage.identityStore().getSessions();
                sessions.forEach((userId, sessionId) => {
                    const user = this._users.get(userId.toGuid());
                    this._sessions.set(sessionId, user);
                });
            }
        });
    }
    getUserForSession(sessionId) {
        return this._sessions.get(sessionId);
    }
    getUser(userId) {
        return this._users.get(userId.toGuid());
    }
    _processIdentityUpdate(message) {
        getOrDefaultArray(message.users).forEach(userData => {
            const domainUser = toDomainUser(userData);
            this._users.set(domainUser.userId.toGuid(), domainUser);
            if (this._storage.isEnabled()) {
                this._storage.identityStore().putUser(domainUser)
                    .catch(e => this._log.error("Error storing user", e));
            }
        });
        objectForEach(getOrDefaultObject(message.sessions), (sessionId, user) => {
            const userType = protoToDomainUserType(user.userType);
            const username = getOrDefaultString(user.username);
            const domainUser = this._users.get(DomainUserId.guid(userType, username));
            this._sessions.set(sessionId, domainUser);
            if (this._storage.isEnabled()) {
                this._storage.identityStore().putSession(sessionId, domainUser.userId)
                    .catch(e => this._log.error("Error storing session", e));
            }
        });
    }
}

class ConvergenceOptions {
    constructor(options) {
        ConvergenceOptions.validate(options);
        const defaultConnectionOptions = {
            timeout: ConvergenceOptions.DEFAULT_CONNECTION_TIMEOUT,
            connectionRequestTimeout: ConvergenceOptions.DEFAULT_CONNECTION_REQUEST_TIMEOUT
        };
        const { timeout, connectionRequestTimeout } = Object.assign(Object.assign({}, defaultConnectionOptions), options.connection);
        this.connectionTimeout = timeout;
        this.connectionRequestTimeout = connectionRequestTimeout;
        const defaultReconnectOptions = {
            autoReconnect: ConvergenceOptions.DEFAULT_AUTO_RECONNECT,
            autoReconnectOnInitial: ConvergenceOptions.DEFAULT_AUTO_RECONNECT_ON_INITIAL,
            reconnectIntervals: ConvergenceOptions.DEFAULT_RECONNECT_INTERVALS,
            fallbackAuth: { jwt: null, password: null, anonymous: null }
        };
        const { autoReconnect, autoReconnectOnInitial, reconnectIntervals, fallbackAuth } = Object.assign(Object.assign({}, defaultReconnectOptions), options.reconnect);
        this.autoReconnect = autoReconnect;
        this.autoReconnectOnInitial = autoReconnectOnInitial;
        this.reconnectIntervals = reconnectIntervals;
        this.fallbackAuth = null;
        if (TypeChecker.isFunction(fallbackAuth)) {
            this.fallbackAuth = fallbackAuth;
        }
        const defaultProtocolOptions = {
            defaultRequestTimeout: ConvergenceOptions.DEFAULT_REQUEST_TIMEOUT,
            heartbeat: {
                enabled: ConvergenceOptions.DEFAULT_HEARTBEAT_ENABLED,
                pingInterval: ConvergenceOptions.DEFAULT_PING_INTERVAL,
                pongTimeout: ConvergenceOptions.DEFAULT_PONG_TIMEOUT
            }
        };
        const { defaultRequestTimeout, heartbeat } = Object.assign(Object.assign({}, defaultProtocolOptions), options.protocol);
        this.defaultRequestTimeout = defaultRequestTimeout;
        this.heartbeatEnabled = heartbeat.enabled !== undefined ?
            heartbeat.enabled : ConvergenceOptions.DEFAULT_HEARTBEAT_ENABLED;
        this.pingInterval = heartbeat.pingInterval !== undefined ?
            heartbeat.pingInterval : ConvergenceOptions.DEFAULT_PING_INTERVAL;
        this.pongTimeout = heartbeat.pongTimeout !== undefined ?
            heartbeat.pongTimeout : ConvergenceOptions.DEFAULT_PONG_TIMEOUT;
        const defaultWebSocketOptions = {
            factory: ConvergenceOptions.DEFAULT_WEBSOCKET_FACTORY,
            constructor: ConvergenceOptions.DEFAULT_WEBSOCKET_CONSTRUCTOR
        };
        const wsOpts = Object.assign(Object.assign({}, defaultWebSocketOptions), options.webSocket);
        this.webSocketFactory = wsOpts.factory || null;
        this.webSocketClass = wsOpts.class || null;
        const offlineOpts = Object.assign({}, options.offline);
        this.storageAdapter = offlineOpts.storage || null;
        this.offlineStorageEnabled = this.storageAdapter !== null;
        this.offlineModelOperationSnapshotInterval = offlineOpts.modelSnapshotInterval || 100;
        const { data } = (options.models || {});
        const dataOptions = Object.assign(Object.assign({}, ConvergenceOptions.DEFAULT_MODEL_DATA_OPTIONS), data);
        this.modelDataUndefinedObjectValues = dataOptions.undefinedObjectValues;
        this.modelDataUndefinedArrayValues = dataOptions.undefinedArrayValues;
    }
    static validate(options) {
        let webSockets = false;
        try {
            webSockets = WebSocket.CLOSING === 2;
        }
        catch (e) {
        }
        if (!webSockets && (!options.webSocket || !options.webSocket.class)) {
            const message = "Convergence depends on the WebSockets API. " +
                "If Convergence is not being run in a browser, you must set the " +
                "'webSocket.class' property in the connection options.";
            throw new ConvergenceError(message, "websockets_not_supported");
        }
    }
    getOptions() {
        return {
            connection: {
                timeout: this.connectionTimeout,
                connectionRequestTimeout: this.connectionRequestTimeout
            },
            protocol: {
                defaultRequestTimeout: this.defaultRequestTimeout,
                heartbeat: {
                    enabled: this.heartbeatEnabled,
                    pingInterval: this.pingInterval,
                    pongTimeout: this.pongTimeout
                }
            },
            reconnect: {
                autoReconnect: this.autoReconnect,
                autoReconnectOnInitial: this.autoReconnectOnInitial,
                reconnectIntervals: this.reconnectIntervals,
                fallbackAuth: this.fallbackAuth
            },
            offline: {
                storage: this.storageAdapter
            },
            webSocket: {
                factory: this.webSocketFactory,
                class: this.webSocketClass
            }
        };
    }
}
ConvergenceOptions.DEFAULT_CONNECTION_TIMEOUT = 5;
ConvergenceOptions.DEFAULT_CONNECTION_REQUEST_TIMEOUT = 10;
ConvergenceOptions.DEFAULT_AUTO_RECONNECT = true;
ConvergenceOptions.DEFAULT_AUTO_RECONNECT_ON_INITIAL = true;
ConvergenceOptions.DEFAULT_RECONNECT_INTERVALS = [0, 5, 10, 20, 30];
ConvergenceOptions.DEFAULT_REQUEST_TIMEOUT = 10;
ConvergenceOptions.DEFAULT_HEARTBEAT_ENABLED = true;
ConvergenceOptions.DEFAULT_PING_INTERVAL = 5;
ConvergenceOptions.DEFAULT_PONG_TIMEOUT = 10;
ConvergenceOptions.DEFAULT_WEBSOCKET_FACTORY = null;
ConvergenceOptions.DEFAULT_WEBSOCKET_CONSTRUCTOR = null;
ConvergenceOptions.DEFAULT_MODEL_DATA_OPTIONS = {
    undefinedObjectValues: "error",
    undefinedArrayValues: "error"
};

class StorageEngine {
    constructor() {
        this._log = Logging.logger("storage");
        this._storage = null;
    }
    configure(storage) {
        this._log.debug("Initializing storage engine: " + storage.adapterId());
        if (!storage) {
            throw new Error("storage must be specified");
        }
        this._storage = storage;
    }
    openStore(namespace, domainId, username) {
        return this._storage.initialize(namespace, domainId, username);
    }
    isEnabled() {
        return this._storage !== null;
    }
    isInitialized() {
        return this._storage !== null && this._storage.isInitialized();
    }
    dispose() {
        if (!this.isInitialized()) {
            throw new Error("Can not disposed a storage adapter that is not initialized");
        }
        if (this.isDisposed()) {
            throw new Error("Already disposed");
        }
        this._storage.dispose();
    }
    isDisposed() {
        return this._storage !== null && this._storage.isDisposed();
    }
    modelStore() {
        return this._storage.modelStore();
    }
    identityStore() {
        return this._storage.identityStore();
    }
}

class ModelOfflineManager extends ConvergenceEventEmitter {
    constructor(connection, snapshotInterval, storage) {
        super();
        this._log = Logging.logger("models.offline");
        this._modelCommitted = (event) => {
            this._onCommitStateChanged(event.src);
        };
        this._modelModified = (event) => {
            this._onCommitStateChanged(event.src);
        };
        this._connection = connection;
        this._storage = storage;
        this._offlineModels = new Map();
        this._openModels = new Map();
        this._ready = new ReplayDeferred();
        this._snapshotInterval = snapshotInterval;
        this._online = false;
        this._downloadInProgress = false;
        this._modelsToDownload = 0;
        this._connection
            .messages()
            .subscribe((messageEvent) => {
            const message = messageEvent.message;
            if (message.modelOfflineUpdated) {
                this._handleModelOfflineUpdated(message.modelOfflineUpdated);
            }
        });
    }
    static _mapClientOperationEvent(modelId, opEvent) {
        const opData = toOfflineOperationData(opEvent.operation);
        return {
            sessionId: opEvent.sessionId,
            modelId,
            sequenceNumber: opEvent.seqNo,
            contextVersion: opEvent.contextVersion,
            timestamp: opEvent.timestamp,
            operation: opData
        };
    }
    init() {
        return __awaiter(this, void 0, void 0, function* () {
            this._log.debug("Initializing offline model manager");
            try {
                yield this._initOfflineModels();
                this._checkAndUpdateModelDownloadStatus(false);
                this._ready.resolve();
            }
            catch (e) {
                this._ready.reject(e);
                throw e;
            }
        });
    }
    isOfflineEnabled() {
        return this._storage.isEnabled();
    }
    ready() {
        return this._ready.promise();
    }
    setOnline() {
        this._online = true;
        return this._resubscribe();
    }
    setOffline() {
        this._online = false;
        if (this._downloadInProgress) {
            this._handleDownloadStopped();
        }
    }
    modelOpened(model, opsSinceSnapshot) {
        const record = { model, opsSinceSnapshot };
        this._openModels.set(model.modelId(), record);
        if (!this._offlineModels.has(model.modelId())) {
            this._offlineModels.set(model.modelId(), {
                subscribed: false,
                available: true,
                deleted: false,
                uncommited: !model.isCommitted(),
                created: model.isLocal(),
                version: model.version(),
                permissions: model.permissions()
            });
            this._emitOfflineStatusChanged(model.modelId());
        }
        model.on(RealTimeModel.Events.MODIFIED, this._modelModified);
        model.on(RealTimeModel.Events.COMMITTED, this._modelCommitted);
    }
    modelClosed(model) {
        this._openModels.delete(model.modelId());
        this._deleteIfNotNeeded(model.modelId())
            .catch(e => this._log.error("Error cleaning up model after close", e));
        model.off(RealTimeModel.Events.MODIFIED, this._modelModified);
        model.off(RealTimeModel.Events.COMMITTED, this._modelCommitted);
    }
    isModelSubscribed(modelId) {
        return this._offlineModels.has(modelId) && this._offlineModels.get(modelId).subscribed;
    }
    getSubscribedModelIds() {
        return new Set(this._offlineModels.keys());
    }
    getModelsRequiringSync() {
        return this._storage.modelStore().getModelsRequiringSync();
    }
    subscribe(modelIds) {
        const subscribe = modelIds.filter(id => !this.isModelSubscribed(id));
        const unsubscribe = [];
        return this._updateSubscriptions(subscribe, unsubscribe, false);
    }
    unsubscribe(modelIds) {
        return __awaiter(this, void 0, void 0, function* () {
            const subscribe = [];
            const unsubscribe = modelIds.filter(id => this.isModelSubscribed(id));
            return this._updateSubscriptions(subscribe, unsubscribe, false);
        });
    }
    setSubscriptions(modelIds) {
        const subscribe = modelIds.filter(modelId => this.isModelSubscribed(modelId));
        const unsubscribe = Array.from(this._getSubscribedModelIds()).filter(modelId => !modelIds.includes(modelId));
        return this._updateSubscriptions(subscribe, unsubscribe, true);
    }
    getModelCreationData(modelId) {
        return this._storage.modelStore().getModelCreationData(modelId);
    }
    modelCreated(modelId) {
        return this._storage.modelStore()
            .completeModelCreation(modelId)
            .then(() => {
            const entry = this._offlineModels.get(modelId);
            if (entry) {
                entry.created = false;
            }
            return this._deleteIfNotNeeded(modelId);
        });
    }
    createOfflineModel(creationData) {
        return this._storage
            .modelStore()
            .initiateModelCreation(creationData)
            .then((metaData) => {
            this._offlineModels.set(creationData.modelId, {
                subscribed: false,
                available: true,
                uncommited: false,
                deleted: metaData.deleted,
                created: true,
                version: 0
            });
            this._emitOfflineStatusChanged(creationData.modelId);
        });
    }
    getOfflineModelState(modelId) {
        return this._storage.modelStore().getModelState(modelId);
    }
    claimValueIdPrefix(modelId) {
        return this._storage.modelStore().claimValueIdPrefix(modelId);
    }
    getModelMetaData(modelId) {
        return this._storage.modelStore().getModelMetaData(modelId);
    }
    getAllModelMetaData() {
        return this._storage.modelStore().getAllModelMetaData();
    }
    processLocalOperation(modelId, clientEvent) {
        const localOpData = ModelOfflineManager._mapClientOperationEvent(modelId, clientEvent);
        const entry = this._offlineModels.get(modelId);
        if (entry) {
            entry.uncommited = true;
        }
        return this._storage
            .modelStore()
            .processLocalOperation(localOpData)
            .then(() => this._handleOperation(modelId));
    }
    processOperationAck(modelId, seqNo, serverOp) {
        const entry = this._offlineModels.get(modelId);
        const model = this._openModels.get(modelId);
        if (entry && model) {
            entry.uncommited = !model.model.isCommitted();
        }
        return this._storage
            .modelStore()
            .processOperationAck(modelId, seqNo, serverOp);
    }
    processServerOperationEvent(modelId, serverOperation, transformedLocalOps) {
        const opData = toOfflineOperationData(serverOperation.operation);
        const serverOp = {
            modelId,
            sessionId: serverOperation.clientId,
            version: serverOperation.version,
            timestamp: serverOperation.timestamp,
            operation: opData
        };
        const currentLocalOps = transformedLocalOps
            .map(clientEvent => ModelOfflineManager._mapClientOperationEvent(modelId, clientEvent));
        return this._storage.modelStore()
            .processServerOperation(serverOp, currentLocalOps)
            .then(() => this._handleOperation(modelId));
    }
    markModelForDeletion(modelId) {
        return this._storage.modelStore().initiateModelDeletion(modelId)
            .then(() => this._deleteIfNotNeeded(modelId));
    }
    modelDeleted(modelId) {
        return this._storage.modelStore().completeModelDeletion(modelId)
            .then(() => this._deleteIfNotNeeded(modelId));
    }
    storeOpenModelOffline(model) {
        const snapshot = this._getSnapshot(model);
        const version = model.version();
        const state = {
            modelId: model.modelId(),
            collection: model.collectionId(),
            valueIdPrefix: {
                prefix: model._valueIdPrefix(),
                increment: 0
            },
            version,
            lastSequenceNumber: snapshot.sequenceNumber,
            createdTime: model.createdTime(),
            modifiedTime: model.time(),
            local: model.isLocal(),
            permissions: model.permissions(),
            snapshot
        };
        return this._storage.modelStore().setModelState(state);
    }
    _resubscribe() {
        this._checkAndUpdateModelDownloadStatus(false);
        const subscriptionRequest = [];
        this._offlineModels.forEach((record, modelId) => {
            subscriptionRequest.push({
                modelId,
                currentPermissions: record.permissions ? record.permissions.toJSON() : undefined,
                currentVersion: record.version ? record.version : 0
            });
        });
        return this._sendSubscriptionRequest(subscriptionRequest, [], true);
    }
    _initOfflineModels() {
        return __awaiter(this, void 0, void 0, function* () {
            const offlineModels = yield this._storage.modelStore().getAllModelMetaData();
            for (const modelMetaData of offlineModels) {
                const entry = {
                    version: modelMetaData.details ? modelMetaData.details.version : 0,
                    permissions: modelMetaData.details ?
                        ModelPermissions.fromJSON(modelMetaData.details.permissions) : undefined,
                    available: modelMetaData.available,
                    subscribed: modelMetaData.subscribed,
                    created: modelMetaData.created,
                    deleted: modelMetaData.deleted,
                    uncommited: modelMetaData.uncommitted
                };
                this._offlineModels.set(modelMetaData.modelId, entry);
                yield this._deleteIfNotNeeded(modelMetaData.modelId);
            }
        });
    }
    _updateSubscriptions(subscribe, unsubscribe, setAll, silent = false) {
        return __awaiter(this, void 0, void 0, function* () {
            if (setAll && unsubscribe.length !== 0) {
                return Promise.reject(new ConvergenceError("Unsubscribe must be empty when setting all subscriptions"));
            }
            subscribe.forEach((modelId) => this._setModeSubscribed(modelId));
            subscribe.forEach(id => this._emitOfflineStatusChanged(id));
            unsubscribe.forEach(id => this._setModelUnsubscribed(id));
            const toRemove = unsubscribe.filter(id => this._canBeDeleted(id));
            toRemove.forEach(id => this._offlineModels.delete(id));
            unsubscribe.forEach(id => this._emitOfflineStatusChanged(id));
            this._checkAndUpdateModelDownloadStatus(false);
            let toSubscribe;
            let toUnsubscribe;
            if (setAll) {
                toSubscribe = [];
                this._offlineModels.forEach((v, k) => {
                    if (v.subscribed) {
                        toSubscribe.push(k);
                    }
                });
                toUnsubscribe = [];
            }
            else {
                toSubscribe = subscribe;
                toUnsubscribe = unsubscribe;
            }
            const subscriptionRequest = toSubscribe.map(modelId => {
                if (this._openModels.has(modelId)) {
                    const model = this._openModels.get(modelId);
                    return {
                        modelId,
                        version: model.model.version(),
                        permissions: model.model.permissions().toJSON()
                    };
                }
                else {
                    return { modelId, version: 0 };
                }
            });
            const modelStore = this._storage.modelStore();
            yield modelStore.updateSubscriptions(subscribe, unsubscribe);
            yield modelStore.deleteModels(toRemove);
            if (!silent) {
                return this._sendSubscriptionRequest(subscriptionRequest, toUnsubscribe, setAll);
            }
        });
    }
    _setModeSubscribed(modelId) {
        const model = this._offlineModels.get(modelId);
        if (model) {
            model.subscribed = true;
        }
        else {
            const entry = {
                version: 0,
                subscribed: true,
                available: false,
                created: false,
                deleted: false,
                uncommited: false
            };
            this._offlineModels.set(modelId, entry);
        }
    }
    _setModelUnsubscribed(modelId) {
        const entry = this._offlineModels.get(modelId);
        if (entry) {
            entry.subscribed = false;
        }
    }
    _canBeDeleted(modelId) {
        const entry = this._offlineModels.get(modelId);
        return entry !== undefined &&
            !entry.subscribed &&
            !entry.created &&
            !entry.uncommited &&
            !entry.deleted &&
            !this._openModels.has(modelId);
    }
    _sendSubscriptionRequest(subscribe, unsubscribe, all) {
        const change = all || subscribe.length > 0 || unsubscribe.length > 0;
        if (this._online && change) {
            const message = {
                modelOfflineSubscriptionChange: {
                    subscribe,
                    unsubscribe,
                    all
                }
            };
            return this._connection.request(message).then(() => undefined);
        }
        else {
            return Promise.resolve();
        }
    }
    _handleModelOfflineUpdated(message) {
        const modelId = getOrDefaultString(message.modelId);
        if (!this._openModels.has(modelId)) {
            if (getOrDefaultBoolean(message.deleted)) {
                this._handleOfflineModelDeleted(modelId);
            }
            else if (getOrDefaultBoolean(message.permissionRevoked)) {
                this._handleOfflineModelPermissionRevoked(modelId);
            }
            else if (message.initial) {
                this._handleOfflineModelInitialDownload(modelId, message);
            }
            else if (message.updated) {
                this._handleOfflineModelUpdated(modelId, message);
            }
        }
    }
    _handleOfflineModelUpdated(modelId, message) {
        if (this.isModelSubscribed(modelId)) {
            const { model, permissions } = message.updated;
            const dataUpdate = model ? {
                version: getOrDefaultNumber(model.version),
                createdTime: timestampToDate(model.createdTime),
                modifiedTime: timestampToDate(model.modifiedTime),
                data: toObjectValue(model.data)
            } : undefined;
            const permissionsUpdate = toModelPermissions(permissions);
            const update = {
                modelId,
                dataUpdate,
                permissionsUpdate
            };
            this._storage.modelStore()
                .processOfflineModelUpdate(update)
                .catch(e => {
                this._emitErrorEvent("Could not update offline model after download.", e);
            })
                .then(() => {
                const current = this._offlineModels.get(modelId);
                if (current && current.available) {
                    if (dataUpdate) {
                        this._offlineModels.set(modelId, {
                            version: getOrDefaultNumber(model.version),
                            permissions: permissionsUpdate,
                            available: true,
                            subscribed: true,
                            created: false,
                            uncommited: false,
                            deleted: false
                        });
                    }
                    const modelPermissions = permissionsUpdate ? ModelPermissions.fromJSON(permissionsUpdate) : null;
                    const version = dataUpdate ? dataUpdate.version : null;
                    this._emitEvent(new OfflineModelUpdatedEvent(modelId, version, modelPermissions));
                }
            });
        }
    }
    _handleOfflineModelInitialDownload(modelId, message) {
        if (this.isModelSubscribed(modelId)) {
            const { collection, model, permissions, valueIdPrefix } = message.initial;
            const modelPermissions = toModelPermissions(permissions);
            const version = getOrDefaultNumber(model.version);
            const modelState = {
                modelId,
                collection,
                valueIdPrefix: { prefix: valueIdPrefix, increment: 0 },
                version,
                lastSequenceNumber: 0,
                createdTime: timestampToDate(model.createdTime),
                modifiedTime: timestampToDate(model.modifiedTime),
                local: false,
                permissions: modelPermissions,
                snapshot: {
                    version: getOrDefaultNumber(model.version),
                    sequenceNumber: 0,
                    data: toObjectValue(model.data),
                    serverOperations: [],
                    localOperations: []
                }
            };
            this._storage
                .modelStore()
                .setModelState(modelState)
                .catch(e => this._emitErrorEvent("Could not store offline model after download.", e))
                .then(() => {
                this._offlineModels.set(modelId, {
                    version,
                    permissions: modelPermissions,
                    available: true,
                    subscribed: true,
                    uncommited: false,
                    created: false,
                    deleted: false
                });
                this._emitEvent(new OfflineModelDownloadedEvent(modelId, version, modelPermissions));
                this._emitOfflineStatusChanged(modelId);
                this._checkAndUpdateModelDownloadStatus(true);
            });
        }
    }
    _handleOfflineModelPermissionRevoked(modelId) {
        this._updateSubscriptions([], [modelId], false, true)
            .then(() => this._deleteIfNotNeeded(modelId))
            .then(() => {
            const revokedEvent = new OfflineModelPermissionsRevokedEvent(modelId);
            this._emitEvent(revokedEvent);
            this._emitOfflineStatusChanged(modelId);
            this._checkAndUpdateModelDownloadStatus(false);
        })
            .catch(e => this._emitErrorEvent("Could not delete offline model after permissions revoked.", e));
    }
    _handleOfflineModelDeleted(modelId) {
        this._updateSubscriptions([], [modelId], false, true)
            .then(() => this._deleteIfNotNeeded(modelId))
            .then(() => {
            const deletedEvent = new OfflineModelDeletedEvent(modelId);
            this._emitEvent(deletedEvent);
            this._emitOfflineStatusChanged(modelId);
            this._checkAndUpdateModelDownloadStatus(false);
        })
            .catch(e => this._emitErrorEvent("Could not delete offline model after permissions revoked.", e));
    }
    _emitOfflineStatusChanged(modelId) {
        const entry = this._offlineModels.get(modelId);
        const statusEvent = entry ?
            new OfflineModelStatusChangedEvent(modelId, entry.subscribed, entry.available, !entry.uncommited, entry.created) :
            new OfflineModelStatusChangedEvent(modelId, false, false, false, false);
        this._emitEvent(statusEvent);
    }
    _handleOperation(modelId) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this._openModels.has(modelId)) {
                let { model, opsSinceSnapshot } = this._openModels.get(modelId);
                opsSinceSnapshot++;
                if (opsSinceSnapshot >= this._snapshotInterval) {
                    const snapshot = this._getSnapshot(model);
                    yield this._storage.modelStore()
                        .snapshotModel(modelId, snapshot.version, snapshot.sequenceNumber, snapshot.data)
                        .then(() => {
                        opsSinceSnapshot = 0;
                    })
                        .catch(e => this._log.error("Error snapshotting model", e));
                }
                this._openModels.set(modelId, { model, opsSinceSnapshot });
            }
        });
    }
    _getSnapshot(model) {
        const modelStateSnapshot = model._getConcurrencyControlStateSnapshot();
        const localOperations = modelStateSnapshot.uncommittedOperations
            .map(op => ModelOfflineManager._mapClientOperationEvent(model.modelId(), op));
        const serverOperations = [];
        return {
            version: model.version(),
            sequenceNumber: modelStateSnapshot.lastSequenceNumber,
            data: modelStateSnapshot.data,
            localOperations,
            serverOperations
        };
    }
    _checkAndUpdateModelDownloadStatus(modelDownloaded) {
        const howManyModelsNeeded = this._howManyModelsToDownload();
        const downloadsNeeded = howManyModelsNeeded > 0;
        if (this._modelsToDownload !== howManyModelsNeeded) {
            this._handleDownloadStatusChanged(modelDownloaded, howManyModelsNeeded);
        }
        if (this._online) {
            if (this._downloadInProgress && !downloadsNeeded) {
                this._handleDownloadStopped();
            }
            else if (!this._downloadInProgress && downloadsNeeded) {
                this._handleDownloadStarted(howManyModelsNeeded);
            }
        }
    }
    _handleDownloadStarted(howManyModelsNeeded) {
        this._downloadInProgress = true;
        this._emitEvent(new OfflineModelsDownloadStartedEvent(howManyModelsNeeded));
    }
    _handleDownloadStopped() {
        this._downloadInProgress = false;
        this._emitEvent(new OfflineModelsDownloadStoppedEvent());
    }
    _handleDownloadStatusChanged(modelDownloaded, howManyModelsNeeded) {
        const trigger = modelDownloaded ? "download" : "subscription_changed";
        this._emitEvent(new OfflineModelsDownloadStatusChangedEvent(howManyModelsNeeded, trigger));
        this._modelsToDownload = howManyModelsNeeded;
    }
    _howManyModelsToDownload() {
        let needed = 0;
        this._offlineModels.forEach(record => {
            if (!record.available) {
                needed++;
            }
        });
        return needed;
    }
    _getSubscribedModelIds() {
        const subscribed = new Set();
        this._offlineModels.forEach((v, k) => {
            if (v.subscribed) {
                subscribed.add(k);
            }
        });
        return subscribed;
    }
    _deleteIfNotNeeded(modelId) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this._canBeDeleted(modelId)) {
                yield this._storage.modelStore().deleteModels([modelId]);
                this._offlineModels.delete(modelId);
                this._emitOfflineStatusChanged(modelId);
            }
        });
    }
    _onCommitStateChanged(model) {
        if (!model.isLocal()) {
            const entry = this._offlineModels.get(model.modelId());
            if (entry) {
                entry.uncommited = !model.isCommitted();
                this._emitOfflineStatusChanged(model.modelId());
            }
            else {
                this._log.warn(`Offline entry not found for open model (${model.modelId()}) handling commit state changed`);
            }
        }
    }
    _emitErrorEvent(errorMessage, e) {
        this._emitEvent(new ErrorEvent(this._connection.session().domain(), errorMessage, e));
    }
}

class ConvergenceDomain extends ConvergenceEventEmitter {
    constructor(url, options) {
        super();
        this._log = Logging.logger("domain");
        Validation.assertString(url, "url");
        const urlExpression = /^(https?|wss?):\/{2}(.+)\/(.+)\/(.+)/;
        const urlParts = urlExpression.exec(url.trim());
        if (!urlParts || urlParts.length !== 5) {
            throw new Error(`Invalid url: ${url}`);
        }
        this._namespace = urlParts[3];
        this._domainId = urlParts[4];
        this._options = new ConvergenceOptions(options || {});
        this._disposed = false;
        this._initialized = false;
        this._connection = new ConvergenceConnection(url, this, this._options);
        this._storage = new StorageEngine();
        this._identityCache = new IdentityCache(this._connection, this._storage);
        this._modelOfflineManager = new ModelOfflineManager(this._connection, this._options.offlineModelOperationSnapshotInterval, this._storage);
        this._modelService = new ModelService(this._connection, this._identityCache, this._modelOfflineManager, this._options.modelDataUndefinedObjectValues, this._options.modelDataUndefinedArrayValues);
        this._identityService = new IdentityService(this._connection);
        this._activityService = new ActivityService(this._connection, this._identityCache);
        this._presenceService = new PresenceService(this._connection, this._identityCache, undefined);
        this._chatService = new ChatService(this._connection, this._identityCache);
        this._bindConnectionEvents();
        if (this._options.storageAdapter) {
            this._log.debug("options.offline.storage is set, configuring offline storage");
            this._storage.configure(this._options.storageAdapter);
        }
    }
    static _toPromiseCallback(value) {
        if (value === undefined) {
            return () => Promise.resolve(undefined);
        }
        else {
            return typeof value === "function" ?
                value :
                () => Promise.resolve(value);
        }
    }
    url() {
        return this._connection.url();
    }
    options() {
        return this._options.getOptions();
    }
    namespace() {
        return this._namespace;
    }
    id() {
        return this._domainId;
    }
    session() {
        return this._connection.session();
    }
    models() {
        return this._modelService;
    }
    identity() {
        return this._identityService;
    }
    activities() {
        return this._activityService;
    }
    presence() {
        return this._presenceService;
    }
    chat() {
        return this._chatService;
    }
    serverTime() {
        const request = {
            serverTimeRequest: {}
        };
        return this._connection.request(request).then((response) => {
            const { serverTime } = response.serverTimeResponse;
            return timestampToDate(serverTime);
        });
    }
    dispose() {
        this._disposed = true;
        if (this._modelService !== undefined) {
            this._modelService._dispose();
        }
        if (this._connection.isConnected()) {
            return this._connection.disconnect();
        }
        else {
            return Promise.resolve();
        }
    }
    isDisposed() {
        return this._disposed;
    }
    connectWithPassword(credentials) {
        const promiseCallback = ConvergenceDomain._toPromiseCallback(credentials);
        return this._connection
            .connectWithPassword(promiseCallback)
            .then(() => this._init(this._connection.session().user().username));
    }
    connectAnonymously(displayName) {
        const promiseCallback = ConvergenceDomain._toPromiseCallback(displayName);
        return this._connection
            .connectAnonymously(promiseCallback)
            .then(() => this._init(this._connection.session().user().username));
    }
    connectWithJwt(jwt) {
        const promiseCallback = ConvergenceDomain._toPromiseCallback(jwt);
        return this._connection
            .connectWithJwt(promiseCallback)
            .then(() => this._init(this._connection.session().user().username));
    }
    reconnect(token) {
        token = token || this._connection.session().reconnectToken();
        if (!TypeChecker.isSet(token)) {
            return Promise.reject(new Error("No reconnect token was provided, and there is not one in memory"));
        }
        else {
            const promiseCallback = ConvergenceDomain._toPromiseCallback(token);
            return this._connection
                .connectWithReconnectToken(promiseCallback)
                .then(() => this._init(this._connection.session().user().username));
        }
    }
    initializeOffline(username) {
        const promiseCallback = ConvergenceDomain._toPromiseCallback(username);
        if (TypeChecker.isNotSet(username)) {
            throw new Error("An username must be provided to initialize the domain offline.");
        }
        if (TypeChecker.isNotSet(this._options.storageAdapter)) {
            throw new Error("'options.offline.storage' must be set to initialize the domain offline.");
        }
        return promiseCallback().then(u => this._init(u));
    }
    disconnect() {
        if (!this._connection.isDisconnected()) {
            return this._connection.disconnect();
        }
        else {
            return Promise.resolve();
        }
    }
    isDisconnected() {
        return this._connection.isDisconnected();
    }
    isConnected() {
        return this._connection.isConnected();
    }
    _emitError(error) {
        this._emitEvent(error);
    }
    _authenticateWithJwt(jwt) {
        return this._connection
            .connectWithJwt(jwt)
            .then(() => undefined);
    }
    _authenticateWithReconnectToken(token) {
        return this._connection
            .connectWithReconnectToken(token)
            .then(() => undefined);
    }
    _authenticateAnonymously(displayName) {
        return this._connection
            .connectAnonymously(displayName)
            .then(() => undefined);
    }
    _bindConnectionEvents() {
        this._connection.on(ConvergenceConnection.Events.CONNECTION_SCHEDULED, (e) => this._emitEvent(new ConnectionScheduledEvent(this, e.delay)));
        this._connection.on(ConvergenceConnection.Events.CONNECTING, () => this._emitEvent(new ConnectingEvent(this)));
        this._connection.on(ConvergenceConnection.Events.AUTHENTICATING, (e) => this._emitEvent(new AuthenticatingEvent(this, e.method)));
        this._connection.on(ConvergenceConnection.Events.CONNECTED, (e) => {
            this._emitEvent(new ConnectedEvent(this));
            this._onAuthenticated(e);
        });
        this._connection.on(ConvergenceConnection.Events.CONNECTION_FAILED, (e) => {
            this._emitEvent(new ConnectionFailedEvent(this, e.code, e.authMethod, e.details));
        });
        this._connection.on(ConvergenceConnection.Events.INTERRUPTED, () => {
            this._emitEvent(new InterruptedEvent(this));
        });
        this._connection.on(ConvergenceConnection.Events.DISCONNECTED, () => {
            this._emitEvent(new DisconnectedEvent(this));
        });
        this._connection.on(ConvergenceConnection.Events.ERROR, (evt) => this._emitEvent(new ErrorEvent(this, evt.error.message)));
    }
    _init(username) {
        if (this._initialized) {
            return Promise.resolve();
        }
        this._log.debug("Initializing domain");
        if (this._options.storageAdapter) {
            this._log.debug("options.offline.storage is set, opening offline storage");
            return this._storage.openStore(this._namespace, this._domainId, username)
                .then(() => __awaiter(this, void 0, void 0, function* () {
                this._initialized = true;
                yield this._modelOfflineManager.init();
                yield this._identityCache.init();
            }));
        }
        else {
            this._initialized = true;
            return Promise.resolve();
        }
    }
    _onAuthenticated(authEvent) {
        const state = mapObjectValues(getOrDefaultObject(authEvent.state), protoValueToJson);
        this._presenceService._setInternalState(StringMap.objectToMap(state));
    }
}
ConvergenceDomain.Events = {
    CONNECTION_SCHEDULED: ConnectionScheduledEvent.NAME,
    CONNECTING: ConnectingEvent.NAME,
    AUTHENTICATING: AuthenticatingEvent.NAME,
    CONNECTED: ConnectedEvent.NAME,
    CONNECTION_FAILED: ConnectionFailedEvent.NAME,
    INTERRUPTED: InterruptedEvent.NAME,
    DISCONNECTED: DisconnectedEvent.NAME,
    ERROR: ErrorEvent.NAME
};
Object.freeze(ConvergenceDomain.Events);

class Convergence {
    static connect(url, username, password, options, cancellationToken) {
        return Convergence.connectWithPassword(url, { username, password }, options, cancellationToken);
    }
    static connectWithPassword(url, credentials, options, cancellationToken) {
        const domain = Convergence._createDomain(url, options, cancellationToken);
        return domain.connectWithPassword(credentials).then(() => domain);
    }
    static connectAnonymously(url, displayName, options, cancellationToken) {
        const domain = Convergence._createDomain(url, options, cancellationToken);
        return domain.connectAnonymously(displayName).then(() => domain);
    }
    static connectWithJwt(url, jwt, options, cancellationToken) {
        const domain = Convergence._createDomain(url, options, cancellationToken);
        return domain.connectWithJwt(jwt).then(() => domain);
    }
    static reconnect(url, token, options, cancellationToken) {
        const domain = Convergence._createDomain(url, options, cancellationToken);
        return domain.reconnect(token).then(() => domain);
    }
    static configureLogging(config) {
        Logging.configure(config);
    }
    static _createDomain(url, options, cancellationToken) {
        const domain = new ConvergenceDomain(url, options);
        if (typeof cancellationToken === "object") {
            cancellationToken._bind(() => domain.dispose());
        }
        return domain;
    }
}
const connect = Convergence.connect;
const connectWithPassword = Convergence.connectWithPassword;
const connectAnonymously = Convergence.connectAnonymously;
const connectWithJwt = Convergence.connectWithJwt;
const reconnect = Convergence.reconnect;
const configureLogging = Convergence.configureLogging;

function toPromise(req) {
    return new Promise((resolve, reject) => {
        req.onsuccess = () => {
            resolve(req.result);
        };
        req.onerror = () => {
            reject(req.error);
        };
    });
}
function toVoidPromise(req) {
    return toPromise(req).then(() => undefined);
}
function txToPromise(tx) {
    return new Promise((resolve, reject) => {
        tx.oncomplete = () => {
            resolve();
        };
        tx.onerror = () => {
            reject(tx.error);
        };
        tx.onabort = () => {
            reject(tx.error);
        };
    });
}

const READONLY = "readonly";
const READWRITE = "readwrite";
class IdbPersistenceStore {
    constructor(db) {
        this._db = db;
    }
    static deleteFromIndex(store, index, range) {
        const onNext = (cursor) => {
            cursor.delete();
        };
        return this._cursorIterator(store, index, onNext, range);
    }
    static _getAllFromOpenIndex(index, query, direction) {
        const results = [];
        const request = index.openCursor(query, direction);
        const onNext = (cursor) => results.push(cursor.value);
        return IdbPersistenceStore._iterateOverCursor(request, onNext).then(() => results);
    }
    static _cursorIterator(objectStore, indexName, onNext, query, direction) {
        let request;
        if (indexName === null) {
            request = objectStore.openCursor(query, direction);
        }
        else {
            const index = objectStore.index(indexName);
            request = index.openCursor(query, direction);
        }
        return IdbPersistenceStore._iterateOverCursor(request, onNext);
    }
    static _iterateOverCursor(request, onNext) {
        return new Promise((resolve, reject) => {
            request.onsuccess = (event) => {
                const cursor = event.target.result;
                if (cursor) {
                    onNext(cursor);
                    cursor.continue();
                }
                else {
                    resolve();
                }
            };
            request.onerror = () => {
                reject(request.error);
            };
        });
    }
    _withWriteStore(store, body) {
        return this._withStores([store], READWRITE, (stores) => body(stores[0]));
    }
    _withReadStore(store, body) {
        return this._withStores([store], READONLY, (stores) => body(stores[0]));
    }
    _withWriteStores(stores, body) {
        return this._withStores(stores, READWRITE, body);
    }
    _withReadStores(stores, body) {
        return this._withStores(stores, READONLY, body);
    }
    _withStores(stores, mode, body) {
        const tx = this._db.transaction(stores, mode);
        const objectStores = stores.map(storeName => tx.objectStore(storeName));
        const result = body(objectStores)
            .catch(e => {
            tx.abort();
            return Promise.reject(e);
        });
        return Promise.all([result, txToPromise(tx)]).then(([r, _]) => r);
    }
    put(storeName, data) {
        return this._withWriteStore(storeName, (store) => {
            return toVoidPromise(store.put(data));
        });
    }
    add(storeName, data) {
        return this._withWriteStore(storeName, (store) => {
            return toVoidPromise(store.add(data));
        });
    }
    _getAll(storeName, query) {
        const results = [];
        return this._readIterator(storeName, null, (value) => {
            results.push(value);
        }, query).then(() => results);
    }
    _getAllFromIndex(storeName, index, query) {
        const results = [];
        return this._readIterator(storeName, index, (value) => {
            results.push(value);
        }, query).then(() => results);
    }
    _readIterator(storeName, indexName, onNext, query, direction) {
        const nextValue = (cursor) => {
            onNext(cursor.value);
        };
        return this._withReadStore(storeName, objectStore => {
            return IdbPersistenceStore._cursorIterator(objectStore, indexName, nextValue, query, direction);
        });
    }
    _deleteIterator(storeName, indexName, query, direction) {
        const onNext = (cursor) => {
            cursor.delete();
        };
        return this._writeIterator(storeName, indexName, onNext, query, direction);
    }
    _writeIterator(storeName, indexName, onNext, query, direction) {
        return this._withWriteStore(storeName, objectStore => {
            return IdbPersistenceStore._cursorIterator(objectStore, indexName, onNext, query, direction);
        });
    }
}

const IdbSchema = {
    ModelCreation: {
        Store: "ModelCreation",
        Fields: {
            ModelId: "modelId"
        },
        Indices: {
            ModelId: "ModelCreation.modelId"
        }
    },
    ModelMetaData: {
        Store: "ModelMetaData",
        Fields: {
            ModelId: "modelId",
            Subscribed: "subscribed",
            Created: "created",
            Deleted: "deleted",
            SyncRequired: "syncRequired",
            Uncommitted: "uncommitted"
        },
        Indices: {
            ModelId: "ModelMetaData.modelId",
            Created: "ModelMetaData.created",
            Deleted: "ModelMetaData.deleted",
            Uncommitted: "ModelMetaData.uncommitted",
            SyncRequired: "ModelMetaData.syncRequired",
            Subscribed: "ModelMetaData.subscribed"
        }
    },
    ModelData: {
        Store: "ModelData",
        Fields: {
            ModelId: "modelId"
        },
        Indices: {
            ModelId: "ModelData.modelId"
        }
    },
    ModelServerOperation: {
        Store: "ModelServerOperation",
        Fields: {
            ModelId: "modelId",
            Version: "version"
        },
        Indices: {
            ModelId: "ModelServerOperation.modelId",
            ModelId_Version: "ModelServerOperation.modelId_version"
        }
    },
    ModelLocalOperation: {
        Store: "ModelLocalOperation",
        Fields: {
            ModelId: "modelId",
            SequenceNumber: "sequenceNumber"
        },
        Indices: {
            ModelId: "ModelLocalOperation.modelId",
            ModelId_SequenceNumber: "ModelLocalOperation.modelId_sequenceNumber"
        }
    },
    DomainUser: {
        Store: "DomainUser",
        Fields: {
            Username: "username",
            UserType: "userType"
        },
        Indices: {
            UserType_Username: "DomainUser.userType_username"
        }
    },
    Session: {
        Store: "Session",
        Fields: {
            SessionId: "sessionId",
            Username: "username",
            UserType: "userType"
        },
        Indices: {
            UserType_Username: "DomainUser.userType_username",
            SessionId: "Session.sessionId"
        }
    }
};

class IdbModelStore extends IdbPersistenceStore {
    static _deleteServerOperationsForModel(store, modelId) {
        return IdbPersistenceStore.deleteFromIndex(store, IdbSchema.ModelServerOperation.Indices.ModelId, IDBKeyRange.only(modelId));
    }
    static _deleteLocalOperationsForModel(store, modelId) {
        return IdbPersistenceStore.deleteFromIndex(store, IdbSchema.ModelLocalOperation.Indices.ModelId, IDBKeyRange.only(modelId));
    }
    static _putLocalOperationsForModel(store, operations) {
        return __awaiter(this, void 0, void 0, function* () {
            for (const op of operations) {
                yield toVoidPromise(store.add(op));
            }
        });
    }
    static _putServerOperationsForModel(store, operations) {
        return __awaiter(this, void 0, void 0, function* () {
            for (const op of operations) {
                yield toVoidPromise(store.add(op));
            }
        });
    }
    static _metaDataDocToMetaData(doc) {
        const metaData = {
            modelId: doc.modelId,
            subscribed: doc.subscribed === 1,
            available: doc.available === 1,
            deleted: doc.deleted === 1,
            created: doc.created === 1,
            uncommitted: doc.uncommitted === 1,
            syncRequired: doc.syncRequired === 1,
        };
        if (doc.details) {
            metaData.details = {
                collection: doc.details.collection,
                valueIdPrefix: doc.details.valueIdPrefix,
                version: doc.details.version,
                lastSequenceNumber: doc.details.lastSequenceNumber,
                createdTime: doc.details.createdTime,
                modifiedTime: doc.details.modifiedTime,
                permissions: new ModelPermissions(doc.details.permissions.read, doc.details.permissions.write, doc.details.permissions.remove, doc.details.permissions.manage),
                snapshotVersion: doc.details.snapshotVersion,
                snapshotSequenceNumber: doc.details.snapshotSequenceNumber
            };
        }
        return metaData;
    }
    static _mergeAndPutModelState(modelState, modelMetaDataStore, modelDataStore, localOpStore, serverOpStore) {
        return __awaiter(this, void 0, void 0, function* () {
            const currentMetaData = yield toPromise(modelMetaDataStore.get(modelState.modelId));
            const valueIdPrefix = {
                prefix: modelState.valueIdPrefix.prefix,
                increment: modelState.valueIdPrefix.increment || 0
            };
            const meta = {
                modelId: modelState.modelId,
                available: 1,
                details: {
                    collection: modelState.collection,
                    valueIdPrefix,
                    createdTime: modelState.createdTime,
                    modifiedTime: modelState.modifiedTime,
                    permissions: {
                        read: modelState.permissions.read,
                        write: modelState.permissions.write,
                        remove: modelState.permissions.remove,
                        manage: modelState.permissions.manage,
                    },
                    lastSequenceNumber: modelState.lastSequenceNumber,
                    version: modelState.version,
                    snapshotVersion: modelState.snapshot.version,
                    snapshotSequenceNumber: modelState.snapshot.sequenceNumber
                }
            };
            if (modelState.local) {
                meta.created = 1;
            }
            meta.available = 1;
            if (modelState.snapshot.localOperations.length > 0) {
                meta.uncommitted = 1;
            }
            else {
                delete meta.uncommitted;
            }
            if (currentMetaData) {
                meta.subscribed = currentMetaData.subscribed;
                meta.deleted = currentMetaData.deleted;
            }
            IdbModelStore._setSyncRequired(meta);
            yield toVoidPromise(modelMetaDataStore.put(meta));
            const data = {
                modelId: modelState.modelId,
                data: modelState.snapshot.data,
            };
            yield toVoidPromise(modelDataStore.put(data));
            yield IdbModelStore._deleteServerOperationsForModel(serverOpStore, meta.modelId);
            yield IdbModelStore._deleteLocalOperationsForModel(localOpStore, meta.modelId);
            yield IdbModelStore._putLocalOperationsForModel(localOpStore, modelState.snapshot.localOperations);
            yield IdbModelStore._putServerOperationsForModel(serverOpStore, modelState.snapshot.serverOperations);
            return IdbModelStore._metaDataDocToMetaData(meta);
        });
    }
    static _removeSubscriptions(modelIds, modelMetaDataStore) {
        return __awaiter(this, void 0, void 0, function* () {
            for (const modelId of modelIds) {
                const metaData = yield toPromise(modelMetaDataStore.get(modelId));
                if (!metaData) {
                    continue;
                }
                delete metaData.subscribed;
                IdbModelStore._setSyncRequired(metaData);
                yield modelMetaDataStore.put(metaData);
            }
        });
    }
    static _deleteModel(modelId, modelCreateStore, modelMetaDataStore, modelDataStore, localOpStore, serverOpStore) {
        return __awaiter(this, void 0, void 0, function* () {
            yield modelCreateStore.delete(modelId);
            yield modelMetaDataStore.delete(modelId);
            yield modelDataStore.delete(modelId);
            yield IdbModelStore._deleteServerOperationsForModel(serverOpStore, modelId);
            yield IdbModelStore._deleteLocalOperationsForModel(localOpStore, modelId);
        });
    }
    static _addSubscriptions(modelIds, metaDataStore) {
        return __awaiter(this, void 0, void 0, function* () {
            for (const modelId of modelIds) {
                const current = yield toPromise(metaDataStore.get(modelId));
                let updated;
                if (current !== undefined) {
                    updated = current;
                    updated.subscribed = 1;
                }
                else {
                    updated = {
                        modelId,
                        subscribed: 1,
                    };
                }
                yield toVoidPromise(metaDataStore.put(updated));
            }
        });
    }
    static _setSyncRequired(meta) {
        if (meta.deleted === 1 || meta.created === 1 || meta.uncommitted === 1) {
            meta.syncRequired = 1;
        }
        else {
            delete meta.syncRequired;
        }
    }
    initiateModelCreation(modelCreation) {
        const stores = [
            IdbSchema.ModelCreation.Store,
            IdbSchema.ModelMetaData.Store,
            IdbSchema.ModelData.Store,
            IdbSchema.ModelLocalOperation.Store,
            IdbSchema.ModelServerOperation.Store
        ];
        return this._withWriteStores(stores, ([creationStore, modelMetaDataStore, modelDataStore, localOpStore, serverOpStore]) => __awaiter(this, void 0, void 0, function* () {
            const modelId = modelCreation.modelId;
            const metaData = yield toPromise(modelMetaDataStore.get(modelId));
            if (TypeChecker.isSet(metaData) && metaData.available) {
                return Promise.reject(new ConvergenceError(`An offline model with the specified id already exists: ${modelId}`));
            }
            yield toVoidPromise(creationStore.put(modelCreation));
            const version = 1;
            const lastSequenceNumber = 0;
            const permissions = new ModelPermissions(true, true, true, true);
            const modelState = {
                modelId,
                collection: modelCreation.collection,
                valueIdPrefix: { prefix: modelCreation.valueIdPrefix, increment: 0 },
                version,
                lastSequenceNumber,
                createdTime: modelCreation.createdTime,
                modifiedTime: modelCreation.createdTime,
                permissions,
                local: true,
                snapshot: {
                    version,
                    sequenceNumber: lastSequenceNumber,
                    data: modelCreation.initialData,
                    localOperations: [],
                    serverOperations: []
                },
            };
            return IdbModelStore._mergeAndPutModelState(modelState, modelMetaDataStore, modelDataStore, localOpStore, serverOpStore);
        }));
    }
    getModelCreationData(modelId) {
        return this._withReadStore(IdbSchema.ModelCreation.Store, (store) => __awaiter(this, void 0, void 0, function* () {
            return toPromise(store.get(modelId)).then(data => data || null);
        }));
    }
    completeModelCreation(modelId) {
        const stores = [
            IdbSchema.ModelCreation.Store,
            IdbSchema.ModelMetaData.Store
        ];
        return this._withWriteStores(stores, ([creationStore, modelMetaDataStore]) => __awaiter(this, void 0, void 0, function* () {
            const data = yield toPromise(creationStore.get(modelId));
            if (!data) {
                throw new ConvergenceError("Can't complete model creation for a model that was created.");
            }
            yield toVoidPromise(creationStore.delete(modelId));
            const metaData = yield toPromise(modelMetaDataStore.get(modelId));
            metaData.available = 1;
            delete metaData.created;
            IdbModelStore._setSyncRequired(metaData);
            yield toVoidPromise(modelMetaDataStore.put(metaData));
        }));
    }
    initiateModelDeletion(modelId) {
        const stores = [
            IdbSchema.ModelCreation.Store,
            IdbSchema.ModelMetaData.Store,
            IdbSchema.ModelData.Store,
            IdbSchema.ModelLocalOperation.Store,
            IdbSchema.ModelServerOperation.Store
        ];
        return this._withWriteStores(stores, ([creationStore, modelMetaDataStore, modelDataStore, localOpStore, serverOpStore]) => __awaiter(this, void 0, void 0, function* () {
            const metaData = yield toPromise(modelMetaDataStore.get(modelId));
            if (!metaData) {
                throw new ConvergenceError(`Can't delete model '${modelId}' because it doesn't exist.`);
            }
            delete metaData.details;
            delete metaData.subscribed;
            delete metaData.available;
            delete metaData.uncommitted;
            if (metaData.created === 1) {
                delete metaData.created;
            }
            else {
                metaData.deleted = 1;
            }
            IdbModelStore._setSyncRequired(metaData);
            yield toVoidPromise(modelMetaDataStore.put(metaData));
            yield toVoidPromise(creationStore.delete(modelId));
            yield IdbModelStore._deleteLocalOperationsForModel(localOpStore, modelId);
            yield IdbModelStore._deleteServerOperationsForModel(serverOpStore, modelId);
            yield toVoidPromise(modelDataStore.delete(modelId));
        }));
    }
    completeModelDeletion(modelId) {
        const stores = [
            IdbSchema.ModelMetaData.Store
        ];
        return this._withWriteStores(stores, ([modelMetaDataStore]) => __awaiter(this, void 0, void 0, function* () {
            const metaData = yield toPromise(modelMetaDataStore.get(modelId));
            if (metaData) {
                delete metaData.deleted;
                IdbModelStore._setSyncRequired(metaData);
                yield toVoidPromise(modelMetaDataStore.put(metaData));
            }
        }));
    }
    deleteModels(modelIds) {
        const storeNames = [
            IdbSchema.ModelCreation.Store,
            IdbSchema.ModelMetaData.Store,
            IdbSchema.ModelData.Store,
            IdbSchema.ModelLocalOperation.Store,
            IdbSchema.ModelServerOperation.Store
        ];
        return this._withWriteStores(storeNames, ([createStore, metaDataStore, dataStore, localOpStore, serverOpStore]) => __awaiter(this, void 0, void 0, function* () {
            for (let modelId of modelIds) {
                yield IdbModelStore._deleteModel(modelId, createStore, metaDataStore, dataStore, localOpStore, serverOpStore);
            }
        }));
    }
    modelMetaDataExists(modelId) {
        if (modelId === undefined || modelId === null) {
            throw new ConvergenceError("modelId must be defined");
        }
        return this._withReadStore(IdbSchema.ModelMetaData.Store, (store) => {
            const idx = store.index(IdbSchema.ModelMetaData.Indices.ModelId);
            return toPromise(idx.count(modelId)).then((count => count > 0));
        });
    }
    getModelMetaData(modelId) {
        return this._withReadStore(IdbSchema.ModelMetaData.Store, (store) => __awaiter(this, void 0, void 0, function* () {
            const doc = yield toPromise(store.get(modelId));
            if (doc) {
                return IdbModelStore._metaDataDocToMetaData(doc);
            }
            else {
                return null;
            }
        }));
    }
    getAllModelMetaData() {
        return this._getAll(IdbSchema.ModelMetaData.Store)
            .then(results => results.map(IdbModelStore._metaDataDocToMetaData));
    }
    getModelsRequiringSync() {
        return this
            ._getAllFromIndex(IdbSchema.ModelMetaData.Store, IdbSchema.ModelMetaData.Indices.SyncRequired)
            .then((docs) => docs.map(IdbModelStore._metaDataDocToMetaData));
    }
    getSubscribedModels() {
        const storeName = IdbSchema.ModelMetaData.Store;
        return this._withReadStore(storeName, (store) => __awaiter(this, void 0, void 0, function* () {
            const index = store.index(IdbSchema.ModelMetaData.Indices.Subscribed);
            const subscribed = yield IdbPersistenceStore._getAllFromOpenIndex(index);
            return subscribed.map(IdbModelStore._metaDataDocToMetaData);
        }));
    }
    updateSubscriptions(toAdd, toRemove) {
        const storeNames = [IdbSchema.ModelMetaData.Store];
        return this._withWriteStores(storeNames, ([metaDataStore]) => __awaiter(this, void 0, void 0, function* () {
            yield IdbModelStore._removeSubscriptions(toRemove, metaDataStore);
            yield IdbModelStore._addSubscriptions(toAdd, metaDataStore);
        }));
    }
    setModelState(modelState) {
        const stores = [
            IdbSchema.ModelMetaData.Store,
            IdbSchema.ModelData.Store,
            IdbSchema.ModelLocalOperation.Store,
            IdbSchema.ModelServerOperation.Store
        ];
        return this._withWriteStores(stores, ([modelMetaData, modelDataStore, localOpStore, serverOpStore]) => __awaiter(this, void 0, void 0, function* () {
            yield IdbModelStore._mergeAndPutModelState(modelState, modelMetaData, modelDataStore, localOpStore, serverOpStore);
        }));
    }
    getModelState(modelId) {
        if (modelId === undefined || modelId === null) {
            return Promise.reject(new ConvergenceError("modelId must be defined"));
        }
        const stores = [
            IdbSchema.ModelMetaData.Store,
            IdbSchema.ModelData.Store,
            IdbSchema.ModelLocalOperation.Store,
            IdbSchema.ModelServerOperation.Store
        ];
        return this._withReadStores(stores, ([modelMetaDataStore, modelDataStore, localOpStore, serverOpStore]) => __awaiter(this, void 0, void 0, function* () {
            const meta = yield toPromise(modelMetaDataStore.get(modelId));
            const data = yield toPromise(modelDataStore.get(modelId));
            if (meta && data) {
                const localOpsIndex = localOpStore.index(IdbSchema.ModelLocalOperation.Indices.ModelId);
                const localOperations = yield IdbPersistenceStore._getAllFromOpenIndex(localOpsIndex, modelId);
                const serverOpsIndex = serverOpStore.index(IdbSchema.ModelServerOperation.Indices.ModelId);
                const serverOperations = yield IdbPersistenceStore._getAllFromOpenIndex(serverOpsIndex, modelId);
                const snapshot = {
                    version: meta.details.snapshotVersion,
                    sequenceNumber: meta.details.snapshotSequenceNumber,
                    data: data.data,
                    localOperations,
                    serverOperations
                };
                const version = meta.details.version;
                const lastSequenceNumber = meta.details.lastSequenceNumber;
                return {
                    modelId: meta.modelId,
                    collection: meta.details.collection,
                    createdTime: meta.details.createdTime,
                    modifiedTime: meta.details.modifiedTime,
                    version,
                    lastSequenceNumber,
                    valueIdPrefix: Object.assign({}, meta.details.valueIdPrefix),
                    permissions: new ModelPermissions(meta.details.permissions.read, meta.details.permissions.write, meta.details.permissions.remove, meta.details.permissions.manage),
                    local: meta.created === 1,
                    snapshot
                };
            }
            else {
                return null;
            }
        }));
    }
    processOfflineModelUpdate(update) {
        const stores = [
            IdbSchema.ModelMetaData.Store,
            IdbSchema.ModelData.Store,
            IdbSchema.ModelServerOperation.Store,
        ];
        return this._withWriteStores(stores, ([modelMetaDataStore, modelDataStore, serverOpStore]) => __awaiter(this, void 0, void 0, function* () {
            const { modelId, dataUpdate, permissionsUpdate } = update;
            const modelMetaData = yield toPromise(modelMetaDataStore.get(modelId));
            if (modelMetaData === undefined) {
                return;
            }
            if (modelMetaData.uncommitted) {
                throw new ConvergenceError(`A model update was received for an model ('${modelId}')with uncommitted operations.`);
            }
            yield IdbModelStore._deleteServerOperationsForModel(serverOpStore, modelId);
            if (dataUpdate) {
                modelMetaData.details.version = dataUpdate.version;
                modelMetaData.details.createdTime = dataUpdate.createdTime;
                modelMetaData.details.modifiedTime = dataUpdate.modifiedTime;
                modelMetaData.details.snapshotSequenceNumber = modelMetaData.details.lastSequenceNumber;
                modelMetaData.details.snapshotVersion = dataUpdate.version;
                const data = {
                    modelId,
                    data: dataUpdate.data
                };
                yield toVoidPromise(modelDataStore.put(data));
            }
            if (permissionsUpdate) {
                modelMetaData.details.permissions = permissionsUpdate;
            }
            return toVoidPromise(modelMetaDataStore.put(modelMetaData));
        }));
    }
    processServerOperation(serverOp, localOps) {
        if (!serverOp) {
            return Promise.reject(new ConvergenceError("serverOp was undefined."));
        }
        if (!Array.isArray(localOps)) {
            return Promise.reject(new ConvergenceError("localOps was not an array."));
        }
        if (!localOps.every(op => op.modelId === serverOp.modelId)) {
            return Promise.reject(new ConvergenceError("localOp modelId did not match the serverOp modelId."));
        }
        const stores = [
            IdbSchema.ModelMetaData.Store,
            IdbSchema.ModelServerOperation.Store,
            IdbSchema.ModelLocalOperation.Store
        ];
        return this._withWriteStores(stores, ([modelMetaDataStore, serverOpStore, localOpStore]) => __awaiter(this, void 0, void 0, function* () {
            const metaData = yield toPromise(modelMetaDataStore.get(serverOp.modelId));
            if (!metaData) {
                throw new ConvergenceError(`Model meta data for model '${serverOp.modelId}' not found when processing a server operation.`);
            }
            if (!metaData.details) {
                throw new ConvergenceError(`Can't store server operation for Model '${serverOp.modelId}' because the model details are missing from storage.`);
            }
            metaData.details.version = serverOp.version + 1;
            metaData.details.modifiedTime = serverOp.timestamp;
            yield toVoidPromise(serverOpStore.add(serverOp));
            yield IdbModelStore._deleteLocalOperationsForModel(localOpStore, serverOp.modelId);
            yield IdbModelStore._putLocalOperationsForModel(localOpStore, localOps);
            yield toVoidPromise(modelMetaDataStore.put(metaData));
        }));
    }
    processLocalOperation(localOp) {
        if (!localOp) {
            return Promise.reject(new ConvergenceError("localOp is required."));
        }
        const stores = [
            IdbSchema.ModelMetaData.Store,
            IdbSchema.ModelLocalOperation.Store
        ];
        return this._withWriteStores(stores, ([modelMetaDataStore, localOpStore]) => __awaiter(this, void 0, void 0, function* () {
            const metaData = yield toPromise(modelMetaDataStore.get(localOp.modelId));
            if (!metaData) {
                throw new ConvergenceError(`Model meta data not found for '${localOp.modelId}' while processing local operation.`);
            }
            if (!metaData.details) {
                throw new ConvergenceError(`Model details not found for '${localOp.modelId}' while processing local operation.`);
            }
            metaData.uncommitted = 1;
            metaData.details.lastSequenceNumber = localOp.sequenceNumber;
            IdbModelStore._setSyncRequired(metaData);
            yield toVoidPromise(localOpStore.add(localOp));
            yield toVoidPromise(modelMetaDataStore.put(metaData));
        }));
    }
    processOperationAck(modelId, seqNo, serverOp) {
        const stores = [
            IdbSchema.ModelLocalOperation.Store,
            IdbSchema.ModelServerOperation.Store,
            IdbSchema.ModelMetaData.Store
        ];
        return this._withWriteStores(stores, ([localOpStore, serverOpStore, modelMetaDataStore]) => __awaiter(this, void 0, void 0, function* () {
            const metaData = yield toPromise(modelMetaDataStore.get(modelId));
            if (!metaData) {
                throw new ConvergenceError(`Model meta data not found for '${modelId}' while processing operation acknowledgement.`);
            }
            if (!metaData.details) {
                throw new ConvergenceError(`Model details not found for '${modelId}' while processing operation acknowledgement.`);
            }
            metaData.details.version = serverOp.version + 1;
            metaData.details.modifiedTime = serverOp.timestamp;
            yield toVoidPromise(localOpStore.delete([modelId, seqNo]));
            yield toVoidPromise(serverOpStore.add(serverOp));
            const idx = localOpStore.index(IdbSchema.ModelLocalOperation.Indices.ModelId);
            const dirty = yield toPromise(idx.count(modelId)).then((count => count > 0));
            if (dirty) {
                metaData.uncommitted = 1;
            }
            else {
                delete metaData.uncommitted;
            }
            IdbModelStore._setSyncRequired(metaData);
            yield toVoidPromise(modelMetaDataStore.put(metaData));
        }));
    }
    snapshotModel(modelId, version, sequenceNumber, modelData) {
        if (!modelData) {
            return Promise.reject(new ConvergenceError(`modelData must be defined`));
        }
        const stores = [
            IdbSchema.ModelMetaData.Store,
            IdbSchema.ModelData.Store,
            IdbSchema.ModelServerOperation.Store
        ];
        return this._withWriteStores(stores, ([metaDataStore, modelDataStore, serverOpStore]) => __awaiter(this, void 0, void 0, function* () {
            const meta = yield toPromise(metaDataStore.get(modelId));
            if (!meta) {
                throw new ConvergenceError(`Model meta data not found for '${modelId}' while snapshotting model.`);
            }
            if (!meta.available) {
                throw new ConvergenceError(`Model '${modelId}' is not available locally.`);
            }
            meta.details.snapshotVersion = version;
            meta.details.snapshotSequenceNumber = sequenceNumber;
            yield toVoidPromise(metaDataStore.put(meta));
            const data = {
                modelId,
                data: modelData
            };
            yield toVoidPromise(modelDataStore.put(data));
            yield IdbModelStore._deleteServerOperationsForModel(serverOpStore, modelId);
        }));
    }
    claimValueIdPrefix(modelId) {
        return this._withWriteStore(IdbSchema.ModelMetaData.Store, (store) => __awaiter(this, void 0, void 0, function* () {
            const meta = yield toPromise(store.get(modelId));
            if (!meta || !meta.details) {
                throw new ConvergenceError("The specified model is not available offline: " + modelId);
            }
            const result = Object.assign({}, meta.details.valueIdPrefix);
            meta.details.valueIdPrefix.increment = meta.details.valueIdPrefix.increment + 1;
            yield toVoidPromise(store.put(meta));
            return result;
        }));
    }
}

class IdbSchemaVersion1 {
    static upgrade(db) {
        const localModelStore = db.createObjectStore(IdbSchema.ModelCreation.Store, { keyPath: IdbSchema.ModelCreation.Fields.ModelId });
        localModelStore.createIndex(IdbSchema.ModelCreation.Indices.ModelId, IdbSchema.ModelCreation.Fields.ModelId, { unique: true });
        const modelMetaData = db.createObjectStore(IdbSchema.ModelMetaData.Store, { keyPath: IdbSchema.ModelMetaData.Fields.ModelId });
        modelMetaData.createIndex(IdbSchema.ModelMetaData.Indices.ModelId, IdbSchema.ModelMetaData.Fields.ModelId, { unique: true });
        modelMetaData.createIndex(IdbSchema.ModelMetaData.Indices.Created, IdbSchema.ModelMetaData.Fields.Created, { unique: false });
        modelMetaData.createIndex(IdbSchema.ModelMetaData.Indices.Deleted, IdbSchema.ModelMetaData.Fields.Deleted, { unique: false });
        modelMetaData.createIndex(IdbSchema.ModelMetaData.Indices.Uncommitted, IdbSchema.ModelMetaData.Fields.Uncommitted, { unique: false });
        modelMetaData.createIndex(IdbSchema.ModelMetaData.Indices.SyncRequired, IdbSchema.ModelMetaData.Fields.SyncRequired, { unique: false });
        modelMetaData.createIndex(IdbSchema.ModelMetaData.Indices.Subscribed, IdbSchema.ModelMetaData.Fields.Subscribed, { unique: false });
        const modelData = db.createObjectStore(IdbSchema.ModelData.Store, { keyPath: IdbSchema.ModelData.Fields.ModelId });
        modelData.createIndex(IdbSchema.ModelData.Indices.ModelId, IdbSchema.ModelData.Fields.ModelId, { unique: true });
        const serverOperationStore = db.createObjectStore(IdbSchema.ModelServerOperation.Store, { keyPath: [IdbSchema.ModelServerOperation.Fields.ModelId, IdbSchema.ModelServerOperation.Fields.Version] });
        serverOperationStore.createIndex(IdbSchema.ModelServerOperation.Indices.ModelId, IdbSchema.ModelServerOperation.Fields.ModelId, { unique: false });
        serverOperationStore.createIndex(IdbSchema.ModelServerOperation.Indices.ModelId_Version, [IdbSchema.ModelServerOperation.Fields.ModelId, IdbSchema.ModelServerOperation.Fields.Version], { unique: true });
        const localOperationStore = db.createObjectStore(IdbSchema.ModelLocalOperation.Store, {
            keyPath: [
                IdbSchema.ModelLocalOperation.Fields.ModelId,
                IdbSchema.ModelLocalOperation.Fields.SequenceNumber
            ]
        });
        localOperationStore.createIndex(IdbSchema.ModelLocalOperation.Indices.ModelId, IdbSchema.ModelLocalOperation.Fields.ModelId, { unique: false });
        localOperationStore.createIndex(IdbSchema.ModelLocalOperation.Indices.ModelId_SequenceNumber, [
            IdbSchema.ModelLocalOperation.Fields.ModelId,
            IdbSchema.ModelLocalOperation.Fields.SequenceNumber
        ], { unique: true });
        const userStore = db.createObjectStore(IdbSchema.DomainUser.Store, {
            keyPath: [
                IdbSchema.DomainUser.Fields.UserType,
                IdbSchema.DomainUser.Fields.Username,
            ]
        });
        userStore.createIndex(IdbSchema.DomainUser.Indices.UserType_Username, [
            IdbSchema.DomainUser.Fields.UserType,
            IdbSchema.DomainUser.Fields.Username,
        ], { unique: true });
        const sessionStore = db.createObjectStore(IdbSchema.Session.Store, {
            keyPath: [
                IdbSchema.Session.Fields.SessionId
            ]
        });
        sessionStore.createIndex(IdbSchema.Session.Indices.UserType_Username, [
            IdbSchema.Session.Fields.UserType,
            IdbSchema.Session.Fields.Username,
        ], { unique: false });
        sessionStore.createIndex(IdbSchema.Session.Indices.SessionId, [
            IdbSchema.Session.Fields.SessionId
        ], { unique: true });
    }
}

class IdbSchemaManager {
    static upgrade(db, targetVersion) {
        switch (targetVersion) {
            case 1:
                IdbSchemaVersion1.upgrade(db);
        }
    }
}

class IdbIdentityStore extends IdbPersistenceStore {
    putUser(user) {
        const store = IdbSchema.DomainUser.Store;
        return this._withWriteStore(store, (userStore) => __awaiter(this, void 0, void 0, function* () {
            const userData = {
                userType: user.userId.userType,
                username: user.userId.username,
                displayName: user.displayName,
                firstName: user.firstName,
                lastName: user.lastName,
                email: user.email
            };
            yield toVoidPromise(userStore.put(userData));
        }));
    }
    getUsers() {
        return __awaiter(this, void 0, void 0, function* () {
            const store = IdbSchema.DomainUser.Store;
            const userData = yield this._getAll(store);
            return userData.map(user => new DomainUser(user.userType, user.username, user.firstName, user.lastName, user.displayName, user.email));
        });
    }
    putSession(sessionId, userId) {
        const store = IdbSchema.Session.Store;
        return this._withWriteStore(store, (sessionStore) => __awaiter(this, void 0, void 0, function* () {
            const data = {
                sessionId,
                userType: userId.userType,
                username: userId.username
            };
            yield toVoidPromise(sessionStore.put(data));
        }));
    }
    getSessions() {
        return __awaiter(this, void 0, void 0, function* () {
            const store = IdbSchema.Session.Store;
            const entries = yield this._getAll(store);
            const sessions = new Map();
            entries.forEach(entry => {
                sessions.set(entry.sessionId, new DomainUserId(entry.userType, entry.username));
            });
            return sessions;
        });
    }
}

class IdbStorageAdapter {
    constructor() {
        this._db = null;
        this._disposed = false;
    }
    adapterId() {
        return "Indexed DB Storage";
    }
    initialize(namespace, domainId, username) {
        if (!namespace) {
            throw new Error("namespace must be a non-empty string");
        }
        if (!domainId) {
            throw new Error("domain must be a non-empty string");
        }
        if (!username) {
            throw new Error("username must be a non-empty string");
        }
        const dbName = `${IdbStorageAdapter._DATABASE_NAME}:${namespace}/${domainId}/${username}`;
        const openRequest = indexedDB.open(dbName, IdbStorageAdapter._VERSION);
        openRequest.onupgradeneeded = () => {
            const db = openRequest.result;
            const version = openRequest.result.version;
            IdbSchemaManager.upgrade(db, version);
        };
        return toPromise(openRequest).then((db) => {
            this._db = db;
            this._modelStore = new IdbModelStore(this._db);
            this._identityStore = new IdbIdentityStore(this._db);
        });
    }
    isInitialized() {
        return this._db !== null;
    }
    modelStore() {
        return this._modelStore;
    }
    identityStore() {
        return this._identityStore;
    }
    destroy() {
        const name = this._db.name;
        this.dispose();
        indexedDB.deleteDatabase(name);
    }
    dispose() {
        if (!this.isInitialized()) {
            throw new Error("Can not disposed a storage adapter that is not initialized");
        }
        if (this.isDisposed()) {
            throw new Error("Already disposed");
        }
        this._db.close();
        this._db = null;
        this._disposed = true;
    }
    isDisposed() {
        return this._disposed;
    }
}
IdbStorageAdapter._DATABASE_NAME = "convergence.offline.storage";
IdbStorageAdapter._VERSION = 1;

export default Convergence;
export { Activity, ActivityParticipant, ActivityPermissionManager, ActivityService, ActivitySessionJoinedEvent, ActivitySessionLeftEvent, ActivityStateClearedEvent, ActivityStateDeltaEvent, ActivityStateRemovedEvent, ActivityStateSetEvent, ArrayInsertEvent, ArrayRemoveEvent, ArrayReorderEvent, ArraySetEvent, ArraySetValueEvent, AuthenticatingEvent, BooleanSetValueEvent, CancellationToken, Chat, ChatChannel, ChatEvent, ChatEventsMarkedSeenEvent, ChatHistoryEntry, ChatJoinedEvent, ChatLeftEvent, ChatMessageEvent, ChatNameChangedEvent, ChatPermissionManager, ChatRemovedEvent, ChatRoom, ChatService, ChatTopicChangedEvent, ChatTypes, CollaboratorClosedEvent, CollaboratorOpenedEvent, ConnectedEvent, ConnectingEvent, ConnectionFailedEvent, ConnectionScheduledEvent, Convergence, ConvergenceDomain, ConvergenceError, ConvergenceEventEmitter, ConvergenceServerError, ConvergenceSession, CreatedChatHistoryEntry, DateSetValueEvent, DirectChat, DisconnectedEvent, DomainUser, DomainUserId, DomainUserIdMap, DomainUserType, ElementDetachedEvent, ElementReference, ErrorEvent, HistoricalArray, HistoricalBoolean, HistoricalDate, HistoricalElement, HistoricalModel, HistoricalNull, HistoricalNumber, HistoricalObject, HistoricalString, HistoricalUndefined, IdbStorageAdapter, IdentityService, IndexReference, InterruptedEvent, LocalElementReference, LocalIndexReference, LocalModelReference, LocalPropertyReference, LocalRangeReference, LogLevel, MembershipChat, MessageChatHistoryEntry, ModelChangedEvent, ModelClosedEvent, ModelCollaborator, ModelCommittedEvent, ModelDeletedEvent, ModelModifiedEvent, ModelOfflineEvent, ModelOnlineEvent, ModelPermissionManager, ModelPermissions, ModelPermissionsChangedEvent, ModelReconnectingEvent, ModelReference, ModelResult, ModelService, ModelServiceEventConstants, NameChangedChatHistoryEntry, NumberDeltaEvent, NumberSetValueEvent, ObjectRemoveEvent, ObjectSetEvent, ObjectSetValueEvent, OfflineModelDeletedEvent, OfflineModelDownloadedEvent, OfflineModelPermissionsRevokedEvent, OfflineModelStatusChangedEvent, OfflineModelSyncCompletedEvent, OfflineModelSyncErrorEvent, OfflineModelSyncStartedEvent, OfflineModelUpdatedEvent, OfflineModelsDownloadStartedEvent, OfflineModelsDownloadStatusChangedEvent, OfflineModelsDownloadStoppedEvent, OfflineModelsSyncAbortedEvent, OfflineModelsSyncCompletedEvent, OfflineModelsSyncProgressEvent, OfflineModelsSyncStartedEvent, PagedData, PresenceAvailabilityChangedEvent, PresenceService, PresenceStateClearedEvent, PresenceStateRemovedEvent, PresenceStateSetEvent, PropertyReference, RangeReference, RealTimeArray, RealTimeBoolean, RealTimeDate, RealTimeElement, RealTimeModel, RealTimeNull, RealTimeNumber, RealTimeObject, RealTimeString, RealTimeUndefined, ReferenceClearedEvent, ReferenceDisposedEvent, ReferenceSetEvent, RemoteReferenceCreatedEvent, RemoteResyncCompletedEvent, RemoteResyncStartedEvent, ResyncCompletedEvent, ResyncErrorEvent, ResyncStartedEvent, StringInsertEvent, StringRemoveEvent, StringSetValueEvent, StringSpliceEvent, TopicChangedChatHistoryEntry, UserAddedChatHistoryEntry, UserAddedEvent, UserJoinedChatHistoryEntry, UserJoinedEvent, UserLeftChatHistoryEntry, UserLeftEvent, UserPresence, UserPresenceSubscription, UserRemovedChatHistoryEntry, UserRemovedEvent, VersionChangedEvent, configureLogging, connect, connectAnonymously, connectWithJwt, connectWithPassword, reconnect };
//# sourceMappingURL=convergence.esm.js.map
